import { commissionCopy } from "../../supabase/functions/_shared/clinicianSettlementPolicy.mjs";
export const LANGUAGE_MESSAGE_PATCHES = {
  "en-GB": {
    auth: {
      login: {
        landing: {
          languageChoice: "Choose language",
          languageEnglish: "English",
          languageFrench: "Français",
        },
      },
    },
    common: {
      actions: {
        close: "Close",
        print: "Print",
        download: "Download",
      },
      status: {
        saving: "Saving...",
      },
      cookies: {
        title: "AfriMed™ Cookie Preferences",
        intro:
          "Choose how optional cookies are used. Optional categories are off by default. Manage cookies here below.",
        support:
          "Designed to support GDPR consent standards and African Union data protection principles for transparency, purpose limitation, and user control.",
        close: "Close cookie preferences",
        alwaysActive: "Always active",
        rejectOptional: "Reject optional",
        saveChoices: "Save choices",
        acceptAll: "Accept all",
        categories: {
          strictly: {
            label: "Strictly",
            body:
              "Required for core functions such as security, session continuity, and accessibility. These cookies are always on.",
          },
          statistics: {
            label: "Statistics",
            body:
              "Measures aggregated usage so we can improve service quality and understand public-health demand. We do not sell personal data.",
          },
          performance: {
            label: "Performance",
            body:
              "Helps us monitor service speed, stability, and resilience across devices and networks.",
          },
          functionality: {
            label: "Functionality",
            body:
              "Remembers choices like language and region so your return experience is smoother.",
          },
          targeting: {
            label: "Targeting",
            body:
              "Used to deliver or limit campaign relevance. We do not sell personal data.",
          },
          social: {
            label: "Social",
            body:
              "Enables social sign-in and social content integrations. Third-party providers may apply their own policies.",
          },
          beacons: {
            label: "Beacons",
            body:
              "Small tags used to understand message delivery and engagement where needed.",
          },
        },
      },
    },
    regionalHealthSignals: {
      loading: "Loading Regional Health…",
      loadErrorTitle: "Regional Health is unavailable",
      loadErrorBody: "The authorised Institution workspace could not be loaded. No external notification has been sent.",
      retry: "Try again",
      title: "Regional Health",
      tabs: { overview: "Overview", registry: "Signal registry", quality: "Data quality", audit: "Audit trail" },
      actions: { report: "Report signal", cancel: "Cancel", saveDraft: "Save draft", submit: "Submit for review", needsReview: "Mark needs review", return: "Return for clarification", reconcile: "Reconcile", close: "Close", closeSignal: "Close signal", reasonPrompt: "Reason for clarification" },
      boundary: { kicker: "Institution evaluation", aggregate: "Aggregate information only. Do not enter patient-identifying information.", external: "No external notification is sent automatically.", externalState: "External notification not connected" },
      readiness: { noFacilityTitle: "No authorised reporting facility", noFacilityBody: "A facility must be enrolled and scoped before a signal can be filed." },
      metrics: { facilityReadiness: "Facility reporting readiness", lastActivity: "Last signal activity" },
      overview: { workflowTitle: "Internal signal workflow", workflowBody: "Record an aggregate signal, submit it for internal review, reconcile it with an auditable decision, and close it when the Institution has completed its review." },
      registry: { emptyTitle: "No signals in this Institution scope", emptyBody: "Report an aggregate health signal when an authorised reporting facility is available.", columns: { reference: "Signal reference", reportDate: "Reported date", facility: "Facility", programme: "Programme", eventCategory: "Event category", classification: "Classification", affected: "Aggregate affected count", status: "Status", updated: "Last updated", actions: "Actions" }, view: "View" },
      quality: { title: "Data-quality checks", empty: "No unresolved data-quality flags in this Institution scope." },
      flags: { date_order: "Report date is before event date.", duplicate_candidate: "Possible duplicate: same facility, category, and nearby event date.", incomplete_draft: "Draft is incomplete and cannot be submitted.", awaiting_review: "Submitted signal is awaiting internal review." },
      audit: { title: "Audit trail", empty: "No signal audit events in this Institution scope.", created: "Created", edited_draft: "Draft edited", submitted: "Submitted for review", needs_review: "Marked needs review", returned_for_clarification: "Returned for clarification", reconciled: "Reconciled", closed: "Closed", data_quality_flag_raised: "Data-quality flag raised", data_quality_flag_resolved: "Data-quality flag resolved" },
      navigation: { regionalHealth: "Regional Health" },
      form: { title: "Report signal", facility: "Reporting facility", selectFacility: "Select facility", selectOption: "Select an option", programme: "Programme (where applicable)", noProgramme: "No programme", eventCategory: "Event category", eventCategoryPlaceholder: "For example: unusual increase in fever", classification: "Classification", eventDate: "Event/onset date", reportDate: "Report date", affected: "Aggregate affected count", deaths: "Aggregate deaths count", narrative: "Concise aggregate narrative", narrativePlaceholder: "No names, identifiers, contact details, or clinical notes." },
      detail: { title: "Signal detail", facility: "Facility", event: "Event category", classification: "Classification", aggregate: "Aggregate affected / deaths", eventDate: "Event date", recordMode: "Record mode", external: "External notification" },
      status: { draft: "Draft", submitted: "Submitted", needsReview: "Needs review", reconciled: "Reconciled", closed: "Closed" },
      classification: { suspected: "Suspected", probable: "Probable", confirmed_external: "Confirmed by an external authority", other_unknown: "Other/unknown" },
      language: { title: "Language", english: "English", french: "Français" },
    },
    institutionShell: {
      search: "Search",
      searchInstitution: "Search Institution",
      navigation: { dashboard: "Dashboard", clinicians: "Clinicians", members: "Members", surveillanceTeam: "Surveillance Team", services: "Services", regionalHealth: "Regional Health", patients: "Patients", settings: "Settings", documentation: "Documentation", messages: "Messages" },
      notifications: { title: "Notifications", support: "Institution activity and member alerts.", empty: "No Institution alerts yet." },
      account: { me: "Me", myAccount: "My account" },
      theme: { title: "Theme", dark: "Dark", light: "Light", system: "System" },
      language: { title: "Language", english: "English", french: "Français" },
      actions: { makeSuggestion: "Make suggestion", signOut: "Sign out" },
      accessibility: { openNavigation: "Open navigation", closeNavigation: "Close navigation", navigation: "Institution navigation", dashboardSections: "Institution dashboard sections" },
    },
    institutionHome: {
      title: "Home", loading: "Loading Institution Home…", loadError: "Institution history could not be loaded. Try again from an authorised Institution workspace.", unavailable: "Institution Home is unavailable until the authorised workspace can be read.",
      recentInstitutions: "Recently viewed institutions", recentMembers: "Recently viewed members", emptyInstitutions: "Opened authorised Medical Institutions appear here.", emptyMembers: "Explicitly opened authorised Members appear here.", browseInstitutions: "Browse authorised Medical Institutions", openMembers: "Open Institution Members", setupStatusTitle: "MoH workspace setup", setupStatusBody: "Your authorised Institution is ready to explore. Complete facilities and roster intake when you are ready.", completeSetup: "Complete setup", visit: "Visit", institutionFallback: "Medical Institution", memberFallback: "Institution member",
    },
    mohOnboarding: {
      workspaceTitle: "Government and workspace details", reviewTitle: "Explore before commitment", reviewIntro: "You can explore the authorised Institution workspace without saving administrator, government, facility, roster, Clinician, Member, Patient, or CSV data.", continueExploring: "Continue exploring", exploreFirst: "Explore AfriMed™ first", completeSetup: "Complete setup", completeValidation: "Enter the required administrator and government details before completing setup.", explorationWorkspaceName: "AfriMed™ MoH exploration workspace", exploreError: "The authorised MoH exploration workspace could not be opened.",
      loading: "Loading authorised MoH setup…", title: "Administrator details", intro: "Create an authorised Institution workspace to explore AfriMed™. Facility and roster intake can be completed later.", fullName: "Full name*", email: "Email*", phone: "Phone*", verificationReadiness: "Email and phone verification are prepared for committed setup. Exploring does not send verification messages.", government: "Government*", governmentPlaceholder: "For example: Government of Botswana", department: "Ministry / Department*", departmentPlaceholder: "For example: Ministry of Health", useCurrentLocation: "Use my current location", locating: "Locating…", locationLookupFailed: "We couldn’t find your address. Enter it manually or try again.", address1: "Address line 1", address2: "Address line 2", city: "City", region: "Region / State", country: "Country*", emailValidation: "Use the official government email for the selected country.", intakeTitle: "Complete the roster later", intakeBody: "Facility, operational Member and Clinician intake is optional at this stage. It will use a governed preview-and-commit workflow; no CSV is uploaded from this screen.", explore: "Explore Institution Home", completeLater: "Complete setup later", saving: "Saving…", saveReturn: "Save and return to Institution Home", saveError: "The authorised MoH workspace could not be saved.", resumeMissingWorkspace: "No authorised MoH workspace was selected.", resumeDenied: "This MoH setup is not available for the active authorised Institution.",
    },
    institutionNotifications: {
      title: "Notifications", unavailableTitle: "Institution notifications are not enabled yet", unavailableBody: "This workspace does not query personal or cross-Institution notifications. Institution-scoped delivery will appear here once its governed recipient and authority contract is available.",
    },
    institutionHelp: {
      title: "Documentation", intro: "Product documentation for the active Institution workspace. It does not grant additional access or replace local legal, clinical, or governance requirements.", back: "Back to Documentation", authoredSource: "Authored source", articlesLabel: "Institution documentation areas",
      status: { current: "Current capability", inProgress: "In progress / partially connected", planned: "Planned / future capability", governance: "Governance boundary" },
      areas: {
        gettingStarted: { title: "Getting started", summary: "Orient yourself in the authorised Institution workspace, facilities, members, and governed access." },
        clinicalOperations: { title: "Clinical operations", summary: "Institution care reuses AfriMed Global identities and care engines. Governed care launch and appointment provenance are in progress." },
        regionalHealth: { title: "Regional Health", summary: "Overview, signal registry, data quality, audit trail, map, reporting readiness, programme scope, and DHIS2-readiness guidance." },
        governanceData: { title: "Governance & data", summary: "Institution authority, lawful basis, audit, scope, privacy, and the aggregate-only Regional Health boundary." },
        integrations: { title: "Integrations", summary: "DHIS2-readiness foundation and future governed integration boundaries. No production reporting or data exchange is implied." },
        communityProgrammes: { title: "Community & programmes", summary: "Future programme participation, CHW workflows, community operations, longitudinal research, and protocol-governed surveillance." },
      },
    },
    institutionClinicians: {
      credentialEvidence: "Credential evidence", credentialEvidenceSupport: "Documents are reviewed after the clinician has claimed their profile.", supplied: "Supplied", pendingReview: "Pending review", done: "Done",
      title: "Clinicians", new: "New Clinician", newClinician: "New Clinician", search: "Search clinicians", noResults: "No clinicians match “{query}”.”", backToClinicians: "Back to Clinicians",
      columns: { name: "Name", email: "Email", phone: "Phone", professionSpecialty: "Profession / specialty", status: "Status" },
      filters: { status: "Filter clinicians by status", open: "Filter clinicians", title: "Filter clinicians", allFacilities: "All facilities", allProfessions: "All professions", allSpecialties: "All specialties" },
      fields: { facility: "Facility", profession: "Profession", specialty: "Specialty", firstName: "First name", lastName: "Last name", email: "Email", phoneNumber: "Phone number", countryCode: "Country code", gender: "Gender", dateOfBirth: "Date of birth", yearsOfExperience: "Years of experience", registrationNumber: "Registration number", registrationDate: "Registration date", registrationYear: "Registration year", registrationCountry: "Registration country", assignedFacilities: "Assigned facilities" },
      actions: { add: "Add", edit: "Edit", cancel: "Cancel", save: "Save", clear: "Clear", apply: "Apply", copyLink: "Copy link", openInNewTab: "Open in new tab", blockClinician: "Block Clinician", delete: "Delete" },
      status: { all: "All", draft: "Draft", pendingInvite: "Pending", invited: "Invited", onboarding: "Onboarding", active: "Active", suspended: "Blocked", removed: "Removed" },
      basicInformation: "Basic Information", specialty: "Specialty", addSpecialty: "Add the specialty of the clinician", specialtySupport: "Make sure to have certification documents at hand.", addDiploma: "Add your diploma", focusOn: "Focus On", focusSupport: "Medical areas of knowledge", formation: "Formation", formationEmpty: "Add the highest level of education and any relevant professional qualifications.", languages: "Languages", languagesEmpty: "Add the language spoken by the clinician.", qualification: "Qualification", institution: "Institution", country: "Country", year: "Year", noFacilities: "Create a Medical Institution first", noClinicians: "No clinicians yet", noCliniciansBody: "Add clinicians manually now. Future MoH and team CSV imports will create the same pending roster relationships.", noFacilitiesBody: "A clinician relationship must be assigned to one or more Medical Institutions in this parent workspace.", createMedicalInstitution: "Create Medical Institution", noFilterResults: "No clinicians match this filter", detailsPending: "Details pending", notSupplied: "Not supplied", selectDate: "Select date", previouslyRecordedYear: "Previously recorded year", female: "Female", male: "Male", nonBinary: "Non-binary", preferNotToSay: "Prefer not to say", native: "Native", professional: "Professional", working: "Working", basic: "Basic", moreActions: "More clinician actions", blockScope: "This blocks only this Institution relationship.", deleteScope: "This removes this Institution relationship while preserving the clinician account and records.", reason: "Reason", assignedFacilities: "Assigned facilities", activeMedicalInstitution: "Active Medical Institution", select: "Select", selected: "Selected", selectFacilities: "Select facilities", facilitySelectionRequired: "Select at least one active Medical Institution before saving.", avatar: "Clinician avatar", addFormation: "Add qualification", alreadyExists: "This clinician already has an Institution roster relationship.", updated: "Institution clinician relationship updated.", savedDraft: "Clinician roster draft saved. No account or invitation was created.", saveError: "The clinician relationship could not be saved.", statusError: "The Institution relationship could not be changed.", linkCopied: "Internal clinician relationship link copied.", linkCopyFallback: "Copy the clinician relationship URL from your browser address bar.", facilityAssignmentSupport: "A clinician relationship requires one or more active Medical Institutions in this parent workspace.", searchGroup: "Clinicians",
    },
    institutionServices: {
      title: "Services", catalogue: "Institution service catalogue", new: "New Service", search: "Search services", searchPlaceholder: "Search services", backToServices: "Back to Services", loading: "Loading Services…", retry: "Try again", missing: "This service is unavailable in the active parent Institution.", archived: "This service is archived and is no longer in the active catalogue.", archivedSuccess: "Service archived", writeDenied: "You do not have permission to manage this parent service catalogue.", notSupplied: "Not supplied", created: "Service Created", updated: "Service Updated",
      columns: { service: "Service", specialty: "Specialty", active: "Active", notes: "Notes", price: "Price" },
      mobileFields: { service: "Service", specialty: "Specialty", status: "Status", price: "Price", facility: "Medical Institution" },
      fields: { serviceName: "Service Name", specialty: "Specialty", price: "Price", status: "Status", currency: "Currency", notes: "Indications & Notes", facility: "Medical Institution", assignedFacilities: "Assigned facilities" },
      filters: { status: "Filter services by status", open: "Filter services", title: "Filter services", allSpecialties: "All specialties", allFacilities: "All Medical Institutions" },
      actions: { save: "Save", saving: "Saving…", cancel: "Cancel", clear: "Clear", apply: "Apply", done: "Done", discard: "Discard", copyLink: "Copy link", openInNewTab: "Open in new tab", delete: "Delete" },
      status: { all: "All", active: "Active", inactive: "Inactive" },
      basicInformation: "Basic Information", selectSpecialty: "Select specialty", selectFacilities: "Select Medical Institutions", facilityCount: "{count} selected", activeMedicalInstitution: "Active Medical Institution", select: "Select", selected: "Selected", noFacilities: "No Medical Institutions assigned", noFacilitiesTitle: "Create a Medical Institution first", noFacilitiesBody: "Add a Medical Institution before adding services.", emptyTitle: "No services yet", emptyBody: "Add the services your Medical Institution provides to patients.", noResultsTitle: "No services match this filter", noResultsBody: "Try a different filter.", facilityAssignmentSupport: "A service requires one or more active Medical Institutions in this parent workspace.", moreActions: "More service actions", linkCopied: "Institution service link copied.", linkCopyFallback: "Copy the Institution service URL from your browser address bar.", discardTitle: "Discard changes?", discardBody: "Are you sure you want to discard changes made? This action cannot be undone.", archiveTitle: "Delete “{name}” Service?", archiveBody: "This service will be deleted from your Medical Institution. This action cannot be undone.", createError: "The service could not be created.", archiveError: "The service could not be archived.", loadError: "Services could not be loaded.", delivery: { title: "Assigned clinicians", description: "Clinicians are authorised to deliver this Institution service at the assigned facility.", assign: "Assign clinician", assignSupport: "Choose a service facility, then an eligible clinician already linked to that facility.", loading: "Loading clinician delivery assignments…", loadError: "Clinician delivery assignments could not be loaded.", emptyTitle: "No clinicians assigned", emptyBody: "Assign an eligible Institution clinician to an authorised facility before this service can be deliverable.", inactiveService: "This service is inactive and cannot be operationally deliverable.", facility: "Facility", clinician: "Clinician", selectFacility: "Select a facility", selectClinician: "Select an eligible clinician", selectFacilityFirst: "Choose a facility first", noneEligible: "No eligible active Institution clinicians are linked to this facility.", effectiveFrom: "Effective from", effectiveTo: "Effective to (optional)", assigned: "Clinician assigned", createError: "The clinician could not be assigned to this service.", credentialWarning: "Credential evidence: {state}", effective: "Effective {from}{until}", unknownFacility: "Authorised facility", unknownClinician: "Linked clinician", states: { actively_deliverable: "Actively deliverable", catalogue_unassigned: "No clinicians assigned", delivery_unavailable: "Delivery unavailable", inactive_service: "Service inactive", archived_service: "Service archived", inactive_assignment: "Assignment inactive", inactive_clinician_relationship: "Clinician relationship inactive", facility_unavailable: "Facility unavailable", facility_clinician_relationship_inactive: "Clinician facility relationship inactive", assignment_expired: "Assignment expired", assignment_not_yet_effective: "Not yet effective", archived_assignment: "Assignment archived" } } },
    },
    navigation: {
      tabs: {
        home: "Home",
        dashboard: "Dashboard",
        appointments: "Appointments",
        messages: "Messages",
        profile: "Profile",
      },
    },
    admin: {
      brand: {
        ariaLabel: "AfriMed Admin",
      },
      actions: {
        openNavigation: "Open navigation",
      },
      nav: {
        kpi: "KPI",
        kycReview: "KYC Review",
        feedback: "Feedback",
      },
      menu: {
        me: "Me",
        meMenu: "Me menu",
        uploadImage: "Upload image",
        myAccount: "My account",
        kpiDashboard: "KPI dashboard",
        kycReview: "KYC review",
        signOut: "Sign out",
        signingOut: "Signing out...",
      },
      theme: {
        title: "Theme",
        option: {
          dark: "Dark",
          light: "Light",
          system: "System",
        },
      },
      search: {
        placeholder: "Search KPI, KYC, users",
      },
      notifications: {
        title: "Notifications",
        support: "Admin alerts and feedback follow-up items will appear here.",
        empty: "No admin alerts yet.",
        close: "Close notifications",
      },
      account: {
        fallbackEmail: "Admin account",
        avatarSupport: "Avatar follows your AfriMed account photo when available.",
      },
      feedback: {
        title: "Feedback inbox",
        loading: "Loading feedback...",
        read: "Read",
        unknownRoute: "Unknown route",
        generalSection: "General section",
        error: {
          load: "Could not load feedback.",
        },
        actions: {
          refresh: "Refresh",
          markRead: "Mark read",
        },
        type: {
          suggestion: "Suggestion",
          issue: "Issue",
          urgentIssue: "Urgent issue",
        },
      },
      kyc: {
        title: "KYC review",
        subtitle: "Identity uploads waiting for admin review.",
        queueTitle: "Verification uploads",
        queueSupport:
          "Review actual stored identity uploads, preview documents, and move rows to approved or rejected truth.",
        awaitingReview: "Uploads awaiting review",
        latestUpload: "Latest upload {timestamp}",
        noStoredUploadsYet: "No stored identity uploads yet.",
        idStepWithoutUpload: "Reached ID step without stored upload",
        idStepWithoutUploadSupport:
          "This means the user reached the onboarding ID checkpoint, not that a document file exists for review.",
        loading: "Loading...",
        unknownMime: "unknown",
        emptyQueue: "No document uploads awaiting review.",
        emptyQueueSupport: "{count} users reached the ID step without a stored upload.",
        rawStatus: "Raw status: {status}",
        previewAlt: "Identity document preview",
        states: {
          identitySubmitted: "Identity submitted",
          ocrPending: "OCR pending",
          ocrCompleted: "OCR completed",
          verified: "Verified",
          rejected: "Rejected",
          unknown: "Unknown",
        },
        table: {
          when: "When",
          user: "User",
          file: "File",
          status: "Status",
          actions: "Actions",
        },
        actions: {
          approve: "Approve",
          reject: "Reject",
          openDocument: "Open document",
        },
      },
      kpi: {
        signup: {
          step: {
            signup: "Signup",
          },
          summary: {
            activeOrRecentlyActive: "Active / recently active",
          },
          state: {
            activeTesterRecentlyEngaged: "Active tester / recently engaged",
            onboardingCompleted: "Onboarding completed",
            onboardingInProgress: "Onboarding in progress",
            verifiedNoOnboardingProgress: "Verified / no onboarding progress",
            signupOnlyNoVerifiedLogin: "Signup only / no verified login",
            stoppedDuringOnboarding: "Stopped during onboarding",
            completionPersistenceIssue: "Completion persistence issue",
          },
          followUp: {
            notNeeded: "Follow-up not needed",
            recommended: "Follow-up recommended",
            internalFixRequired: "Internal fix required",
          },
          status: {
            followUpNotNeeded: "Follow-up not needed",
            followUpRecommended: "Follow-up recommended",
            internalFixRequired: "Internal fix required",
            activeTesterRecentlyEngaged: "Active tester / recently engaged",
            accessReadyRecentlyActive: "Access ready / recently active",
            signupOnlyNoVerifiedLogin: "Signup only / no verified login",
            stoppedDuringOnboarding: "Stopped during onboarding",
            completionPersistenceIssue: "Completion persistence issue",
          },
          blocker: {
            completedSuccessfully: "Completed successfully",
            completedAccessNotReady: "Completed, but access setup is not ready",
            recentEngagement: "Recent engagement detected",
            recentAccess: "Recent access detected",
            noVerifiedLogin: "No verified login yet",
            completionPersistence: "Completion state did not persist",
            stoppedAfterStep: "Stopped after {stepLabel}",
          },
          diagnostic: {
            recentMeaningfulActivity:
              "Recent appointment, consultation, or prescription activity exists for this user.",
            completedFlagTrue:
              "The completion flag is persisted and no recent blocker is visible.",
            completionFlagMissing:
              "A completion step was recorded, but the completion flag did not persist.",
            noVerifiedLogin:
              "No confirmed login or verified email signal has been recorded yet.",
            verifiedRecentAccessNoProgress:
              "The user has verified access, but no onboarding step has persisted yet.",
            recentOnboardingProgress:
              "A recent onboarding step was recorded, so this user should not be chased yet.",
            onboardingStale:
              "The user stopped after the latest persisted onboarding step.",
            completionPersistenceIssue:
              "Reached the completion step, but the completion flag did not persist.",
            completedAccessPending:
              "Onboarding completed, but access setup is still incomplete.",
            onboardingComplete: "Onboarding completed and access is ready.",
            signupOnlyNoVerifiedLogin: "Signed up only. No verified login yet.",
            recentEngagementDetected:
              "Recent activity exists after signup, so outreach should pause while usage is monitored.",
            recentAccessDetected:
              "Verified access or a recent sign-in exists after signup, but no downstream care activity has been recorded yet.",
            stoppedAfterStep: "Stopped after {stepLabel}.",
            activeTesterWithSource:
              "Recent {activityLabel} activity exists after signup, so outreach should pause while usage is monitored.",
            manualReview:
              "Signed up, but no recent verified activity or completion signal is available.",
          },
          action: {
            waitAndMonitorActivity: "Pause outreach and monitor continued usage.",
            noFollowUpCompleted:
              "No follow-up is needed unless you want explicit product feedback.",
            inspectCompletionPersistence:
              "Review completion persistence before any outreach.",
            noFollowUpRecentAccess:
              "Do not follow up yet. Recent verified access is already visible.",
            waitOnboardingInProgress:
              "Allow the current onboarding session to continue before intervening.",
            reviewCompletionPersistence:
              "Do not email yet. Review completion persistence first.",
            fixAccessSetup: "Fix access setup before outreach.",
            holdOutreachCollectFeedback:
              "No follow-up needed unless you want explicit usage feedback.",
            sendLoginReminder: "Send a login reminder.",
            holdOutreachMonitorUsage: "Pause outreach and watch for continued usage.",
            holdOutreachWatchAccess: "Pause outreach and watch for downstream usage.",
            sendOnboardingReminder: "Send an onboarding continuation reminder.",
            manualReview:
              "Review manually and then decide whether follow-up is needed.",
          },
          activity: {
            consultationReport: "Consultation report",
            prescription: "Prescription",
            consultation: "Consultation",
            appointment: "Appointment",
            onboardingStep: "Onboarding step",
            passcodeReady: "Passcode setup",
            login: "Login",
            emailVerified: "Email verified",
            signup: "Signup",
            ehrAccess: "Health record access",
            recentAccess: "recent access",
          },
        },
      },
    },
    auth: {
      callback: {
        finishing: "Finishing sign-in...",
        redirectingToLogin: "Redirecting to login to request a new sign-in link...",
        validating: "Validating sign-in link...",
        verifyingCode: "Verifying sign-in code...",
        linkNoLongerValidOnDevice:
          "This sign-in link is no longer valid on this device. Request a new secure sign-in link and open it on the same browser.",
        linkVerificationFailed:
          "Sign-in link could not be verified. Please request another sign-in link.",
        sessionEmailMismatch: "Session email mismatch after secure-link verification.",
        sessionNotEstablished: "Session not established.",
        checkingProfile: "Signed in. Checking your profile...",
        pharmacyDashboardAs:
          "Logging you in to Pharmacy Dashboard as {email}... moment please.",
        pharmacyDashboard: "Logging you in to Pharmacy Dashboard... moment please.",
        takingToAfrimed: "Signed in. Taking you to AfriMed™...",
        takingToAdmin: "Welcome back. Taking you to AfriMed admin...",
        takingHome: "Welcome back. Taking you to Home...",
        takingOnboarding: "Signed in. Taking you to onboarding...",
        resolvingAccountType: "Resolving account type...",
        takingBack: "Signed in. Taking you back...",
        chooseHowToContinue: "Signed in. Choose how you want to continue...",
        slowHint:
          "Still verifying sign-in code... If this takes too long, request another sign-in link.",
        timeout:
          "Sign-in could not be completed. Please request another sign-in link.",
        differentSession:
          "This sign-in link belongs to a different session on this browser. Please request a new sign-in link and open only the latest one.",
        pharmacyEmailNotLinked: "This email is not linked to a pharmacy branch yet.",
        pharmacyLinkFailed:
          "We couldn't complete that pharmacy sign-in link. Please request a fresh secure sign-in link.",
        linkExpired: "This sign-in link has expired",
        linkExpiredBody: "Please request a new secure sign-in link to continue.",
        browserMismatch:
          "Sign-in link could not be validated on this browser. Request another sign-in link and open the latest one in the same browser.",
        failed:
          "Sign-in failed. Please try again or request another sign-in link.",
        requestAnotherLink: "Request new sign-in link",
      },
    },
    appointments: {
      missedDialog: {
        title: "Missed appointment",
        description:
          "Participants for this appointment did not arrive within the booked time window. This appointment is no longer available. If you still need care, please choose a new available time and make a fresh booking.",
      },
    },
    notifications: {
      filters: {
        all: "All",
        urgent: "Urgent",
        unread: "Unread",
        appointments: "Appointments",
      },
      common: {
        title: "Notifications",
        today: "Today",
        notification: "Notification",
        patient: "Patient",
        openDetails: "Open to view details.",
        loading: "Loading notifications...",
        emptyTitle: "No notifications to display",
        emptyBody: "Your inbox is currently empty. Check back later!",
        errors: {
          loadFailed: "Could not load notifications.",
        },
      },
      doctor: {
        titles: {
          newAppointment: "New appointment",
          cancellationRequest: "Cancellation request",
          appointmentUpdated: "Appointment updated",
        },
        support: {
          bookingAccepted: "{name}'s appointment request was approved.",
          bookingDeclined: "{name}'s appointment request was declined.",
          bookingPending: "{name} requested an appointment. Pending for your approval.",
          rescheduleAccepted: "Appointment with {name} has been rescheduled.",
          rescheduleDeclined: "New appointment time request from {name} was declined.",
          reschedulePending:
            "{name} requested a new appointment time. Pending for your approval.",
          cancellationAccepted: "{name}'s cancellation request was approved.",
          cancellationDeclined: "{name}'s cancellation request was declined.",
          cancellationPending:
            "{name} requested to cancel appointment. Pending for your approval.",
        },
      },
      title: "Notifications",
      loadingSingle: "Loading notification...",
      singleTitle: "Notification",
      openDetailHelp: "Open the details to review this update.",
      pendingSchedule: "Pending schedule",
      time: {
        today: "Today",
        todayAt: "Today {time}",
      },
      fields: {
        doctor: "Doctor",
        patient: "Patient",
        assignedDoctor: "Assigned doctor",
        consultationLocation: "Consultation location",
        dateTime: "Date and time",
        location: "Location",
        details: "Details",
      },
      headers: {
        newAppointment: "New appointment",
        newAppointmentTimeRequest: "New appointment time request",
        cancellationRequest: "Cancellation request",
        appointmentUpdate: "Appointment update",
        cancellationRequestSent: "Cancellation request sent",
        appointmentConfirmed: "Appointment confirmed",
        appointmentCancelled: "Appointment cancelled",
        appointmentDeclined: "Appointment declined",
        cancellationRequestDeclined: "Cancellation request declined",
        newTimeRequestApproved: "New time request approved",
        newTimeRequestDeclined: "New time request declined",
        upcomingAppointment: "Upcoming appointment",
        ehrAccessRequest: "EHR access request",
      },
      actions: {
        accept: "Accept",
        decline: "Decline",
        grantAccess: "Grant access",
      },
      status: {
        accepted: "Accepted",
        declined: "Declined",
        granted: "Granted",
        granting: "Granting...",
        unavailable: "Unavailable",
      },
      dialogs: {
        cancelAppointmentTitle: "Cancel appointment?",
        cancelAppointmentBody: "Are you sure you want to cancel this appointment?",
        declineAppointmentTitle: "Decline appointment?",
        declineAppointmentBody: "Are you sure you want to decline this appointment request?",
        confirmCancel: "Yes, cancel",
        confirmDecline: "Yes, decline",
      },
      errors: {
        signInAgain: "Please sign in again.",
        notFound: "Notification not found.",
        emptyInbox: "Your inbox is currently empty. Check back later!",
        load: "Could not load notification.",
        ehrGrant: "Could not grant EHR access right now.",
        cancelAppointment: "Could not cancel the appointment right now.",
        acceptAppointment: "Could not accept the appointment.",
        declineAppointment: "Could not decline the appointment.",
        approveNewTimeRequest: "Could not approve the new time request.",
        declineNewTimeRequest: "Could not decline the new time request.",
        acceptAppointmentChange: "Could not accept the appointment change right now.",
        declineAppointmentChange: "Could not decline the appointment change right now.",
      },
      toast: {
        appointmentAccepted: "Appointment accepted",
        appointmentDeclined: "Appointment declined",
        appointmentUpdated: "Appointment updated",
        appointmentCancelled: "Appointment cancelled",
        yourAppointmentUpdated: "Your appointment was updated",
        cancellationApproved: "Cancellation approved",
        cancellationDeclined: "Cancellation declined",
        cancellationRequestSent: "Cancellation request sent",
        cancellationApprovedRefundProcessing: "Cancellation approved. Refund is processing.",
        cancellationApprovedReversalFollowUp:
          "Cancellation approved. Payment reversal needs follow-up.",
        cancellationApprovedReversalAttention:
          "Cancellation approved. Payment reversal still needs attention.",
        appointmentDeclinedRefundProcessing: "Appointment declined. Refund is processing.",
        appointmentDeclinedReversalFollowUp:
          "Appointment declined. Payment reversal needs follow-up.",
        appointmentDeclinedReversalAttention:
          "Appointment declined. Payment reversal still needs attention.",
      },
      messages: {
        acceptedNewTime: "You accepted the new appointment time.",
        patientAcceptedNewTime: "Patient accepted the new appointment time.",
        declinedNewTime: "You declined the new appointment time.",
        patientDeclinedNewTime: "Patient declined the new appointment time.",
        doctorProposedNewTime:
          "Your doctor proposed a new appointment time. Review and confirm the change.",
        ehrAccessRequested:
          "Your doctor requested access to your health record for this appointment.",
        upcomingAppointmentForDate: "Your appointment is scheduled for {date}.",
        upcomingAppointmentGeneric: "Your appointment is scheduled.",
        bookingApprovedForDate: "{name}'s appointment request for {date} was approved.",
        bookingApprovedGeneric: "{name}'s appointment request was approved.",
        bookingPendingForDate:
          "{name} requested an appointment for {date}. Pending for your approval.",
        yourAppointmentAcceptedForDate: "Your appointment for {date} was accepted.",
        yourAppointmentAcceptedGeneric: "Your appointment request was accepted.",
        bookingDeclinedForDate: "{name}'s appointment request for {date} was declined.",
        bookingDeclinedGeneric: "{name}'s appointment request was declined.",
        yourAppointmentDeclinedForDate: "Your appointment for {date} was declined.",
        yourAppointmentDeclinedGeneric: "Your appointment request was declined.",
        cancellationApprovedForDate:
          "{name} requested to cancel appointment for {date}. Approved.",
        cancellationApprovedGeneric: "{name} requested to cancel appointment. Approved.",
        yourCancellationApprovedForDate: "Your cancellation request for {date} was approved.",
        yourCancellationApprovedGeneric: "Your cancellation request was approved.",
        cancellationDeclinedForDate:
          "{name} requested to cancel appointment for {date}. Declined.",
        cancellationDeclinedGeneric: "{name} requested to cancel appointment. Declined.",
        yourCancellationDeclinedForDate: "Your cancellation request for {date} was declined.",
        yourCancellationDeclinedGeneric: "Your cancellation request was declined.",
        rescheduleApprovedGeneric: "{name} requested a new appointment time. Approved.",
        yourRescheduleApprovedGeneric: "Your new appointment time request was approved.",
        rescheduleDeclinedGeneric: "{name} requested a new appointment time. Declined.",
        yourRescheduleDeclinedGeneric: "Your new appointment time request was declined.",
        cancellationRequestSentForDate:
          "Your request to cancel the appointment for {date} was sent.",
        cancellationRequestSentGeneric: "Your request to cancel this appointment was sent.",
        patientCancellationPendingForDate:
          "{name} requested to cancel appointment for {date}. Pending for approval.",
        patientCancellationPendingGeneric:
          "{name} requested to cancel appointment. Pending for approval.",
      },
      goToAppointment: "Open appointment",
      goToAppointmentDetails: "Open appointment details",
      referral: {
        label: "Referral",
        openAction: "Open referral",
      },
    },
    messages: {
      label: "Message",
      openAction: "Open message",
      loadingConversation: "Loading conversation...",
      noMessagesYet: "No messages yet.",
      inbox: {
        fallbackTitle: "Conversation",
      },
      conversation: {
        now: "Now",
        deletedForMe: "You deleted this message",
        deletedBySender: "Sender deleted message",
        deleteDialog: {
          close: "Close delete dialog",
          title: "Delete message?",
          body:
            "Delete this message for you only, or remove it for both participants. This action cannot be undone.",
          deleting: "Deleting...",
          forMe: "Delete for me",
          forBoth: "Delete for both",
        },
        toast: {
          dismiss: "OK",
          undo: "Undo",
          deletedForMe: "Message deleted for me",
          deletedForBoth: "Message deleted for both",
          deleteError: "Could not delete the message right now.",
        },
        composer: {
          all: "All",
          attach: "Attach photo",
          send: "Send message",
          thinking: "Fola is thinking",
          selectedAttachmentAlt: "Selected attachment",
        },
        topBar: {
          back: "Back to messages",
          openFola: "Open Fola conversation",
          compose: "Compose message",
        },
        sourceThreadFallback: "Source thread",
        errors: {
          resolve: "Could not resolve this conversation.",
          load: "Could not load this conversation.",
          send: "Could not send the message.",
        },
        fulfillmentFola: {
          selectedPharmacy: "your pharmacy",
          responses: {
            reviewing:
              "Your prescription is currently being reviewed by {pharmacy_name}. You will be notified once it is ready.",
            readyForPickup: "Your prescription is ready for pickup at {pharmacy_name}.",
            pendingSelection: "Please select a pharmacy to continue with your prescription.",
            priced:
              "Your prescription has been priced. Please proceed with payment to continue.",
            fallback:
              "I'm here to help with your prescription. You can ask for status updates or next steps.",
          },
        },
      },
    },
    fola: {
      widget: {
        title: "Fola - AfriMed™ Assistant",
        greeting: "Hi, I am Fola. I can help with OTP, magic links, and settings.",
        open: "Open Fola",
        placeholder: "Ask Fola...",
        sending: "Sending...",
        send: "Send",
        error: "Sorry, I hit a snag. Try again.",
      },
    },
    pharmacy: {
      invite: {
        title: "Pharmacy invitation",
        subtitle:
          "Review your AfriMed Pharmacy team invite, confirm the invited email, then request the secure sign-in link for that exact pharmacy member account.",
        body:
          "AfriMed will use the invited email below for the pharmacy member sign-in step. Open the newest sign-in email on this same browser or device, then AfriMed will continue to the Pharmacy Dashboard.",
        demoSeedCopy:
          "After launching your Pharmacy Dashboard, you will find seeded demo stock and up to three sample prescription fulfilment requests. Please use these to explore how AfriMed™ supports pharmacy preparation, verification, dispensing, payments, and fulfilment workflows during staging.",
        emailLabel: "Invited email",
        emailMissing: "No invited email was attached to this link.",
        emailMissingBody:
          "This invite link is missing the invited email. Return to the sender and request the latest AfriMed pharmacy invite.",
        alreadySignedIn:
          "This invited email is already signed in on this browser. Continue straight to Pharmacy Dashboard.",
        switchEmail:
          "A different account is signed in on this browser. Continue with the invited email so AfriMed can connect the correct pharmacy member account.",
        cta: {
          dashboard: "Continue to Pharmacy Dashboard",
          opening: "Opening sign in...",
          invitedEmail: "Continue with invited email",
          returnLogin: "Return to sign in",
          requestLink: "Continue to request sign-in link",
        },
      },
      dashboard: {
        income: {
          range: {
            threeMonths: "Past 3 Months",
            sixMonths: "Past 6 Months",
            year: "Past Year",
          },
          menu: "Pharmacy income date range",
        },
        lowStock: {
          title: "Low Stock",
          subtitle:
            "An overview of low-stock medicine lines that need reordering before prescriptions reroute away.",
          openPrompt: "Open Fola prompt",
          emptyTitle: "No low-stock lines right now",
          emptyCopy:
            "AfriMed will surface low-stock medicine lines here once branch inventory is added and tracked against reorder thresholds.",
        },
      },
      settings: {
        language: {
          availableNow: "Available now",
          availableNowCopy:
            "Choose the current AfriMed app language for this device and account.",
          comingSoonTitle: "Coming soon",
          comingSoonCopy:
            "Southern African languages are being prepared carefully for healthcare-safe rollout.",
        },
        notificationPreferences: {
          appointmentReminders: {
            title: "Appointment reminder scheduling",
            introDoctor:
              "AfriMed sends a single near-start reminder one hour before confirmed visits. Morning digest is planned next.",
            introPatient:
              "AfriMed sends reminders 24 hours and 2 hours before confirmed visits. SMS helps even when you are not signed in.",
            timingDoctor: "Current timing: 1 hour before the appointment.",
            timingPatient: "Current timing: 24 hours and 2 hours before the appointment.",
          },
          channels: {
            inApp: {
              title: "In-app reminders",
              support:
                "Shows appointment reminders and care alerts inside AfriMed™ while you are signed in.",
            },
            sms: {
              title: "SMS reminders",
              support: "SMS reminders are coming soon.",
            },
            email: {
              title: "Email reminders",
              support: "Sends appointment reminder emails when a reminder is triggered.",
            },
            push: {
              title: "Push notifications",
              supportGranted:
                "Uses browser notifications for important and urgent care alerts when AfriMed is not in the foreground.",
              supportPrompt:
                "Enable browser notifications for important and urgent care alerts when prompted.",
              unsupported: "This browser does not support notification prompts here.",
            },
            sound: {
              title: "Urgent alert sound",
              support: "Plays a short tone for urgent care alerts while AfriMed is open.",
            },
            vibration: {
              title: "Urgent vibration",
              support:
                "Adds a short haptic pulse on supported devices for urgent care alerts.",
            },
          },
        },
        fola: {
          title: "Pharmacy operations prompt",
          copy:
            "Open Fola with a pharmacy operations prompt for drafting and general AfriMed workflow guidance inside Messages.",
          cta: "Open Fola",
        },
        members: {
          title: "Members",
          subtitle:
            "Principal owner onboarding is already done. Add the branch team here and keep roles, payouts, delivery, and inventory handoff aligned.",
          kpi: {
            branchOwner: "Owner seats",
            branchOwnerCopy:
              "The principal owner/admin remains the permission anchor for this branch.",
            activeMembers: "Active members",
            activeMembersCopy:
              "Only accepted members count as active. Invited rows stay pending until the person signs in officially.",
            pendingInvites: "Pending invites",
            pendingInvitesCopy:
              "Use the invited email on the AfriMed login page. The row flips only after the invite is accepted and linked.",
          },
          form: {
            name: "Full name",
            email: "Email",
            phone: "Phone",
            role: "Role",
          },
          sheet: {
            send: "Send invite",
          },
          status: {
            invited: "Invite • Pending",
            active: "Active",
          },
          inviteTitle: "Invite or import branch members",
          inviteCopy:
            "Copy a setup brief for payroll or operations teams, or import a staff CSV when the branch already manages staff in HR software.",
          inviteFlowCopy:
            "New members stay Invited until they sign in with the invited email. Only then should the branch treat them as active.",
          copyBrief: "Copy invite brief",
          importCsv: "Import CSV",
          addCta: "New member",
          columns: {
            name: "Name",
            role: "Role",
            actions: "Actions",
          },
          copied: "Invite brief copied",
          copyFailed: "Could not copy right now",
          imported: "Imported {count} staff records from {file}.",
          importEmpty: "Uploaded {file}, but no usable staff rows were detected.",
          importFailed: "The staff CSV could not be parsed.",
          removed: "Member removed",
          removeFailedDetail: "Remove member failed: {message}",
          removeFailed: "Could not remove this member right now.",
          inviteLinkCopied: "Sign-in link copied",
          inviteLinkCopyFailed: "Could not copy sign-in link right now",
          inviteSending: "Sending invitation email...",
          inviteEmailSent: "Invitation email sent",
          inviteEmailFailed:
            "Invite email could not be delivered right now. Use Copy sign-in link instead.",
          inviteFailedDetail: "Invite email failed: {message}",
          pendingHint: "Waiting for invitation acceptance",
          resendInvite: "Resend invite",
          resendInviteBusy: "Sending...",
          resendInviteSent: "Sent",
          resendInviteFailed: "Retry invite",
          copySignInLink: "Copy sign-in link",
          copySignInLinkBusy: "Copying...",
          copySignInLinkDone: "Copied",
          copySignInLinkRetry: "Copy again",
          remove: "Remove member",
          removeLabel: "Remove",
          removeDialog: {
            title: "Notice",
            body: "Remove {member} from the branch team?",
            memberFallback: "this member",
            cancel: "Cancel",
            confirm: "Yes",
          },
          create: {
            title: "New member",
            subtitle:
              "Add a branch team member for dispensing, stock, finance, or delivery handoff.",
            copy:
              "The principal owner or admin stays as the branch permission anchor. Use this step to add the day-to-day pharmacy team cleanly so staff access stays consistent across Pharmacy, Admin, and Medical Institute lanes.",
            roleGuideTitle: "Role guide",
            roleGuideCopy:
              "Mirror the same clean handoff logic across Pharmacy, Admin, and Medical Institutes. Assign only the branch role this person needs right now.",
            formTitle: "Member details",
            formCopy:
              "Keep this invite lightweight now. Roles can still be adjusted later from Settings.",
            saving: "Sending invite...",
            inviteSent:
              "Invitation email sent. The member stays pending until they sign in with the invited email.",
            inviteFailed:
              "Invite email could not be delivered right now. Please try again.",
            inviteFailedDetail: "Invite email failed: {message}",
          },
          roles: {
            dispensing: {
              label: "Dispensing pharmacist",
              note:
                "Dispense prescriptions, approve substitutions, and hand off patient-ready orders.",
            },
            inventory: {
              label: "Inventory manager",
              note: "Maintain stock, supplier notes, and low-stock actions.",
            },
            finance: {
              label: "Finance lead",
              note: "Track settlement exports, fee visibility, and reconciliation.",
            },
            delivery: {
              label: "Delivery lead",
              note: "Coordinate dispatch, verified addresses, and delivery readiness.",
            },
            branchAdmin: {
              label: "Branch admin",
              note: "Support day-to-day branch operations and team coordination.",
            },
          },
          fola: {
            title: "Start a pharmacy setup prompt",
            subtitle:
              "Open Fola with a seeded pharmacy operations prompt for role setup, CSV prep, or delivery handoff planning inside Messages.",
            cta: "Open Fola",
            roles: "Start roles prompt",
            csv: "Start CSV prompt",
            handoff: "Start handoff prompt",
          },
        },
      },
      inventory: {
        kpi: {
          tracked: "Tracked SKUs",
          activeCatalog: "Active catalog",
          lowStockAlerts: "Low-stock alerts",
          needsAction: "Needs action",
          coldChain: "Cold-chain items",
          verifiedStorage: "Verified storage",
        },
        stockControl: {
          title: "Stock control",
          subtitle:
            "Manage medicine availability so AfriMed can show signed-up branch availability, stock confidence, and patient-facing pricing clearly.",
        },
        actions: {
          openFolaPrompt: "Open Fola prompt",
          addStockItem: "Add stock item",
        },
        loading: "Loading branch inventory...",
        onHand: "On hand",
        reorderLevel: "Reorder level",
        retailPrice: "Retail price",
        reorderNow: "Reorder now",
        healthy: "Healthy",
        empty: {
          title: "No stock items added yet",
          copy:
            "Add real stock items here so low-stock alerts, prescription routing, and patient-facing pricing can stay credible.",
        },
        create: {
          title: "Add stock item",
          subtitle:
            "Create a real inventory line so stock control, prescription routing, and pharmacy totals can start hydrating from branch truth.",
          copy:
            "This writes directly into the branch inventory profile used by the Pharmacy dashboard. It replaces the old template fallback with real stock data.",
          details: "Item details",
          save: "Save stock item",
          saveError: "We couldn’t save this stock item yet.",
        },
        edit: {
          title: "Edit stock item",
          subtitle:
            "Update this branch inventory line so stock control and low-stock alerts stay current.",
          missing: "We couldn’t find this stock item anymore.",
          loadError: "We couldn’t load this stock item yet.",
          save: "Save changes",
        },
        detail: "Strength / detail",
        currentStock: "Current stock",
        minimalStock: "Minimal stock",
        unitPrice: "Unit price",
        supplier: "Supplier",
        branch: "Branch",
        batchNumber: "Batch number",
        expiryDate: "Expiry date",
        binLocation: "Bin location",
        imageUrl: "Image URL",
        imagePreview: "Image preview",
        imageHelp:
          "Paste a hosted image URL for this medicine. If left empty, AfriMed will keep the stock placeholder.",
        errors: {
          loadFailed: "We couldn’t load branch inventory yet.",
          removeFailed: "We couldn’t remove this stock item yet.",
        },
      },
      search: {
        results: "Search results",
      },
      prescriptions: {
        price: "Price",
        medicineSubtotal: "Medicine subtotal",
        deliveryCharge: "Delivery charge",
        quoteTotal: "Quote total",
        confirmDeliveryQuote: "I confirm the delivery charge and total before requesting payment.",
        priceSet: "Price set: {subtotal}",
        priceReady: "Price ready",
        actions: {
          setPrice: "Set price",
          reject: "Reject",
          accept: "Accept for review",
          markReadyForPickup: "Mark ready for pickup",
        },
        errors: {
          invalidPrice: "Enter a valid price before saving.",
          invalidDelivery: "Enter a valid delivery charge.",
          confirmDelivery: "Confirm the delivery charge before requesting payment.",
          updateFailed: "Could not update this prescription right now.",
        },
      },
      order: {
        status: {
          selected: "Pharmacy selected",
          preparing: "Preparing",
          awaitingPayment: "Awaiting payment",
          paid: "Paid",
          readyForPickup: "Ready for pickup",
          rejected: "Rejected",
          issued: "Issued",
        },
        errors: {
          missingId: "Prescription id is missing.",
          loadFailed: "Could not load this pharmacy order.",
        },
        defaults: {
          prescriptionItem: "Prescription item",
          instructions: "Review the prescription instructions attached to this order.",
          pharmacy: "AfriMed pharmacy",
        },
        title: "Prescription order",
        patient: "Patient",
        medication: "Medication",
        medicationList: "Medication list",
        medicationItem: "Medication {index}",
        quotedPrice: "Quoted price",
        pendingPrice: "Pending price",
        pendingPriceHelper: "Set the price from the pharmacy queue when stock is confirmed.",
        rxId: "RxID",
        issuedAt: "Issued {date}",
        updatedAt: "Updated {date}",
        openQueue: "Open prescription queue",
        print: "Print order",
        qr: {
          title: "QR",
          copy: "Pass 1 uses the simple AfriMed order route for verification.",
          alt: "QR for {id}",
          unavailable: "QR unavailable",
        },
      },
      payments: {
        link: {
          accessMissing:
            "Only linked pharmacy owner or branch-member accounts can create payment links here.",
          createError: "We could not create the pharmacy payment link right now.",
          copyError: "We couldn’t copy the payment link right now.",
          title: "Create payment link",
          subtitle:
            "Prepare a secure sponsor payment link for quick refills, OTC items, or diaspora-paid pharmacy purchases.",
          description: "Description",
          amount: "Amount",
          recipientEmail: "Recipient email",
          generate: "Generate payment link",
          copy: "Copy link",
          email: "Send by email",
          footnote:
            "This reuses AfriMed’s existing sponsor payment flow and applies the same 10% platform fee structure as current Stripe-linked sponsor payments.",
          generatedLink: "Generated link",
          generatedLinkEmpty:
            "Generate the secure payment link first, then copy or send it cleanly.",
          settlementPreview: "Settlement preview",
        },
      },
      help: {
        playbook: {
          title: "Pharmacy playbook",
          subtitle: "The first operator guide for the Pharmacy lane.",
        },
        step1: {
          title: "1. Verify the prescription",
          copy: "Use the QR scanner to confirm the AfriMed prescription before dispensing.",
        },
        step2: {
          title: "2. Confirm stock and price",
          copy: "Show patient-facing availability, medicine subtotal, and delivery cost clearly.",
        },
        step3: {
          title: "3. Hand off cleanly",
          copy:
            "Mark pickup or delivery completed so the doctor, patient, and pharmacy stay aligned.",
        },
      },
      folaPrompts: {
        lowStockFallback:
          "Help me draft calm reorder wording and branch handoff notes for pharmacy stock monitoring. Do not assume inventory is auto-synced or make autonomous decisions.",
        lowStockIntro:
          "Help me draft calm reorder wording and branch handoff notes for these low-stock items.",
        lowStockOutro:
          "Stay support-only. Do not assume inventory is auto-synced or make autonomous decisions.",
        helpPlaybook:
          "Help me run the AfriMed pharmacy lane cleanly: team setup, prescription handoff, low-stock communication, and delivery workflow.",
      },
    stagingGate: {
      checking: "Checking private staging access...",
      privateTitle: "Private staging",
      enterTitle: "Private staging access",
      privateBody:
        "AfriMed™ is currently in a private testing phase. We’re onboarding a limited number of healthcare professionals and partners. Leave your email and we’ll notify you when access opens.",
      enterBody: "Enter your invited email to continue into AfriMed™ staging.",
      emailLabel: "Email",
      emailPlaceholder: "name@example.com",
      checkingAction: "Checking access...",
      requesting: "Requesting access...",
      continueCta: "Continue",
      requestCta: "Request access",
      requestSaved:
        "AfriMed has saved your request. We’ll reach out when your staging access is ready.",
      errors: {
        lookupFailed:
          "AfriMed could not verify staging access right now. Please request access below.",
        submitFailed:
          "AfriMed could not save your request right now. Please try again shortly.",
      },
    },
    verify: {
      phoneOtp: {
        title: "Enter verification code",
        copy: "We sent a 6-digit code to",
      },
      prescription: {
        pageTitle: "Verify prescription",
        checking: "Checking...",
        status: "Status",
        signed: "Signed",
        notSigned: "Not signed",
        patient: "Patient",
        dob: "DOB",
        idLabel: "ID",
        reg: "Reg",
        title: "E-Prescription",
        number: "Prescription No",
        issued: "Issued",
        schedule: "Schedule",
        frequency: "Frequency",
        dosage: "Dosage",
        starts: "Starts",
        ends: "Ends",
        verification: "Verification",
        expires: "Expires",
        digitalSeal: "Digital seal",
        prescriberVerification: "Prescriber verification",
        signatureAlt: "Prescriber signature",
        publicRouteCopy: "{source}. Public verification route for pharmacy or manual checks.",
        verifiedVia:
          "Verified via {issuer}. The prescriber remains responsible for clinical decisions.",
        source: {
          manualPharmacy: "Entered manually at pharmacy",
          scannedPharmacy: "Scanned at pharmacy",
          public: "Public verification view",
        },
        actions: {
          scanAnother: "Scan another prescription",
          returnToPrescription: "Return to prescription",
        },
        defaults: {
          prescriber: "Prescriber",
          medication: "Medication",
          patient: "Patient",
          facility: "Facility",
        },
        errors: {
          invalidCode: "Invalid prescription code.",
          unavailable: "Prescription verification data is unavailable.",
          failed: "Verification failed.",
        },
        payment: {
          title: "Prepare patient payment",
          copy:
            "Quote the medicine subtotal and delivery terms now so the patient can pay the dispensing branch directly without a second chase.",
          medicineSubtotal: "Medicine subtotal",
          fulfillmentMode: "Fulfilment mode",
          deliveryFee: "Delivery fee",
          patientTotal: "Patient total",
          send: "Send patient to payment",
          saving: "Saving quote...",
          success:
            "{pharmacy} is now ready. The patient can open this prescription and pay {total}.",
          auditCopy:
            "{currency} quote stored against this prescription for audit and checkout.",
          defaults: {
            quote: "Pharmacy quote",
          },
          errors: {
            missingSubtotal:
              "Medicine subtotal is required before you can send the patient to payment.",
            prepareFailed: "Could not prepare patient payment right now.",
          },
        },
      },
    },
  },
  pt: {
    common: {
      actions: {
        close: "Fechar",
        print: "Imprimir",
        download: "Transferir",
      },
      status: {
        saving: "A guardar...",
      },
    },
    navigation: {
      tabs: {
        home: "Home",
        dashboard: "Painel",
        appointments: "Consultas",
        messages: "Mensagens",
        profile: "Perfil",
      },
    },
    auth: {
      callback: {
        finishing: "A concluir inicio de sessao...",
        redirectingToLogin:
          "A redirecionar para o login para pedir um novo link de acesso...",
        validating: "A validar o link de acesso...",
        verifyingCode: "A verificar o codigo de acesso...",
        linkNoLongerValidOnDevice:
          "Este link de acesso ja nao e valido neste dispositivo. Peca um novo link seguro de acesso e abra-o no mesmo navegador.",
        linkVerificationFailed:
          "Nao foi possivel verificar o link de acesso. Peca outro link de acesso.",
        sessionEmailMismatch:
          "O email da sessao nao corresponde apos a verificacao do link seguro de acesso.",
        sessionNotEstablished: "Sessao nao estabelecida.",
        checkingProfile: "Sessao iniciada. A verificar o seu perfil...",
        pharmacyDashboardAs:
          "A iniciar sessao no Painel da Farmacia como {email}... um momento por favor.",
        pharmacyDashboard:
          "A iniciar sessao no Painel da Farmacia... um momento por favor.",
        takingToAfrimed: "Sessao iniciada. A levar para a AfriMed™...",
        takingToAdmin: "Bem-vindo de volta. A levar para a administracao AfriMed...",
        takingHome: "Bem-vindo de volta. A levar para Home...",
        takingOnboarding: "Sessao iniciada. A levar para o onboarding...",
        resolvingAccountType: "A resolver o tipo de conta...",
        takingBack: "Sessao iniciada. A levar de volta...",
        chooseHowToContinue: "Sessao iniciada. Escolha como quer continuar...",
        slowHint:
          "Ainda a verificar o codigo de acesso... Se isto demorar demasiado, peca outro link de acesso.",
        timeout:
          "Nao foi possivel concluir o inicio de sessao. Peca outro link de acesso.",
        differentSession:
          "Este link de acesso pertence a uma sessao diferente neste navegador. Peca um novo link e abra apenas o mais recente.",
        pharmacyEmailNotLinked:
          "Este email ainda nao esta ligado a uma filial de farmacia.",
        pharmacyLinkFailed:
          "Nao foi possivel concluir este link de acesso da farmacia. Peca um novo link seguro de acesso.",
        linkExpired: "O link de acesso expirou. Peca outro link de acesso.",
        browserMismatch:
          "Nao foi possivel validar o link de acesso neste navegador. Peca outro link e abra o mais recente no mesmo navegador.",
        failed:
          "Falha no inicio de sessao. Tente novamente ou peca outro link de acesso.",
        requestAnotherLink: "Pedir outro link de acesso",
      },
    },
    appointments: {
      missedDialog: {
        title: "Consulta perdida",
        description:
          "Os participantes desta consulta nao chegaram dentro da janela marcada. Esta consulta ja nao esta disponivel. Se ainda precisa de cuidados, escolha um novo horario disponivel e faca uma nova marcacao.",
      },
    },
    notifications: {
      filters: {
        all: "Todos",
        urgent: "Urgentes",
        unread: "Por ler",
        appointments: "Consultas",
      },
      common: {
        title: "Notificacoes",
        today: "Hoje",
        notification: "Notificacao",
        patient: "Paciente",
        openDetails: "Abra para ver os detalhes.",
        loading: "A carregar notificacoes...",
        emptyTitle: "Nao ha notificacoes para mostrar",
        emptyBody: "A sua caixa de entrada esta vazia neste momento. Volte mais tarde!",
        errors: {
          loadFailed: "Nao foi possivel carregar as notificacoes.",
        },
      },
      doctor: {
        titles: {
          newAppointment: "Nova consulta",
          cancellationRequest: "Pedido de cancelamento",
          appointmentUpdated: "Consulta atualizada",
        },
        support: {
          bookingAccepted: "O pedido de consulta de {name} foi aprovado.",
          bookingDeclined: "O pedido de consulta de {name} foi recusado.",
          bookingPending: "{name} pediu uma consulta. Aguarda a sua aprovacao.",
          rescheduleAccepted: "A consulta com {name} foi remarcada.",
          rescheduleDeclined: "O novo pedido de horario de {name} foi recusado.",
          reschedulePending:
            "{name} pediu um novo horario de consulta. Aguarda a sua aprovacao.",
          cancellationAccepted: "O pedido de cancelamento de {name} foi aprovado.",
          cancellationDeclined: "O pedido de cancelamento de {name} foi recusado.",
          cancellationPending:
            "{name} pediu para cancelar a consulta. Aguarda a sua aprovacao.",
        },
      },
      title: "Notificacoes",
      loadingSingle: "A carregar notificacao...",
      singleTitle: "Notificacao",
      openDetailHelp: "Abra os detalhes para rever esta atualizacao.",
      pendingSchedule: "Agendamento pendente",
      time: {
        today: "Hoje",
        todayAt: "Hoje {time}",
      },
      fields: {
        doctor: "Medico",
        patient: "Paciente",
        assignedDoctor: "Medico atribuido",
        consultationLocation: "Local da consulta",
        dateTime: "Data e hora",
        location: "Localizacao",
        details: "Detalhes",
      },
      headers: {
        newAppointment: "Nova consulta",
        newAppointmentTimeRequest: "Novo pedido de horario",
        cancellationRequest: "Pedido de cancelamento",
        appointmentUpdate: "Atualizacao da consulta",
        cancellationRequestSent: "Pedido de cancelamento enviado",
        appointmentConfirmed: "Consulta confirmada",
        appointmentCancelled: "Consulta cancelada",
        appointmentDeclined: "Consulta recusada",
        cancellationRequestDeclined: "Pedido de cancelamento recusado",
        newTimeRequestApproved: "Novo horario aprovado",
        newTimeRequestDeclined: "Novo horario recusado",
        upcomingAppointment: "Consulta futura",
        ehrAccessRequest: "Pedido de acesso ao EHR",
      },
      actions: {
        accept: "Aceitar",
        decline: "Recusar",
        grantAccess: "Conceder acesso",
      },
      status: {
        accepted: "Aceite",
        declined: "Recusado",
        granted: "Concedido",
        granting: "A conceder...",
        unavailable: "Indisponivel",
      },
      dialogs: {
        cancelAppointmentTitle: "Cancelar consulta?",
        cancelAppointmentBody: "Tem a certeza de que quer cancelar esta consulta?",
        declineAppointmentTitle: "Recusar consulta?",
        declineAppointmentBody: "Tem a certeza de que quer recusar este pedido de consulta?",
        confirmCancel: "Sim, cancelar",
        confirmDecline: "Sim, recusar",
      },
      errors: {
        signInAgain: "Inicie sessao novamente.",
        notFound: "Notificacao nao encontrada.",
        emptyInbox: "A sua caixa de entrada esta vazia neste momento. Volte mais tarde!",
        load: "Nao foi possivel carregar a notificacao.",
        ehrGrant: "Nao foi possivel conceder acesso ao EHR agora.",
        cancelAppointment: "Nao foi possivel cancelar a consulta agora.",
        acceptAppointment: "Nao foi possivel aceitar a consulta.",
        declineAppointment: "Nao foi possivel recusar a consulta.",
        approveNewTimeRequest: "Nao foi possivel aprovar o novo horario.",
        declineNewTimeRequest: "Nao foi possivel recusar o novo horario.",
        acceptAppointmentChange: "Nao foi possivel aceitar a alteracao da consulta agora.",
        declineAppointmentChange: "Nao foi possivel recusar a alteracao da consulta agora.",
      },
      toast: {
        appointmentAccepted: "Consulta aceite",
        appointmentDeclined: "Consulta recusada",
        appointmentUpdated: "Consulta atualizada",
        appointmentCancelled: "Consulta cancelada",
        yourAppointmentUpdated: "A sua consulta foi atualizada",
        cancellationApproved: "Cancelamento aprovado",
        cancellationDeclined: "Cancelamento recusado",
        cancellationRequestSent: "Pedido de cancelamento enviado",
        cancellationApprovedRefundProcessing: "Cancelamento aprovado. O reembolso esta em processamento.",
        cancellationApprovedReversalFollowUp:
          "Cancelamento aprovado. A reversao do pagamento precisa de acompanhamento.",
        cancellationApprovedReversalAttention:
          "Cancelamento aprovado. A reversao do pagamento ainda precisa de atencao.",
        appointmentDeclinedRefundProcessing: "Consulta recusada. O reembolso esta em processamento.",
        appointmentDeclinedReversalFollowUp:
          "Consulta recusada. A reversao do pagamento precisa de acompanhamento.",
        appointmentDeclinedReversalAttention:
          "Consulta recusada. A reversao do pagamento ainda precisa de atencao.",
      },
      messages: {
        acceptedNewTime: "Aceitou o novo horario da consulta.",
        patientAcceptedNewTime: "O paciente aceitou o novo horario da consulta.",
        declinedNewTime: "Recusou o novo horario da consulta.",
        patientDeclinedNewTime: "O paciente recusou o novo horario da consulta.",
        doctorProposedNewTime:
          "O seu medico propos um novo horario para a consulta. Reveja e confirme a alteracao.",
        ehrAccessRequested:
          "O seu medico pediu acesso ao seu registo de saude para esta consulta.",
        upcomingAppointmentForDate: "A sua consulta esta marcada para {date}.",
        upcomingAppointmentGeneric: "A sua consulta esta agendada.",
        bookingApprovedForDate: "O pedido de consulta de {name} para {date} foi aprovado.",
        bookingApprovedGeneric: "O pedido de consulta de {name} foi aprovado.",
        bookingPendingForDate:
          "{name} pediu uma consulta para {date}. Aguarda a sua aprovacao.",
        yourAppointmentAcceptedForDate: "A sua consulta para {date} foi aceite.",
        yourAppointmentAcceptedGeneric: "O seu pedido de consulta foi aceite.",
        bookingDeclinedForDate: "O pedido de consulta de {name} para {date} foi recusado.",
        bookingDeclinedGeneric: "O pedido de consulta de {name} foi recusado.",
        yourAppointmentDeclinedForDate: "A sua consulta para {date} foi recusada.",
        yourAppointmentDeclinedGeneric: "O seu pedido de consulta foi recusado.",
        cancellationApprovedForDate:
          "{name} pediu para cancelar a consulta de {date}. Aprovado.",
        cancellationApprovedGeneric: "{name} pediu para cancelar a consulta. Aprovado.",
        yourCancellationApprovedForDate: "O seu pedido de cancelamento para {date} foi aprovado.",
        yourCancellationApprovedGeneric: "O seu pedido de cancelamento foi aprovado.",
        cancellationDeclinedForDate:
          "{name} pediu para cancelar a consulta de {date}. Recusado.",
        cancellationDeclinedGeneric: "{name} pediu para cancelar a consulta. Recusado.",
        yourCancellationDeclinedForDate: "O seu pedido de cancelamento para {date} foi recusado.",
        yourCancellationDeclinedGeneric: "O seu pedido de cancelamento foi recusado.",
        rescheduleApprovedGeneric: "{name} pediu um novo horario de consulta. Aprovado.",
        yourRescheduleApprovedGeneric: "O seu pedido de novo horario foi aprovado.",
        rescheduleDeclinedGeneric: "{name} pediu um novo horario de consulta. Recusado.",
        yourRescheduleDeclinedGeneric: "O seu pedido de novo horario foi recusado.",
        cancellationRequestSentForDate:
          "O seu pedido para cancelar a consulta de {date} foi enviado.",
        cancellationRequestSentGeneric: "O seu pedido para cancelar esta consulta foi enviado.",
        patientCancellationPendingForDate:
          "{name} pediu para cancelar a consulta de {date}. Aguarda aprovacao.",
        patientCancellationPendingGeneric:
          "{name} pediu para cancelar a consulta. Aguarda aprovacao.",
      },
      goToAppointment: "Abrir consulta",
      goToAppointmentDetails: "Abrir detalhes da consulta",
      referral: {
        label: "Encaminhamento",
        openAction: "Abrir encaminhamento",
      },
    },
    messages: {
      label: "Mensagem",
      openAction: "Abrir mensagem",
      loadingConversation: "A carregar conversa...",
      noMessagesYet: "Ainda nao ha mensagens.",
      inbox: {
        fallbackTitle: "Conversa",
      },
      conversation: {
        now: "Agora",
        deletedForMe: "Apagou esta mensagem",
        deletedBySender: "A mensagem foi apagada pelo remetente",
        deleteDialog: {
          close: "Fechar dialogo de apagamento",
          title: "Apagar mensagem?",
          body:
            "Apague esta mensagem so para si, ou remova-a para ambas as pessoas. Esta acao nao pode ser desfeita.",
          deleting: "A apagar...",
          forMe: "Apagar para mim",
          forBoth: "Apagar para ambos",
        },
        toast: {
          dismiss: "OK",
          undo: "Desfazer",
          deletedForMe: "Mensagem apagada para mim",
          deletedForBoth: "Mensagem apagada para ambos",
          deleteError: "Nao foi possivel apagar a mensagem agora.",
        },
        composer: {
          all: "Todos",
          attach: "Anexar foto",
          send: "Enviar mensagem",
          thinking: "A Fola esta a pensar",
          selectedAttachmentAlt: "Anexo selecionado",
        },
        topBar: {
          back: "Voltar as mensagens",
          openFola: "Abrir conversa com a Fola",
          compose: "Escrever mensagem",
        },
        sourceThreadFallback: "Conversa de origem",
        errors: {
          resolve: "Nao foi possivel resolver esta conversa.",
          load: "Nao foi possivel carregar esta conversa.",
          send: "Nao foi possivel enviar a mensagem.",
        },
      },
    },
    fola: {
      widget: {
        title: "Fola - Assistente AfriMed™",
        greeting: "Ola, eu sou a Fola. Posso ajudar com OTP, magic links e definicoes.",
        open: "Abrir Fola",
        placeholder: "Pergunte a Fola...",
        sending: "A enviar...",
        send: "Enviar",
        error: "Desculpe, ocorreu um problema. Tente novamente.",
      },
    },
    pharmacy: {
      invite: {
        title: "Convite da farmacia",
        subtitle:
          "Reveja o convite da equipa AfriMed Pharmacy, confirme o email convidado e depois solicite o link seguro de acesso para essa conta exata do membro da farmacia.",
        body:
          "A AfriMed vai usar o email convidado abaixo para a etapa de acesso do membro da farmacia. Abra o email de acesso mais recente neste mesmo navegador ou dispositivo e a AfriMed continuara para o Painel da Farmacia.",
        emailLabel: "Email convidado",
        emailMissing: "Nenhum email convidado foi anexado a este link.",
        emailMissingBody:
          "Este link de convite nao inclui o email convidado. Volte ao remetente e peca o convite AfriMed Pharmacy mais recente.",
        alreadySignedIn:
          "Este email convidado ja tem sessao iniciada neste navegador. Continue diretamente para o Painel da Farmacia.",
        switchEmail:
          "Uma conta diferente esta com sessao iniciada neste navegador. Continue com o email convidado para que a AfriMed ligue a conta correta do membro da farmacia.",
        cta: {
          dashboard: "Continuar para o Painel da Farmacia",
          opening: "A abrir inicio de sessao...",
          invitedEmail: "Continuar com o email convidado",
          returnLogin: "Voltar ao inicio de sessao",
          requestLink: "Continuar para pedir o link de acesso",
        },
      },
      dashboard: {
        income: {
          range: {
            threeMonths: "Ultimos 3 meses",
            sixMonths: "Ultimos 6 meses",
            year: "Ultimo ano",
          },
          menu: "Intervalo de datas de receita da farmacia",
        },
        lowStock: {
          title: "Stock baixo",
          subtitle:
            "Uma vista das linhas com stock baixo que precisam de reposicao antes de as receitas serem desviadas.",
          openPrompt: "Abrir prompt da Fola",
          emptyTitle: "Nao ha linhas de stock baixo agora",
          emptyCopy:
            "A AfriMed vai mostrar aqui as linhas de medicamentos com stock baixo quando o inventario da filial for adicionado e acompanhado pelos limites de reposicao.",
        },
      },
      settings: {
        language: {
          availableNow: "Disponivel agora",
          availableNowCopy:
            "Escolha o idioma atual da aplicacao AfriMed para este dispositivo e conta.",
          comingSoonTitle: "Em breve",
          comingSoonCopy:
            "As linguas da Africa Austral estao a ser preparadas com cuidado para uma disponibilizacao segura em contexto de saude.",
        },
        notificationPreferences: {
          appointmentReminders: {
            title: "Lembretes de consulta",
            introDoctor:
              "A AfriMed envia um unico lembrete proximo do inicio, uma hora antes das consultas confirmadas. O resumo da manha fica para a proxima fase.",
            introPatient:
              "A AfriMed envia lembretes 24 horas e 2 horas antes das consultas confirmadas. O SMS ajuda mesmo quando nao tem sessao iniciada.",
            timingDoctor: "Momento atual: 1 hora antes da consulta.",
            timingPatient: "Momento atual: 24 horas e 2 horas antes da consulta.",
          },
          channels: {
            inApp: {
              title: "Lembretes na aplicacao",
              support: "Mostra na caixa de notificacoes da AfriMed e nas superfices da consulta.",
            },
            sms: {
              title: "Lembretes por SMS",
              support: "Melhor alternativa quando nao tem sessao iniciada.",
            },
            email: {
              title: "Lembretes por email",
              support: "Entrega de apoio com os detalhes da consulta.",
            },
            push: {
              title: "Notificacoes push",
              supportGranted:
                "Usa notificacoes do navegador para alertas importantes e urgentes quando a AfriMed nao esta em primeiro plano.",
              supportPrompt:
                "Ative as notificacoes do navegador para alertas importantes e urgentes quando for pedido.",
              unsupported: "Este navegador nao suporta pedidos de notificacao aqui.",
            },
            sound: {
              title: "Som de alerta urgente",
              support: "Toca um sinal curto para alertas urgentes enquanto a AfriMed esta aberta.",
            },
            vibration: {
              title: "Vibracao urgente",
              support:
                "Adiciona um impulso haptico curto em dispositivos suportados para alertas urgentes.",
            },
          },
          footer:
            "As notificacoes do navegador cobrem agora alertas de cuidados durante sessoes ativas quando a permissao e concedida. A entrega push completa fora de sessao continua dependente da infraestrutura de service worker e permanece adiada.",
        },
        fola: {
          title: "Prompt de operacoes da farmacia",
          copy:
            "Abra a Fola com um prompt de operacoes da farmacia para redacao e orientacao geral do fluxo AfriMed dentro de Mensagens.",
          cta: "Abrir Fola",
        },
        members: {
          title: "Membros",
          subtitle:
            "A integracao do proprietario principal ja esta concluida. Adicione aqui a equipa da filial e mantenha funcoes, pagamentos, entrega e handoff de inventario alinhados.",
          kpi: {
            branchOwner: "Lugares de proprietario",
            branchOwnerCopy:
              "O proprietario principal ou administrador continua a ser a ancora de permissoes desta filial.",
            activeMembers: "Membros ativos",
            activeMembersCopy:
              "Apenas os membros aceites contam como ativos. As linhas convidadas ficam pendentes ate a pessoa iniciar sessao oficialmente.",
            pendingInvites: "Convites pendentes",
            pendingInvitesCopy:
              "Use o email convidado na pagina de inicio de sessao da AfriMed. A linha muda apenas depois de o convite ser aceite e ligado.",
          },
          form: {
            name: "Nome completo",
            email: "Email",
            phone: "Telefone",
            role: "Funcao",
          },
          sheet: {
            send: "Enviar convite",
          },
          status: {
            invited: "Convite • Pendente",
            active: "Ativo",
          },
          inviteTitle: "Convidar ou importar membros da filial",
          inviteCopy:
            "Copie um resumo de configuracao para equipas de payroll ou operacoes, ou importe um CSV de pessoal quando a filial ja gere a equipa em software de RH.",
          inviteFlowCopy:
            "Os novos membros ficam Convidados ate iniciarem sessao com o email convidado. So depois a filial os deve tratar como ativos.",
          copyBrief: "Copiar resumo do convite",
          importCsv: "Importar CSV",
          addCta: "Novo membro",
          columns: {
            name: "Nome",
            role: "Funcao",
            actions: "Acoes",
          },
          copied: "Resumo do convite copiado",
          copyFailed: "Nao foi possivel copiar agora",
          imported: "Foram importados {count} registos de pessoal de {file}.",
          importEmpty: "{file} foi carregado, mas nao foram detetadas linhas de pessoal utilizaveis.",
          importFailed: "Nao foi possivel analisar o CSV do pessoal.",
          removed: "Membro removido",
          removeFailedDetail: "Falha ao remover o membro: {message}",
          removeFailed: "Nao foi possivel remover este membro agora.",
          inviteLinkCopied: "Link de inicio de sessao copiado",
          inviteLinkCopyFailed: "Nao foi possivel copiar o link de inicio de sessao agora",
          inviteSending: "A enviar email de convite...",
          inviteEmailSent: "Email de convite enviado",
          inviteEmailFailed:
            "O email de convite nao pode ser entregue agora. Use antes Copiar link de inicio de sessao.",
          inviteFailedDetail: "Falha no email de convite: {message}",
          pendingHint: "A aguardar aceitacao do convite",
          resendInvite: "Reenviar convite",
          resendInviteBusy: "A enviar...",
          resendInviteSent: "Enviado",
          resendInviteFailed: "Tentar convite novamente",
          copySignInLink: "Copiar link de inicio de sessao",
          copySignInLinkBusy: "A copiar...",
          copySignInLinkDone: "Copiado",
          copySignInLinkRetry: "Copiar de novo",
          remove: "Remover membro",
          removeLabel: "Remover",
          removeDialog: {
            title: "Aviso",
            body: "Remover {member} da equipa da filial?",
            memberFallback: "este membro",
            cancel: "Cancelar",
            confirm: "Sim",
          },
          create: {
            title: "Novo membro",
            subtitle:
              "Adicione um membro da equipa da filial para dispensa, stock, financas ou handoff de entrega.",
            copy:
              "O proprietario principal ou administrador continua como ancora de permissoes da filial. Use este passo para adicionar a equipa diaria da farmacia com clareza para manter o acesso consistente.",
            roleGuideTitle: "Guia de funcoes",
            roleGuideCopy:
              "Mantenha a mesma logica de handoff limpa em Pharmacy, Admin e Medical Institutes. Atribua apenas a funcao de filial que esta pessoa precisa agora.",
            formTitle: "Detalhes do membro",
            formCopy:
              "Mantenha este convite leve por agora. As funcoes ainda podem ser ajustadas mais tarde em Settings.",
            saving: "A enviar convite...",
            inviteSent:
              "Email de convite enviado. O membro fica pendente ate iniciar sessao com o email convidado.",
            inviteFailed:
              "O email de convite nao pode ser entregue agora. Tente novamente.",
            inviteFailedDetail: "Falha no email de convite: {message}",
          },
          fola: {
            title: "Iniciar um prompt de configuracao da farmacia",
            subtitle:
              "Abra a Fola com um prompt de operacoes da farmacia para configuracao de funcoes, preparacao de CSV ou planeamento do handoff de entrega dentro de Mensagens.",
            cta: "Abrir Fola",
            roles: "Iniciar prompt de funcoes",
            csv: "Iniciar prompt de CSV",
            handoff: "Iniciar prompt de handoff",
          },
          roles: {
            dispensing: {
              label: "Farmaceutico dispensador",
              note:
                "Dispensa receitas, aprova substituicoes e faz o handoff de pedidos prontos para o paciente.",
            },
            inventory: {
              label: "Gestor de inventario",
              note: "Mantem stock, notas de fornecedor e acoes de stock baixo.",
            },
            finance: {
              label: "Responsavel financeiro",
              note: "Acompanha exportacoes de liquidacao, visibilidade de taxas e reconciliacao.",
            },
            delivery: {
              label: "Responsavel de entrega",
              note: "Coordena despacho, moradas verificadas e prontidao de entrega.",
            },
            branchAdmin: {
              label: "Administrador da filial",
              note: "Apoia as operacoes diarias da filial e a coordenacao da equipa.",
            },
          },
        },
      },
      inventory: {
        kpi: {
          tracked: "SKUs rastreados",
          activeCatalog: "Catalogo ativo",
          lowStockAlerts: "Alertas de stock baixo",
          needsAction: "Precisa de acao",
          coldChain: "Itens de cadeia fria",
          verifiedStorage: "Armazenamento verificado",
        },
        stockControl: {
          title: "Controlo de stock",
          subtitle:
            "Gira a disponibilidade dos medicamentos para que a AfriMed mostre claramente disponibilidade da filial, confianca de stock e preco para o paciente.",
        },
        actions: {
          openFolaPrompt: "Abrir prompt da Fola",
          addStockItem: "Adicionar item de stock",
        },
        loading: "A carregar o inventario da filial...",
        onHand: "Em stock",
        reorderLevel: "Nivel de reposicao",
        retailPrice: "Preco de venda",
        reorderNow: "Repor agora",
        healthy: "Saudavel",
        empty: {
          title: "Ainda nao ha itens de stock adicionados",
          copy:
            "Adicione aqui itens reais de stock para que os alertas, o encaminhamento das receitas e o preco visivel ao paciente se mantenham crediveis.",
        },
        create: {
          title: "Adicionar item de stock",
          subtitle:
            "Crie uma linha real de inventario para que o controlo de stock, o encaminhamento de receitas e os totais da farmacia comecem a refletir a realidade da filial.",
          copy:
            "Isto escreve diretamente no perfil de inventario da filial usado pelo painel da Pharmacy. Substitui o antigo fallback de modelo por dados reais de stock.",
          details: "Detalhes do item",
          save: "Guardar item de stock",
          saveError: "Ainda nao foi possivel guardar este item de stock.",
        },
        edit: {
          title: "Editar item de stock",
          subtitle:
            "Atualize esta linha de inventario da filial para manter o controlo de stock e os alertas de stock baixo atuais.",
          missing: "Ja nao foi possivel encontrar este item de stock.",
          loadError: "Ainda nao foi possivel carregar este item de stock.",
          save: "Guardar alteracoes",
        },
        detail: "Dosagem / detalhe",
        currentStock: "Stock atual",
        minimalStock: "Stock minimo",
        unitPrice: "Preco unitario",
        supplier: "Fornecedor",
        branch: "Filial",
        batchNumber: "Numero do lote",
        expiryDate: "Data de validade",
        binLocation: "Localizacao do lote",
        imageUrl: "URL da imagem",
        imagePreview: "Pre-visualizacao da imagem",
        imageHelp:
          "Cole uma URL publica da imagem deste medicamento. Se ficar vazio, a AfriMed mantem o placeholder de stock.",
        errors: {
          loadFailed: "Ainda nao foi possivel carregar o inventario da filial.",
          removeFailed: "Ainda nao foi possivel remover este item de stock.",
        },
      },
      search: {
        results: "Resultados da pesquisa",
      },
      prescriptions: {
        price: "Preco",
        priceSet: "Preco definido: {subtotal}",
        priceReady: "Preco pronto",
        actions: {
          setPrice: "Definir preco",
          reject: "Rejeitar",
          accept: "Aceitar",
          markReadyForPickup: "Marcar como pronto para recolha",
        },
        errors: {
          invalidPrice: "Introduza um preco valido antes de guardar.",
          updateFailed: "Nao foi possivel atualizar esta receita agora.",
        },
      },
      order: {
        status: {
          selected: "Farmacia selecionada",
          preparing: "Em preparacao",
          awaitingPayment: "Aguardando pagamento",
          paid: "Pago",
          readyForPickup: "Pronto para recolha",
          rejected: "Rejeitado",
          issued: "Emitido",
        },
        errors: {
          missingId: "Falta o id da receita.",
          loadFailed: "Nao foi possivel carregar este pedido da farmacia.",
        },
        defaults: {
          prescriptionItem: "Item da receita",
          instructions: "Reveja as instrucoes da receita anexadas a este pedido.",
          pharmacy: "Farmacia AfriMed",
        },
        title: "Pedido de receita",
        patient: "Paciente",
        medication: "Medicamento",
        medicationList: "Lista de medicamentos",
        medicationItem: "Medicamento {index}",
        quotedPrice: "Preco cotado",
        pendingPrice: "Preco pendente",
        pendingPriceHelper: "Defina o preco a partir da fila da farmacia quando o stock estiver confirmado.",
        rxId: "RxID",
        issuedAt: "Emitido em {date}",
        updatedAt: "Atualizado em {date}",
        openQueue: "Abrir fila de receitas",
        print: "Imprimir pedido",
        qr: {
          title: "QR",
          copy: "A passagem 1 usa a rota simples de pedido AfriMed para verificacao.",
          alt: "QR para {id}",
          unavailable: "QR indisponivel",
        },
      },
      payments: {
        link: {
          accessMissing:
            "Apenas contas ligadas do proprietario da farmacia ou de membros da filial podem criar links de pagamento aqui.",
          createError: "Nao foi possivel criar o link de pagamento da farmacia agora.",
          copyError: "Nao foi possivel copiar o link de pagamento agora.",
          title: "Criar link de pagamento",
          subtitle:
            "Prepare um link seguro de pagamento do patrocinador para recargas rapidas, OTC ou compras de farmacia pagas pela diaspora.",
          description: "Descricao",
          amount: "Montante",
          recipientEmail: "Email do destinatario",
          generate: "Gerar link de pagamento",
          copy: "Copiar link",
          email: "Enviar por email",
          footnote:
            "Isto reutiliza o fluxo atual de pagamento de patrocinador da AfriMed e aplica a mesma estrutura de taxa de plataforma de 10% dos pagamentos atuais ligados ao Stripe.",
          generatedLink: "Link gerado",
          generatedLinkEmpty:
            "Gere primeiro o link seguro de pagamento e depois copie-o ou envie-o de forma limpa.",
          settlementPreview: "Previsao de liquidacao",
        },
      },
      help: {
        playbook: {
          title: "Guia da farmacia",
          subtitle: "O primeiro guia operacional para a via Pharmacy.",
        },
        step1: {
          title: "1. Verificar a receita",
          copy: "Use o leitor QR para confirmar a receita AfriMed antes de dispensar.",
        },
        step2: {
          title: "2. Confirmar stock e preco",
          copy: "Mostre claramente disponibilidade, subtotal do medicamento e custo de entrega.",
        },
        step3: {
          title: "3. Entregar de forma limpa",
          copy:
            "Marque a recolha ou entrega como concluida para manter medico, paciente e farmacia alinhados.",
        },
      },
      folaPrompts: {
        lowStockFallback:
          "Ajude-me a redigir texto calmo de reposicao e notas de handoff da filial para a monitorizacao do stock da farmacia. Nao assuma sincronizacao automatica do inventario nem tome decisoes autonomas.",
        lowStockIntro:
          "Ajude-me a redigir texto calmo de reposicao e notas de handoff da filial para estes itens com stock baixo.",
        lowStockOutro:
          "Mantenha apoio apenas. Nao assuma sincronizacao automatica do inventario nem tome decisoes autonomas.",
        helpPlaybook:
          "Ajude-me a gerir a via AfriMed da farmacia com clareza: configuracao da equipa, handoff de receitas, comunicacao de stock baixo e fluxo de entrega.",
      },
    },
    stagingGate: {
      checking: "A verificar acesso privado de staging...",
      privateTitle: "Staging privado",
      enterTitle: "Acesso privado ao staging",
      privateBody:
        "A AfriMed esta atualmente numa fase privada de testes. Estamos a integrar um numero limitado de profissionais de saude e parceiros. Deixe o seu email e notificaremos quando o acesso abrir.",
      enterBody: "Introduza o seu email convidado para continuar para o staging AfriMed.",
      emailLabel: "Email",
      emailPlaceholder: "nome@exemplo.com",
      checkingAction: "A verificar acesso...",
      requesting: "A pedir acesso...",
      continueCta: "Continuar",
      requestCta: "Pedir acesso",
      requestSaved:
        "A AfriMed guardou o seu pedido. Entraremos em contacto quando o seu acesso de staging estiver pronto.",
      errors: {
        lookupFailed:
          "A AfriMed nao conseguiu verificar o acesso de staging agora. Por favor, peca acesso abaixo.",
        submitFailed:
          "A AfriMed nao conseguiu guardar o seu pedido neste momento. Tente novamente dentro de instantes.",
      },
    },
    verify: {
      phoneOtp: {
        title: "Introduza o codigo de verificacao",
        copy: "Enviamos um codigo de 6 digitos para",
      },
      prescription: {
        pageTitle: "Verificar receita",
        checking: "A verificar...",
        status: "Estado",
        signed: "Assinada",
        notSigned: "Nao assinada",
        patient: "Paciente",
        dob: "Data de nascimento",
        idLabel: "ID",
        reg: "Reg",
        title: "Receita eletronica",
        number: "No da receita",
        issued: "Emitida",
        schedule: "Posologia",
        frequency: "Frequencia",
        dosage: "Dosagem",
        starts: "Inicio",
        ends: "Fim",
        verification: "Verificacao",
        expires: "Expira",
        digitalSeal: "Selo digital",
        prescriberVerification: "Verificacao do prescritor",
        signatureAlt: "Assinatura do prescritor",
        publicRouteCopy: "{source}. Rota publica de verificacao para farmacia ou verificacoes manuais.",
        verifiedVia:
          "Verificado via {issuer}. O prescritor continua responsavel pelas decisoes clinicas.",
        source: {
          manualPharmacy: "Introduzida manualmente na farmacia",
          scannedPharmacy: "Digitalizada na farmacia",
          public: "Vista publica de verificacao",
        },
        actions: {
          scanAnother: "Digitalizar outra receita",
          returnToPrescription: "Voltar a receita",
        },
        defaults: {
          prescriber: "Prescritor",
          medication: "Medicamento",
          patient: "Paciente",
          facility: "Unidade",
        },
        errors: {
          invalidCode: "Codigo de receita invalido.",
          unavailable: "Os dados de verificacao da receita nao estao disponiveis.",
          failed: "Falha na verificacao.",
        },
        payment: {
          title: "Preparar pagamento do paciente",
          copy:
            "Defina agora o subtotal do medicamento e as condicoes de entrega para que o paciente possa pagar diretamente ao ramo dispensador sem segunda cobranca.",
          medicineSubtotal: "Subtotal do medicamento",
          fulfillmentMode: "Modo de entrega",
          deliveryFee: "Taxa de entrega",
          patientTotal: "Total do paciente",
          send: "Enviar paciente para pagamento",
          saving: "A guardar cotacao...",
          success:
            "{pharmacy} esta pronto. O paciente pode abrir esta receita e pagar {total}.",
          auditCopy:
            "Cotacao em {currency} guardada nesta receita para auditoria e checkout.",
          defaults: {
            quote: "Cotacao da farmacia",
          },
          errors: {
            missingSubtotal:
              "O subtotal do medicamento e obrigatorio antes de enviar o paciente para pagamento.",
            prepareFailed: "Nao foi possivel preparar o pagamento do paciente neste momento.",
          },
        },
      },
    },
  },
  fr: {
    auth: {
      login: {
        sheet: {
          manageCookies: "Gérer les cookies",
        },
        landing: {
          languageChoice: "Choisir la langue",
          languageEnglish: "Anglais",
          languageFrench: "Français",
        },
      },
    },
    fields: {
      country: {
        label: "Pays",
        requiredLabel: "Pays*",
        placeholder: "Sélectionner un pays",
        searchPlaceholder: "Rechercher des pays",
        selected: "Sélectionné",
        chooseMarket: "Choisissez votre marché",
        current: "Actuel",
        noMatchesLong: "Aucun marché ne correspond à cette recherche.",
        noMatchesShort: "Aucun résultat",
      },
    },
    regionalHealthSignals: {
      loading: "Chargement de Santé régionale…",
      loadErrorTitle: "Santé régionale est indisponible",
      loadErrorBody: "L’espace Institution autorisé n’a pas pu être chargé. Aucune notification externe n’a été envoyée.",
      retry: "Réessayer",
      title: "Santé régionale",
      tabs: { overview: "Vue d’ensemble", registry: "Registre des signaux", quality: "Qualité des données", audit: "Piste d’audit" },
      actions: { report: "Signaler un signal", cancel: "Annuler", saveDraft: "Enregistrer le brouillon", submit: "Soumettre pour examen", needsReview: "Marquer à examiner", return: "Renvoyer pour clarification", reconcile: "Rapprocher", close: "Fermer", closeSignal: "Clôturer le signal", reasonPrompt: "Motif de clarification" },
      boundary: { kicker: "Évaluation institutionnelle", aggregate: "Informations agrégées uniquement. Ne saisissez aucune information permettant d’identifier un patient.", external: "Aucune notification externe n’est envoyée automatiquement.", externalState: "Notification externe non connectée" },
      readiness: { noFacilityTitle: "Aucun établissement déclarant autorisé", noFacilityBody: "Un établissement doit être enregistré et rattaché à un périmètre avant qu’un signal puisse être déclaré." },
      metrics: { facilityReadiness: "Disponibilité de déclaration des établissements", lastActivity: "Dernière activité de signal" },
      overview: { workflowTitle: "Flux interne des signaux", workflowBody: "Enregistrez un signal agrégé, soumettez-le à l’examen interne, rapprochez-le au moyen d’une décision auditable et clôturez-le lorsque l’Institution a terminé son examen." },
      registry: { emptyTitle: "Aucun signal dans ce périmètre Institution", emptyBody: "Signalez un signal sanitaire agrégé lorsqu’un établissement déclarant autorisé est disponible.", columns: { reference: "Référence du signal", reportDate: "Date de déclaration", facility: "Établissement", programme: "Programme", eventCategory: "Catégorie d’événement", classification: "Classification", affected: "Nombre agrégé de personnes touchées", status: "Statut", updated: "Dernière mise à jour", actions: "Actions" }, view: "Voir" },
      quality: { title: "Contrôles de qualité des données", empty: "Aucun indicateur de qualité non résolu dans ce périmètre Institution." },
      flags: { date_order: "La date de déclaration est antérieure à la date de l’événement.", duplicate_candidate: "Doublon possible : même établissement, catégorie et date d’événement proche.", incomplete_draft: "Le brouillon est incomplet et ne peut pas être soumis.", awaiting_review: "Le signal soumis attend un examen interne." },
      audit: { title: "Piste d’audit", empty: "Aucun événement d’audit de signal dans ce périmètre Institution.", created: "Créé", edited_draft: "Brouillon modifié", submitted: "Soumis pour examen", needs_review: "Marqué à examiner", returned_for_clarification: "Renvoyé pour clarification", reconciled: "Rapproché", closed: "Clôturé", data_quality_flag_raised: "Indicateur de qualité déclenché", data_quality_flag_resolved: "Indicateur de qualité résolu" },
      navigation: { regionalHealth: "Santé régionale" },
      form: { title: "Signaler un signal", facility: "Établissement déclarant", selectFacility: "Sélectionner un établissement", selectOption: "Sélectionner une option", programme: "Programme (le cas échéant)", noProgramme: "Aucun programme", eventCategory: "Catégorie d’événement", eventCategoryPlaceholder: "Par exemple : hausse inhabituelle de fièvre", classification: "Classification", eventDate: "Date de l’événement/apparition", reportDate: "Date de déclaration", affected: "Nombre agrégé de personnes touchées", deaths: "Nombre agrégé de décès", narrative: "Récit agrégé concis", narrativePlaceholder: "Aucun nom, identifiant, coordonnée ou note clinique." },
      detail: { title: "Détail du signal", facility: "Établissement", event: "Catégorie d’événement", classification: "Classification", aggregate: "Personnes touchées / décès agrégés", eventDate: "Date de l’événement", recordMode: "Mode d’enregistrement", external: "Notification externe" },
      status: { draft: "Brouillon", submitted: "Soumis", needsReview: "À examiner", reconciled: "Rapproché", closed: "Clôturé" },
      classification: { suspected: "Suspect", probable: "Probable", confirmed_external: "Confirmé par une autorité externe", other_unknown: "Autre/inconnu" },
      language: { title: "Langue", english: "Anglais", french: "Français" },
    },
    institutionShell: {
      search: "Rechercher",
      searchInstitution: "Rechercher dans l’établissement",
      navigation: { dashboard: "Tableau de bord", clinicians: "Cliniciens", members: "Membres", surveillanceTeam: "Équipe de surveillance", services: "Services", regionalHealth: "Santé régionale", patients: "Patients", settings: "Paramètres", documentation: "Documentation", messages: "Messages" },
      notifications: { title: "Notifications", support: "Activité de l’établissement et alertes des membres.", empty: "Aucune alerte de l’établissement pour le moment." },
      account: { me: "Moi", myAccount: "Mon compte" },
      theme: { title: "Thème", dark: "Sombre", light: "Clair", system: "Système" },
      language: { title: "Langue", english: "Anglais", french: "Français" },
      actions: { makeSuggestion: "Faire une suggestion", signOut: "Se déconnecter" },
      accessibility: { openNavigation: "Ouvrir la navigation", closeNavigation: "Fermer la navigation", navigation: "Navigation de l’établissement", dashboardSections: "Sections du tableau de bord de l’établissement" },
    },
    institutionHome: {
      title: "Accueil", loading: "Chargement de l’accueil Institution…", loadError: "L’historique de l’Institution n’a pas pu être chargé. Réessayez depuis un espace Institution autorisé.", unavailable: "L’accueil Institution est indisponible tant que l’espace autorisé ne peut pas être lu.",
      recentInstitutions: "Établissements récemment consultés", recentMembers: "Membres récemment consultés", emptyInstitutions: "Les établissements médicaux autorisés que vous ouvrez apparaîtront ici.", emptyMembers: "Les membres autorisés que vous ouvrez explicitement apparaîtront ici.", browseInstitutions: "Parcourir les établissements médicaux autorisés", openMembers: "Ouvrir les membres de l’Institution", setupStatusTitle: "Configuration de l’espace ministère de la Santé", setupStatusBody: "Votre Institution autorisée est prête à être explorée. Vous pourrez compléter les établissements et le registre d’équipe ultérieurement.", completeSetup: "Terminer la configuration", visit: "Voir", institutionFallback: "Établissement médical", memberFallback: "Membre de l’Institution",
    },
    mohOnboarding: {
      workspaceTitle: "Informations sur le gouvernement et l’espace", reviewTitle: "Explorer avant de s’engager", reviewIntro: "Vous pouvez explorer l’espace Institution autorisé sans enregistrer de données d’administrateur, de gouvernement, d’établissement, de registre, de clinicien, de membre, de patient ou de CSV.", continueExploring: "Continuer à explorer", exploreFirst: "Explorer AfriMed™ d’abord", completeSetup: "Terminer la configuration", completeValidation: "Saisissez les coordonnées obligatoires de l’administrateur et du gouvernement avant de terminer la configuration.", explorationWorkspaceName: "Espace d’exploration MoH AfriMed™", exploreError: "L’espace d’exploration MoH autorisé n’a pas pu être ouvert.",
      loading: "Chargement de la configuration MoH autorisée…", title: "Coordonnées de l’administrateur", intro: "Créez un espace Institution autorisé pour explorer AfriMed™. L’ajout des établissements et du registre d’équipe peut être terminé ultérieurement.", fullName: "Nom complet*", email: "E-mail*", phone: "Téléphone*", verificationReadiness: "La vérification de l’e-mail et du téléphone est préparée pour la configuration validée. L’exploration n’envoie aucun message de vérification.", government: "Gouvernement*", governmentPlaceholder: "Par exemple : Gouvernement du Botswana", department: "Ministère / département*", departmentPlaceholder: "Par exemple : Ministère de la Santé", useCurrentLocation: "Utiliser ma position actuelle", locating: "Localisation…", locationLookupFailed: "Nous n’avons pas pu trouver votre adresse. Saisissez-la manuellement ou réessayez.", address1: "Adresse, ligne 1", address2: "Adresse, ligne 2", city: "Ville", region: "Région / province", country: "Pays*", emailValidation: "Utilisez l’adresse e-mail officielle du gouvernement du pays sélectionné.", intakeTitle: "Terminer le registre ultérieurement", intakeBody: "L’ajout des établissements, des membres opérationnels et des cliniciens est facultatif à cette étape. Il utilisera un flux gouverné de prévisualisation puis de validation ; aucun CSV n’est envoyé depuis cet écran.", explore: "Explorer l’accueil Institution", completeLater: "Terminer la configuration plus tard", saving: "Enregistrement…", saveReturn: "Enregistrer et revenir à l’accueil Institution", saveError: "L’espace MoH autorisé n’a pas pu être enregistré.", resumeMissingWorkspace: "Aucun espace MoH autorisé n’a été sélectionné.", resumeDenied: "Cette configuration MoH n’est pas disponible pour l’Institution autorisée active.",
    },
    institutionNotifications: {
      title: "Notifications", unavailableTitle: "Les notifications Institution ne sont pas encore activées", unavailableBody: "Cet espace ne consulte pas de notifications personnelles ou inter-Institutions. La diffusion rattachée à l’Institution apparaîtra ici lorsque son contrat de destinataire et d’autorité sera gouverné.",
    },
    institutionHelp: {
      title: "Documentation", intro: "Documentation produit de l’espace Institution actif. Elle n’accorde aucun accès supplémentaire et ne remplace pas les exigences locales juridiques, cliniques ou de gouvernance.", back: "Retour à la documentation", authoredSource: "Source rédigée", articlesLabel: "Domaines de documentation Institution",
      status: { current: "Fonctionnalité actuelle", inProgress: "En cours / partiellement connectée", planned: "Fonctionnalité prévue / future", governance: "Limite de gouvernance" },
      areas: {
        gettingStarted: { title: "Bien démarrer", summary: "Découvrez l’espace Institution autorisé, les établissements, les membres et les accès gouvernés." },
        clinicalOperations: { title: "Opérations cliniques", summary: "Les soins Institution réutilisent les identités et moteurs de soins AfriMed Global. Le lancement de soins gouverné et la provenance des rendez-vous sont en cours." },
        regionalHealth: { title: "Santé régionale", summary: "Vue d’ensemble, registre des signaux, qualité des données, piste d’audit, carte, préparation des établissements, périmètre de programme et préparation à DHIS2." },
        governanceData: { title: "Gouvernance et données", summary: "Autorité Institution, base légale, audit, périmètre, confidentialité et limite agrégée de Santé régionale." },
        integrations: { title: "Intégrations", summary: "Fondation de préparation à DHIS2 et futures limites d’intégration gouvernée. Aucun rapport de production ni échange de données n’est impliqué." },
        communityProgrammes: { title: "Communauté et programmes", summary: "Participation future aux programmes, flux des agents de santé communautaire, opérations communautaires, recherche longitudinale et surveillance régie par protocole." },
      },
    },
    institutionClinicians: {
      credentialEvidence: "Justificatifs professionnels", credentialEvidenceSupport: "Les documents sont examinés lorsque le clinicien a revendiqué son profil.", supplied: "Fourni", pendingReview: "En attente d’examen", done: "Terminer",
      title: "Cliniciens", new: "Nouveau clinicien", newClinician: "Nouveau clinicien", search: "Rechercher des cliniciens", noResults: "Aucun clinicien ne correspond à « {query} ».", backToClinicians: "Retour aux cliniciens",
      columns: { name: "Nom", email: "E-mail", phone: "Téléphone", professionSpecialty: "Profession / spécialité", status: "Statut" },
      filters: { status: "Filtrer les cliniciens par statut", open: "Filtrer les cliniciens", title: "Filtrer les cliniciens", allFacilities: "Tous les établissements", allProfessions: "Toutes les professions", allSpecialties: "Toutes les spécialités" },
      fields: { facility: "Établissement", profession: "Profession", specialty: "Spécialité", firstName: "Prénom", lastName: "Nom", email: "E-mail", phoneNumber: "Numéro de téléphone", countryCode: "Indicatif pays", gender: "Genre", dateOfBirth: "Date de naissance", yearsOfExperience: "Années d’expérience", registrationNumber: "Numéro d’enregistrement", registrationDate: "Date d’enregistrement", registrationYear: "Année d’enregistrement", registrationCountry: "Pays d’enregistrement", assignedFacilities: "Établissements attribués" },
      actions: { add: "Ajouter", edit: "Modifier", cancel: "Annuler", save: "Enregistrer", clear: "Effacer", apply: "Appliquer", copyLink: "Copier le lien", openInNewTab: "Ouvrir dans un nouvel onglet", blockClinician: "Bloquer le clinicien", delete: "Supprimer" },
      status: { all: "Tous", draft: "Brouillon", pendingInvite: "En attente", invited: "Invité", onboarding: "Intégration", active: "Actif", suspended: "Bloqué", removed: "Supprimé" },
      basicInformation: "Informations de base", specialty: "Spécialité", addSpecialty: "Ajouter la spécialité du clinicien", specialtySupport: "Assurez-vous d’avoir les documents de certification à portée de main.", addDiploma: "Ajouter votre diplôme", focusOn: "Domaines d’intérêt", focusSupport: "Domaines de connaissances médicales", formation: "Formation", formationEmpty: "Ajoutez le plus haut niveau d’études et toute qualification professionnelle pertinente.", languages: "Langues", languagesEmpty: "Ajoutez la langue parlée par le clinicien.", qualification: "Qualification", institution: "Institution", country: "Pays", year: "Année", noFacilities: "Créez d’abord un établissement médical", noClinicians: "Aucun clinicien pour le moment", noCliniciansBody: "Ajoutez des cliniciens manuellement dès maintenant. Les futures importations MoH et CSV d’équipe créeront les mêmes relations de liste en attente.", noFacilitiesBody: "Une relation avec un clinicien doit être attribuée à un ou plusieurs établissements médicaux dans cet espace Institution.", createMedicalInstitution: "Créer un établissement médical", noFilterResults: "Aucun clinicien ne correspond à ce filtre", detailsPending: "Détails en attente", notSupplied: "Non fourni", selectDate: "Sélectionner une date", previouslyRecordedYear: "Année précédemment enregistrée", female: "Femme", male: "Homme", nonBinary: "Non binaire", preferNotToSay: "Préfère ne pas répondre", native: "Langue maternelle", professional: "Professionnel", working: "Opérationnel", basic: "Élémentaire", moreActions: "Autres actions du clinicien", blockScope: "Cette action bloque uniquement cette relation Institution.", deleteScope: "Cette action retire cette relation Institution tout en préservant le compte et les dossiers du clinicien.", reason: "Motif", assignedFacilities: "Établissements attribués", activeMedicalInstitution: "Établissement médical actif", select: "Sélectionner", selected: "Sélectionné", selectFacilities: "Sélectionner des établissements", facilitySelectionRequired: "Sélectionnez au moins un établissement médical actif avant d’enregistrer.", avatar: "Avatar du clinicien", addFormation: "Ajouter une qualification", alreadyExists: "Ce clinicien possède déjà une relation de liste Institution.", updated: "Relation Institution du clinicien mise à jour.", savedDraft: "Brouillon de liste du clinicien enregistré. Aucun compte ni invitation n’a été créé.", saveError: "La relation du clinicien n’a pas pu être enregistrée.", statusError: "La relation Institution n’a pas pu être modifiée.", linkCopied: "Lien interne de la relation du clinicien copié.", linkCopyFallback: "Copiez l’URL de la relation du clinicien depuis la barre d’adresse de votre navigateur.", facilityAssignmentSupport: "Une relation avec un clinicien nécessite un ou plusieurs établissements médicaux actifs dans cet espace Institution.", searchGroup: "Cliniciens",
    },
    common: {
      cancel: "Annuler",
      actions: {
        close: "Fermer",
        print: "Imprimer",
        download: "Télécharger",
      },
      status: {
        saving: "Enregistrement...",
      },
      loading: "Chargement...",
      showAll: "Tout afficher",
      cookies: {
        title: "Préférences relatives aux cookies AfriMed™",
        intro:
          "Choisissez comment les cookies facultatifs sont utilisés. Les catégories facultatives sont désactivées par défaut.",
        support:
          "Conçu pour soutenir les principes de transparence, de limitation des finalités et de contrôle utilisateur prévus par le RGPD et les cadres africains de protection des données.",
        close: "Fermer les préférences relatives aux cookies",
        alwaysActive: "Toujours actif",
        rejectOptional: "Refuser les cookies facultatifs",
        saveChoices: "Enregistrer les choix",
        acceptAll: "Tout accepter",
        categories: {
          strictly: {
            label: "Essentiels",
            body:
              "Nécessaires aux fonctions de base comme la sécurité, la continuité de session et l’accessibilité. Ces cookies restent toujours actifs.",
          },
          statistics: {
            label: "Statistiques",
            body:
              "Mesure l’utilisation agrégée afin d’améliorer la qualité du service et de comprendre la demande de santé publique. AfriMed™ ne vend pas les données personnelles.",
          },
          performance: {
            label: "Performance",
            body:
              "Aide à surveiller la rapidité, la stabilité et la résilience du service sur différents appareils et réseaux.",
          },
          functionality: {
            label: "Fonctionnalité",
            body:
              "Mémorise certains choix, comme la langue et la région, afin de rendre votre retour plus fluide.",
          },
          targeting: {
            label: "Ciblage",
            body:
              "Utilisé pour limiter ou adapter la pertinence des campagnes. AfriMed™ ne vend pas les données personnelles.",
          },
          social: {
            label: "Social",
            body:
              "Permet la connexion sociale et certaines intégrations de contenu social. Les fournisseurs tiers peuvent appliquer leurs propres politiques.",
          },
          beacons: {
            label: "Balises",
            body:
              "Petits marqueurs utilisés pour comprendre la livraison et l’engagement des messages lorsque cela est nécessaire.",
          },
        },
      },
    },
    navigation: {
      me: "Moi",
      meMenu: "Menu du profil",
      search: "Rechercher",
      globalSearch: "Recherche globale",
      tabs: {
        home: "Accueil",
        dashboard: "Tableau de bord",
        appointments: "Rendez-vous",
        messages: "Messages",
        profile: "Profil",
      },
      menu: {
        viewProfile: "Voir le profil",
        dashboard: "Tableau de bord",
        ePrescriptions: "Ordonnances électroniques",
        availability: "Disponibilité",
        settings: "Réglages",
        signOut: "Se déconnecter",
      },
    },
    auth: {
      callback: {
        finishing: "Finalisation de la connexion...",
        redirectingToLogin:
          "Redirection vers la connexion pour demander un nouveau lien...",
        validating: "Validation du lien de connexion...",
        verifyingCode: "Verification du code de connexion...",
        linkNoLongerValidOnDevice:
          "Ce lien de connexion n'est plus valable sur cet appareil. Demandez un nouveau lien de connexion securise et ouvrez-le dans le meme navigateur.",
        linkVerificationFailed:
          "Le lien de connexion n'a pas pu etre verifie. Merci de demander un autre lien.",
        sessionEmailMismatch:
          "L'email de session ne correspond pas apres verification du lien de connexion securise.",
        sessionNotEstablished: "Session non etablie.",
        checkingProfile: "Connecte. Verification de votre profil...",
        pharmacyDashboardAs:
          "Connexion au tableau de bord pharmacie avec {email}... un instant s'il vous plait.",
        pharmacyDashboard:
          "Connexion au tableau de bord pharmacie... un instant s'il vous plait.",
        takingToAfrimed: "Connecte. Redirection vers AfriMed™...",
        takingToAdmin: "Bon retour. Redirection vers l'administration AfriMed...",
        takingHome: "Bon retour. Redirection vers Home...",
        takingOnboarding: "Connecte. Redirection vers l'onboarding...",
        resolvingAccountType: "Resolution du type de compte...",
        takingBack: "Connecte. Retour en cours...",
        chooseHowToContinue: "Connecte. Choisissez comment vous souhaitez continuer...",
        slowHint:
          "Le code de connexion est encore en cours de verification... Si cela dure trop longtemps, demandez un autre lien.",
        timeout:
          "La connexion n'a pas pu etre finalisee. Merci de demander un autre lien.",
        differentSession:
          "Ce lien de connexion appartient a une autre session sur ce navigateur. Demandez un nouveau lien et ouvrez uniquement le plus recent.",
        pharmacyEmailNotLinked:
          "Cet email n'est pas encore relie a une branche pharmacie.",
        pharmacyLinkFailed:
          "Nous n'avons pas pu finaliser ce lien de connexion pharmacie. Merci de demander un nouveau lien securise.",
        linkExpired: "Le lien de connexion a expire. Merci de demander un autre lien.",
        browserMismatch:
          "Le lien de connexion n'a pas pu etre valide dans ce navigateur. Demandez un autre lien et ouvrez le plus recent dans le meme navigateur.",
        failed:
          "La connexion a echoue. Veuillez reessayer ou demander un autre lien.",
        requestAnotherLink: "Demander un autre lien de connexion",
      },
    },
    appointments: {
      directory: {
        common: {
          verified: "Vérifié",
          nearby: "À proximité",
          specialty: "Spécialité",
          date: "Date",
          language: "Langue",
          virtual: "Virtuel",
          filter: "Filtrer",
        },
        sheet: {
          title: "Filtrer",
          data: "Données",
          bestRating: "Meilleure note",
          nearYou: "Près de vous",
          verified: "Vérifié",
          specialty: "Spécialité",
          languages: "Langues",
          availability: "Disponibilité",
          any: "Tous",
          searchSpecialty: "Rechercher des services",
          searchLanguages: "Rechercher des langues",
          specialists: "Spécialistes",
          selectOneOrMore: "Sélectionnez une ou plusieurs options",
          selectDate: "Sélectionner une date",
        },
        preview: {
          title: "Clinicien",
          profile: "Profil",
          missing: "Clinicien introuvable. Revenez en arrière et sélectionnez de nouveau un clinicien.",
          back: "Retour",
          bookNow: "Réserver",
          loading: "Chargement...",
        },
      },
      book: {
        title: "Choisissez une date et une heure",
        prompt: {
          title: "Prendre rendez-vous",
          body:
            "Continuez et suivez les étapes à l’écran. Au paiement sécurisé, une carte de test sera préremplie. Sélectionnez Payer et réserver pour confirmer le rendez-vous.",
          proceed: "Continuer",
          cancel: "Annuler",
        },
        doctorMissing:
          "Médecin introuvable. Revenez en arrière et sélectionnez de nouveau un médecin.",
        backToDoctors: "Retour aux médecins",
        sections: {
          patientSubject: "Pour qui est ce rendez-vous ?",
          service: "Sélectionner un service",
          type: "Sélectionner le type de rendez-vous",
          location: "Lieu",
          date: "Date",
          time: "Heure",
          payment: "Sélectionnez votre mode de paiement",
        },
        myself: "Moi-même",
        dependant: "Personne à charge",
        subjectSelf: "Votre compte reste propriétaire de la réservation et responsable du paiement.",
        loading: {
          dependants: "Chargement des personnes à charge...",
          services: "Chargement des services du médecin...",
          type: "Chargement des types de rendez-vous...",
          dates: "Chargement des dates disponibles...",
          times: "Chargement des horaires disponibles...",
          payments: "Chargement des modes de paiement du médecin...",
        },
        empty: {
          services: "Ce médecin n’a pas encore ajouté de services.",
          dates: "Ce médecin n’a pas encore ouvert de jours de rendez-vous.",
          unavailableDay: "Ce médecin n’est pas disponible le jour sélectionné.",
          noTimes: "Aucun horaire de rendez-vous n’est encore disponible pour ce jour.",
          payments: "Ce médecin n’a pas encore défini ses modes de paiement.",
          onlinePaymentsOnly:
            "Ce médecin n’a pas encore activé le paiement sécurisé en ligne par carte. La réservation reste désactivée jusqu’à ce qu’un mode de paiement réel soit disponible.",
        },
        errors: {
          loadServices: "Impossible de charger les services du médecin.",
          loadDates: "Impossible de charger les dates réservables.",
          loadTimes: "Impossible de charger les horaires disponibles.",
          loadPayments: "Impossible de charger les modes de paiement.",
        },
        mode: {
          virtual: "Virtuel",
          inPerson: "En présentiel",
        },
        evisitUnavailable:
          "Les consultations virtuelles ne sont pas encore activées pour ce médecin. La réservation reste donc alignée sur les disponibilités en présentiel.",
        virtualVisitNotice:
          "Cette réservation sera une consultation virtuelle pour un parcours patient plus rapide.",
        inpersonFallback: "Rendez-vous en présentiel au lieu de consultation du médecin.",
        selectDate: "Sélectionner une date",
        months: {
          label: "Mois disponibles",
        },
        insuranceAccepted: "Assurance acceptée",
        insuranceNotListed: "Assurance non listée",
        insuranceUseLabel: "Utiliser l’assurance",
        insuranceUse: "Utiliser l’assurance",
        insuranceSelfPay: "Paiement personnel",
        selfPay: "Paiement personnel",
        insuranceProviderPlaceholder: "Assureur",
        confirm: "Confirmer",
        makeSuggestion: "Faire une suggestion",
        minutesShort: "{count} min",
      },
      confirm: {
        payment: {
          creditCard: "Carte de crédit",
          debitCard: "Carte de débit",
          credit: "Cartes de crédit / débit",
        },
      },
      missedDialog: {
        title: "Rendez-vous manque",
        description:
          "Les participants a ce rendez-vous ne sont pas arrives dans la plage horaire reservee. Ce rendez-vous n'est plus disponible. Si vous avez toujours besoin de soins, choisissez un nouveau creneau disponible et effectuez une nouvelle reservation.",
      },
    },
    notifications: {
      filters: {
        all: "Tous",
        urgent: "Urgent",
        unread: "Non lus",
        appointments: "Rendez-vous",
      },
      common: {
        title: "Notifications",
        today: "Aujourd'hui",
        notification: "Notification",
        patient: "Patient",
        openDetails: "Ouvrir pour voir les details.",
        loading: "Chargement des notifications...",
        emptyTitle: "Aucune notification a afficher",
        emptyBody: "Votre boite de reception est vide pour le moment. Revenez plus tard !",
        errors: {
          loadFailed: "Impossible de charger les notifications.",
        },
      },
      doctor: {
        titles: {
          newAppointment: "Nouveau rendez-vous",
          cancellationRequest: "Demande d'annulation",
          appointmentUpdated: "Rendez-vous mis a jour",
        },
        support: {
          bookingAccepted: "La demande de rendez-vous de {name} a ete approuvee.",
          bookingDeclined: "La demande de rendez-vous de {name} a ete refusee.",
          bookingPending: "{name} a demande un rendez-vous. En attente de votre approbation.",
          rescheduleAccepted: "Le rendez-vous avec {name} a ete reprogramme.",
          rescheduleDeclined: "La nouvelle demande d'horaire de {name} a ete refusee.",
          reschedulePending:
            "{name} a demande un nouvel horaire de rendez-vous. En attente de votre approbation.",
          cancellationAccepted: "La demande d'annulation de {name} a ete approuvee.",
          cancellationDeclined: "La demande d'annulation de {name} a ete refusee.",
          cancellationPending:
            "{name} a demande l'annulation du rendez-vous. En attente de votre approbation.",
        },
      },
      title: "Notifications",
      loadingSingle: "Chargement de la notification...",
      singleTitle: "Notification",
      openDetailHelp: "Ouvrez les details pour consulter cette mise a jour.",
      pendingSchedule: "Planification en attente",
      time: {
        today: "Aujourd'hui",
        todayAt: "Aujourd'hui {time}",
      },
      fields: {
        doctor: "Medecin",
        patient: "Patient",
        assignedDoctor: "Medecin attribue",
        consultationLocation: "Lieu de consultation",
        dateTime: "Date et heure",
        location: "Lieu",
        details: "Details",
      },
      headers: {
        newAppointment: "Nouveau rendez-vous",
        newAppointmentTimeRequest: "Nouvelle demande d'horaire",
        cancellationRequest: "Demande d'annulation",
        appointmentUpdate: "Mise a jour du rendez-vous",
        cancellationRequestSent: "Demande d'annulation envoyee",
        appointmentConfirmed: "Rendez-vous confirme",
        appointmentCancelled: "Rendez-vous annule",
        appointmentDeclined: "Rendez-vous refuse",
        cancellationRequestDeclined: "Demande d'annulation refusee",
        newTimeRequestApproved: "Nouvel horaire approuve",
        newTimeRequestDeclined: "Nouvel horaire refuse",
        upcomingAppointment: "Rendez-vous a venir",
        ehrAccessRequest: "Demande d'acces au DME",
      },
      actions: {
        accept: "Accepter",
        decline: "Refuser",
        grantAccess: "Accorder l'acces",
      },
      status: {
        accepted: "Accepte",
        declined: "Refuse",
        granted: "Accorde",
        granting: "Attribution...",
        unavailable: "Indisponible",
      },
      dialogs: {
        cancelAppointmentTitle: "Annuler le rendez-vous ?",
        cancelAppointmentBody: "Etes-vous sur de vouloir annuler ce rendez-vous ?",
        declineAppointmentTitle: "Refuser le rendez-vous ?",
        declineAppointmentBody: "Etes-vous sur de vouloir refuser cette demande de rendez-vous ?",
        confirmCancel: "Oui, annuler",
        confirmDecline: "Oui, refuser",
      },
      errors: {
        signInAgain: "Veuillez vous reconnecter.",
        notFound: "Notification introuvable.",
        emptyInbox: "Votre boite de reception est vide pour le moment. Revenez plus tard !",
        load: "Impossible de charger la notification.",
        ehrGrant: "Impossible d'accorder l'acces au DME pour le moment.",
        cancelAppointment: "Impossible d'annuler le rendez-vous pour le moment.",
        acceptAppointment: "Impossible d'accepter le rendez-vous.",
        declineAppointment: "Impossible de refuser le rendez-vous.",
        approveNewTimeRequest: "Impossible d'approuver le nouvel horaire.",
        declineNewTimeRequest: "Impossible de refuser le nouvel horaire.",
        acceptAppointmentChange:
          "Impossible d'accepter le changement de rendez-vous pour le moment.",
        declineAppointmentChange:
          "Impossible de refuser le changement de rendez-vous pour le moment.",
      },
      toast: {
        appointmentAccepted: "Rendez-vous accepte",
        appointmentDeclined: "Rendez-vous refuse",
        appointmentUpdated: "Rendez-vous mis a jour",
        appointmentCancelled: "Rendez-vous annule",
        yourAppointmentUpdated: "Votre rendez-vous a ete mis a jour",
        cancellationApproved: "Annulation approuvee",
        cancellationDeclined: "Annulation refusee",
        cancellationRequestSent: "Demande d'annulation envoyee",
        cancellationApprovedRefundProcessing:
          "Annulation approuvee. Le remboursement est en cours.",
        cancellationApprovedReversalFollowUp:
          "Annulation approuvee. L'inversion du paiement demande un suivi.",
        cancellationApprovedReversalAttention:
          "Annulation approuvee. L'inversion du paiement demande encore de l'attention.",
        appointmentDeclinedRefundProcessing:
          "Rendez-vous refuse. Le remboursement est en cours.",
        appointmentDeclinedReversalFollowUp:
          "Rendez-vous refuse. L'inversion du paiement demande un suivi.",
        appointmentDeclinedReversalAttention:
          "Rendez-vous refuse. L'inversion du paiement demande encore de l'attention.",
      },
      messages: {
        acceptedNewTime: "Vous avez accepte le nouvel horaire du rendez-vous.",
        patientAcceptedNewTime: "Le patient a accepte le nouvel horaire du rendez-vous.",
        declinedNewTime: "Vous avez refuse le nouvel horaire du rendez-vous.",
        patientDeclinedNewTime: "Le patient a refuse le nouvel horaire du rendez-vous.",
        doctorProposedNewTime:
          "Votre medecin a propose un nouvel horaire. Verifiez-le et confirmez le changement.",
        ehrAccessRequested:
          "Votre medecin a demande l'acces a votre dossier de sante pour ce rendez-vous.",
        upcomingAppointmentForDate: "Votre rendez-vous est prevu pour le {date}.",
        upcomingAppointmentGeneric: "Votre rendez-vous est programme.",
        bookingApprovedForDate: "La demande de rendez-vous de {name} pour {date} a ete approuvee.",
        bookingApprovedGeneric: "La demande de rendez-vous de {name} a ete approuvee.",
        bookingPendingForDate:
          "{name} a demande un rendez-vous pour le {date}. En attente de votre approbation.",
        yourAppointmentAcceptedForDate: "Votre rendez-vous du {date} a ete accepte.",
        yourAppointmentAcceptedGeneric: "Votre demande de rendez-vous a ete acceptee.",
        bookingDeclinedForDate: "La demande de rendez-vous de {name} pour {date} a ete refusee.",
        bookingDeclinedGeneric: "La demande de rendez-vous de {name} a ete refusee.",
        yourAppointmentDeclinedForDate: "Votre rendez-vous du {date} a ete refuse.",
        yourAppointmentDeclinedGeneric: "Votre demande de rendez-vous a ete refusee.",
        cancellationApprovedForDate:
          "{name} a demande l'annulation du rendez-vous du {date}. Approuve.",
        cancellationApprovedGeneric: "{name} a demande l'annulation du rendez-vous. Approuve.",
        yourCancellationApprovedForDate:
          "Votre demande d'annulation pour le {date} a ete approuvee.",
        yourCancellationApprovedGeneric: "Votre demande d'annulation a ete approuvee.",
        cancellationDeclinedForDate:
          "{name} a demande l'annulation du rendez-vous du {date}. Refuse.",
        cancellationDeclinedGeneric: "{name} a demande l'annulation du rendez-vous. Refuse.",
        yourCancellationDeclinedForDate:
          "Votre demande d'annulation pour le {date} a ete refusee.",
        yourCancellationDeclinedGeneric: "Votre demande d'annulation a ete refusee.",
        rescheduleApprovedGeneric: "{name} a demande un nouvel horaire. Approuve.",
        yourRescheduleApprovedGeneric: "Votre demande de nouvel horaire a ete approuvee.",
        rescheduleDeclinedGeneric: "{name} a demande un nouvel horaire. Refuse.",
        yourRescheduleDeclinedGeneric: "Votre demande de nouvel horaire a ete refusee.",
        cancellationRequestSentForDate:
          "Votre demande d'annulation du rendez-vous du {date} a ete envoyee.",
        cancellationRequestSentGeneric:
          "Votre demande d'annulation de ce rendez-vous a ete envoyee.",
        patientCancellationPendingForDate:
          "{name} a demande l'annulation du rendez-vous du {date}. En attente d'approbation.",
        patientCancellationPendingGeneric:
          "{name} a demande l'annulation du rendez-vous. En attente d'approbation.",
      },
      goToAppointment: "Ouvrir le rendez-vous",
      goToAppointmentDetails: "Ouvrir les details du rendez-vous",
      referral: {
        label: "Orientation",
        openAction: "Ouvrir l'orientation",
      },
    },
    messages: {
      label: "Message",
      openAction: "Ouvrir le message",
      loadingConversation: "Chargement de la conversation...",
      noMessagesYet: "Aucun message pour le moment.",
      inbox: {
        fallbackTitle: "Conversation",
      },
      conversation: {
        now: "Maintenant",
        deletedForMe: "Vous avez supprime ce message",
        deletedBySender: "Le message a ete supprime par l'expediteur",
        deleteDialog: {
          close: "Fermer la boite de suppression",
          title: "Supprimer le message ?",
          body:
            "Supprimez ce message pour vous seulement, ou retirez-le pour les deux participants. Cette action est irreversible.",
          deleting: "Suppression...",
          forMe: "Supprimer pour moi",
          forBoth: "Supprimer pour les deux",
        },
        toast: {
          dismiss: "OK",
          undo: "Annuler",
          deletedForMe: "Message supprime pour moi",
          deletedForBoth: "Message supprime pour les deux",
          deleteError: "Impossible de supprimer ce message pour le moment.",
        },
        composer: {
          all: "Tous",
          attach: "Joindre une photo",
          send: "Envoyer le message",
          thinking: "Fola reflechit",
          selectedAttachmentAlt: "Piece jointe selectionnee",
        },
        topBar: {
          back: "Retour aux messages",
          openFola: "Ouvrir la conversation Fola",
          compose: "Rediger un message",
        },
        sourceThreadFallback: "Conversation source",
        errors: {
          resolve: "Impossible de resoudre cette conversation.",
          load: "Impossible de charger cette conversation.",
          send: "Impossible d'envoyer le message.",
        },
      },
    },
    fola: {
      widget: {
        title: "Fola - Assistant AfriMed™",
        greeting: "Bonjour, je suis Fola. Je peux aider avec les OTP, les magic links et les reglages.",
        open: "Ouvrir Fola",
        placeholder: "Demandez a Fola...",
        sending: "Envoi...",
        send: "Envoyer",
        error: "Desole, un probleme est survenu. Veuillez reessayer.",
      },
    },
    pharmacy: {
      invite: {
        title: "Invitation pharmacie",
        subtitle:
          "Verifiez votre invitation d'equipe AfriMed Pharmacy, confirmez l'email invite, puis demandez le lien de connexion securise pour ce compte exact du membre de la pharmacie.",
        body:
          "AfriMed utilisera l'email invite ci-dessous pour l'etape de connexion du membre de la pharmacie. Ouvrez le tout dernier email de connexion sur ce meme navigateur ou appareil, puis AfriMed poursuivra vers le tableau de bord pharmacie.",
        emailLabel: "Email invite",
        emailMissing: "Aucun email invite n'etait joint a ce lien.",
        emailMissingBody:
          "Ce lien d'invitation ne contient pas l'email invite. Revenez vers l'expediteur et demandez la derniere invitation pharmacie AfriMed.",
        alreadySignedIn:
          "Cet email invite est deja connecte sur ce navigateur. Continuez directement vers le tableau de bord pharmacie.",
        switchEmail:
          "Un autre compte est connecte sur ce navigateur. Continuez avec l'email invite afin qu'AfriMed relie le bon compte de membre pharmacie.",
        cta: {
          dashboard: "Continuer vers le tableau de bord pharmacie",
          opening: "Ouverture de la connexion...",
          invitedEmail: "Continuer avec l'email invite",
          returnLogin: "Retour a la connexion",
          requestLink: "Continuer pour demander le lien de connexion",
        },
      },
      dashboard: {
        income: {
          range: {
            threeMonths: "3 derniers mois",
            sixMonths: "6 derniers mois",
            year: "Derniere annee",
          },
          menu: "Periode de revenus pharmacie",
        },
        lowStock: {
          title: "Stock bas",
          subtitle:
            "Une vue des lignes de medicaments en stock bas a reapprovisionner avant que les prescriptions soient reroutees.",
          openPrompt: "Ouvrir le prompt Fola",
          emptyTitle: "Aucune ligne en stock bas pour le moment",
          emptyCopy:
            "AfriMed affichera ici les lignes de medicaments en stock bas une fois que l'inventaire de la branche sera ajoute et suivi selon les seuils de reapprovisionnement.",
        },
      },
      settings: {
        language: {
          availableNow: "Disponible maintenant",
          availableNowCopy:
            "Choisissez la langue actuelle de l'application AfriMed pour cet appareil et ce compte.",
          comingSoonTitle: "Bientot disponible",
          comingSoonCopy:
            "Les langues d'Afrique australe sont preparees avec soin pour un deploiement fiable en contexte de sante.",
        },
        notificationPreferences: {
          appointmentReminders: {
            title: "Rappels de rendez-vous",
            introDoctor:
              "AfriMed envoie un seul rappel proche du debut, une heure avant les visites confirmees. Le resume du matin viendra ensuite.",
            introPatient:
              "AfriMed envoie des rappels 24 heures et 2 heures avant les visites confirmees. Le SMS aide meme quand vous n'etes pas connecte.",
            timingDoctor: "Horaire actuel : 1 heure avant le rendez-vous.",
            timingPatient: "Horaire actuel : 24 heures et 2 heures avant le rendez-vous.",
          },
          channels: {
            inApp: {
              title: "Rappels dans l'application",
              support: "S'affiche dans la boite de notifications AfriMed et dans les surfaces de rendez-vous.",
            },
            sms: {
              title: "Rappels SMS",
              support: "Meilleure solution de secours quand vous n'etes pas connecte.",
            },
            email: {
              title: "Rappels par email",
              support: "Livraison de secours avec les details du rendez-vous.",
            },
            push: {
              title: "Notifications push",
              supportGranted:
                "Utilise les notifications du navigateur pour les alertes de soins importantes et urgentes quand AfriMed n'est pas au premier plan.",
              supportPrompt:
                "Activez les notifications du navigateur pour les alertes de soins importantes et urgentes lorsqu'elles sont proposees.",
              unsupported: "Ce navigateur ne prend pas en charge les demandes de notification ici.",
            },
            sound: {
              title: "Son d'alerte urgente",
              support: "Joue un court signal sonore pour les alertes urgentes pendant qu'AfriMed est ouvert.",
            },
            vibration: {
              title: "Vibration urgente",
              support:
                "Ajoute une courte impulsion haptique sur les appareils compatibles pour les alertes urgentes.",
            },
          },
          footer:
            "Les notifications du navigateur couvrent maintenant les alertes de soins pendant la session quand l'autorisation est accordee. La livraison push complete hors session depend encore de l'infrastructure d'abonnement service worker et reste differee.",
        },
        fola: {
          title: "Prompt operations pharmacie",
          copy:
            "Ouvrez Fola avec un prompt operations pharmacie pour la redaction et l'orientation generale du flux AfriMed dans Messages.",
          cta: "Ouvrir Fola",
        },
        members: {
          title: "Membres",
          subtitle:
            "L'integration du proprietaire principal est deja terminee. Ajoutez ici l'equipe de la branche et gardez les roles, paiements, livraison et handoff d'inventaire bien alignes.",
          kpi: {
            branchOwner: "Places proprietaire",
            branchOwnerCopy:
              "Le proprietaire principal ou l'administrateur reste l'ancre des permissions de cette branche.",
            activeMembers: "Membres actifs",
            activeMembersCopy:
              "Seuls les membres acceptes comptent comme actifs. Les lignes invitees restent en attente jusqu'a la connexion officielle.",
            pendingInvites: "Invitations en attente",
            pendingInvitesCopy:
              "Utilisez l'email invite sur la page de connexion AfriMed. La ligne change seulement apres acceptation et liaison de l'invitation.",
          },
          form: {
            name: "Nom complet",
            email: "Email",
            phone: "Telephone",
            role: "Role",
          },
          sheet: {
            send: "Envoyer l'invitation",
          },
          status: {
            invited: "Invitation • En attente",
            active: "Actif",
          },
          inviteTitle: "Inviter ou importer des membres de la branche",
          inviteCopy:
            "Copiez un brief de configuration pour les equipes paie ou operations, ou importez un CSV du personnel lorsque la branche gere deja son equipe dans un logiciel RH.",
          inviteFlowCopy:
            "Les nouveaux membres restent Invites jusqu'a leur connexion avec l'email invite. Ce n'est qu'alors que la branche doit les considerer comme actifs.",
          copyBrief: "Copier le brief d'invitation",
          importCsv: "Importer CSV",
          addCta: "Nouveau membre",
          columns: {
            name: "Nom",
            role: "Role",
            actions: "Actions",
          },
          copied: "Brief d'invitation copie",
          copyFailed: "Impossible de copier pour le moment",
          imported: "{count} lignes du personnel ont ete importees depuis {file}.",
          importEmpty: "{file} a ete televerse, mais aucune ligne de personnel exploitable n'a ete detectee.",
          importFailed: "Le CSV du personnel n'a pas pu etre analyse.",
          removed: "Membre supprime",
          removeFailedDetail: "Echec de suppression du membre : {message}",
          removeFailed: "Impossible de supprimer ce membre pour le moment.",
          inviteLinkCopied: "Lien de connexion copie",
          inviteLinkCopyFailed: "Impossible de copier le lien de connexion pour le moment",
          inviteSending: "Envoi de l'email d'invitation...",
          inviteEmailSent: "Email d'invitation envoye",
          inviteEmailFailed:
            "L'email d'invitation ne peut pas etre envoye pour le moment. Utilisez plutot Copier le lien de connexion.",
          inviteFailedDetail: "Echec de l'email d'invitation : {message}",
          pendingHint: "En attente d'acceptation de l'invitation",
          resendInvite: "Renvoyer l'invitation",
          resendInviteBusy: "Envoi...",
          resendInviteSent: "Envoye",
          resendInviteFailed: "Reessayer l'invitation",
          copySignInLink: "Copier le lien de connexion",
          copySignInLinkBusy: "Copie...",
          copySignInLinkDone: "Copie",
          copySignInLinkRetry: "Copier a nouveau",
          remove: "Supprimer le membre",
          removeLabel: "Supprimer",
          removeDialog: {
            title: "Notice",
            body: "Supprimer {member} de l'equipe de la branche ?",
            memberFallback: "ce membre",
            cancel: "Annuler",
            confirm: "Oui",
          },
          create: {
            title: "Nouveau membre",
            subtitle:
              "Ajoutez un membre d'equipe de la branche pour la dispensation, le stock, la finance ou le handoff de livraison.",
            copy:
              "Le proprietaire principal ou l'administrateur reste l'ancre des permissions de la branche. Utilisez cette etape pour ajouter proprement l'equipe quotidienne de la pharmacie.",
            roleGuideTitle: "Guide des roles",
            roleGuideCopy:
              "Gardez la meme logique de handoff propre entre Pharmacy, Admin et Medical Institutes. Attribuez seulement le role de branche necessaire maintenant.",
            formTitle: "Details du membre",
            formCopy:
              "Gardez cette invitation legere pour le moment. Les roles peuvent encore etre ajustes plus tard dans Settings.",
            saving: "Envoi de l'invitation...",
            inviteSent:
              "Email d'invitation envoye. Le membre reste en attente jusqu'a sa connexion avec l'email invite.",
            inviteFailed:
              "L'email d'invitation n'a pas pu etre envoye pour le moment. Veuillez reessayer.",
            inviteFailedDetail: "Echec de l'email d'invitation : {message}",
          },
          fola: {
            title: "Lancer un prompt de configuration pharmacie",
            subtitle:
              "Ouvrez Fola avec un prompt d'operations pharmacie pour la configuration des roles, la preparation CSV ou la planification du handoff de livraison dans Messages.",
            cta: "Ouvrir Fola",
            roles: "Lancer le prompt des roles",
            csv: "Lancer le prompt CSV",
            handoff: "Lancer le prompt de handoff",
          },
          roles: {
            dispensing: {
              label: "Pharmacien dispensateur",
              note:
                "Delivre les prescriptions, approuve les substitutions et assure le handoff des commandes pretes pour le patient.",
            },
            inventory: {
              label: "Responsable inventaire",
              note: "Maintient le stock, les notes fournisseurs et les actions de stock bas.",
            },
            finance: {
              label: "Responsable finance",
              note: "Suit les exports de reglement, la visibilite des frais et le rapprochement.",
            },
            delivery: {
              label: "Responsable livraison",
              note: "Coordonne l'expedition, les adresses verifiees et l'etat de preparation de la livraison.",
            },
            branchAdmin: {
              label: "Administrateur de branche",
              note: "Soutient les operations quotidiennes de la branche et la coordination de l'equipe.",
            },
          },
        },
      },
      inventory: {
        kpi: {
          tracked: "SKU suivis",
          activeCatalog: "Catalogue actif",
          lowStockAlerts: "Alertes de stock bas",
          needsAction: "Action requise",
          coldChain: "Articles de chaine du froid",
          verifiedStorage: "Stockage verifie",
        },
        stockControl: {
          title: "Controle du stock",
          subtitle:
            "Gerez la disponibilite des medicaments afin qu'AfriMed puisse afficher clairement la disponibilite de la branche, la confiance de stock et le prix visible pour le patient.",
        },
        actions: {
          openFolaPrompt: "Ouvrir le prompt Fola",
          addStockItem: "Ajouter un article de stock",
        },
        loading: "Chargement de l'inventaire de la branche...",
        onHand: "En stock",
        reorderLevel: "Seuil de reapprovisionnement",
        retailPrice: "Prix de vente",
        reorderNow: "Reapprovisionner",
        healthy: "Sain",
        empty: {
          title: "Aucun article de stock ajoute pour le moment",
          copy:
            "Ajoutez ici de vrais articles de stock pour que les alertes, le routage des prescriptions et le prix visible au patient restent credibles.",
        },
        create: {
          title: "Ajouter un article de stock",
          subtitle:
            "Creez une ligne d'inventaire reelle afin que le controle du stock, le routage des prescriptions et les totaux pharmacie commencent a refleter la realite de la branche.",
          copy:
            "Cela ecrit directement dans le profil d'inventaire de la branche utilise par le tableau de bord Pharmacy. Cela remplace l'ancien modele de secours par de vraies donnees de stock.",
          details: "Details de l'article",
          save: "Enregistrer l'article de stock",
          saveError: "Nous n'avons pas encore pu enregistrer cet article de stock.",
        },
        edit: {
          title: "Modifier l'article de stock",
          subtitle:
            "Mettez a jour cette ligne d'inventaire de la branche afin que le controle du stock et les alertes de stock bas restent a jour.",
          missing: "Nous n'avons pas retrouve cet article de stock.",
          loadError: "Nous n'avons pas encore pu charger cet article de stock.",
          save: "Enregistrer les modifications",
        },
        detail: "Dosage / detail",
        currentStock: "Stock actuel",
        minimalStock: "Stock minimal",
        unitPrice: "Prix unitaire",
        supplier: "Fournisseur",
        branch: "Branche",
        batchNumber: "Numero de lot",
        expiryDate: "Date d'expiration",
        binLocation: "Emplacement de bac",
        imageUrl: "URL de l'image",
        imagePreview: "Apercu de l'image",
        imageHelp:
          "Collez une URL d'image hebergee pour ce medicament. Si elle est vide, AfriMed gardera l'image de secours du stock.",
        errors: {
          loadFailed: "Nous n'avons pas encore pu charger l'inventaire de la branche.",
          removeFailed: "Nous n'avons pas encore pu retirer cet article de stock.",
        },
      },
      search: {
        results: "Resultats de recherche",
      },
      prescriptions: {
        price: "Prix",
        medicineSubtotal: "Sous-total des médicaments",
        deliveryCharge: "Frais de livraison",
        quoteTotal: "Total du devis",
        confirmDeliveryQuote: "Je confirme les frais de livraison et le total avant de demander le paiement.",
        priceSet: "Prix defini : {subtotal}",
        priceReady: "Prix pret",
        actions: {
          setPrice: "Definir le prix",
          reject: "Refuser",
          accept: "Accepter pour examen",
          markReadyForPickup: "Marquer pret pour retrait",
        },
        errors: {
          invalidPrice: "Saisissez un prix valide avant d'enregistrer.",
          invalidDelivery: "Saisissez des frais de livraison valides.",
          confirmDelivery: "Confirmez les frais de livraison avant de demander le paiement.",
          updateFailed: "Impossible de mettre a jour cette prescription pour le moment.",
        },
      },
      order: {
        status: {
          selected: "Pharmacie selectionnee",
          preparing: "Preparation",
          awaitingPayment: "Paiement en attente",
          paid: "Paye",
          readyForPickup: "Pret pour retrait",
          rejected: "Refuse",
          issued: "Emis",
        },
        errors: {
          missingId: "L'identifiant de prescription est manquant.",
          loadFailed: "Impossible de charger cette commande pharmacie.",
        },
        defaults: {
          prescriptionItem: "Element de prescription",
          instructions: "Consultez les instructions de prescription jointes a cette commande.",
          pharmacy: "Pharmacie AfriMed",
        },
        title: "Commande de prescription",
        patient: "Patient",
        medication: "Medicament",
        medicationList: "Liste des medicaments",
        medicationItem: "Medicament {index}",
        quotedPrice: "Prix propose",
        pendingPrice: "Prix en attente",
        pendingPriceHelper: "Definissez le prix depuis la file pharmacie lorsque le stock est confirme.",
        rxId: "RxID",
        issuedAt: "Emis le {date}",
        updatedAt: "Mis a jour le {date}",
        openQueue: "Ouvrir la file de prescriptions",
        print: "Imprimer la commande",
        qr: {
          title: "QR",
          copy: "La phase 1 utilise la route simple de commande AfriMed pour la verification.",
          alt: "QR pour {id}",
          unavailable: "QR indisponible",
        },
      },
      payments: {
        link: {
          accessMissing:
            "Seuls les comptes lies du proprietaire pharmacie ou des membres de branche peuvent creer des liens de paiement ici.",
          createError: "Nous n'avons pas pu creer le lien de paiement pharmacie pour le moment.",
          copyError: "Nous n'avons pas pu copier le lien de paiement pour le moment.",
          title: "Creer un lien de paiement",
          subtitle:
            "Preparez un lien de paiement securise pour des renouvellements rapides, des achats OTC ou des achats pharmacie payes par la diaspora.",
          description: "Description",
          amount: "Montant",
          recipientEmail: "Email du destinataire",
          generate: "Generer le lien de paiement",
          copy: "Copier le lien",
          email: "Envoyer par email",
          footnote:
            "Cela reutilise le flux actuel de paiement sponsor AfriMed et applique la meme structure de frais de plateforme de 10 % que les paiements sponsors lies a Stripe.",
          generatedLink: "Lien genere",
          generatedLinkEmpty:
            "Generez d'abord le lien de paiement securise, puis copiez-le ou envoyez-le proprement.",
          settlementPreview: "Apercu du reglement",
        },
      },
      help: {
        playbook: {
          title: "Guide pharmacie",
          subtitle: "Le premier guide operateur pour la voie Pharmacy.",
        },
        step1: {
          title: "1. Verifier la prescription",
          copy: "Utilisez le scanner QR pour confirmer la prescription AfriMed avant la dispensation.",
        },
        step2: {
          title: "2. Confirmer le stock et le prix",
          copy: "Affichez clairement la disponibilite, le sous-total du medicament et le cout de livraison.",
        },
        step3: {
          title: "3. Assurer un handoff propre",
          copy:
            "Marquez le retrait ou la livraison comme termines afin que le medecin, le patient et la pharmacie restent alignes.",
        },
      },
      folaPrompts: {
        lowStockFallback:
          "Aidez-moi a rediger un message calme de reapprovisionnement et des notes de handoff de branche pour le suivi du stock pharmacie. N'assumez pas une synchronisation automatique de l'inventaire et ne prenez pas de decisions autonomes.",
        lowStockIntro:
          "Aidez-moi a rediger un message calme de reapprovisionnement et des notes de handoff de branche pour ces articles en stock bas.",
        lowStockOutro:
          "Restez uniquement dans le support. N'assumez pas une synchronisation automatique de l'inventaire et ne prenez pas de decisions autonomes.",
        helpPlaybook:
          "Aidez-moi a faire fonctionner proprement la voie pharmacie AfriMed : configuration d'equipe, handoff des prescriptions, communication de stock bas et flux de livraison.",
      },
    },
    francophoneActivation: {
      label: "Parcours médecin francophone",
    },
    common: {
      actions: {
        close: "Fermer",
        print: "Imprimer",
        download: "Télécharger",
        done: "Terminé",
        continue: "Continuer",
        clearSearch: "Effacer la recherche",
      },
      status: {
        saving: "Enregistrement...",
      },
      saving: "Enregistrement...",
      save: "Enregistrer",
      continue: "Continuer",
      cancel: "Annuler",
      confirm: "Confirmer",
      delete: "Supprimer",
      discard: "Quitter sans enregistrer",
      keepEditing: "Continuer la modification",
      changesSaved: "Modifications enregistrées",
      selectPlaceholder: "Sélectionner...",
      yes: "Oui",
      no: "Non",
      back: "Retour",
      close: "Fermer",
      search: "Rechercher",
      today: "Aujourd’hui",
      previous: "Précédent",
      next: "Suivant",
      expand: "Développer",
      collapse: "Réduire",
      legal: {
        terms: "Conditions générales",
        privacy: "Politique de confidentialité",
      },
    },
    fola: {
      widget: {
        title: "Fola - Assistant AfriMed™",
        greeting: "Bonjour, je suis Fola. Je peux vous aider avec les codes OTP, les liens sécurisés et les réglages.",
        open: "Ouvrir Fola",
        placeholder: "Demandez à Fola...",
        sending: "Envoi...",
        send: "Envoyer",
        error: "Désolé, un problème est survenu. Veuillez réessayer.",
      },
      readiness: {
        doctor: {
          profileReadinessReview: "Vérification du profil",
          profileReadinessReviewDetail: "Vos informations principales sont en place. Vérifiez votre profil, votre photo, votre signature et vos disponibilités avant d’ouvrir les réservations aux patients.",
          profileReadinessReviewCta: "Vérifier le profil",
          identity: "Confirmez votre identité",
          identityDetail: "Complétez vos informations d’identité afin que votre profil clinicien soit correctement rattaché à votre compte.",
          identityCta: "Identité",
          registrationDetails: "Renseignez votre inscription professionnelle",
          registrationDetailsDetail: "Ajoutez votre numéro d’enregistrement, votre pays d’inscription et l’ordre professionnel concerné.",
          registrationDetailsCta: "Inscription",
          registrationUpload: "Ajoutez votre justificatif professionnel",
          registrationUploadDetail: "Téléversez votre preuve d’inscription ou de licence pour faciliter la vérification du profil.",
          registrationUploadCta: "Ajouter le justificatif",
          languages: "Ajoutez vos langues de consultation",
          languagesDetail: "Indiquez votre langue principale et les autres langues dans lesquelles vous pouvez prendre en charge les patients.",
          languagesCta: "Langues",
          specialty: "Choisissez votre spécialité",
          specialtyDetail: "Sélectionnez ou ajoutez votre spécialité afin que les patients comprennent clairement votre domaine clinique.",
          specialtyCta: "Spécialité",
          subspecialty: "Ajoutez vos sous-spécialités",
          subspecialtyDetail: "Précisez votre expertise clinique lorsque cela aide les patients à trouver le bon médecin.",
          subspecialtyCta: "Sous-spécialité",
          practiceMode: "Choisissez votre mode de pratique",
          practiceModeDetail: "Indiquez si vous consultez en ligne, en présentiel ou les deux. Cela n’affecte pas les règles de vérification.",
          practiceModeCta: "Mode de pratique",
          evisit: "Activez les consultations virtuelles",
          evisitDetail: "Autorisez les patients à réserver des consultations virtuelles selon vos disponibilités.",
          evisitCta: "Consultations virtuelles",
          services: "Ajoutez vos services et tarifs",
          servicesDetail: "Créez au moins un service réservable avec durée, devise et tarif.",
          servicesCta: "Services",
          consultation: "Préparez votre flux de consultation",
          consultationDetail: "Vos rendez-vous, notes VitalRep™, prescriptions et actions post-consultation seront regroupés dans le dossier de rendez-vous.",
          consultancyCta: "Consultations",
          availability: "Définissez vos disponibilités",
          availabilityDetail: "Ouvrez les jours et horaires où les patients peuvent demander un rendez-vous.",
          availabilityCta: "Disponibilités",
          editIntro: "Peaufinez votre profil public",
          editIntroDetail: "Ajoutez votre photo, votre résumé, vos domaines d’intérêt, votre signature et votre galerie avant la démonstration patient.",
          editIntroCta: "Modifier l’introduction",
          discoveryReview: "Excellent. Rendons votre profil visible.",
          discoveryReviewDetail: "Votre profil principal est prêt. AfriMed™ peut maintenant vérifier si les patients peuvent vous trouver et demander un rendez-vous.",
          discoveryReviewCta: "Vérifier le profil",
          ready: "Votre profil médecin est prêt",
          readyDetail: "Vous pouvez maintenant recevoir des demandes de rendez-vous et guider un patient jusqu’à la consultation.",
          readyCta: "Voir le profil",
          nextStepCta: "Étape suivante",
          identityCurrent: "Complétez votre identité",
          identityCurrentDetail: "Renseignez les informations requises, enregistrez, puis poursuivez l’étape suivante.",
          registrationDetailsCurrent: "Complétez votre inscription professionnelle",
          registrationDetailsCurrentDetail: "Ajoutez votre numéro, votre pays et votre organisme d’inscription, puis enregistrez.",
          registrationUploadCurrent: "Ajoutez votre preuve d’inscription",
          registrationUploadCurrentDetail: "Téléversez ou capturez votre document professionnel, puis enregistrez.",
          languagesCurrent: "Ajoutez vos langues",
          languagesCurrentDetail: "Ajoutez votre langue principale et les autres langues parlées, puis enregistrez.",
          specialtyCurrent: "Sélectionnez votre spécialité",
          specialtyCurrentDetail: "Recherchez une spécialité localisée ou ajoutez-la manuellement en français.",
          subspecialtyCurrent: "Ajoutez vos sous-spécialités",
          subspecialtyCurrentDetail: "Ajoutez les domaines d’expertise qui décrivent réellement votre pratique.",
          practiceModeCurrent: "Choisissez votre mode de pratique",
          practiceModeCurrentDetail: "Sélectionnez virtuel, présentiel ou hybride, puis enregistrez.",
          evisitCurrent: "Activez les consultations virtuelles",
          evisitCurrentDetail: "Activez les consultations virtuelles et vérifiez que vos horaires peuvent recevoir des réservations.",
          servicesCurrent: "Ajoutez un service réservable",
          servicesCurrentDetail: "Ajoutez un service, une durée, une devise et un tarif afin que les patients puissent réserver correctement.",
          consultancyCurrent: "Préparez l’espace de consultation",
          consultancyCurrentDetail: "Vérifiez le dossier de rendez-vous, VitalRep™, la prescription et les actions post-consultation avant la démonstration.",
          availabilityCurrent: "Ouvrez vos disponibilités",
          availabilityCurrentDetail: "Activez vos jours réguliers et vérifiez l’horizon de réservation.",
          editIntroCurrent: "Peaufinez votre profil",
          editIntroCurrentDetail: "Ajoutez votre photo, votre résumé, vos domaines d’intérêt, vos langues et votre galerie.",
          editDobCurrent: "Ajoutez votre date de naissance",
          editDobCurrentDetail: "Complétez cette information d’identité, puis enregistrez.",
          identityComplete: "Identité complétée",
          identityCompleteDetail: "Votre identité de base est enregistrée.",
          registrationDetailsComplete: "Inscription professionnelle complétée",
          registrationDetailsCompleteDetail: "Vos informations d’inscription sont enregistrées.",
          registrationUploadComplete: "Justificatif ajouté",
          registrationUploadCompleteDetail: "Votre document professionnel est disponible pour vérification.",
          languagesComplete: "Langues complétées",
          languagesCompleteDetail: "Vos langues de consultation sont enregistrées.",
          specialtyComplete: "Spécialité complétée",
          specialtyCompleteDetail: "Votre spécialité principale est enregistrée.",
          subspecialtyComplete: "Sous-spécialités complétées",
          subspecialtyCompleteDetail: "Vos domaines d’expertise sont enregistrés.",
          practiceModeComplete: "Mode de pratique complété",
          practiceModeCompleteDetail: "Votre modèle de consultation est enregistré.",
          evisitComplete: "Consultations virtuelles activées",
          evisitCompleteDetail: "Les patients peuvent réserver des consultations virtuelles selon vos règles.",
          servicesComplete: "Services complétés",
          servicesCompleteDetail: "Vos services réservable et vos tarifs sont enregistrés.",
          consultancyComplete: "Flux de consultation prêt",
          consultancyCompleteDetail: "Le dossier de rendez-vous, VitalRep™ et les prescriptions sont prêts pour la démonstration.",
          availabilityComplete: "Disponibilités complétées",
          availabilityCompleteDetail: "Votre calendrier de réservation est configuré.",
        },
      },
    },
    home: {
      patient: {
        welcome: "Bienvenue, {firstName} !",
        fallbackName: "Utilisateur AfriMed™",
        actions: {
          openProfile: "Ouvrir le profil",
          notifications: "Notifications",
          settings: "Réglages",
          viewAll: "Voir tout",
        },
        appointments: {
          title: "Vos rendez-vous",
          open: "Ouvrir le rendez-vous",
          tabs: {
            upcoming: "À venir",
            past: "Passés",
            pending: "En attente d'approbation",
          },
          empty: {
            title: "Aucun rendez-vous pour le moment",
            body: "Prenez rendez-vous avec un médecin pour commencer vos soins.",
          },
          bookAction: "Prendre rendez-vous",
          error: "Nous chargeons vos rendez-vous. Veuillez réessayer dans un instant.",
          status: {
            pending: "En attente d'approbation",
            confirmed: "Confirmé",
            inProgress: "En cours",
            completed: "Terminé",
            cancelled: "Annulé",
          },
          defaults: {
            doctor: "Médecin",
            appointment: "Rendez-vous",
          },
        },
        serviceCategories: {
          title: "Catégories de services",
          doctors: "Médecins",
          hospitals: "Hôpitaux",
          pharmacies: "Pharmacies",
          ambulances: "Ambulances",
        },
        popularServices: {
          title: "Services populaires",
          action: "Tous les services",
          labels: {
            generalPractitioner: "Médecin généraliste",
            telemedicine: "Consultation virtuelle",
            childHealthPediatrics: "Santé de l'enfant / pédiatrie",
            maternalCareAntenatal: "Suivi maternel / prénatal",
            dermatologist: "Dermatologue",
            hivCare: "Prise en charge du VIH",
            hypertension: "Hypertension",
            tbScreening: "Dépistage de la tuberculose",
            diabetes: "Diabète",
            malaria: "Paludisme",
            stiScreening: "Dépistage IST",
            dental: "Soins dentaires",
            mentalHealth: "Santé mentale",
          },
        },
        nearby: {
          title: "Soins à proximité",
          action: "Trouver des soins près de moi",
          selectedFacility: "Établissement sélectionné",
          seeDoctors: "Voir les cliniciens de cet établissement",
          loading: "Chargement des établissements à proximité...",
          findDoctors: "Trouver des médecins",
          searchPlaceholder: "Rechercher des établissements",
          clearSearch: "Effacer la recherche",
          showingNear: "Affichage des établissements près de",
          clearSelectedFacility: "Effacer l'établissement sélectionné",
          defaultFacilityName: "Établissement de santé",
        },
        verifyIdentity: {
          title: "Vérifiez votre identité",
          body: "Complétez la vérification pour sécuriser votre accès à AfriMed™.",
          reviewPendingTitle: "Vérification en cours",
          reviewPendingBody: "Votre identité est en cours de vérification.",
          reviewPendingAction: "Voir l'état de vérification",
          pending: "Vérification en attente",
          action: "Vérifier maintenant",
          enabled: "Activé",
        },
      },
      doctor: {
        welcome: "Bienvenue {doctorName}",
        elevate: {
          title: "Valorisez votre profil professionnel",
        },
        gallery: {
          title: "Mettez votre profil en valeur",
          body: "Ajoutez des photos professionnelles de votre profil.",
          manageBody: "Gardez vos photos de profil professionnel à jour et visibles.",
          notNow: "Pas maintenant",
        },
        regional: {
          title: "Santé régionale",
          headline: "Restez informé sur les",
          subtitle: "tendances et conditions de santé",
        },
      },
    },
    feedback: {
      trigger: "Faire une suggestion",
    },
    settings: {
      home: {
        title: "Réglages",
        loginSecurity: "Connexion et sécurité",
        availability: "Disponibilité",
        preferences: "Préférences",
        legal: "Mentions légales",
        faq: "Centre d’aide",
        profileSupport: "Modifiez vos pages, votre profil et bien plus encore...",
      },
      preferences: {
        title: "Préférences",
      },
      availability: {
        pageTitle: "Planification du médecin",
        pageSubtitle: "Définissez vos horaires habituels, puis planifiez les exceptions à l’avance.",
      },
      edit: {
        common: {
          sessionNotReady: "Vous devez être connecté.",
        },
        about: {
          title: "À propos de vous",
          subtitle: "Présentez-vous à vos patients",
          label: "À propos de vous",
          placeholder: "Présentez brièvement votre parcours, votre expérience et votre approche clinique.",
          counter: "{count}/{max} caractères",
          minimum: "{min} caractères minimum requis",
          updated: "Présentation mise à jour",
          folaHelpTitle: "Besoin d'aide pour rédiger votre biographie professionnelle ?",
          folaHelpCopy: "Générez un brouillon modifiable à partir de vos informations d'onboarding. Relisez et ajustez le texte avant d'enregistrer.",
          folaHintsCopy: "Fola peut produire un brouillon plus précis lorsque la spécialité, le diplôme ou l'établissement sont renseignés.",
          folaPreparing: "Fola prépare un brouillon...",
          folaGenerate: "Générer une biographie avec Fola",
          folaImprove: "Améliorer la biographie actuelle avec Fola",
          folaTitle: "Puis-je vous aider à rédiger une biographie professionnelle aujourd'hui ?",
          folaLoading: "Fola prépare un brouillon...",
          folaApply: "Appliquer le brouillon de Fola",
          folaEdit: "Modifier le brouillon",
          folaImprovedNotice: "Fola a préparé une version améliorée. Relisez-la avant de l'appliquer.",
          folaGeneratedNotice: "Brouillon généré à partir de vos informations d'onboarding et de vos spécialités sélectionnées.",
          folaOfflineNotice: "Fola est indisponible. AfriMed™ a préparé un brouillon sûr à partir de vos informations d'onboarding. Relisez-le avant de l'appliquer.",
          folaAppliedNotice: "Le brouillon de Fola a été appliqué. Relisez et ajustez le texte avant d'enregistrer.",
          folaEditNotice: "Le brouillon a été déplacé dans À propos de vous. Modifiez-le avant d'enregistrer.",
        },
        formation: {
          title: "Modifier la formation",
          catalogHidden: "Les suggestions de formation sont encore des données monolingues et sont masquées dans cette langue pour le moment.",
          institutionLabel: "Établissement de formation*",
          institutionPlaceholder: "Saisissez votre établissement de formation",
          degreeLabel: "Qualification académique*",
          degreePlaceholder: "Saisissez votre qualification",
          fieldLabel: "Domaine d'étude*",
          fieldPlaceholder: "Saisissez votre domaine d'étude",
          startLabel: "Date de début*",
          endLabel: "Date de fin*",
          datePlaceholder: "JJ MMM, AAAA",
          achievementLabel: "Résultat ou mention",
          achievementPlaceholder: "Saisissez votre résultat ou mention",
          clearInstitution: "Effacer l'établissement",
          clearDegree: "Effacer la qualification",
          clearField: "Effacer le domaine d'étude",
          clearAchievement: "Effacer le résultat",
          deleteFailed: "Impossible de supprimer cette formation. Veuillez réessayer.",
        },
      },
      profile: {
        title: "Profil",
        common: {
          editName: "Modifier le nom",
          editDob: "Modifier la date de naissance",
          editPhone: "Modifier le téléphone",
          editEmail: "Modifier l'email",
          editAddress: "Modifier l'adresse",
          idPassport: "Carte d'identité • Passeport",
          languages: "Langues",
        },
        doctor: {
          servicesPricing: "Services et tarifs",
          profileSupport: "Profil médecin",
          practiceMode: "Mode de pratique",
          languages: "Langues",
          specialty: "Spécialité",
          subspecialty: "Sous-spécialité",
          eVisit: "Consultations virtuelles",
          availability: "Disponibilités",
          editIntro: "Modifier l'intro",
          registrationDetails: "Détails d'inscription",
          uploadRegistration: "Téléverser l'inscription",
          consultancies: "Lieux de consultation",
          yourFocus: "Domaines d'intérêt",
          editFormation: "Modifier la formation",
          about: "À propos de vous",
        },
      },
      focus: {
        title: "Domaines d'intérêt",
        subtitle: "Indiquez les sujets cliniques pour lesquels vous souhaitez être reconnu.",
        add: "Ajouter",
        empty: "Aucun domaine d'intérêt ajouté pour le moment.",
      },
      patientsAge: {
        infants: "Nourrissons",
        children: "Enfants",
        childrenShort: "Enfants",
        adults: "Adultes",
      },
      practiceMode: {
        title: "Mode de pratique",
        shortTitle: "Choisir votre type de pratique",
        notSelected: "Non sélectionné",
        question: "Comment souhaitez-vous consulter sur AfriMed™ ?",
        intentOnly: "Cette étape indique uniquement votre modèle de consultation. La vérification, la réservation et les règles de visibilité restent inchangées.",
        loading: "Chargement du mode de pratique...",
        saved: "Mode de pratique enregistré",
        helpers: {
          virtual: "Les consultations virtuelles peuvent se dérouler sans afficher d’adresse physique de cabinet.",
          physical: "Les consultations en présentiel utilisent vos lieux de pratique et vos disponibilités.",
          hybrid: "Les patients peuvent réserver une consultation virtuelle ou en présentiel lorsque les deux sont disponibles.",
        },
        options: {
          virtual: { label: "Uniquement virtuel", description: "Proposer des consultations en ligne sécurisées sans afficher d’adresse physique de cabinet." },
          physical: { label: "Cabinet physique", description: "Proposer des consultations en présentiel dans un ou plusieurs lieux de pratique." },
          hybrid: { label: "Hybride", description: "Proposer à la fois des consultations virtuelles et en présentiel." },
        },
        errors: {
          load: "Impossible de charger votre mode de pratique. Veuillez réessayer.",
          required: "Choisissez votre type de pratique avant de continuer.",
          save: "Impossible d’enregistrer votre mode de pratique. Veuillez réessayer.",
        },
      },
      subflows: {
        discardTitle: "Quitter sans enregistrer ?",
        languages: {
          title: "Ajouter des langues",
          searchPlaceholder: "Rechercher ou ajouter",
          clearSearch: "Effacer la recherche",
          addAria: "Ajouter une langue",
          preferredLabel: "Langue principale / préférée",
          preferredPlaceholder: "Sélectionner la langue principale",
          preferredHelper: "Indiquez la langue que vous préférez pour les soins et la communication.",
          otherLanguages: "Autres langues parlées",
          catalogHidden: "Saisissez une langue et touchez + pour l’ajouter. Les suggestions non traduites sont masquées dans cette langue.",
          noResults: "Aucune langue ne correspond à votre recherche.",
          saveError: "Impossible d’enregistrer les modifications. Veuillez réessayer.",
          discardBody: "Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter sans enregistrer ?",
        },
      },
      specialtyEdit: {
        title: "Spécialité",
        subtitle: "Recherchez ou ajoutez votre spécialité si elle n’est pas proposée.",
        search: "Rechercher ou ajouter",
        clearSearch: "Effacer la recherche",
        addSpecialty: "Ajouter une spécialité",
        none: "Aucune",
        empty: "Aucun résultat. Touchez + pour ajouter « {query} ».",
        updated: "Spécialité mise à jour",
        saveFailed: "Impossible d’enregistrer les modifications. Veuillez réessayer.",
        catalogHidden: "Le catalogue de spécialités n’est pas encore localisé pour cette langue. Ajoutez votre spécialité manuellement en français.",
      },
      subspecialty: {
        title: "Sous-spécialité",
        subtitle: "Les sous-spécialités aident les patients à repérer une expertise plus précise.",
        search: "Rechercher ou ajouter",
        clearSearch: "Effacer la recherche",
        add: "Ajouter une sous-spécialité",
        empty: "Aucun résultat. Touchez + pour ajouter « {query} ».",
        catalogHidden: "Saisissez une sous-spécialité et touchez + pour l’ajouter. Les suggestions non traduites sont masquées dans cette langue.",
        saveFailed: "Impossible d’enregistrer les modifications. Veuillez réessayer.",
      },
      services: {
        title: "Services et tarifs",
        price: "Tarif",
        pricePlaceholder: "0,00",
        clearPrice: "Effacer le tarif",
        duration: "Durée",
        durationMinutes: "{minutes} minutes",
        durationShort: "{minutes} min",
        requireService: "Ajoutez au moins un service avant de continuer.",
        signInRequired: "Vous devez être connecté.",
        saved: "Services enregistrés.",
        saveFailed: "Impossible d’enregistrer les services. Veuillez réessayer.",
        emptyTitle: "Aucun service pour le moment",
        emptySupport: "Ajoutez votre premier service pour proposer des durées et tarifs réservables.",
        moreOptions: "Plus d’options pour {name}",
        add: "Ajouter un service",
        addTitle: "Ajouter un service",
        searchPlaceholder: "Rechercher ou ajouter",
        clearSearch: "Effacer la recherche",
        addCustom: "Ajouter un service personnalisé",
        catalogTitle: "Catalogue de services",
        setPricingTitle: "Définir le tarif",
        editPrice: "Modifier le tarif",
        deleteService: "Supprimer le service",
        deleteNamedTitle: "Supprimer le service {name} ?",
        deleteBody: "Ce service sera supprimé de votre profil.",
        added: "Service ajouté",
        duplicate: "Un service avec la même durée existe déjà. Modifiez plutôt le service existant.",
        catalog: {
          eczemaCare: "Prise en charge de l’eczéma",
          rosaceaTreatments: "Traitements spécialisés de la rosacée",
          moleAssessment: "Évaluation professionnelle des grains de beauté",
          allergyTesting: "Tests et prise en charge des allergies",
        },
      },
      evisit: {
        title: "Consultations virtuelles",
        toggleTitle: "Consultations virtuelles",
        helper: "Autorisez les patients à programmer des consultations virtuelles dans votre calendrier.",
        availability: "Disponibilités",
        servicesPricing: "Services et tarifs",
        servicesCount: "{count} services",
        manage: "Gérer",
        manageInAvailability: "Gérer dans Disponibilités",
        patientsAge: "Âge des patients",
        paymentMethods: "Méthodes de paiement",
        insurancePolicy: "Politique d’assurance",
        price: "Tarif de consultation virtuelle",
        setPrice: "Définir le tarif",
        priceLabel: "Tarif",
        pricePlaceholder: "0,00",
        clearPrice: "Effacer le tarif",
        availabilityHelp: "Les consultations virtuelles utilisent vos disponibilités médecin pour les horaires et les règles de réservation. Gérez les durées et les tarifs séparément dans Services et tarifs.",
        priceDialogTitle: "Consultations virtuelles",
        closeTimePicker: "Fermer le sélecteur d’heure",
        selectTime: "Sélectionner l’heure",
        switchTimeField: "Changer de champ horaire",
        cancel: "Annuler",
        confirm: "OK",
        dialogConfirm: "Confirmer",
        accepting: "Accepte les réservations",
        enableRequired: "Activez les consultations virtuelles avant de continuer.",
        priceRequired: "Ajoutez un tarif de consultation virtuelle avant de continuer.",
        saved: "Paramètres de consultation virtuelle enregistrés.",
        saveFailed: "Impossible d’enregistrer les paramètres de consultation virtuelle. Veuillez réessayer.",
        summary: {
          regularDaysAhead: "{count} jours réguliers • {days} jours à l’avance",
          regularDaysOnly: "{count} jours réguliers",
          daysAheadOnly: "{days} jours à l’avance",
        },
      },
      availability: {
        pageTitle: "Planification du médecin",
        pageSubtitle: "Définissez vos horaires habituels, puis planifiez les exceptions à l’avance.",
        calendarMode: "Planification de la semaine",
        selectedDate: "Jour sélectionné",
        openThisDay: "Ouvrir ce jour",
        closeSelectedDay: "Fermer le jour sélectionné",
        addSpecialHours: "Ajouter des horaires spéciaux",
        addBreak: "Ajouter une pause",
        appointmentsToday: "Rendez-vous du jour",
        noAppointmentsBooked: "Aucun rendez-vous réservé pour ce jour.",
        selectedDateOpen: "Les patients peuvent réserver entre {start} et {end}.",
        selectedDateClosed: "Les patients ne peuvent pas réserver ce jour.",
        closed: "Fermé",
        timeAxis: "Heure",
        days: {
          monday: "Lundi",
          tuesday: "Mardi",
          wednesday: "Mercredi",
          thursday: "Jeudi",
          friday: "Vendredi",
          saturday: "Samedi",
          sunday: "Dimanche",
        },
        toggleOn: "Activé",
        toggleOff: "Désactivé",
        optionMinutes5: "5 minutes",
        optionMinutes10: "10 minutes",
        optionMinutes15: "15 minutes",
        optionMinutes20: "20 minutes",
        optionMinutes30: "30 minutes",
        optionMinutes45: "45 minutes",
        optionHour1: "1 heure",
        optionHours2: "2 heures",
        optionHours12: "12 heures",
        optionHours24: "24 heures",
        optionHours48: "48 heures",
        optionHours72: "72 heures",
        optionNoBuffer: "Aucune pause",
        optionWeeks2: "2 semaines",
        optionMonth1: "1 mois",
        optionMonths2: "2 mois",
        optionMonths3: "3 mois",
        optionMonths6: "6 mois",
        optionMonths12: "12 mois",
        loadError: "Impossible de charger les disponibilités pour le moment.",
        signInRequired: "Vous devez être connecté.",
        saveError: "Impossible d’enregistrer ces disponibilités.",
        unavailableLabel: "Indisponible à la réservation",
        weeklyCard: "Modèle hebdomadaire",
        weeklyCardValue: "{count} jours réguliers",
        weeklyCardSupport: "Votre planning de base visible par les patients chaque semaine.",
        bookingCard: "Horizon de réservation",
        bookingCardValue: "{count} jours à l’avance",
        bookingCardSupport: "Permet aux patients de réserver suffisamment tôt.",
        overrideCard: "Changements planifiés",
        overrideCardValue: "{count} en place",
        overrideCardSupport: "{away} absences et {special} horaires spéciaux.",
        calendarTitle: "Calendrier des disponibilités",
        calendarSupport: "Utilisez le calendrier pour vérifier une date future, puis ajustez vos horaires réguliers et changements planifiés.",
        calendarModeWeek: "Planification par semaine",
        weeklyTitle: "Disponibilités hebdomadaires régulières",
        weeklySupport: "Commencez par les horaires qui se répètent la plupart des semaines. Les congés et horaires spéciaux par date se gèrent ci-dessous.",
        copyWeekdays: "Utiliser lundi pour les jours ouvrables",
        copyAll: "Utiliser lundi pour tous les jours",
        weeklyHint: "Gardez un modèle hebdomadaire simple. Utilisez les changements planifiés pour les jours fériés, congés et cliniques ponctuelles.",
        opens: "Ouvre",
        closes: "Ferme",
        applyWeekdays: "Jours ouvrables",
        applyAllDays: "Tous les jours",
        dayActiveCompact: "Ouvert à la réservation",
        dayInactiveCompact: "Fermé par défaut",
        transitionFallback: "Valeur transitoire",
        overrideTitle: "Changements de dates planifiés",
        overrideSupport: "Utilisez cette section pour les congés, jours fériés, conférences ou horaires prolongés ponctuels. Ces entrées remplacent le modèle hebdomadaire.",
        overrideHint: "Idéal pour planifier plusieurs mois à l’avance",
        rangeSafetyNet: "Filet de sécurité de période",
        addOverride: "Ajouter un changement planifié",
        addOverrideSupport: "Vous pouvez bloquer une période complète d’absence ou ouvrir une journée clinique ponctuelle avec d’autres horaires.",
        overrideSetup: "Détails du changement",
        overrideSetupSupport: "Choisissez d’abord le type de changement, puis donnez-lui un libellé clair pour votre équipe.",
        overrideTypeAvailable: "Horaires spéciaux",
        overrideTypeUnavailable: "Absence",
        overrideType: "Type de changement",
        overrideDateRequired: "Choisissez une date de début et une date de fin.",
        overrideDateOrder: "La date de fin doit être le même jour ou plus tard.",
        overrideTimeOrder: "Les horaires spéciaux nécessitent une heure de fermeture postérieure à l’ouverture.",
        specialHours: "Horaires spéciaux",
        break: "Pause",
        focusedDateLabel: "{weekday} {day} {month} {year}",
        closeReason: { clinicClosed: "Cabinet fermé" },
      },
    },
    doctor: {
      profile: {
        about: "À propos",
        focus: "Domaines d’intérêt",
        languages: "Langues",
        registration: "Inscription professionnelle",
        experience: "Expérience",
        virtualConsultations: "Consultations virtuelles",
        services: "Services",
        gallery: "Galerie",
        noAbout: "Aucune présentation ajoutée pour le moment.",
        noFocus: "Aucun domaine d’intérêt ajouté pour le moment.",
        noLanguages: "Aucune langue ajoutée pour le moment.",
        noRegistration: "Informations d’inscription non renseignées.",
        noExperience: "Expérience non renseignée.",
        noServices: "Aucun service ajouté pour le moment.",
        noGallery: "Aucune image ajoutée pour le moment.",
        virtualReady: "Consultations virtuelles disponibles",
        virtualNotReady: "Consultations virtuelles non configurées",
        bookable: "Réservable",
        notBookable: "Non réservable",
        yearsExperience: "{count} ans d’expérience",
        stats: {
          experienceOneYear: "1 an",
          experienceYears: "{count} ans",
        },
        registrationNumber: "Numéro d’inscription",
        registrationCountry: "Pays d’inscription",
        openProfile: "Ouvrir le profil",
        editProfile: "Modifier le profil",
      },
      editIntro: {
        title: "Modifier l'intro",
        subtitle: "Peaufinez la façon dont les patients voient votre profil médecin.",
        about: "À propos",
        aboutPlaceholder: "Présentez brièvement votre approche clinique, votre expérience et les soins que vous proposez.",
        focus: "Domaines d’intérêt",
        focusPlaceholder: "Ajoutez vos domaines d’intérêt clinique.",
        languages: "Langues",
        specialty: "Spécialité",
        signature: "Signature",
        gallery: "Galerie",
        save: "Enregistrer la présentation",
        saved: "Présentation enregistrée",
        saveFailed: "Impossible d’enregistrer la présentation. Veuillez réessayer.",
      },
    },
    patient: {
      editIntro: {
        title: "Modifier l'intro",
        sections: {
          personalInfo: "Informations personnelles",
          contactData: "Coordonnées",
          residenceAddress: "Adresse de résidence",
          medicalInfo: "Informations médicales",
        },
        verification: {
          verified: "Vérifié à partir de la pièce d'identité fournie.",
        },
        actions: {
          addLanguage: "Ajouter une langue",
        },
        placeholders: {
          fullName: "Ajoutez votre nom complet",
          dob: "Ajoutez votre date de naissance",
          phone: "Ajoutez votre numéro de téléphone",
          email: "Ajoutez votre adresse email",
          idPassport: "Ajoutez votre carte d'identité ou passeport",
          street: "Ajoutez votre adresse",
          postalCode: "Ajoutez votre code postal",
          country: "Ajoutez votre pays",
          bloodType: "Ajoutez votre groupe sanguin",
          allergies: "Ajoutez vos allergies",
          conditions: "Ajoutez vos maladies connues",
          organDonor: "Indiquez si vous êtes donneur d'organes",
          heightWeight: "Ajoutez votre taille et votre poids",
        },
        languages: {
          empty: "Ajoutez les langues que vous parlez afin que les cliniciens puissent communiquer correctement avec vous.",
        },
      },
      profile: {
        fullName: "Nom complet",
        dateOfBirth: "Date de naissance",
        verified: "Vérifié",
        pendingVerification: "Vérification en cours",
        notProvided: "Non renseigné",
        notConfigured: "Non configuré",
        unknown: "Inconnu",
        noneRecorded: "Aucun élément enregistré",
        notRecorded: "Non renseigné",
        dependantFallback: "Personne à charge",
        dependantsCount: "{count} personnes à charge",
        insuranceSupport: "Ajoutez votre assureur et votre numéro d'adhérent pour faciliter les demandes et paiements.",
        notificationsSupport: "Choisissez comment recevoir les mises à jour de rendez-vous, prescriptions et actualités.",
        medicalHistoryLocked: "L'historique médical reste verrouillé jusqu'à ce que le patient autorise l'accès au DME.",
        sections: {
          appointments: "Historique des rendez-vous",
          contact: "Coordonnées",
          insurance: "Assurance santé",
          dependants: "Personnes à charge",
          address: "Adresse de résidence",
          medicalHistory: "Historique médical",
          photos: "Photos du patient",
          languages: "Langues",
          notifications: "Notifications",
        },
        labels: {
          phone: "Téléphone",
          email: "Email",
          nationalId: "Carte d'identité / Passeport",
          street: "Adresse",
          postalCode: "Code postal",
          country: "Pays",
          bloodType: "Groupe sanguin",
          allergies: "Allergies",
          chronicConditions: "Maladies chroniques",
          medications: "Médicaments",
          emergencyAlerts: "Alertes médicales",
          organDonor: "Donneur d'organes",
          heightWeight: "Taille et poids",
        },
        placeholders: {
          phone: "Votre numéro de téléphone",
          email: "vous@exemple.com",
          nationalId: "Carte d'identité / Passeport",
          street: "Votre adresse, ville",
          postalCode: "00000",
          country: "Pays",
          languages: "Langues préférées",
        },
        actions: {
          addEdit: "Ajouter / Modifier",
          addDependant: "Ajouter une personne à charge",
          addFavorite: "Ajouter aux favoris",
          removeFavorite: "Retirer des favoris",
        },
        history: {
          visit: "Rendez-vous",
          visitAt: "Rendez-vous · {day}, {time}",
        },
        consultType: {
          private: "Consultation privée",
          moh: "Consultation ministère de la Santé",
          evisit: "Consultation virtuelle",
          default: "Consultation",
        },
        fallback: {
          addressNotProvided: "Adresse non renseignée",
          consultationLocation: "Lieu de consultation",
          generalPractice: "Médecine générale",
          notSpecified: "Non précisé",
        },
        notifications: {
          title: "Actualités et mises à jour",
          off: "Désactivé",
          emailPush: "Email et notifications push",
          emailPushLocalNews: "Email et notifications push · Actualités et règles locales",
        },
        empty: {
          appointments: "Vous n'avez pas encore de rendez-vous.",
          dependants: "Aucune personne à charge enregistrée",
          photos: "Aucune photo pour le moment.",
        },
      },
    },
    doctors: {
      appointmentDetails: {
        patientFallback: "Patient",
        doctorFallback: "Médecin",
        title: "Détails du rendez-vous",
        loading: "Chargement des détails du rendez-vous...",
        notFound: "Rendez-vous introuvable. Revenez en arrière et ouvrez-le à nouveau.",
        backToAppointments: "Retour aux rendez-vous",
        firstAppointment: "Premier rendez-vous",
        diagnosisTitleFallback: "Diagnostic et notes",
        meetPending: "Salle de consultation sécurisée AfriMed™",
        actions: {
          profile: "Profil",
          editDiagnosis: "Modifier le diagnostic",
          add: "Ajouter",
          view: "Voir",
          request: "Demander",
          consultationCompleted: "Consultation terminée",
          showAll: "Tout afficher",
          message: "Message",
          call: "Appeler",
          join: "Rejoindre",
          joinClosed: "Fenêtre fermée",
          updating: "Mise à jour...",
          details: "Détails",
          changeAppointment: "Modifier, reprogrammer ou annuler le rendez-vous",
          startConsultation: "Démarrer la consultation",
          markInProgress: "Marquer en cours",
          completeConsultation: "Terminer la consultation",
          open: "Ouvrir",
        },
        sections: {
          diagnosis: "Diagnostic",
          prescription: "Prescription",
          testsAndLabs: "Examens et résultats de laboratoire",
          testsAndDocuments: "Examens et documents",
          additionalNotes: "Notes complémentaires",
          photos: "Photos du patient",
          ehr: "DME",
          support: "Assistance et reprogrammation",
        },
        requestEhr: {
          title: "Demander le DME",
          body: "Améliorez la prise en charge en accédant au dossier médical électronique partagé du patient.",
        },
        vitalRep: {
          section: "Note de consultation VitalRep™",
          generate: "Ouvrir VitalRep™ bêta",
          reviewDraft: "Continuer le brouillon VitalRep™",
          amend: "Modifier la note",
          draftReady: "Brouillon VitalRep™ enregistré",
          saved: "Note de consultation enregistrée",
          loading: "Chargement de la note VitalRep™",
          helper: "Documentation clinique structurée. Créez, vérifiez et finalisez les notes de consultation.",
          previewFallback: "Capturez ou collez les notes de consultation, puis générez un brouillon SOAP à vérifier par le médecin avant signature.",
          editorFinalHelper: "Cette note finalisée est verrouillée. Toute modification nécessite un motif et sera auditée.",
          transcriptDraft: { title: "Brouillon de transcription VitalRep™ bêta", body: "Capturez ou collez les notes de consultation, puis générez un brouillon SOAP à vérifier par le médecin avant signature." },
          amendmentIntro: "Ajoutez un motif avant de continuer vers VitalRep™ bêta. La modification sera enregistrée avec les métadonnées d’audit.",
          amendmentReason: "Motif de modification",
          amendmentReasonPlaceholder: "Expliquez pourquoi cette note finalisée doit être modifiée.",
          continueToBeta: "Continuer vers VitalRep™ bêta",
          errors: { amendmentReason: "Ajoutez un motif de modification avant de continuer." },
          vitalsSource: {
            notCaptured: "Non saisi",
            clinicianCaptured: "Saisi par le clinicien",
            patientReported: "Déclaré par le patient",
            homeDevice: "Appareil à domicile",
            uploadedDocument: "Document téléversé",
          },
          fields: {
            bloodPressure: "Tension artérielle",
            heartRate: "Fréquence cardiaque",
            temperature: "Température",
            oxygenSaturation: "Saturation en oxygène",
            respiratoryRate: "Fréquence respiratoire",
            weight: "Poids",
            reasonForVisit: "Motif de consultation",
            clinicalObservations: "Observations cliniques",
            testsLabsRequested: "Examens et laboratoires demandés",
            assessment: "Diagnostic et évaluation",
            medicationSummary: "Prescription et résumé médicamenteux",
            treatmentNextSteps: "Traitement et prochaines étapes",
            followUpAdvice: "Conseils de suivi",
          },
          placeholders: {
            bloodPressure: "Saisir la tension artérielle",
            heartRate: "Saisir la fréquence cardiaque",
            temperature: "Saisir la température",
            oxygenSaturation: "Saisir la saturation en oxygène",
            respiratoryRate: "Saisir la fréquence respiratoire",
            weight: "Saisir le poids",
          },
          sections: {
            visitSummary: "Résumé de la consultation",
            subjective: "Subjectif",
            objective: "Objectif",
            assessment: "Évaluation",
            plan: "Plan",
            followUp: "Suivi",
          },
          display: {
            visitReason: "Subjectif",
            objective: "Objectif",
            clinicalImpression: "Évaluation",
            treatmentPlan: "Plan",
            notRecorded: "Non renseigné.",
          },
        },
        completeConsultation: { requirements: "Enregistrez le diagnostic et les notes avant de terminer la consultation." },
        completeConsultationDialog: {
          bodyWithEhr: "Le DME du patient ne sera plus accessible. Pour y accéder à nouveau, vous devrez redemander l’accès.",
          bodyWithoutEhr: "Cette action marque la consultation comme terminée pour ce rendez-vous.",
          confirmShort: "Terminer",
        },
        patientRecord: {
          title: "Dossier patient",
          ehrBody: "Demandez ou consultez le dossier de santé partagé de ce patient.",
          pastConsultations: "Consultations précédentes",
          pastConsultationsBody: "Consultez les consultations antérieures une fois l’accès au dossier accordé.",
          prescriptionsBody: "Voir les prescriptions émises et leur statut de dispensation.",
          labsBody: "Consultez les examens, résultats de laboratoire et documents justificatifs téléversés.",
          labsUnavailable: "Les examens et résultats de laboratoire sont disponibles depuis la zone de documentation de consultation.",
        },
        referrals: { handoverTitle: "Orientation et relais", handoverBody: "Adressez ce patient à des soins spécialisés ou créez un relais structuré." },
        interoperabilityRoadmap: {
          title: "Feuille de route d’interopérabilité",
          body: "AfriMed™ est préparé pour des échanges basés sur le consentement avec les DME hospitaliers, laboratoires, imagerie, pharmacies, assureurs et systèmes nationaux de santé via FHIR.",
          cta: "Voir la feuille de route",
          disclaimer: "L’architecture existe. Les intégrations externes en direct nécessitent l’onboarding des partenaires et des endpoints approuvés.",
        },
        ehr: { accessGranted: "Accès accordé", accessRequested: "Accès demandé" },
        fields: { bloodType: "Groupe sanguin", allergies: "Allergies", chronicConditions: "Maladies chroniques", organDonor: "Donneur d’organes" },
        notesPlaceholder: "Ajouter des notes...",
        notesAutosave: "Les notes sont enregistrées automatiquement lorsque vous quittez ce champ.",
        cancelDialog: { title: "Annuler le rendez-vous ?", description: "Pensez à contacter le patient avant de continuer.", confirm: "Oui, annuler" },
        deleteDialog: { title: "Voulez-vous vraiment supprimer ce rendez-vous médical ?", description: "Après suppression, ce dossier de rendez-vous ne pourra pas être récupéré.", confirm: "Supprimer" },
        changeSheet: { title: "Modifier • mettre à jour le rendez-vous", requestNewTime: "Demander un nouvel horaire", cancelAppointment: "Annuler le rendez-vous" },
        support: { unavailablePastAppointments: "Indisponible pour les rendez-vous passés" },
        startsIn: { default: "Commence dans 1 heure", minutes: "Commence dans {count} min", hours: "Commence dans {count} heure{suffix}" },
        toast: {
          rescheduleRequested: "Nouvelle demande d’horaire envoyée. En attente d’approbation.",
          callOnlyDuringAppointment: "Disponible uniquement pour les rendez-vous",
          cameraPermissionRequired: "Autorisez l’accès à la caméra et au microphone pour démarrer la consultation.",
          consultationStartFailed: "La consultation vidéo n’a pas pu démarrer. Veuillez réessayer.",
        },
        facilityFallback: "Établissement de santé",
        yearsOld: "{count} ans",
        status: { pending: "En attente", scheduled: "Planifié", inProgress: "En cours", completed: "Terminé", missed: "Manqué", cancelled: "Annulé", confirmed: "Confirmé" },
        diagnosisDisplay: { clinicalImpression: "Diagnostic et impression clinique", notRecorded: "Non renseigné." },
        fulfillment: {
          title: "Dispensation de la prescription",
          deliveryMode: "Livraison",
          pickupMode: "Retrait",
          selectedPharmacy: "La pharmacie sélectionnée",
          readyForPickup: "Prêt pour retrait",
          readyForPickupDescription: "{pharmacy} a marqué cette prescription comme prête pour retrait.",
          fulfillmentModeComplete: "Le relais {mode} est terminé côté pharmacie.",
          paid: "Payé",
          paidDescription: "{pharmacy} a confirmé le paiement de cette prescription.",
          waitForPickup: "Attendez que la pharmacie marque la commande comme prête pour retrait.",
          paymentReady: "Paiement prêt",
          paymentReadyDescription: "{pharmacy} a défini le devis et le patient peut maintenant payer dans AfriMed™.",
          waitingForPayment: "Le relais {mode} attend le paiement du patient.",
          pharmacyPreparing: "Pharmacie en préparation",
          pharmacyPreparingDescription: "{pharmacy} a accepté la prescription et la prépare.",
          patientHandledExternally: "La coordination {mode} est gérée par le patient, Fola et la pharmacie hors du flux clinicien.",
          needsNewPharmacy: "Nouvelle pharmacie requise",
          needsNewPharmacyDescription: "{pharmacy} ne peut pas honorer cette prescription.",
          chooseAnotherPharmacy: "Le patient doit choisir une autre pharmacie inscrite.",
          pharmacySelected: "Pharmacie sélectionnée",
          pharmacySelectedDescription: "{pharmacy} est la branche active de relais {mode} pour cette prescription.",
          issued: "Émise",
          issuedDescription: "La prescription a été émise. Le patient démarre la coordination pharmacie après l’avoir ouverte dans AfriMed™.",
        },
        errors: { update: "Impossible de mettre à jour le rendez-vous pour le moment.", auth: "Authentification requise.", completeConsultation: "Impossible de terminer la consultation pour le moment.", requestEhr: "Impossible de demander l’accès au DME pour le moment." },
        attachments: { untitled: "Pièce jointe", byDoctor: "Par le médecin", addedSingle: "Pièce jointe ajoutée.", addedMultiple: "{count} pièces jointes ajoutées.", removed: "Pièce jointe supprimée.", remove: "Supprimer la pièce jointe" },
        photosAlt: "Photo du patient",
        photosEmpty: "Aucune photo patient partagée pour le moment.",
        responsibleAdult: "Adulte responsable",
      },
    },
    prescriptions: {
      wizard: {
        title: "Créer une prescription",
        reviewPrescription: "Vérifier la prescription",
        medicinesCount: "{count} médicament(s)",
        prescriber: "Prescripteur",
        poweredBy: "Propulsé par AfriMed™. Le prescripteur reste responsable des décisions cliniques.",
        qrCode: "Code QR",
        issueFirstForQr: "Émettez d’abord la prescription pour activer le QR vérifiable.",
        saveMedicine: "Enregistrer le médicament",
        signAndPrescribe: "Signer et prescrire",
        issuing: "Émission...",
        viewPrescription: "Voir la prescription",
        addImagesToGallery: "Ajouter des images à la galerie",
        uploadFromGallery: "Téléverser depuis la galerie",
        takePhoto: "Prendre une photo",
        chooseUnit: "Choisir l’unité",
        startDate: "Date de début",
        endDate: "Date de fin",
        timeInterval: "Intervalle horaire",
        hoursValue: "{count} heures",
        frequency: { title: "Fréquence" },
        days: { title: "Jours de la semaine" },
      },
    },
    doctorDashboard: {
      patientFallback: "Patient",
      errors: { sessionUnavailable: "Session indisponible." },
      patientsAppointments: { columns: { primaryCondition: "Condition principale" }, noDiagnosis: "Aucun diagnostic renseigné" },
      prescriptions: {
        status: { draft: "Brouillon", issued: "Émise", dispensed: "Délivrée", cancelled: "Annulée", expired: "Expirée" },
        searchPlaceholder: "Rechercher par ID patient",
        searchAria: "Rechercher les prescriptions récentes par ID patient",
        columns: { patient: "Patient", issued: "Émise", expires: "Expire", status: "Statut" },
        loading: "Chargement des prescriptions...",
        empty: "Aucune prescription pour le moment.",
        noMatches: "Aucun résultat trouvé.",
      },
      prescriptionsPage: {
        title: "Historique des prescriptions",
        heading: "Historique des prescriptions",
        subtitle: "Prescriptions émises dans le cadre de soins liés à un rendez-vous.",
        issuedPast30Days: "{count} émises au cours des 30 derniers jours.",
        columns: { refill: "Renouvellement" },
        errors: { load: "Impossible de charger les prescriptions." },
      },
    },
    stagingGate: {
      checking: "Verification de l'acces prive de staging...",
      privateTitle: "Staging prive",
      enterTitle: "Acces prive au staging",
      privateBody:
        "AfriMed est actuellement dans une phase privee de tests. Nous integrons un nombre limite de professionnels de sante et de partenaires. Laissez votre email et nous vous informerons lorsque l'acces ouvrira.",
      enterBody: "Saisissez votre email invite pour continuer vers le staging AfriMed.",
      emailLabel: "Email",
      emailPlaceholder: "nom@exemple.com",
      checkingAction: "Verification de l'acces...",
      requesting: "Demande d'acces...",
      continueCta: "Continuer",
      requestCta: "Demander l'acces",
      requestSaved:
        "AfriMed a enregistre votre demande. Nous vous contacterons lorsque votre acces de staging sera pret.",
      errors: {
        lookupFailed:
          "AfriMed n'a pas pu verifier l'acces au staging pour le moment. Merci de demander l'acces ci-dessous.",
        submitFailed:
          "AfriMed n'a pas pu enregistrer votre demande pour le moment. Veuillez reessayer dans un instant.",
      },
    },
    verify: {
      phoneOtp: {
        title: "Saisir le code de verification",
        copy: "Nous avons envoye un code a 6 chiffres a",
      },
      prescription: {
        pageTitle: "Verifier la prescription",
        checking: "Verification...",
        status: "Statut",
        signed: "Signee",
        notSigned: "Non signee",
        patient: "Patient",
        dob: "Date de naissance",
        idLabel: "ID",
        reg: "Reg",
        title: "Prescription electronique",
        number: "No de prescription",
        issued: "Emise",
        schedule: "Posologie",
        frequency: "Frequence",
        dosage: "Dosage",
        starts: "Debut",
        ends: "Fin",
        verification: "Verification",
        expires: "Expire",
        digitalSeal: "Sceau numerique",
        prescriberVerification: "Verification du prescripteur",
        signatureAlt: "Signature du prescripteur",
        publicRouteCopy: "{source}. Route publique de verification pour la pharmacie ou les controles manuels.",
        verifiedVia:
          "Verifie via {issuer}. Le prescripteur reste responsable des decisions cliniques.",
        source: {
          manualPharmacy: "Saisie manuellement a la pharmacie",
          scannedPharmacy: "Scannee a la pharmacie",
          public: "Vue publique de verification",
        },
        actions: {
          scanAnother: "Scanner une autre prescription",
          returnToPrescription: "Retour a la prescription",
        },
        defaults: {
          prescriber: "Prescripteur",
          medication: "Medicament",
          patient: "Patient",
          facility: "Etablissement",
        },
        errors: {
          invalidCode: "Code de prescription invalide.",
          unavailable: "Les donnees de verification de la prescription sont indisponibles.",
          failed: "Echec de la verification.",
        },
        payment: {
          title: "Preparer le paiement du patient",
          copy:
            "Definissez maintenant le sous-total du medicament et les conditions de livraison afin que le patient puisse payer directement la branche de dispensation sans nouvelle relance.",
          medicineSubtotal: "Sous-total du medicament",
          fulfillmentMode: "Mode d'execution",
          deliveryFee: "Frais de livraison",
          patientTotal: "Total patient",
          send: "Envoyer le patient au paiement",
          saving: "Enregistrement du devis...",
          success:
            "{pharmacy} est pret. Le patient peut ouvrir cette prescription et payer {total}.",
          auditCopy:
            "Devis en {currency} enregistre sur cette prescription pour audit et checkout.",
          defaults: {
            quote: "Devis pharmacie",
          },
          errors: {
            missingSubtotal:
              "Le sous-total du medicament est requis avant d'envoyer le patient au paiement.",
            prepareFailed: "Impossible de preparer le paiement du patient pour le moment.",
          },
        },
      },
    },
  },
  de: {
    common: {
      actions: {
        close: "Schliessen",
        print: "Drucken",
        download: "Herunterladen",
      },
      status: {
        saving: "Wird gespeichert...",
      },
    },
    navigation: {
      tabs: {
        home: "Home",
        dashboard: "Dashboard",
        appointments: "Termine",
        messages: "Nachrichten",
        profile: "Profil",
      },
    },
    auth: {
      callback: {
        finishing: "Anmeldung wird abgeschlossen...",
        redirectingToLogin:
          "Weiterleitung zur Anmeldung, um einen neuen Anmeldelink anzufordern...",
        validating: "Anmeldelink wird gepruft...",
        verifyingCode: "Anmeldecode wird verifiziert...",
        linkNoLongerValidOnDevice:
          "Dieser Anmeldelink ist auf diesem Gerat nicht mehr gultig. Fordere einen neuen Magic Link an und offne ihn im selben Browser.",
        linkVerificationFailed:
          "Der Anmeldelink konnte nicht verifiziert werden. Bitte fordere einen neuen Anmeldelink an.",
        sessionEmailMismatch:
          "Die Sitzungs-E-Mail stimmt nach der Magic-Link-Verifikation nicht uberein.",
        sessionNotEstablished: "Sitzung wurde nicht hergestellt.",
        checkingProfile: "Angemeldet. Profil wird gepruft...",
        pharmacyDashboardAs:
          "Anmeldung im Pharmacy Dashboard als {email}... einen Moment bitte.",
        pharmacyDashboard:
          "Anmeldung im Pharmacy Dashboard... einen Moment bitte.",
        takingToAfrimed: "Angemeldet. Weiter zu AfriMed™...",
        takingToAdmin: "Willkommen zuruck. Weiter zur AfriMed-Administration...",
        takingHome: "Willkommen zuruck. Weiter zu Home...",
        takingOnboarding: "Angemeldet. Weiter zum Onboarding...",
        resolvingAccountType: "Kontotyp wird ermittelt...",
        takingBack: "Angemeldet. Weiter zuruck...",
        chooseHowToContinue: "Angemeldet. Wahlen Sie, wie Sie fortfahren mochten...",
        slowHint:
          "Der Anmeldecode wird noch verifiziert... Wenn das zu lange dauert, fordern Sie einen neuen Anmeldelink an.",
        timeout:
          "Die Anmeldung konnte nicht abgeschlossen werden. Bitte fordern Sie einen neuen Anmeldelink an.",
        differentSession:
          "Dieser Anmeldelink gehort zu einer anderen Sitzung in diesem Browser. Fordern Sie einen neuen Link an und offnen Sie nur den neuesten.",
        pharmacyEmailNotLinked:
          "Diese E-Mail ist noch keiner Apothekenfiliale zugeordnet.",
        pharmacyLinkFailed:
          "Dieser Apotheken-Anmeldelink konnte nicht abgeschlossen werden. Bitte fordern Sie einen neuen sicheren Link an.",
        linkExpired: "Der Anmeldelink ist abgelaufen. Bitte fordern Sie einen neuen Link an.",
        browserMismatch:
          "Der Anmeldelink konnte in diesem Browser nicht validiert werden. Fordern Sie einen neuen Link an und offnen Sie den neuesten im selben Browser.",
        failed:
          "Die Anmeldung ist fehlgeschlagen. Bitte versuchen Sie es erneut oder fordern Sie einen neuen Link an.",
        requestAnotherLink: "Neuen Anmeldelink anfordern",
      },
    },
    appointments: {
      missedDialog: {
        title: "Termin verpasst",
        description:
          "Die Beteiligten dieses Termins sind nicht innerhalb des gebuchten Zeitfensters erschienen. Dieser Termin ist nicht mehr verfugbar. Wenn du weiterhin Versorgung brauchst, wahl bitte eine neue verfugbare Zeit und buche erneut.",
      },
    },
    notifications: {
      filters: {
        all: "Alle",
        urgent: "Dringend",
        unread: "Ungelesen",
        appointments: "Termine",
      },
      common: {
        title: "Benachrichtigungen",
        today: "Heute",
        notification: "Benachrichtigung",
        patient: "Patient",
        openDetails: "Offnen, um Details zu sehen.",
        loading: "Benachrichtigungen werden geladen...",
        emptyTitle: "Keine Benachrichtigungen zum Anzeigen",
        emptyBody: "Dein Posteingang ist im Moment leer. Schau spater wieder vorbei!",
        errors: {
          loadFailed: "Benachrichtigungen konnten nicht geladen werden.",
        },
      },
      doctor: {
        titles: {
          newAppointment: "Neuer Termin",
          cancellationRequest: "Stornierungsanfrage",
          appointmentUpdated: "Termin aktualisiert",
        },
        support: {
          bookingAccepted: "Die Terminanfrage von {name} wurde genehmigt.",
          bookingDeclined: "Die Terminanfrage von {name} wurde abgelehnt.",
          bookingPending: "{name} hat einen Termin angefragt. Wartet auf deine Genehmigung.",
          rescheduleAccepted: "Der Termin mit {name} wurde verschoben.",
          rescheduleDeclined: "Die neue Terminanfrage von {name} wurde abgelehnt.",
          reschedulePending:
            "{name} hat eine neue Terminzeit angefragt. Wartet auf deine Genehmigung.",
          cancellationAccepted: "Die Stornierungsanfrage von {name} wurde genehmigt.",
          cancellationDeclined: "Die Stornierungsanfrage von {name} wurde abgelehnt.",
          cancellationPending:
            "{name} hat die Stornierung des Termins angefragt. Wartet auf deine Genehmigung.",
        },
      },
      title: "Benachrichtigungen",
      loadingSingle: "Benachrichtigung wird geladen...",
      singleTitle: "Benachrichtigung",
      openDetailHelp: "Offne die Details, um dieses Update zu prufen.",
      pendingSchedule: "Termin ausstehend",
      time: {
        today: "Heute",
        todayAt: "Heute {time}",
      },
      fields: {
        doctor: "Arzt",
        patient: "Patient",
        assignedDoctor: "Zugeordneter Arzt",
        consultationLocation: "Behandlungsort",
        dateTime: "Datum und Uhrzeit",
        location: "Ort",
        details: "Details",
      },
      headers: {
        newAppointment: "Neuer Termin",
        newAppointmentTimeRequest: "Neue Terminanfrage",
        cancellationRequest: "Stornierungsanfrage",
        appointmentUpdate: "Terminaktualisierung",
        cancellationRequestSent: "Stornierungsanfrage gesendet",
        appointmentConfirmed: "Termin bestatigt",
        appointmentCancelled: "Termin storniert",
        appointmentDeclined: "Termin abgelehnt",
        cancellationRequestDeclined: "Stornierungsanfrage abgelehnt",
        newTimeRequestApproved: "Neue Terminzeit genehmigt",
        newTimeRequestDeclined: "Neue Terminzeit abgelehnt",
        upcomingAppointment: "Bevorstehender Termin",
        ehrAccessRequest: "EHR-Zugriffsanfrage",
      },
      actions: {
        accept: "Annehmen",
        decline: "Ablehnen",
        grantAccess: "Zugang gewahren",
      },
      status: {
        accepted: "Angenommen",
        declined: "Abgelehnt",
        granted: "Gewahrt",
        granting: "Wird gewahrt...",
        unavailable: "Nicht verfugbar",
      },
      dialogs: {
        cancelAppointmentTitle: "Termin stornieren?",
        cancelAppointmentBody: "Mochtest du diesen Termin wirklich stornieren?",
        declineAppointmentTitle: "Termin ablehnen?",
        declineAppointmentBody: "Mochtest du diese Terminanfrage wirklich ablehnen?",
        confirmCancel: "Ja, stornieren",
        confirmDecline: "Ja, ablehnen",
      },
      errors: {
        signInAgain: "Bitte melde dich erneut an.",
        notFound: "Benachrichtigung nicht gefunden.",
        emptyInbox: "Dein Posteingang ist im Moment leer. Schau spater wieder vorbei!",
        load: "Benachrichtigung konnte nicht geladen werden.",
        ehrGrant: "EHR-Zugang konnte gerade nicht gewahrt werden.",
        cancelAppointment: "Der Termin konnte gerade nicht storniert werden.",
        acceptAppointment: "Der Termin konnte nicht angenommen werden.",
        declineAppointment: "Der Termin konnte nicht abgelehnt werden.",
        approveNewTimeRequest: "Die neue Terminzeit konnte nicht genehmigt werden.",
        declineNewTimeRequest: "Die neue Terminzeit konnte nicht abgelehnt werden.",
        acceptAppointmentChange:
          "Die Terminanderung konnte gerade nicht angenommen werden.",
        declineAppointmentChange:
          "Die Terminanderung konnte gerade nicht abgelehnt werden.",
      },
      toast: {
        appointmentAccepted: "Termin angenommen",
        appointmentDeclined: "Termin abgelehnt",
        appointmentUpdated: "Termin aktualisiert",
        appointmentCancelled: "Termin storniert",
        yourAppointmentUpdated: "Dein Termin wurde aktualisiert",
        cancellationApproved: "Stornierung genehmigt",
        cancellationDeclined: "Stornierung abgelehnt",
        cancellationRequestSent: "Stornierungsanfrage gesendet",
        cancellationApprovedRefundProcessing:
          "Stornierung genehmigt. Die Erstattung wird bearbeitet.",
        cancellationApprovedReversalFollowUp:
          "Stornierung genehmigt. Die Zahlungsumkehr braucht Nachverfolgung.",
        cancellationApprovedReversalAttention:
          "Stornierung genehmigt. Die Zahlungsumkehr braucht weiter Aufmerksamkeit.",
        appointmentDeclinedRefundProcessing:
          "Termin abgelehnt. Die Erstattung wird bearbeitet.",
        appointmentDeclinedReversalFollowUp:
          "Termin abgelehnt. Die Zahlungsumkehr braucht Nachverfolgung.",
        appointmentDeclinedReversalAttention:
          "Termin abgelehnt. Die Zahlungsumkehr braucht weiter Aufmerksamkeit.",
      },
      messages: {
        acceptedNewTime: "Du hast die neue Terminzeit angenommen.",
        patientAcceptedNewTime: "Der Patient hat die neue Terminzeit angenommen.",
        declinedNewTime: "Du hast die neue Terminzeit abgelehnt.",
        patientDeclinedNewTime: "Der Patient hat die neue Terminzeit abgelehnt.",
        doctorProposedNewTime:
          "Dein Arzt hat eine neue Terminzeit vorgeschlagen. Bitte prufe sie und bestatige die Anderung.",
        ehrAccessRequested:
          "Dein Arzt hat fur diesen Termin Zugriff auf deine Gesundheitsakte angefragt.",
        upcomingAppointmentForDate: "Dein Termin ist fur {date} vorgesehen.",
        upcomingAppointmentGeneric: "Dein Termin ist vorgesehen.",
        bookingApprovedForDate: "Die Terminanfrage von {name} fur {date} wurde genehmigt.",
        bookingApprovedGeneric: "Die Terminanfrage von {name} wurde genehmigt.",
        bookingPendingForDate:
          "{name} hat einen Termin fur {date} angefragt. Wartet auf deine Genehmigung.",
        yourAppointmentAcceptedForDate: "Dein Termin fur {date} wurde angenommen.",
        yourAppointmentAcceptedGeneric: "Deine Terminanfrage wurde angenommen.",
        bookingDeclinedForDate: "Die Terminanfrage von {name} fur {date} wurde abgelehnt.",
        bookingDeclinedGeneric: "Die Terminanfrage von {name} wurde abgelehnt.",
        yourAppointmentDeclinedForDate: "Dein Termin fur {date} wurde abgelehnt.",
        yourAppointmentDeclinedGeneric: "Deine Terminanfrage wurde abgelehnt.",
        cancellationApprovedForDate:
          "{name} hat die Stornierung des Termins fur {date} angefragt. Genehmigt.",
        cancellationApprovedGeneric:
          "{name} hat die Stornierung des Termins angefragt. Genehmigt.",
        yourCancellationApprovedForDate:
          "Deine Stornierungsanfrage fur {date} wurde genehmigt.",
        yourCancellationApprovedGeneric: "Deine Stornierungsanfrage wurde genehmigt.",
        cancellationDeclinedForDate:
          "{name} hat die Stornierung des Termins fur {date} angefragt. Abgelehnt.",
        cancellationDeclinedGeneric:
          "{name} hat die Stornierung des Termins angefragt. Abgelehnt.",
        yourCancellationDeclinedForDate:
          "Deine Stornierungsanfrage fur {date} wurde abgelehnt.",
        yourCancellationDeclinedGeneric: "Deine Stornierungsanfrage wurde abgelehnt.",
        rescheduleApprovedGeneric: "{name} hat eine neue Terminzeit angefragt. Genehmigt.",
        yourRescheduleApprovedGeneric: "Deine Anfrage nach einer neuen Terminzeit wurde genehmigt.",
        rescheduleDeclinedGeneric: "{name} hat eine neue Terminzeit angefragt. Abgelehnt.",
        yourRescheduleDeclinedGeneric: "Deine Anfrage nach einer neuen Terminzeit wurde abgelehnt.",
        cancellationRequestSentForDate:
          "Deine Anfrage zur Stornierung des Termins am {date} wurde gesendet.",
        cancellationRequestSentGeneric:
          "Deine Anfrage zur Stornierung dieses Termins wurde gesendet.",
        patientCancellationPendingForDate:
          "{name} hat die Stornierung des Termins am {date} angefragt. Wartet auf Genehmigung.",
        patientCancellationPendingGeneric:
          "{name} hat die Stornierung des Termins angefragt. Wartet auf Genehmigung.",
      },
      goToAppointment: "Termin offnen",
      goToAppointmentDetails: "Termindetails offnen",
      referral: {
        label: "Uberweisung",
        openAction: "Uberweisung offnen",
      },
    },
    messages: {
      label: "Nachricht",
      openAction: "Nachricht offnen",
      loadingConversation: "Unterhaltung wird geladen...",
      noMessagesYet: "Noch keine Nachrichten.",
      inbox: {
        fallbackTitle: "Unterhaltung",
      },
      conversation: {
        now: "Jetzt",
        deletedForMe: "Du hast diese Nachricht geloscht",
        deletedBySender: "Nachricht wurde vom Absender geloscht",
        deleteDialog: {
          close: "Loschdialog schliessen",
          title: "Nachricht loschen?",
          body:
            "Losche diese Nachricht nur fur dich oder entferne sie fur beide Beteiligten. Diese Aktion kann nicht ruckgangig gemacht werden.",
          deleting: "Wird geloscht...",
          forMe: "Fur mich loschen",
          forBoth: "Fur beide loschen",
        },
        toast: {
          dismiss: "OK",
          undo: "Ruckgangig",
          deletedForMe: "Nachricht fur mich geloscht",
          deletedForBoth: "Nachricht fur beide geloscht",
          deleteError: "Die Nachricht konnte gerade nicht geloscht werden.",
        },
        composer: {
          all: "Alle",
          attach: "Foto anhangen",
          send: "Nachricht senden",
          thinking: "Fola denkt nach",
          selectedAttachmentAlt: "Ausgewahlter Anhang",
        },
        topBar: {
          back: "Zuruck zu Nachrichten",
          openFola: "Fola-Konversation offnen",
          compose: "Nachricht verfassen",
        },
        sourceThreadFallback: "Quellverlauf",
        errors: {
          resolve: "Diese Unterhaltung konnte nicht aufgelost werden.",
          load: "Diese Unterhaltung konnte nicht geladen werden.",
          send: "Die Nachricht konnte nicht gesendet werden.",
        },
      },
    },
    fola: {
      widget: {
        title: "Fola - AfriMed™ Assistent",
        greeting: "Hallo, ich bin Fola. Ich kann bei OTP, Magic Links und Einstellungen helfen.",
        open: "Fola offnen",
        placeholder: "Frag Fola...",
        sending: "Wird gesendet...",
        send: "Senden",
        error: "Entschuldigung, es gab ein Problem. Bitte versuche es erneut.",
      },
    },
    pharmacy: {
      invite: {
        title: "Apothekeneinladung",
        subtitle:
          "Prufe deine Einladung zum AfriMed Pharmacy Team, bestatige die eingeladene E-Mail und fordere dann den sicheren Anmeldelink fur genau dieses Apothekenkonto an.",
        body:
          "AfriMed nutzt die unten angegebene eingeladene E-Mail fur den Anmeldeschritt des Apothekenmitglieds. Offne die neueste Anmelde-E-Mail in diesem Browser oder auf diesem Gerat, dann fuhrt AfriMed zum Pharmacy Dashboard weiter.",
        demoSeedCopy:
          "Nach dem Offnen deines Pharmacy Dashboards findest du Demo-Bestand und bis zu drei Beispielanfragen zur Rezeptabwicklung. Nutze sie bitte, um zu testen, wie AfriMed™ Apothekenvorbereitung, Prufung, Abgabe, Zahlungen und Fulfilment-Workflows in der Staging-Umgebung unterstutzt.",
        emailLabel: "Eingeladene E-Mail",
        emailMissing: "An diesen Link war keine eingeladene E-Mail angehangt.",
        emailMissingBody:
          "Diesem Einladungslink fehlt die eingeladene E-Mail. Geh bitte zur Absenderin oder zum Absender zuruck und fordere die neueste AfriMed-Apothekeneinladung an.",
        alreadySignedIn:
          "Diese eingeladene E-Mail ist in diesem Browser bereits angemeldet. Weiter direkt zum Pharmacy Dashboard.",
        switchEmail:
          "In diesem Browser ist ein anderes Konto angemeldet. Fahre mit der eingeladenen E-Mail fort, damit AfriMed das richtige Apothekenkonto zuordnet.",
        cta: {
          dashboard: "Weiter zum Pharmacy Dashboard",
          opening: "Anmeldung wird geoffnet...",
          invitedEmail: "Mit eingeladener E-Mail fortfahren",
          returnLogin: "Zuruck zur Anmeldung",
          requestLink: "Weiter zum Anfordern des Anmeldelinks",
        },
      },
      dashboard: {
        income: {
          range: {
            threeMonths: "Letzte 3 Monate",
            sixMonths: "Letzte 6 Monate",
            year: "Letztes Jahr",
          },
          menu: "Datumsbereich fur Apothekenumsatz",
        },
        lowStock: {
          title: "Niedriger Bestand",
          subtitle:
            "Ein Uberblick uber Medikamente mit niedrigem Bestand, die nachbestellt werden mussen, bevor Rezepte umgeleitet werden.",
          openPrompt: "Fola-Prompt offnen",
          emptyTitle: "Zurzeit keine Positionen mit niedrigem Bestand",
          emptyCopy:
            "AfriMed zeigt hier Positionen mit niedrigem Bestand an, sobald der Filialbestand erfasst und gegen Nachbestellschwellen verfolgt wird.",
        },
      },
      settings: {
        language: {
          availableNow: "Jetzt verfugbar",
          availableNowCopy:
            "Wahle die aktuelle AfriMed-App-Sprache fur dieses Gerat und dieses Konto.",
          comingSoonTitle: "Kommt bald",
          comingSoonCopy:
            "Sprachen des sudlichen Afrika werden sorgfaltig fur einen sicheren Einsatz im Gesundheitskontext vorbereitet.",
        },
        notificationPreferences: {
          appointmentReminders: {
            title: "Termin-Erinnerungen",
            introDoctor:
              "AfriMed sendet eine einzige Erinnerung nahe am Start, eine Stunde vor bestatigten Terminen. Ein Morgenuberblick folgt spater.",
            introPatient:
              "AfriMed sendet Erinnerungen 24 Stunden und 2 Stunden vor bestatigten Terminen. SMS hilft auch dann, wenn du nicht angemeldet bist.",
            timingDoctor: "Aktuelle Zeit: 1 Stunde vor dem Termin.",
            timingPatient: "Aktuelle Zeit: 24 Stunden und 2 Stunden vor dem Termin.",
          },
          channels: {
            inApp: {
              title: "Erinnerungen in der App",
              support: "Erscheint im AfriMed-Benachrichtigungseingang und in den Terminansichten.",
            },
            sms: {
              title: "SMS-Erinnerungen",
              support: "Beste Ausweichoption, wenn du nicht angemeldet bist.",
            },
            email: {
              title: "E-Mail-Erinnerungen",
              support: "Sicherungszustellung mit den Termindetails.",
            },
            push: {
              title: "Push-Benachrichtigungen",
              supportGranted:
                "Nutzt Browser-Benachrichtigungen fur wichtige und dringende Versorgungshinweise, wenn AfriMed nicht im Vordergrund ist.",
              supportPrompt:
                "Aktiviere Browser-Benachrichtigungen fur wichtige und dringende Versorgungshinweise, wenn du dazu aufgefordert wirst.",
              unsupported: "Dieser Browser unterstutzt Benachrichtigungsabfragen hier nicht.",
            },
            sound: {
              title: "Ton fur dringende Hinweise",
              support: "Spielt einen kurzen Ton fur dringende Versorgungshinweise ab, wahrend AfriMed geoffnet ist.",
            },
            vibration: {
              title: "Dringende Vibration",
              support:
                "Fugt auf unterstutzten Geraten einen kurzen haptischen Impuls fur dringende Versorgungshinweise hinzu.",
            },
          },
          footer:
            "Browser-Benachrichtigungen decken jetzt Versorgungshinweise wahrend aktiver Sitzungen ab, wenn die Berechtigung erteilt wurde. Die vollstandige Push-Zustellung ausserhalb der Sitzung benotigt weiterhin Service-Worker-Abonnements und bleibt vorerst zuruckgestellt.",
        },
        fola: {
          title: "Apothekenbetriebs-Prompt",
          copy:
            "Offne Fola mit einem Apothekenbetriebs-Prompt fur Entwurfe und allgemeine AfriMed-Ablaufhilfe in Nachrichten.",
          cta: "Fola offnen",
        },
        members: {
          title: "Mitglieder",
          subtitle:
            "Das Onboarding der hauptverantwortlichen Person ist bereits abgeschlossen. Fuge hier das Filialteam hinzu und halte Rollen, Auszahlungen, Lieferung und Bestands-Handoff sauber ausgerichtet.",
          kpi: {
            branchOwner: "Eigentumer-Sitze",
            branchOwnerCopy:
              "Die hauptverantwortliche Person oder der Admin bleibt der Berechtigungsanker fur diese Filiale.",
            activeMembers: "Aktive Mitglieder",
            activeMembersCopy:
              "Nur akzeptierte Mitglieder zahlen als aktiv. Eingeladene Zeilen bleiben ausstehend, bis sich die Person offiziell anmeldet.",
            pendingInvites: "Ausstehende Einladungen",
            pendingInvitesCopy:
              "Nutze die eingeladene E-Mail auf der AfriMed-Anmeldeseite. Die Zeile wechselt erst nach Annahme und Verknupfung der Einladung.",
          },
          form: {
            name: "Vollstandiger Name",
            email: "E-Mail",
            phone: "Telefon",
            role: "Rolle",
          },
          sheet: {
            send: "Einladung senden",
          },
          status: {
            invited: "Einladung • Ausstehend",
            active: "Aktiv",
          },
          inviteTitle: "Filialmitglieder einladen oder importieren",
          inviteCopy:
            "Kopiere ein Einrichtungs-Briefing fur Lohn- oder Betriebsteams oder importiere eine Mitarbeiter-CSV, wenn die Filiale ihr Team bereits in HR-Software verwaltet.",
          inviteFlowCopy:
            "Neue Mitglieder bleiben Eingeladen, bis sie sich mit der eingeladenen E-Mail anmelden. Erst dann sollte die Filiale sie als aktiv behandeln.",
          copyBrief: "Einladungs-Briefing kopieren",
          importCsv: "CSV importieren",
          addCta: "Neues Mitglied",
          columns: {
            name: "Name",
            role: "Rolle",
            actions: "Aktionen",
          },
          copied: "Einladungs-Briefing kopiert",
          copyFailed: "Konnte gerade nicht kopieren",
          imported: "{count} Mitarbeitereinträge aus {file} importiert.",
          importEmpty: "{file} wurde hochgeladen, aber es wurden keine nutzbaren Mitarbeiterzeilen erkannt.",
          importFailed: "Die Mitarbeiter-CSV konnte nicht gelesen werden.",
          removed: "Mitglied entfernt",
          removeFailedDetail: "Mitglied konnte nicht entfernt werden: {message}",
          removeFailed: "Dieses Mitglied konnte gerade nicht entfernt werden.",
          inviteLinkCopied: "Anmeldelink kopiert",
          inviteLinkCopyFailed: "Anmeldelink konnte gerade nicht kopiert werden",
          inviteSending: "Einladungs-E-Mail wird gesendet...",
          inviteEmailSent: "Einladungs-E-Mail gesendet",
          inviteEmailFailed:
            "Die Einladungs-E-Mail konnte gerade nicht zugestellt werden. Nutze stattdessen Anmeldelink kopieren.",
          inviteFailedDetail: "Einladungs-E-Mail fehlgeschlagen: {message}",
          pendingHint: "Wartet auf Annahme der Einladung",
          resendInvite: "Einladung erneut senden",
          resendInviteBusy: "Wird gesendet...",
          resendInviteSent: "Gesendet",
          resendInviteFailed: "Einladung erneut versuchen",
          copySignInLink: "Anmeldelink kopieren",
          copySignInLinkBusy: "Wird kopiert...",
          copySignInLinkDone: "Kopiert",
          copySignInLinkRetry: "Erneut kopieren",
          remove: "Mitglied entfernen",
          removeLabel: "Entfernen",
          removeDialog: {
            title: "Hinweis",
            body: "{member} aus dem Filialteam entfernen?",
            memberFallback: "dieses Mitglied",
            cancel: "Abbrechen",
            confirm: "Ja",
          },
          create: {
            title: "Neues Mitglied",
            subtitle:
              "Fuge ein Filialmitglied fur Abgabe, Bestand, Finanzen oder Liefer-Handoff hinzu.",
            copy:
              "Die hauptverantwortliche Person oder der Admin bleibt der Berechtigungsanker der Filiale. Nutze diesen Schritt, um das tagliche Apothekenteam sauber hinzuzufugen.",
            roleGuideTitle: "Rollenleitfaden",
            roleGuideCopy:
              "Behalte dieselbe saubere Handoff-Logik in Pharmacy, Admin und Medical Institutes bei. Weise nur die Filialrolle zu, die diese Person jetzt braucht.",
            formTitle: "Mitgliedsdaten",
            formCopy:
              "Halte diese Einladung jetzt leichtgewichtig. Rollen konnen spater in Settings angepasst werden.",
            saving: "Einladung wird gesendet...",
            inviteSent:
              "Einladungs-E-Mail gesendet. Das Mitglied bleibt ausstehend, bis es sich mit der eingeladenen E-Mail anmeldet.",
            inviteFailed:
              "Die Einladungs-E-Mail konnte im Moment nicht gesendet werden. Bitte versuche es erneut.",
            inviteFailedDetail: "Einladungs-E-Mail fehlgeschlagen: {message}",
          },
          fola: {
            title: "Apotheken-Setup-Prompt starten",
            subtitle:
              "Offne Fola mit einem vorbereiteten Apothekenbetriebs-Prompt fur Rollen-Setup, CSV-Vorbereitung oder Liefer-Handoff-Planung in Nachrichten.",
            cta: "Fola offnen",
            roles: "Rollen-Prompt starten",
            csv: "CSV-Prompt starten",
            handoff: "Handoff-Prompt starten",
          },
          roles: {
            dispensing: {
              label: "Abgebender Apotheker",
              note:
                "Gibt Rezepte ab, genehmigt Substitutionen und ubergibt patientenbereite Bestellungen.",
            },
            inventory: {
              label: "Bestandsmanager",
              note: "Pflegt Bestand, Lieferantennotizen und Aktionen bei niedrigem Bestand.",
            },
            finance: {
              label: "Finanzverantwortlicher",
              note: "Verfolgt Auszahlungs-Exporte, Gebuhrensichtbarkeit und Abstimmung.",
            },
            delivery: {
              label: "Lieferverantwortlicher",
              note: "Koordiniert Versand, verifizierte Adressen und Lieferbereitschaft.",
            },
            branchAdmin: {
              label: "Filialadmin",
              note: "Unterstutzt das Tagesgeschaft der Filiale und die Teamkoordination.",
            },
          },
        },
      },
      inventory: {
        kpi: {
          tracked: "Verfolgte SKUs",
          activeCatalog: "Aktiver Katalog",
          lowStockAlerts: "Warnungen bei niedrigem Bestand",
          needsAction: "Aktion erforderlich",
          coldChain: "Kuhlkettenartikel",
          verifiedStorage: "Geprufte Lagerung",
        },
        stockControl: {
          title: "Bestandskontrolle",
          subtitle:
            "Verwalte die Medikamentenverfugbarkeit, damit AfriMed Filialverfugbarkeit, Bestandsvertrauen und patientenseitige Preise klar anzeigen kann.",
        },
        actions: {
          openFolaPrompt: "Fola-Prompt offnen",
          addStockItem: "Bestandsartikel hinzufugen",
        },
        loading: "Filialbestand wird geladen...",
        onHand: "Verfugbar",
        reorderLevel: "Nachbestellgrenze",
        retailPrice: "Verkaufspreis",
        reorderNow: "Jetzt nachbestellen",
        healthy: "Stabil",
        empty: {
          title: "Noch keine Bestandsartikel hinzugefugt",
          copy:
            "Fuge hier echte Bestandsartikel hinzu, damit Warnungen, Rezeptweiterleitung und patientenseitige Preise glaubwurdig bleiben.",
        },
        create: {
          title: "Bestandsartikel hinzufugen",
          subtitle:
            "Erstelle eine echte Inventarzeile, damit Bestandskontrolle, Rezeptweiterleitung und Apothekensummen aus der Filialrealitat gespeist werden konnen.",
          copy:
            "Dies schreibt direkt in das Filialinventarprofil, das vom Pharmacy-Dashboard genutzt wird. Es ersetzt den alten Vorlagen-Fallback durch echte Bestandsdaten.",
          details: "Artikeldetails",
          save: "Bestandsartikel speichern",
          saveError: "Dieser Bestandsartikel konnte noch nicht gespeichert werden.",
        },
        edit: {
          title: "Bestandsartikel bearbeiten",
          subtitle:
            "Aktualisiere diese Filialinventarzeile, damit Bestandskontrolle und Warnungen bei niedrigem Bestand aktuell bleiben.",
          missing: "Dieser Bestandsartikel wurde nicht mehr gefunden.",
          loadError: "Dieser Bestandsartikel konnte noch nicht geladen werden.",
          save: "Anderungen speichern",
        },
        detail: "Starke / Detail",
        currentStock: "Aktueller Bestand",
        minimalStock: "Mindestbestand",
        unitPrice: "Stuckpreis",
        supplier: "Lieferant",
        branch: "Filiale",
        batchNumber: "Chargennummer",
        expiryDate: "Verfallsdatum",
        binLocation: "Lagerort",
        imageUrl: "Bild-URL",
        imagePreview: "Bildvorschau",
        imageHelp:
          "Fuge eine gehostete Bild-URL fur dieses Medikament ein. Wenn sie leer bleibt, verwendet AfriMed den Platzhalter fur Lagerbestand.",
        errors: {
          loadFailed: "Der Filialbestand konnte noch nicht geladen werden.",
          removeFailed: "Dieser Bestandsartikel konnte noch nicht entfernt werden.",
        },
      },
      search: {
        results: "Suchergebnisse",
      },
      prescriptions: {
        price: "Preis",
        priceSet: "Preis festgelegt: {subtotal}",
        priceReady: "Preis bereit",
        actions: {
          setPrice: "Preis festlegen",
          reject: "Ablehnen",
          accept: "Annehmen",
          markReadyForPickup: "Als abholbereit markieren",
        },
        errors: {
          invalidPrice: "Gib vor dem Speichern einen gultigen Preis ein.",
          updateFailed: "Dieses Rezept konnte gerade nicht aktualisiert werden.",
        },
      },
      order: {
        status: {
          selected: "Apotheke ausgewahlt",
          preparing: "In Vorbereitung",
          awaitingPayment: "Wartet auf Zahlung",
          paid: "Bezahlt",
          readyForPickup: "Bereit zur Abholung",
          rejected: "Abgelehnt",
          issued: "Ausgestellt",
        },
        errors: {
          missingId: "Die Rezept-ID fehlt.",
          loadFailed: "Diese Apothekenbestellung konnte nicht geladen werden.",
        },
        defaults: {
          prescriptionItem: "Rezeptposition",
          instructions: "Prufe die mit dieser Bestellung verbundenen Rezeptanweisungen.",
          pharmacy: "AfriMed Apotheke",
        },
        title: "Rezeptbestellung",
        patient: "Patient",
        medication: "Medikament",
        medicationList: "Medikamentenliste",
        medicationItem: "Medikament {index}",
        quotedPrice: "Angegebener Preis",
        pendingPrice: "Preis ausstehend",
        pendingPriceHelper: "Lege den Preis in der Apothekenwarteschlange fest, sobald der Bestand bestatigt ist.",
        rxId: "RxID",
        issuedAt: "Ausgestellt {date}",
        updatedAt: "Aktualisiert {date}",
        openQueue: "Rezeptwarteschlange offnen",
        print: "Bestellung drucken",
        qr: {
          title: "QR",
          copy: "Phase 1 nutzt die einfache AfriMed-Bestellroute zur Verifikation.",
          alt: "QR fur {id}",
          unavailable: "QR nicht verfugbar",
        },
      },
      payments: {
        link: {
          accessMissing:
            "Nur verknupfte Konten von Apothekeninhabern oder Filialmitgliedern konnen hier Zahlungslinks erstellen.",
          createError: "Der Apotheken-Zahlungslink konnte gerade nicht erstellt werden.",
          copyError: "Der Zahlungslink konnte gerade nicht kopiert werden.",
          title: "Zahlungslink erstellen",
          subtitle:
            "Bereite einen sicheren Sponsor-Zahlungslink fur schnelle Nachfullungen, OTC-Artikel oder diaspora-bezahlte Apothekenkaufe vor.",
          description: "Beschreibung",
          amount: "Betrag",
          recipientEmail: "Empfanger-E-Mail",
          generate: "Zahlungslink erstellen",
          copy: "Link kopieren",
          email: "Per E-Mail senden",
          footnote:
            "Dies nutzt den bestehenden AfriMed-Sponsorzahlungsfluss wieder und wendet dieselbe 10-%-Plattformgebuhrenstruktur wie die aktuellen Stripe-verknupften Sponsorzahlungen an.",
          generatedLink: "Generierter Link",
          generatedLinkEmpty:
            "Erstelle zuerst den sicheren Zahlungslink und kopiere oder sende ihn dann sauber weiter.",
          settlementPreview: "Auszahlungs-Vorschau",
        },
      },
      help: {
        playbook: {
          title: "Apotheken-Playbook",
          subtitle: "Der erste Leitfaden fur den Pharmacy-Bereich.",
        },
        step1: {
          title: "1. Rezept verifizieren",
          copy: "Nutze den QR-Scanner, um das AfriMed-Rezept vor der Abgabe zu bestatigen.",
        },
        step2: {
          title: "2. Bestand und Preis bestatigen",
          copy: "Zeige Verfugbarkeit, Medikamentensumme und Lieferkosten klar an.",
        },
        step3: {
          title: "3. Sauber ubergeben",
          copy:
            "Markiere Abholung oder Lieferung als abgeschlossen, damit Arzt, Patient und Apotheke abgestimmt bleiben.",
        },
      },
      folaPrompts: {
        lowStockFallback:
          "Hilf mir, ruhige Nachbestelltexte und Filial-Handoff-Notizen fur die Apotheken-Bestandsuberwachung zu formulieren. Gehe nicht von automatischer Bestandssynchronisierung aus und triff keine autonomen Entscheidungen.",
        lowStockIntro:
          "Hilf mir, ruhige Nachbestelltexte und Filial-Handoff-Notizen fur diese Positionen mit niedrigem Bestand zu formulieren.",
        lowStockOutro:
          "Bleib beim Support. Gehe nicht von automatischer Bestandssynchronisierung aus und triff keine autonomen Entscheidungen.",
        helpPlaybook:
          "Hilf mir, den AfriMed-Apothekenablauf sauber zu betreiben: Teamaufbau, Rezept-Handoff, Kommunikation bei niedrigem Bestand und Lieferablauf.",
      },
    },
    stagingGate: {
      checking: "Privater Staging-Zugang wird gepruft...",
      privateTitle: "Privates Staging",
      enterTitle: "Privater Staging-Zugang",
      privateBody:
        "AfriMed befindet sich derzeit in einer privaten Testphase. Wir nehmen eine begrenzte Zahl von Gesundheitsfachpersonen und Partnern auf. Hinterlasse deine E-Mail-Adresse und wir informieren dich, sobald der Zugang offen ist.",
      enterBody: "Gib deine eingeladene E-Mail ein, um mit dem AfriMed-Staging fortzufahren.",
      emailLabel: "E-Mail",
      emailPlaceholder: "name@beispiel.com",
      checkingAction: "Zugang wird gepruft...",
      requesting: "Zugang wird angefragt...",
      continueCta: "Weiter",
      requestCta: "Zugang anfragen",
      requestSaved:
        "AfriMed hat deine Anfrage gespeichert. Wir melden uns, sobald dein Staging-Zugang bereit ist.",
      errors: {
        lookupFailed:
          "AfriMed konnte den Staging-Zugang im Moment nicht prufen. Bitte fordere den Zugang unten an.",
        submitFailed:
          "AfriMed konnte deine Anfrage im Moment nicht speichern. Bitte versuche es in Kurze erneut.",
      },
    },
    verify: {
      phoneOtp: {
        title: "Bestatigungscode eingeben",
        copy: "Wir haben einen 6-stelligen Code gesendet an",
      },
      prescription: {
        pageTitle: "Rezept verifizieren",
        checking: "Wird gepruft...",
        status: "Status",
        signed: "Signiert",
        notSigned: "Nicht signiert",
        patient: "Patient",
        dob: "Geburtsdatum",
        idLabel: "ID",
        reg: "Reg",
        title: "E-Rezept",
        number: "Rezept-Nr",
        issued: "Ausgestellt",
        schedule: "Schema",
        frequency: "Haufigkeit",
        dosage: "Dosierung",
        starts: "Beginn",
        ends: "Ende",
        verification: "Verifikation",
        expires: "Lauft ab",
        digitalSeal: "Digitales Siegel",
        prescriberVerification: "Verifikation des Verordnenden",
        signatureAlt: "Unterschrift des Verordnenden",
        publicRouteCopy: "{source}. Offentliche Verifikationsroute fur Apotheke oder manuelle Prufungen.",
        verifiedVia:
          "Verifiziert uber {issuer}. Die verordnende Person bleibt fur klinische Entscheidungen verantwortlich.",
        source: {
          manualPharmacy: "Manuell in der Apotheke eingegeben",
          scannedPharmacy: "In der Apotheke gescannt",
          public: "Offentliche Verifikationsansicht",
        },
        actions: {
          scanAnother: "Weiteres Rezept scannen",
          returnToPrescription: "Zuruck zum Rezept",
        },
        defaults: {
          prescriber: "Verordnende Person",
          medication: "Medikament",
          patient: "Patient",
          facility: "Einrichtung",
        },
        errors: {
          invalidCode: "Ungultiger Rezeptcode.",
          unavailable: "Die Rezeptverifikationsdaten sind nicht verfugbar.",
          failed: "Verifikation fehlgeschlagen.",
        },
        payment: {
          title: "Patientenzahlung vorbereiten",
          copy:
            "Lege jetzt Medikamentensumme und Lieferbedingungen fest, damit der Patient direkt an die ausgebende Filiale zahlen kann, ohne eine zweite Nachverfolgung.",
          medicineSubtotal: "Medikamentensumme",
          fulfillmentMode: "Erfullungsmodus",
          deliveryFee: "Liefergebuhr",
          patientTotal: "Patientengesamtsumme",
          send: "Patienten zur Zahlung senden",
          saving: "Angebot wird gespeichert...",
          success:
            "{pharmacy} ist jetzt bereit. Der Patient kann dieses Rezept offnen und {total} bezahlen.",
          auditCopy:
            "{currency}-Angebot wurde fur Audit und Checkout an diesem Rezept gespeichert.",
          defaults: {
            quote: "Apothekenangebot",
          },
          errors: {
            missingSubtotal:
              "Die Medikamentensumme ist erforderlich, bevor der Patient zur Zahlung weitergeleitet werden kann.",
            prepareFailed:
              "Die Patientenzahlung konnte im Moment nicht vorbereitet werden.",
          },
        },
      },
    },
  },
  sn: {
    common: {
      back: "Dzokera",
      continue: "Ramba",
      discard: "Rasa",
      changesSaved: "Shanduko dzachengetwa",
      keepEditing: "Ramba uchigadzirisa",
      saving: "Kuchengeta...",
      loading: "Kurodha...",
      edit: "Gadzirisa",
      add: "Wedzera",
      all: "Zvese",
      showAll: "Ratidza zvese",
      manage: "Ronga",
      open: "Vhura",
      change: "Chinja",
      save: "Chengeta",
      delete: "Bvisa",
      cancel: "Kanzura",
      close: "Vhara",
      map: "Mepu",
      yes: "Ehe",
      no: "Kwete",
      on: "Yakabatidzwa",
      off: "Yakadzimwa",
      verified: "Yasimbiswa",
      pendingVerification: "Kusimbisa kuchiri kuitika",
      download: "Dhawunirodha",
      clear: "Bvisa",
      review: "Ongorora",
      refresh: "Vandudza",
      done: "Zvaitwa",
      selectPlaceholder: "Sarudza...",
      comingSoon: "Zvichauya munguva pfupi",
      availableSoon: "Zvichawanikwa munguva pfupi",
      reviewRequired: "Ongororo inodiwa",
      logout: "Buda",
      loggingOut: "Kuchibuda...",
      cookies: {
        title: "Zvinodiwa zveCookie zveAfriMed™",
        intro:
          "Sarudza mashandisirwo anoitwa ma-cookie asingamanikidzwi. Zvimwe zvikamu zvakadzimwa pakutanga. Rongedza ma-cookie ako pano pazasi.",
        support:
          "Zvakagadzirirwa kutsigira mitemo yeGDPR nemisimboti yeAfrican Union yekudzivirira data kuitira kujeka, chinangwa chakajeka, uye kutonga kwemushandisi.",
        close: "Vhara zvinodiwa zvecookie",
        alwaysActive: "Zvinogara zvichishanda",
        rejectOptional: "Ramba zvisingamanikidzwi",
        saveChoices: "Chengeta zvasarudzwa",
        acceptAll: "Gamuchira zvese",
        categories: {
          strictly: {
            label: "Ma-cookie anokosha chete",
            body:
              "Anodiwa pakushanda kukuru kwakaita sekuchengetedzwa, kuenderera kwesession, uye accessibility. Aya ma-cookie anogara achishanda.",
          },
          statistics: {
            label: "Ma-cookie ehuwandu",
            body:
              "Anoyera mashandisirwo akabatanidzwa kuitira kuti tivandudze mhando yebasa nekunzwisisa kweutano hweveruzhinji. Hatitengesi data remunhu.",
          },
          performance: {
            label: "Ma-cookie ekushanda",
            body:
              "Anotibatsira kuona kumhanya, kugadzikana, uye kusimba kwebasa pamidziyo nemanetiweki akasiyana.",
          },
          functionality: {
            label: "Ma-cookie ezvaunofarira",
            body:
              "Anorangarira zvaunofarira zvakaita semutauro nenzvimbo kuitira kuti kudzoka kwako kuve nyore.",
          },
          targeting: {
            label: "Ma-cookie ekushambadza",
            body:
              "Ano shandiswa kuunza kana kuderedza kukosha kwemashoko ekushambadza. Hatitengesi data remunhu.",
          },
          social: {
            label: "Ma-cookie esocial media",
            body:
              "Anoita kuti social login uye zvemukati zvemumasocial zviitike. Vamwe vanopa vanogona kushandisa mitemo yavo.",
          },
          beacons: {
            label: "Web beacons",
            body:
              "Matagi madiki anoshandiswa kunzwisisa kuendeswa nekupindurwa kwemashoko kana zvichishanda.",
          },
        },
      },
      datePicker: {
        enterDate: "Isa zuva",
        today: "Nhasi",
        yesterday: "Nezuro",
        last7Days: "Mazuva 7 apfuura",
        selectDate: "Sarudza zuva",
        selectMonth: "Sarudza mwedzi",
        selectYear: "Sarudza gore",
        toggleCalendar: "Chinja karenda",
        calendar: "Karenda",
        previousMonth: "Mwedzi wapfuura",
        nextMonth: "Mwedzi unotevera",
        displayDate: "{day} {month} {year}",
        headlineDate: "{weekday}, {day} {month}",
        monthYear: "{month} {year}",
        months: {
          january: "Ndira",
          february: "Kukadzi",
          march: "Kurume",
          april: "Kubvumbi",
          may: "Chivabvu",
          june: "Chikumi",
          july: "Chikunguru",
          august: "Nyamavhuvhu",
          september: "Gunyana",
          october: "Gumiguru",
          november: "Mbudzi",
          december: "Zvita",
        },
        weekdays: {
          sunday: "Sv",
          monday: "M",
          tuesday: "C",
          wednesday: "C",
          thursday: "C",
          friday: "C",
          saturday: "M",
        },
      },
      signature: {
        title: "Siginecha",
        subtitle:
          "Saina kamwe chete. AfriMed™ ichatora siginecha iyi pane prescription yega yega inoburitswa.",
        signInToAdd: "Ndapota pinda kuti uwedzere siginecha yako.",
        signInToSave: "Ndapota pinda kuti uchengetedze siginecha yako.",
        loadFailed: "Hatina kukwanisa kurodha siginecha.",
        readFailed: "Hatina kukwanisa kuverenga mufananidzo.",
        addFirst: "Ndapota wedzera siginecha yako kutanga.",
        saveFailed: "Hatina kukwanisa kuchengetedza siginecha.",
        saved: "Siginecha yachengetwa.",
        current: "Siginecha yazvino",
        alt: "Siginecha yechiremba",
        storedAt: "Yakachengetwa pa",
        none: "Hakusati kwava nesiginecha yakachengetwa.",
        draw: "Dhirowa siginecha yako",
        upload: "Isa",
        camera: "Kamera",
        save: "Chengeta siginecha",
        tip:
          "Zano: stylus kana pen rine rubber tip rinowanzo shanda zviri nani kupfuura chigunwe. Pressure inotsigirwa kana chishandiso chichikwanisa.",
        imageLoadFailed: "Hatina kukwanisa kurodha mufananidzo.",
      },
      legal: {
        terms: "Mitemo nemamiriro",
        privacy: "Mutemo wekuvanzika",
        dataDeletion: "Kubviswa kwedata",
        copyright:
          "© 2026 AfriMed™ Telehealth. HIPAA & GDPR zvinoenderana nedhijitari yezveutano.",
      },
    },
    routeFallback: {
      loadingPatientSettings: "Kurodha zvirongwa zvemurwere...",
      loadingDoctorSettings: "Kurodha zvirongwa zvechiremba...",
    },
    onboarding: {
      idPassport: {
        title: "ID yenyika • Pasipoti",
        subtitle:
          "Isa nhamba yako yeID/pasipoti kana mutsara weMRZ (mavara 30).",
        continue: "Ramba",
        verifying: "Kusimbisa...",
        label: "ID / Pasipoti",
        placeholder: "A12345678  ·  kana mutsara weMRZ",
        done: "Zvaitwa",
        invalidHint:
          "Shandisa nhamba yepasipoti kana unamire mutsara weMRZ une mavara 30.",
        processingTitle: "Kugadzirisa ID",
        processingPending: "Kuri kuongororwa gwaro rako...",
        processingIdle: "Tora mufananidzo kana isa scan yakajeka",
        working: "Kuri kushanda...",
        retake: "Tora zvakare",
        camera: "Kamera",
        uploadFile: "Isa faira",
        aiProcessing: "Kugadzirisa kweAI",
        sessionNotReady: "Session haisati yagadzirira. Ndapota pinda zvakare.",
        fileTooLarge:
          "Faira rakakura zvikuru. Ndapota isa mufananidzo uri pasi pe5 MB.",
        uploading: "Kuri kuiswa gwaro...",
        receivedExtracting: "Gwaro ragamuchirwa. Tiri kuburitsa ruzivo...",
        uploadFailed: "Kuisa kwatadza. Ndapota edzazve.",
        retention: "Kuvanzika: chengetedzo yakavharirwa inopera {date}.",
        picker: {
          files: "Isa kubva kumafaira",
          camera: "Tora mufananidzo",
          remove: "Bvisa",
        },
        status: {
          pending: "Kuri kuongororwa gwaro...",
          done: "Chitupa chagamuchirwa",
          failed: "Kugadzirisa kwatadza",
          received: "Gwaro ragamuchirwa",
          idle: "Kugadzirisa...",
        },
        alt: {
          passed: "Zvabudirira",
          failed: "Zvakatadza",
          received: "Zvatambirwa",
        },
        summary: {
          surname: "Sibhireni",
          idNumber: "Nhamba yeID",
          dob: "Zuva rekuzvarwa",
          expiry: "Zuva rekupera",
          givenNames: "Mazita ekutanga",
        },
      },
    },
    legal: {
      common: {
        category: "Zvemutemo",
        effectiveLabel: "Inoshanda kubva",
        effectiveDate: "5 Kurume 2026",
        empty:
          "Zvinyorwa zvemutemo hazviwanikwe pari zvino. Edzazve munguva pfupi.",
      },
      terms: {
        title: "Mitemo nemamiriro",
        subtitle:
          "Mitemo yekushandisa masevhisi eAfriMed™, kusanganisira kushandisa akhawunti, kuwana masevhisi, maitiro epapuratifomu, nemabasa emutemo.",
        sections: {
          acceptanceOfTerms: {
            title: "Kubvuma mitemo",
            body: {
              1: "Nekupinda kana kushandisa masevhisi eAfriMed™, unobvuma Mitemo nemamiriro aya pamwe nemimwe mitemo yose inotaurwa pano.",
              2: "Kana usingabvumirani nazvo, usashandise sevhisi iyi.",
            },
          },
          eligibilityAndAccountRegistration: {
            title: "Kukodzera uye kunyoresa akhawunti",
            body: {
              1: "Unofanira kupa ruzivo rwakarurama paunonyoresa uye kuchengetedza zvakavanzika zveaccount yako zvakachengeteka.",
              2: "Une mutoro wezvinoitwa paaccount yako kunze kwekunge zvakakonzerwa nekutadza kwekuchengetedzwa kwedu.",
            },
          },
          serviceScope: {
            title: "Miganhu yesevhisi",
            body: {
              1: "AfriMed™ inopa hwaro hwedhijitari hwehutano, kusanganisira kutaurirana kwe telehealth uye nzira dzekuronga kutarisirwa.",
              2: "AfriMed™ haitsivi rubatsiro rwechimbichimbi. Kana pane dambudziko rine ngozi kana rinoda kukurumidzira, taura nemasevhisi echimbichimbi emunharaunda yako pakarepo.",
            },
          },
          appointmentsBookingsAndCancellations: {
            title: "Maappointment, mabhukiro, uye kukanzura",
            body: {
              1: "Kuwanikwa kweappointment, kuronga, kuchinja, uye nguva dzekukanzura zvinogona kutsamira pachiremba, sangano, uye mitemo yekushanda.",
              2: "Vashandisi vanofanira kupa ruzivo rwakarurama rwekubhuka uye kuita nokutendeka pakuchinjazve kana kukanzura.",
            },
          },
          paymentsAndBilling: {
            title: "Kubhadhara nekubhiriwa",
            body: {
              1: "Kana masevhisi anobhadharwa achishanda, mari, mitemo yekubhiriwa, uye nzira dzekubhadhara zvicharatidzwa munzira dzechigadzirwa kana marongero emupi wesevhisi.",
              2: "Unobvumidza kubhadharwa kwemari inoenderana nemasevhisi aunenge wasarudza nekushandisa.",
            },
          },
          userResponsibilitiesAndAcceptableUse: {
            title: "Mabasa emushandisi uye kushandisa kunotenderwa",
            body: {
              1: "Unobvuma kusashandisa zvisiri izvo papuratifomu, kusavhiringa nzira dzekuchengetedza, kusapa ruzivo rwenhema, kana kushandisa AfriMed™ pazvinhu zvisiri pamutemo.",
              2: "Vashandisi vezvehutano vanofanira kutevedzera mitemo yehunyanzvi pamwe nekuvanzika kunodiwa nebasa ravo.",
            },
          },
          intellectualProperty: {
            title: "Pfuma yehungwaru",
            body: {
              1: "Zviratidzo zveAfriMed™, magadzirirwo echigadzirwa, zvemukati, uye software yepapuratifomu zvinodzivirirwa nemitemo yepfuma yehungwaru inoshanda.",
              2: "Haubvumidzwe kukopa, kushandura kuti uone mashandiro, kana kugoverazve zvinhu zvinodzivirirwa kunze kwekunge mutemo wabvumira kana wabvumidzwa zvakanyorwa.",
            },
          },
          thirdPartyServices: {
            title: "Masevhisi ebato rechitatu",
            body: {
              1: "Dzimwe nzira dzinogona kutsamira pamasevhisi ebato rechitatu atinovimba nawo, semuenzaniso kusimbisa kuzivikanwa, hwaro hwetekinoroji, kutaurirana, kana vanopa mepu.",
              2: "Masevhisi avo anogona kutongwa nemitemo yavo pamwe nemaziviso avo ekuvanzika.",
            },
          },
          privacyAndDataHandling: {
            title: "Kuvanzika nekubata data",
            body: {
              1: "Kushandiswa kwedata remunhu kunotongwa neMutemo wekuvanzika weAfriMed™.",
              2: "Nekushandisa papuratifomu, unobvuma kugadziriswa kwedata kunodiwa pakusimbisa kuzivikanwa, kuendeswa kwemasevhisi ehutano, uye nzira dzekutevedzera mitemo.",
            },
          },
          disclaimersAndLimitationOfLiability: {
            title: "Kuzivisa uye miganhu yemhosva",
            body: {
              1: "AfriMed™ inopiwa 'sezvayakamira', pasi pekugadziriswa, miganhu yekushanda, uye kutsamira kune zvimwe zvinhu zvekunze.",
              2: "Kusvika panobvumidzwa nemutemo, mhosva dzinoganhurirwa pane kukuvara kusiri kwakananga, kwekudonha, kana kunokonzerwa nekushandiswa kwepapuratifomu.",
            },
          },
          suspensionAndTermination: {
            title: "Kumiswa uye kupedzwa",
            body: {
              1: "Tinogona kumisa kana kupedza kuwana kana zvichidikanwa nekuda kwekuchengetedzwa, kutevedzera mutemo, kutyora mitemo, kana kudzivirira kushungurudzwa.",
              2: "Unogona kumira kushandisa sevhisi chero nguva zvichienderana nemitemo nemabasa echibvumirano anoramba achishanda.",
            },
          },
          governingLawAndUpdates: {
            title: "Mutemo unotonga uye kuvandudzwa",
            body: {
              1: "Mitemo iyi inotongwa nemitemo nemirau inoshanda pakushanda kweAfriMed™ pamwe nenyika kana nzvimbo dzinopihwa masevhisi.",
              2: "Tinogona kugadzirisa mitemo iyi; shanduro yazvino pamwe nezuva rayo rekushanda zvinoburitswa papeji rino.",
            },
          },
          contact: {
            title: "Kubata",
            body: {
              1: "AfriMed™ Telehealth",
              2: "Imeyili: info@afrimedtelehealth.com",
              3: "Webhusaiti: https://afrimedtelehealth.com",
            },
          },
        },
      },
      privacy: {
        title: "Mutemo wekuvanzika",
        subtitle:
          "Maitiro anotevera AfriMed™ pakuunganidza, kushandisa, kuchengetedza, uye kutonga data remunhu papuratifomu yedu ye telehealth.",
        sections: {
          whoWeAre: {
            title: "Tiri ani",
            body: {
              1: "AfriMed™ Telehealth inopa masevhisi ehutano edhijitari kuvarwere nevashandi vezvehutano, kusanganisira nzira dze teleconsultation, kuronga maappointment, mameseji akachengeteka, kuseta profile pakutanga, uye mabasa ehutano anosimbiswa nekuongorora.",
              2: "Mutemo wekuvanzika uyu unoshanda kune data remunhu rinounganidzwa kuburikidza newebhu neapp dzeAfriMed™, kusanganisira madomain ekuedzwa nekuburitswa kwechokwadi.",
            },
          },
          informationWeCollect: {
            title: "Ruzivo rwatinounganidza",
            body: {
              1: "Tinounganidza data reakawunti (zita, imeyili, foni, basa), data reprofayili nekuiswa kwekutanga (semuenzaniso demography uye mamiriro ehutano aunopa), data rezviitiko zvesevhisi (maappointment, mameseji, marongero), uye data rehunyanzvi (ruzivo rwetsananguro rwedhivhaisi nebrowser, malogs, uye masaini ekuchengetedza).",
              2: "Paunorodha magwaro ehutano kana ekuongorora, tinogadzirisa mafaera iwayo pamwe neruzivo rwetsananguro rwayo kuitira kusimbisa akhawunti, nzira dzekutevedzera mitemo, uye kuendesa sevhisi.",
            },
          },
          howWeUseYourInformation: {
            title: "Mashandisirwo atinoita ruzivo rwako",
            body: {
              1: "Tinotora data remunhu kuti tigadzire nekuchengetedza maaccount, kusimbisa vashandisi, kuendesa zvimiro zve telehealth, kutsigira maappointment nekutaurirana, kuvandudza kuvimbika, kuona kushungurudzwa, uye kutevedzera zvinodiwa zvemutemo nezvehutano.",
              2: "Tinogona kushandisa operational analytics kuvandudza mhando yesevhisi nezviitiko zvevashandisi tichishandisa nzira dzekuderedza data pamwe nekudzora kupinda.",
            },
          },
          howWeProtectYourInformation: {
            title: "Maitiro atinochengetedza ruzivo rwako",
            body: {
              1: "AfriMed™ inoshandisa nzira dzakawanda dzekuchengetedza, kusanganisira kudzora kupinda, kuvharirwa kwedata panguva yekutumira, mvumo inoenderana nebasa, kutarisa, uye nzira dzakagadzirirwa ongororo.",
              2: "Hapana sisitimu yepamhepo isina ngozi zvachose; tinoramba tichivandudza kudzora ngozi yekuchengetedzwa nekuvanzika.",
            },
          },
          howWeShareInformation: {
            title: "Maitiro atinogovera ruzivo",
            body: {
              1: "Tinogovera data chete kana zvichidikanwa pakupa sevhisi, kutevedzera mutemo, nzira dzinotungamirirwa nemushandisi, kana vanobatsira vatakavimba navo vari pasi pezvisungo zvekuvanzika nekudzivirirwa kwedata.",
              2: "Hatitengesi ruzivo rwemunhu.",
            },
          },
          dataRetention: {
            title: "Kuchengetwa kwedata",
            body: {
              1: "Tinochengeta data remunhu kwenguva inodiwa kupa masevhisi, kusangana nezvinodiwa zvemutemo nemirau, kugadzirisa makakatanwa, kusimbisa zvibvumirano, uye kuchengetedza marekodhi ekuongorora.",
              2: "Nguva dzekuchengetera dzinogona kusiyana zvinoenderana nerudzi rwedata nezvinodiwa nenzvimbo dzakasiyana.",
            },
          },
          cookiesAndSimilarTechnologies: {
            title: "Makuki uye matekinoroji akafanana",
            body: {
              1: "Tinoshandisa makuki anokosha pamwe nematekinoroji akafanana kuitira kuchengetedzwa, kuenderera kwekupinda, uye kushanda kwechigadzirwa.",
              2: "Pazvinobvira, zvikamu zvisiri zvekumanikidzwa zvinotungamirirwa kuburikidza nezvidzoreso zvezvido zvemakuki zviri muapp.",
            },
          },
          yourDataRights: {
            title: "Kodzero dzako padata",
            body: {
              1: "Zvichienderana nenzvimbo yauri, unogona kuva nekodzero dzekuona, kugadzirisa, kubvisa, kudzora, kuramba, kana kutamisa rimwe data remunhu.",
              2: "Unogonawo kukumbira rubatsiro rweaccount nekuvanzika kuburikidza nenzira dzekutibata dziri pazasi.",
            },
          },
          childrensPrivacy: {
            title: "Kuvanzika kwevana",
            body: {
              1: "AfriMed™ haina kugadzirirwa kushandiswa nevana vasina kutarisirwa pazvinorambidzwa nemutemo.",
              2: "Kana uchifunga kuti data rakapihwa zvisiri izvo, taura nesu uye tichariongorora tigoita zvinoenderana nezvinodiwa nemutemo.",
            },
          },
          internationalDataUse: {
            title: "Kushandiswa kwedata pasi rese",
            body: {
              1: "AfriMed™ inogona kugadzirisa data mumatunhu akasiyana sezvinodiwa kuti sevhisi ishande zvakachengeteka.",
              2: "Kana kutamiswa kwemuganhu kuchidikanwa, tinoshandisa dziviriro dzakakodzera zvinoenderana nemutemo unoshanda.",
            },
          },
          changesToThisPolicy: {
            title: "Shanduko kumutemo uyu",
            body: {
              1: "Tinogona kugadzirisa Mutemo wekuvanzika uyu sezvo papuratifomu, mitemo, kana zvinodiwa zvekuchengetedza zvichichinja.",
              2: "Shanduko dzakakosha dzicharatidzwa nekuchinja zuva rekushanda uye kuburitsa shanduro yakagadziridzwa papeji rino.",
            },
          },
          contactUs: {
            title: "Taura nesu",
            body: {
              1: "AfriMed™ Telehealth",
              2: "Imeyili: info@afrimedtelehealth.com",
              3: "Webhusaiti: https://afrimedtelehealth.com",
            },
          },
        },
      },
      dataDeletion: {
        title: "Mirayiridzo yekubviswa kwedata",
        subtitle:
          "Maitiro anogona kushandiswa nevashandisi veAfriMed™ kukumbira kubviswa kwedata repapuratifomu rakabatana neaccount, kusanganisira kusimbisa kuzivikanwa, chiyero, uye zvinoregererwa nekuda kwekutevedzera mitemo.",
        sections: {
          purposeAndScope: {
            title: "Chinangwa nechiyero",
            body: {
              1: "Peji iri rinotsanangura maitiro anogona kushandiswa nevashandisi veAfriMed™ kukumbira kubviswa kwedata repapuratifomu rakabatana neaccount yavo.",
              2: "Data repapuratifomu pano rinosanganisira marekodhi eakhawunti neprofile, marekodhi ekushandiswa kwesevhisi, uye ruzivo rwetsananguro runogadzirwa neapp iyo AfriMed™ inodzora mumasisitimu ayo.",
            },
          },
          howToRequestDeletion: {
            title: "Maitiro ekukumbira kubviswa",
            body: {
              1: "Vashandisi vanogona kukumbira kubviswa nekutumira imeyili ku info@afrimedtelehealth.com vachishandisa imeyili yakabatana neaccount yavo yeAfriMed™.",
              2: "Kana imeyili isingawanikwe, vashandisi vanogona kuendesa chikumbiro kuburikidza nenzira dzekutsigira dzeAfriMed™ vachibatanidza mashoko ekuzivisa kuti vasimbiswe.",
              3: "Kuti zvigadziriswe nekukurumidza, sanganisai zita rizere, imeyili kana nhamba yefoni yeaccount, basa (murwere kana chiremba), pamwe nemashoko anoti: 'Ndinokumbira kubviswa kwedata reaccount yangu yeAfriMed™.'",
            },
          },
          identityVerificationBeforeDeletion: {
            title: "Kusimbisa kuzivikanwa kusati kwabviswa data",
            body: {
              1: "Kuchengetedza uye kudzivirira kuvanzika kwehutano, AfriMed™ inosimbisa kuzivikanwa kwemunhu akumbira kubviswa isati yagadzirisa chikumbiro chacho.",
              2: "Kusimbisa kunogona kusanganisira kusimbiswa kuburikidza neemail kana foni yakanyoreswa pamwe nezvimwe zviongorori pazvinodiwa kudzivirira kubviswa kweaccount kusina mvumo.",
            },
          },
          whatWeDelete: {
            title: "Zvatinobvisa",
            body: {
              1: "Kana kusimbiswa kwabudirira, AfriMed™ inobvisa kana kubvisa kuzivikanwa kusisadzokeri kwedata repapuratifomu rakabatana neaccount risingachadiwi pamutemo, mutemo wemirau, kuchengetedzwa, kana kudzivirira huori.",
              2: "Mienzaniso inogona kusanganisira mashoko eprofile, mafaera enhau asisadiwi, uye marekodhi ezviitiko zveapplication akabatana neaccount.",
            },
          },
          whatMayBeRetained: {
            title: "Zvinogona kusara zvakachengetwa",
            body: {
              1: "Panzira dzekukumbira kubviswa, AfriMed™ inogona kuchengeta marekodhi mashoma kusvika kumazuva makumi mapfumbamwe kuti ipedze kusimbisa, kugadzirisa makakatanwa, kuongorora kuchengetedzwa, uye dziviriro dzekushanda.",
              2: "Mushure menguva iyoyo, kubviswa kunoitwa otomatiki kunze kwekunge paine chikonzero chemutemo, mirau, kudzivirira huori, kana kumiswa kwekuongorora kunoda kuchengetwa kwenguva refu.",
              3: "Chero data rinoramba riri rekupinda kwakaganhurirwa uye rinochengetwa chete kwenguva inodiwa.",
            },
          },
          processingTimeline: {
            title: "Nguva yekugadzirisa",
            body: {
              1: "AfriMed™ inobvuma zvikumbiro zvekubviswa uye inotanga kusimbisa nekugadzirisa nekukurumidza zvinobvira.",
              2: "Nguva yekupedzisa inoenderana nekusimbiswa kwekuzivikanwa pamwe nezvinodiwa nemutemo, asi zvikumbiro zvinowanzopedzwa mukati memazuva makumi matatu.",
            },
          },
          outcomeConfirmation: {
            title: "Kusimbisa mhedziso",
            body: {
              1: "Kana kugadzirisa kwapera, AfriMed™ inotumira mhinduro yekusimbisa kumunhu akakumbira.",
              2: "Kana paine data risingakwanisi kubviswa nekuda kwezvinodiwa nemutemo, AfriMed™ inotsanangura chikonzero pamwe nesisitimu yekuchengetera inoshanda.",
            },
          },
          contactForDataDeletion: {
            title: "Kubata pamusoro pekubviswa kwedata",
            body: {
              1: "AfriMed™ Telehealth",
              2: "Imeyili: info@afrimedtelehealth.com",
              3: "URL yekureva: https://staging.afrimed.tech/legal/data-deletion",
            },
          },
        },
      },
    },
    feedback: {
      trigger: "Taura dambudziko",
      dialog: {
        title: "Taura dambudziko",
        subtitle:
          "Chengeta mamiriro eAfriMed aya kuti chikwata chikwanise kuvhura zvakare nzvimbo iyi.",
        sending: "Kutumira...",
        send: "Tumira chirevo",
        context: "Mamiro aripo zvino",
        role: "Basa",
        section: "Chikamu",
        entity: "Chinhu",
        note: "Chii chaitika?",
        placeholder:
          "Nyora pfupi zvawaitarisira uye zvakaratidzwa neAfriMed pano.",
        sent: "Chirevo chachengetwa pamwe chete nemamiriro eAfriMed aripo.",
      },
      errors: {
        save: "Hatina kukwanisa kuchengetedza chirevo chako parizvino.",
      },
    },
    appointments: {
      common: {
        yearsExperience: "makore {count}",
      },
      details: {
        ehr: {
          title: "EHR",
        },
        deleteDialog: {
          title: "Uri kuda kubvisa rekodhi rekuonana iri here?",
          description:
            "Kana wabvisa rekodhi iri, hauzokwanisi kuridzorera.",
          confirm: "Bvisa",
        },
      },
      book: {
        title: "Sarudza zuva, rudzi nenguva",
        doctorMissing:
          "Chiremba haawanikwi. Dzokera kumashure usarudze chiremba zvakare.",
        backToDoctors: "Dzokera kuvanachiremba",
        sections: {
          service: "Sarudza sevhisi",
          type: "Sarudza rudzi rwekuonana",
          location: "Nzvimbo",
          date: "Zuva",
          time: "Nguva",
          payment: "Sarudza nzira yekubhadhara",
        },
        mode: {
          virtual: "Kuri kure",
          inPerson: "Pachezvapo",
        },
        selectDate: "Sarudza zuva",
        minutesShort: "{count} min",
        loading: {
          services: "Kurodha masevhisi echiremba...",
          type: "Kurodha marudzi ekuonana...",
          dates: "Kurodha mazuva aripo...",
          times: "Kurodha nguva dziripo...",
          payments: "Kurodha nzira dzekubhadhara dzechiremba...",
        },
        empty: {
          services: "Chiremba uyu haasati awedzera masevhisi.",
          dates: "Chiremba uyu haasati avhura mazuva ekuonana.",
          unavailableDay: "Chiremba uyu haawanikwi pazuva rasarudzwa.",
          noTimes: "Hapasisina nguva dziripo pazuva iri.",
          payments: "Chiremba uyu haasati aisa nzira dzekubhadhara.",
          onlinePaymentsOnly:
            "Chiremba uyu haasati avhura kubhadhara kwakachengeteka kwekadhi. Kubhuka kunoramba kwakavharwa kusvikira nzira chaiyo yekubhadhara yavapo.",
        },
        errors: {
          loadPayments: "Hatina kukwanisa kurodha nzira dzekubhadhara.",
        },
        evisitUnavailable:
          "Kuonana kuri kure hakusati kwavhurwa nachiremba uyu, saka kubhuka kunoramba kwakabatana nekuwanikwa kwepachezvapo.",
        virtualVisitNotice:
          "Kubhukha uku kuchava kuonana kuri kure kuitira nzira inokurumidza inobatsira murwere.",
        inpersonFallback:
          "Kuonana kwepachezvapo panzvimbo yekuonana yechiremba.",
        confirm: "Simbisa",
      },
      edit: {
        title: "Chinja kuonana",
        notFound:
          "Kuonana hakuwanikwi. Dzokera kumashure worivhura zvakare.",
        backToAppointments: "Dzokera kukuonana",
        paymentMethodFallback: "Makadhi edhibhiti kana ekiredhiti",
        chooseFacility: "Sarudza nzvimbo",
        chooseFacilityError:
          "Ndapota sarudza nzvimbo yekuonana kwepachezvapo.",
        virtualVisitNotice: "Kuonana uku kuchava kuri kure.",
        selectDate: "Sarudza zuva",
        confirm: "Simbisa",
        mode: {
          inPerson: "Pachezvapo",
          eVisit: "Kuri kure",
        },
        sections: {
          type: "Rudzi",
          location: "Nzvimbo",
          date: "Zuva",
          time: "Nguva",
          payment: "Nzira yekubhadhara",
        },
        loading: {
          dates: "Kurodha mazuva aripo...",
          times: "Kurodha nguva dziripo...",
        },
        empty: {
          dates: "Chiremba uyu haasati avhura mazuva ekuonana.",
          unavailableDay: "Chiremba uyu haawanikwi pazuva rasarudzwa.",
          noTimes: "Hapasisina nguva dziripo pazuva iri.",
        },
        errors: {
          loadDates: "Hatina kukwanisa kurodha mazuva aripo.",
          loadTimes: "Hatina kukwanisa kurodha nguva dziripo.",
        },
      },
      editLocation: {
        title: "Chinja nzvimbo",
        searchPlaceholder: "Tsvaga zvipatara kana makiriniki",
        evisitSubtitle:
          "Shandura kuonana uku kuve kufona kwevhidhiyo kuri kure",
        selectEvisit: "Sarudza kuonana kuri kure",
        selectFacility: "Sarudza {name}",
        empty: "Hapana nzvimbo dzawanikwa.",
        facilityFallback: "Nzvimbo",
        evisitUnavailable:
          "Kuonana kuri kure hakusati kwavhurwa nachiremba uyu parizvino, saka kubhuka kunoramba kwakabatana nenzvimbo dzekuonana kwepachezvapo.",
        inpersonUnavailable:
          "Chiremba uyu haana nzvimbo yekuonana kwepachezvapo iripo yekubhukwa parizvino.",
        errors: {
          loadCsv: "Hatina kukwanisa kurodha CSV yenzvimbo",
          emptyCsv: "CSV yenzvimbo haina chinhu",
          load: "Hatina kukwanisa kurodha nzvimbo",
        },
      },
      confirm: {
        title: "Pfupiso yekuonana",
        continueToPayment: "Enderera kukubhadhara",
        continueToPaymentOptions: "Enderera kusarudzo dzekubhadhara",
        schedule: "Purogiramu",
        patientFallback: "Murwere",
        doctorFallback: "Chiremba",
        serviceFallback: "Kuonana",
        firstConsultancy: "Kuonana kwekutanga",
        videoConsultation: "Kuonana kuri kure",
        facilityFallback: "Chipatara chakazvimirira",
        inPersonLower: "pachezvapo",
        payment: {
          credit: "Makadhi edhibhiti kana ekiredhiti",
          bank: "Kutamiswa kubhengi",
          cash: "Mari pakushandiswa",
          eft: "EFT yekukurumidza",
          wallet: "Ma-wallet epamhepo",
          fallback: "Nzira yekubhadhara",
        },
        fields: {
          doctor: "Chiremba",
          service: "Sevhisi",
          date: "Zuva",
          time: "Nguva",
          amountPaid: "Mari inobhadharwa",
        },
        errors: {
          invalidDateTime: "Zuva kana nguva yekuonana haisi iyo.",
          update: "Hatina kukwanisa kugadzirisa kuonana.",
        },
        notifications: {
          newAppointmentTitle: "Kuonana kutsva",
          newTimeRequestTitle: "Chikumbiro chenguva itsva",
          appointmentUpdateTitle: "Kuvandudzwa kwekuonana",
          appointmentUpdateMessage:
            "Chiremba wako akakurudzira imwe nguva itsva yekuonana. Ongorora wobva wasimbisa shanduko yacho.",
          modeChangeLead: "akumbira kuchinja kubva ku",
          modeChangeTo: "ku",
          newTimeLead: "akumbira imwe nguva itsva yekuonana.",
          pendingApproval: "Chakamirira mvumo.",
        },
      },
      summary: {
        title: "Kuonana kwapera",
        subtitle: "Kuonana #{id}",
        patientFallback: "Murwere",
        doctorFallback: "Chiremba",
        finishedWith: "Uchangopedza kuonana na",
        endedAt: "Chapera: {datetime}",
        vitalRepStatus: "Mamiro eVitalRep™",
        status: {
          readyTitle: "Zvinyorwa zvagadzirira",
          updatingTitle: "Tiri kuvandudza EHR...",
          generatingTitle: "Tiri kugadzira zvinyorwa...",
          readyBodyDoctor:
            "Zvinyorwa zvekuonana zvagadzirira. Wava kukwanisa kuongorora EHR yemurwere.",
          readyBodyPatient:
            "Zvinyorwa zvekuonana zvagadzirira. Wava kukwanisa kuona kuvandudzwa kweEHR yako.",
          updatingBodyDoctor:
            "Zvinyorwa zvapera. Tava kuvandudza EHR yemurwere.",
          updatingBodyPatient:
            "Zvinyorwa zvapera. Tava kuvandudza EHR yako.",
          generatingBody:
            "Izvi zvinogona kutora chinguva. Unogona kuenderera mberi; tichavandudza mamiro aya.",
        },
        steps: {
          ended: "Kufona kwapera",
          notes: "Zvinyorwa zveVitalRep™ zviri kugadzirwa",
          ehr: "EHR yavandudzwa",
          prescription: "Resiphi yagadzirira",
        },
        actions: {
          viewPatientEhr: "Ona EHR yemurwere",
          viewEhrUpdate: "Ona kuvandudzwa kweEHR",
          backToAppointments: "Dzokera kukuonana",
          opening: "Kuvhura...",
          prescription: "Resiphi",
          viewPrescription: "Ona resiphi",
          goHome: "Enda kumba",
        },
        errors: {
          prescriptionUnavailable:
            "Resiphi haisati yawanikwa pakuonana uku.",
          openPrescription: "Hatina kukwanisa kuvhura resiphi parizvino.",
        },
      },
      past: {
        common: {
          doctorFallback: "Chiremba",
          unknownValue: "—",
          unknownDate: "—",
          monthFallback: "Mwedzi {year}",
        },
        list: {
          title: "Kuonana kwakapfuura",
          searchPlaceholder: "Tsvaga",
          clearSearch: "Bvisa",
          loading: "Kurodha...",
          empty: "Hapana kuonana kwakapfuura kwawanikwa.",
          reviewSubmitted: "Ongororo yatumirwa",
          reviewPending: "Ratidza chiyero chako pachiremba nezvakaitika",
          tabs: {
            upcoming: "Zviri kuuya ({count})",
            past: "Zvakapfuura ({count})",
            pending: "Zvakamirira mvumo ({count})",
          },
        },
        details: {
          title: "Kuonana kwakapfuura",
          loading: "Kurodha kuonana kwakapfuura...",
          notFound: "Kuonana kwakapfuura hakuwanikwi.",
          descriptionFallbackMissed:
            "Kuonana uku kwashaikwa. Hapana kuongorora kana zvinyorwa zvakawedzerwa.",
          descriptionFallback:
            "Hapana kuongorora kana zvinyorwa zvakanyorwa pakuonana uku.",
          videoConsultation: "Kuonana kuri kure",
          locationFallback: "Nzvimbo yekuonana",
          paymentFallback: "Makadhi edhibhiti kana ekiredhiti",
          paymentPending: "Mubhadharo wakamirira",
          bookAgain: "Bhuka zvakare",
          mode: {
            inPerson: "Pachezvapo",
            virtual: "Kuri kure",
          },
          sections: {
            time: "Nguva yekuonana",
            status: "Mamiro",
            description: "Tsananguro yekuonana",
            payment: "Nzira yekubhadhara",
          },
          status: {
            completed: "Zvapera",
            missed: "Zvashaikwa",
            rescheduled: "Zvakarongwa patsva",
            declined: "Zvakarambwa",
            cancelled: "Zvadzimwa",
            confirmed: "Zvabvumidzwa",
            scheduled: "Zvakarongwa",
            pending: "Zvakamirira",
            past: "Zvakapfuura",
            completedHelp: "Kuonana uku kwakapedzwa.",
            missedHelp:
              "Kuonana uku kwashaikwa. Hwindo rekuonana kuri kure rakavhara vasati vatanga kushanya.",
            rescheduledHelp:
              "Slot iri rekuonana rakachinjirwa kune imwe nguva itsva yakabvumidzwa.",
            declinedHelp:
              "Kuonana uku kwakarambwa kusati kwaitika kushanya.",
            cancelledHelp:
              "Kuonana uku kwakadzimwa kusati kwaitika kushanya.",
            pendingCloseoutHelp:
              "Kuonana uku kwatopfuura asi hakusati kwaratidzwa sekwapera.",
            rescheduleHistory:
              "Pakutanga kwakabhukirwa {oldDate}. Kwakazochinjirwa ku {newDate}.",
          },
          errors: {
            load: "Hatina kukwanisa kurodha kuonana kwakapfuura.",
          },
        },
        review: {
          section: "Ongororo yechiremba",
          title: "Ratidza chiyero chako pachiremba nezvakaitika",
          subtitle:
            "Mhinduro yako inobatsira vamwe varwere kuita sarudzo dzine ruzivo mushure mekuonana kwapera.",
          submitted: "Ongororo yatumirwa",
          lastUpdated: "Yakapedzisira kuvandudzwa {date}",
          loading: "Kurodha ongororo yako...",
          doctorPrompt: "Ungapa chiremba wako chiyero chipi?",
          doctorHint: "Funga nezvekujeka, hanya, nehunyanzvi.",
          experiencePrompt: "Ungapa ruzivo rwako rwese chiyero chipi?",
          experienceHint:
            "Funga nezvekubhuka, nguva, uye kuti kushanya kwakafamba sei zvakanaka.",
          textPrompt: "Tiudze zvakawanda nezveruzivo rwako",
          textPlaceholder:
            "Govera chakanyanya kumira pachena pakuonana. Zvinogona kuva zvipfupi.",
          textHelper: "{count}/1200 mavara",
          savedHint:
            "Ongororo yako yachengetwa. Chinja chiyero kana tsananguro kana uchida kuigadzirisa.",
          reset: "Dzorerazve",
          updateCta: "Vandudza ongororo",
          savedCta: "Ongororo yachengetwa",
          submitCta: "Tumira ongororo",
          updated: "Ongororo yako yavandudzwa.",
          saved: "Ongororo yako yatumirwa.",
          unavailableMissed:
            "Ongororo yechiremba haiwanikwi pakuonana kwashaikwa.",
          unavailableRescheduled:
            "Ongororo yechiremba haiwanikwi pa slot yakachinjirwa patsva.",
          unavailableCancelled:
            "Ongororo yechiremba haiwanikwi pakuonana kwakadzimwa.",
          unavailablePendingCloseout:
            "Ongororo yechiremba inowanikwa chete kana kuonana kwaratidzwa sekwapera.",
          errors: {
            load:
              "Hatina kukwanisa kurodha ongororo yako yechiremba pakuonana uku.",
            save: "Hatina kukwanisa kuchengetedza ongororo yako parizvino.",
          },
        },
      },
      checkout: {
        title: "Kubhadhara kwakachengeteka",
        notFound:
          "Mashoko ekubhuka haawanikwi. Dzokera kumashure utange zvakare.",
        steps: {
          appointment: "Kuonana",
          payAndBook: "Bhadhara ubhuke",
          paymentOptions: "Sarudzo dzekubhadhara",
          sponsorPayment: "Mubhadharo wemutsigiri",
        },
        paymentDetails: "Mashoko ekubhadhara",
        cardTitle: "Kubhadhara nekadhi",
        cardIntro:
          "Isa mashoko ekadhi kuti usimbise kuonana kwakachengeteka. Slot yawasarudza inoramba iripo paunenge uchipedza kubhadhara kana kushandura kumubhadharo wemutsigiri.",
        savedCards: "Makadhi akachengetwa",
        loadingSavedCards: "Kurodha makadhi ako akachengetwa...",
        savedCardLabel: "{brand} inopera ne {last4} · inopera {month}/{year}",
        cardBrandFallback: "Kadhi",
        useSavedCardHint:
          "Shandisa kadhi iri rakachengetwa pasina kunyora nhamba zvakare.",
        useDifferentCard: "Shandisa rimwe kadhi",
        loadingCardForm: "Kurodha fomu rekadhi rakachengeteka...",
        rememberCard:
          "Rangarira mashoko ekadhi rechikwereti nguva inotevera",
        processing: "Kugadzirisa...",
        payAndBook: "Bhadhara ubhuke",
        chooseDifferentMethod: "Sarudza imwe nzira yekubhadhara",
        refresh: "Rodhazve mashoko ekubhadhara",
        summary: "Pfupiso",
        summaryHelp:
          "Chengeta slot yawasarudza iripo paunenge uchisimbisa mubhadharo.",
        total: "Mari yose",
        fields: {
          doctor: "Chiremba",
          service: "Sevhisi",
          appointment: "Kuonana",
          paymentMethod: "Nzira yekubhadhara",
        },
        doctorFallback: "Chiremba weAfriMed",
        chooseSlot: "Sarudza slot",
        sponsorOption: "Sarudzo yemutsigiri",
        sponsorTitle: "Shandisa link yakachengeteka yemutsigiri",
        sponsorHelp:
          "Mhuri, vachengeti, kana vatsigiri vari diaspora vanogona kubhadhara kushanya pasina kuti murwere atange kubhuka patsva.",
        prepareSponsorPayment: "Gadzirira mubhadharo wemutsigiri",
        selectedAppointment: "Kuonana kwakasarudzwa",
        hideDetails: "Vanza zvakadzama",
        viewDetails: "Ona zvakadzama",
        sponsorFinalising:
          "Tiri kupedzisa mubhadharo wemutsigiri nekudzorera kuonana...",
        retrySponsorSync: "Edza zvakare kuwiriranisa mubhadharo wemutsigiri",
        methodNotSupported:
          "Nzira iyi haisati yaunganidza mubhadharo mukati meAfriMed, saka kuonana hakusimbiswi pano. Sarudza kubhadhara kwakachengeteka kwekadhi kana kugadzirira mubhadharo wemutsigiri usingarasikirwi ne slot yakabhukwa.",
        preparing: "Tiri kugadzirira kubhadhara kwakachengeteka kwekadhi...",
        doctorTerms: {
          title: "Mitemo yechiremba / nzvimbo",
          available: "Mitemo yakasiyana yechiremba iripo",
          none: "Hapana mitemo yakasiyana yechiremba yaiswa",
          availableHelp:
            "Mitemo iyi ndeyechiremba kana nzvimbo uye yakasiyana nemitemo yepuratifomu yeAfriMed.",
          noneHelp:
            "Mitemo yepuratifomu yeAfriMed ichiri kushanda. Chiremba uyu haasati aisa rimwe gwaro remitemo renzvimbo.",
          updated: "Yakavandudzwa {date}",
          ready: "Yakagadzirira kuongororwa",
          view: "Ona T&Cs echiremba",
        },
        notifications: {
          newAppointmentSecured:
            "{name} akumbira kuonana, pa {datetime}. Mubhadharo watosimbiswa.",
        },
        errors: {
          intentMissing:
            "Stripe haina kuwana payment intent iyi yekuchouta mubrowser rino. Rodhazve mashoko ekubhadhara woedza zvakare.",
          doctorReconnect:
            "Kugamuchira mari kwechiremba kunofanira kubatanidzwa zvakare kubhadhara nekadhi kusati kwaenderera. Kumbira chiremba abatanidzezve Stripe kubva pamapayout preferences, wobva warodhazve mashoko ekubhadhara.",
          cardFormLoading:
            "Fomu rekadhi rakachengeteka richiri kurodha. Mirira zvishoma woedza zvakare.",
          cardFormSlow:
            "Fomu rekadhi rakachengeteka riri kutora nguva kupfuura zvaitarisirwa. Rodhazve mashoko ekubhadhara woedza zvakare.",
          prepare:
            "Kubhadhara kwakachengeteka hakuna kukwanisa kugadzirirwa zvakanaka. Rodhazve mashoko ekubhadhara woedza zvakare.",
          processing:
            "Mubhadharo wako uchiri kugadziriswa. Mirira zvishoma woedza zvakare.",
          finalize:
            "Mubhadharo unogona kunge wapfuura, asi hatina kukwanisa kupedzisa kubhuka. Bata rutsigiro rweAfriMed.",
          intentMismatch:
            "Stripe haina kuwana payment intent iyi yekuchouta mufomu rekadhi riri kushanda. Rodhazve mashoko ekubhadhara woedza zvakare.",
          intentMismatchWithAccount:
            "Stripe haina kuwana payment intent iyi yekuchouta mufomu rekadhi riri kushanda. AfriMed yakaigadzira muplatform account {account}. Rodhazve mashoko ekubhadhara woedza zvakare.",
          bookingIncomplete: "Mashoko ekubhuka haana kukwana.",
          bookingIncompleteRetry:
            "Mashoko ekubhuka haana kukwana. Rodhazve mashoko ekubhadhara woedza zvakare.",
          stripeNotConfigured:
            "Stripe checkout haisati yagadzirirwa munzvimbo iyi.",
          stripeLoad:
            "Stripe checkout haina kukwanisa kurodha parizvino.",
          sponsorRestore:
            "Mubhadharo wemutsigiri wapera, asi kuonana hakuna kukwanisa kudzorerwa.",
          sponsorSync:
            "Mubhadharo wemutsigiri wapera, asi AfriMed haina kukwanisa kudzorera kuonana parizvino.",
          missingAmount:
            "Mari yekuonana haipo paku bhuka uku.",
          cardFormRefresh:
            "Fomu rekadhi rakachengeteka richiri kurodha. Rodhazve mashoko ekubhadhara.",
          stagingKey:
            "Staging checkout iri kunongedzera kune Stripe secret yepamhenyu. Chinja staging STRIPE_SECRET_KEY kuita sk_test key wobva warodhazve mashoko ekubhadhara.",
          prepareIntent:
            "Stripe haina kukwanisa kugadzirira payment intent iyi.",
          paymentFailed: "Mubhadharo haukwanisi kupedzwa.",
          paymentReferenceMissing:
            "Mubhadharo wapera asi reference yemubhadharo yashaikwa.",
          invalidTime:
            "Mubhadharo wabudirira asi nguva yekuonana haisi iyo.",
          createAppointment:
            "Mubhadharo wabudirira asi kuonana hakuna kukwanisa kugadzirwa.",
        },
      },
      index: {
        title: "Kuonana",
        more: "Zvimwe",
        pastAppointments: "Kuonana kwakapfuura",
        tabs: {
          upcoming: "Zviri kuuya",
          past: "Zvakapfuura",
          pending: "Zvakamirira mvumo",
        },
        upcomingEmpty: {
          title: "Hapana kuonana kuri kuuya",
          body: "Hausati uine kuonana kwakarongwa.\nBhuka kuonana kwako kunotevera kuti kuonekwe pano.",
        },
        pendingEmpty: {
          title: "Hapana kuonana kwakamirira",
          body: "Parizvino hapana chakamirira mvumo.",
        },
        empty: {
          title: "Hapana kuonana kwekuratidza",
          body: "Rondedzero yako haina chinhu parizvino. Dzokazve gare gare.",
        },
        toast: {
          deleted: "Kuonana kwako kwabviswa",
        },
        bookNow: "Bhuka zvino",
        nearby: "Utano huri pedyo newe",
      },
    },
    home: {
      patient: {
        welcome: "Mauya, {firstName}!",
        actions: {
          openProfile: "Vhura mbiri",
          notifications: "Zviziviso",
          viewAll: "Ona zvese",
        },
        appointments: {
          title: "Kuonana kwako",
          open: "Vhura kuonana",
          tabs: {
            upcoming: "Zviri kuuya",
            past: "Zvakapfuura",
            pending: "Zvakamirira mvumo",
          },
          empty: {
            title: "Hausati uine kuonana",
            body: "Bhuka kuonana kutsva kuti kutarisirwa kwako kutange pano.",
          },
          bookAction: "Bhuka kuonana",
          error: "Hatina kukwanisa kurodha kuonana kwako.",
          status: {
            pending: "Zvakamirira mvumo",
            confirmed: "Zvabvumidzwa",
            inProgress: "Zviri kufamba",
            completed: "Zvapera",
            cancelled: "Zvadzimwa",
          },
          defaults: {
            doctor: "Chiremba",
            appointment: "Kuonana",
          },
        },
        serviceCategories: {
          title: "Mapoka emasevhisi",
          doctors: "Vanachiremba",
          hospitals: "Zvipatara",
          pharmacies: "MaPharmacy",
          ambulances: "MaAmbulance",
        },
        popularServices: {
          title: "Masevhisi anodiwa",
          action: "Masevhisi ese",
          labels: {
            generalPractitioner: "Chiremba mukuru",
            telemedicine: "Kuonana kuri kure",
            childHealthPediatrics: "Utano hwevana",
            maternalCareAntenatal: "Kuchengeta pamuviri",
            dermatologist: "Chiremba weganda",
            hivCare: "Rubatsiro rweHIV",
            hypertension: "BP yepamusoro",
            tbScreening: "Kuongorora TB",
            diabetes: "Chirwere cheshuga",
            malaria: "Marariya",
            stiScreening: "Kuongorora STI",
            dental: "Mazino",
            mentalHealth: "Utano hwepfungwa",
          },
        },
        nearby: {
          title: "Utano huri pedyo newe",
          action: "Tsvaga utano huri pedyo neni",
          selectedFacility: "Nzvimbo yakasarudzwa",
          seeDoctors: "Ona vanachiremba panzvimbo iyi",
          loading: "Kurodha nzvimbo dziri pedyo...",
          findDoctors: "Tsvaga vanachiremba",
          searchPlaceholder: "Tsvaga nzvimbo",
          clearSearch: "Bvisa kutsvaga",
          showingNear: "Kuronga nzvimbo dziri pedyo ne",
          clearSelectedFacility: "Bvisa nzvimbo yasarudzwa",
          defaultFacilityName: "Nzvimbo yezveutano",
        },
        verifyIdentity: {
          title: "Simbisa kuzivikanwa kwako",
          body: "Pedzisa kusimbisa kuti uwane kuwana kwakachengeteka kweAfriMed™.",
          pending: "Zvakamirira ongororo",
          action: "Simbisa zvino",
        },
        fallbackName: "Mushandisi weAfriMed",
      },
    },
    navigation: {
      tabs: {
        dashboard: "Kumba",
        appointments: "Kuonana",
        messages: "Meseji",
        profile: "Ini",
      },
      menu: {
        viewProfile: "Tarisa mbiri",
        dashboard: "Kumba",
        ePrescriptions: "E-Maresiphi",
        settings: "Zvirongwa",
        signOut: "Buda",
        logOut: "Buda",
        loggingOut: "Kuchibuda...",
        logoutConfirmTitle: "Une chokwadi here kuti unoda kubuda?",
        logoutConfirmBody: "Uchafanira kupinda zvakare kuti uenderere mberi.",
      },
      search: "Tsvaga",
      globalSearch: "Tsvaga muAfriMed",
      me: "Ini",
    },
    fields: {
      country: {
        label: "Nyika",
        requiredLabel: "Nyika*",
        placeholder: "Sarudza nyika",
        searchPlaceholder: "Tsvaga nyika",
        selected: "Yasarudzwa",
        chooseMarket: "Sarudza musika wako",
        current: "Parizvino",
        noMatchesLong: "Hapana misika inoenderana nekutsvaga uku.",
        noMatchesShort: "Hapana zvawana",
      },
    },
    doctors: {
      appointmentDetails: {
        attachments: {
          untitled: "Gwaro",
          byDoctor: "NaChiremba",
          addedSingle: "Gwaro rawedzerwa.",
          addedMultiple: "Magwaro {count} awedzerwa.",
        },
      },
      diagnosisNotes: {
        title: "Kuongorora nezvinyorwa",
        subtitle: "Wedzera kuongorora kwekuonana uye zvinyorwa zvinotsigira.",
        fields: {
          diagnosis: "Kuongorora",
          diagnosisPlaceholder: "Muenzaniso: Chirwere cheshuga",
          notes: "Zvinyorwa",
        },
      },
    },
    messages: {
      composeModal: {
        title: "Hurukuro itsva",
        addParticipants: "Wedzera vanhu",
        close: "Vhara",
        types: {
          doctorPatient: "Chiremba-Murwere",
          doctorDoctor: "Chiremba-Chiremba",
          group: "Boka rehurukuro",
        },
        typeDescriptions: {
          doctorPatient: "Taura zvakananga nemurwere kana chiremba.",
          doctorDoctor: "Hurukuro iri pakati pevashandi vezveutano.",
          group: "Wedzera vanhu vakawanda muhurukuro imwe.",
        },
        conversationTitle: "Musoro wehurukuro",
        conversationTitlePlaceholder: "Wedzera musoro kana zvichidiwa",
        autoGenerateHint: "Kana usingaise musoro, AfriMed ichaugadzira.",
        searchUsers: "Tsvaga vashandisi",
        searchPlaceholder: "Tsvaga nemazita kana imeyili",
        selectedParticipants: "Vakatosarudzwa",
        searchResults: "Mhedzisiro yekutsvaga",
        unknownUserInitial: "M",
        unknownUser: "Mushandisi asingazivikanwi",
        noUsersFound: "Hapana vashandisi vawanikwa",
        startTyping: "Tanga kunyora kuti utsvage",
        back: "Dzokera",
        cancel: "Kanzura",
        nextAddParticipants: "Inotevera: wedzera vanhu",
        createConversation: "Gadzira hurukuro",
        chatWith: "Taura na {name}",
        groupChatMembers: "Boka rehurukuro ({count} nhengo)",
      },
      inbox: {
        search: "Tsvaga",
        searchRecipients: "Tsvaga vanhu",
        clearSearch: "Bvisa kutsvaga",
        openFola: "Vhura Fola",
        newMessage: "Meseji itsva",
        composeTitle: "Tanga meseji itsva",
        loadingMessagesTitle: "Kurodha meseji",
        loadingMessagesBody: "Mirira tichikutorera hurukuro dzako dzichangobva kuitika.",
        loadingRecipientsTitle: "Kurodha vanogamuchira",
        loadingRecipientsBody: "Mirira tichitsvaga vanhu vaunogona kutumira meseji.",
        fallbackTitle: "Hurukuro",
        noRecipientsTitle: "Hapana vanhu varipo",
        noRecipientsMatch: "Hapana munhu awirirana nekutsvaga kwako",
        noRecipientsBody: "Edza rimwe zita, imeyili kana nhamba.",
        noConversationsMatch: "Hapana hurukuro dzawana",
        noMessagesPreview: "Hapana meseji parizvino.",
        new: "Itsva",
        opening: "Kuvhura...",
      },
      conversation: {
        now: "Zvino",
        deletedForMe: "Wabvisa meseji iyi kwauri",
        deletedBySender: "Mutumiri abvisa meseji iyi",
        deleteDialog: {
          close: "Vhara bhokisi rekubvisa",
          body:
            "Bvisa meseji iyi kwauri chete kana kubvisa kumativi ese. Izvi hazvidzorerwe.",
          deleting: "Kubvisa...",
        },
        toast: {
          dismiss: "Zvakanaka",
          undo: "Dzosa",
          deletedForMe: "Meseji yabviswa kwauri",
          deletedForBoth: "Meseji yabviswa kune vese",
          deleteError: "Hatina kukwanisa kubvisa meseji iyi parizvino.",
        },
        composer: {
          all: "Zvese",
          attach: "Batanidza mufananidzo",
          selectedAttachmentAlt: "Mufananidzo wasarudzwa",
        },
        topBar: {
          back: "Dzokera kumeseji",
          openFola: "Vhura Fola",
          compose: "Nyora meseji",
        },
      },
    },
    auth: {
      callback: {
        failed: "Kupinda kwaramba. Edza zvakare.",
        linkVerificationFailed:
          "Kodhi yekupinda haina kusimbiswa. Kumbira imwe link.",
        requestAnotherLink: "Kumbira imwe link yekupinda",
      },
    },
    notifications: {
      title: "Zviziviso",
      filters: {
        all: "Zvese",
        urgent: "Zvekukurumidza",
        unread: "Zvisati zvaverengwa",
        appointments: "Kuonana",
      },
      common: {
        title: "Zviziviso",
        loading: "Kurodha zviziviso...",
        emptyTitle: "Hapana zviziviso",
        emptyBody: "Parizvino hapana zviziviso.",
      },
      loadingSingle: "Kurodha chiziviso...",
      singleTitle: "Chiziviso",
      headers: {
        newAppointment: "Kuonana kutsva",
        appointmentUpdate: "Kuonana kwachinjwa",
        appointmentConfirmed: "Kuonana kwabvumidzwa",
        appointmentDeclined: "Kuonana kwarambwa",
        upcomingAppointment: "Kuonana kuri kuuya",
        newTimeRequestApproved: "Nguva itsva yabvumidzwa",
        newTimeRequestDeclined: "Nguva itsva yarambwa",
      },
      messages: {
        bookingApprovedForDate:
          "{name} akumbira kuonana kwa {date}. Zvabvumidzwa.",
        bookingApprovedGeneric: "{name} akumbira kuonana. Zvabvumidzwa.",
        yourAppointmentAcceptedForDate:
          "Kuonana kwako kwa {date} kwabvumidzwa.",
        yourAppointmentAcceptedGeneric:
          "Chikumbiro chako chekuonana chabvumidzwa.",
        bookingDeclinedForDate:
          "{name} akumbira kuonana kwa {date}. Zvarambwa.",
        bookingDeclinedGeneric: "{name} akumbira kuonana. Zvarambwa.",
        yourAppointmentDeclinedForDate:
          "Kuonana kwako kwa {date} kwarambwa.",
        yourAppointmentDeclinedGeneric:
          "Chikumbiro chako chekuonana charambwa.",
        upcomingAppointmentForDate: "Kuonana kwako kuri pa {date}.",
        upcomingAppointmentGeneric: "Kuonana kwako kwakarongwa.",
        rescheduleApprovedGeneric: "Nguva itsva yekuonana yabvumidzwa.",
        yourRescheduleApprovedGeneric:
          "Chikumbiro chako chenguva itsva chabvumidzwa.",
        rescheduleDeclinedGeneric: "Nguva itsva yekuonana yarambwa.",
        yourRescheduleDeclinedGeneric:
          "Chikumbiro chako chenguva itsva charambwa.",
      },
      errors: {
        notFound: "Chiziviso hachina kuwanikwa.",
        load: "Hatina kukwanisa kurodha chiziviso.",
      },
    },
    patient: {
      editIntro: {
        title: "Gadzirisa sumo",
        sections: {
          personalInfo: "Mashoko ako",
          contactData: "Mashoko ekubata",
          residenceAddress: "Kero yekugara",
          medicalInfo: "Mashoko ezveutano",
        },
        verification: {
          verified: "Yasimbiswa neID yepamutemo yawapa.",
        },
        actions: {
          addLanguage: "Wedzera mutauro",
        },
        placeholders: {
          fullName: "Wedzera zita rako rakazara",
          dob: "Wedzera zuva rako rekuzvarwa",
          phone: "Wedzera nhamba yako yefoni",
          email: "Wedzera kero yako yeimeyili",
          idPassport: "Wedzera ID yako kana pasipoti",
          street: "Wedzera kero yemugwagwa wako",
          postalCode: "Wedzera kodhi yako yepositi",
          country: "Wedzera nyika yako",
          bloodType: "Wedzera rudzi rweropa rwako",
          allergies: "Wedzera ma-allergy ako",
          conditions: "Wedzera zvirwere zvako",
          organDonor: "Tiudze kana uri mupi wenhengo",
          heightWeight: "Wedzera kureba nehuremu hwako",
        },
        languages: {
          empty:
            "Wedzera mitauro yaunotaura kuitira kuti vanachiremba vasarudze inokukodzera.",
        },
        photoSheet: {
          title: "Gadzirisa mufananidzo wako weprofile",
          uploadImage: "Isa mufananidzo",
          takePhoto: "Tora mufananidzo",
        },
        errors: {
          loadProfile: "Hatina kukwanisa kurodha profile yako.",
          fileType: "Ndapota isa mufananidzo weJPG, PNG kana WebP.",
          fileSize: "Saizi huru yefaira i5MB.",
          signInToUpload: "Unofanira kupinda kuti uise mufananidzo weprofile.",
          uploadPhoto: "Hatina kukwanisa kuisa mufananidzo wako.",
          signInToSave: "Unofanira kupinda kuti uchengete shanduko.",
          savePhoto:
            "Hatina kukwanisa kuchengetedza mufananidzo wako weprofile. Edza zvakare gare gare.",
        },
      },
      profile: {
        mock: {
          specialtyDermatologist: "Chiremba weganda",
          yearsExperience: "makore 14",
          serviceLabel: "Basa",
          serviceNameFirstConsultancy: "Kuonana kwekutanga",
          languagesEnglishTswana: "Chirungu, Setswana",
          distanceFromCityCentre: "5.2 km kubva pakati peguta",
          chronicConditionsHypertensionDiabetes:
            "BP yepamusoro, chirwere cheshuga",
        },
        fullName: "Zita rizere",
        dateOfBirth: "Zuva rekuzvarwa",
        verified: "Yasimbiswa",
        pendingVerification: "Kusimbisa kuchiri kuitika",
        notProvided: "Hazvina kupihwa",
        notConfigured: "Hazvisati zvagadziriswa",
        unknown: "Hazvizivikanwi",
        noneRecorded: "Hapana zvakanyorwa",
        notRecorded: "Hazvina kunyorwa",
        dependantFallback: "Anovimba naye",
        dependantsCount: "{count} anovimba naye",
        insuranceSupport:
          "Wedzera mupi wako nenhamba dzekuve nhengo kuti zvikumbiro nebhadharo zvimhanye.",
        notificationsSupport:
          "Ronga kuti unogamuchira sei zvitsva zvekuonana, maresiphi nenhau.",
        medicalHistoryLocked:
          "Nhoroondo yezveutano yakakiyiwa kusvika murwere abvumidza kupinda kuEHR.",
        sections: {
          appointments: "Nhoroondo yekuonana",
          contact: "Mashoko ekubata",
          insurance: "Inishuwarenzi yehutano",
          dependants: "Vanovimba newe",
          address: "Kero yekugara",
          medicalHistory: "Nhoroondo yezveutano",
          photos: "Mifananidzo yemurwere",
          languages: "Mitauro",
          notifications: "Zviziviso",
        },
        labels: {
          phone: "Foni",
          email: "Imeyili",
          nationalId: "ID yenyika / Pasipoti",
          street: "Mugwagwa",
          postalCode: "Kodhi yepositi",
          country: "Nyika",
          bloodType: "Rudzi rweropa",
          allergies: "Ma-allergy",
          chronicConditions: "Zvirwere zvisingaperi",
          organDonor: "Mupi wenhengo",
          heightWeight: "Kureba nehuremu",
        },
        placeholders: {
          phone: "Nhamba yako yefoni",
          email: "iwe@imeyili.com",
          nationalId: "ID yako / Pasipoti",
          street: "Mugwagwa wako, Guta",
          postalCode: "00000",
          country: "Nyika",
          languages: "Mitauro yaunoda",
        },
        actions: {
          addEdit: "Wedzera / Gadzirisa",
          addDependant: "Wedzera anovimba newe",
          addFavorite: "Wedzera kuzvaunofarira",
          removeFavorite: "Bvisa kuzvaunofarira",
        },
        history: {
          visit: "Kuonana",
          visitAt: "Kuonana · {day}, {time}",
        },
        consultType: {
          private: "Kuonana kwega",
          moh: "Kuonana kweMoH",
          evisit: "E-Kuonana",
          default: "Kuonana",
        },
        fallback: {
          addressNotProvided: "Kero haina kupihwa",
          consultationLocation: "Nzvimbo yekuonana",
          generalPractice: "Chiremba mukuru",
          notSpecified: "Hazvina kutsanangurwa",
        },
        notifications: {
          title: "Nhau nezvinobuda",
          off: "Yakadzimwa",
          emailPush: "Imeyili nePush",
          emailPushLocalNews: "Imeyili nePush · Nhau nemitemo yemuno",
        },
        empty: {
          appointments: "Hausati uine kuonana.",
          dependants: "Hapana vanovimba newe vakanyorwa",
          photos: "Hakuna mifananidzo parizvino.",
        },
      },
    },
    doctor: {
      profile: {
        fallback: {
          doctor: "Chiremba",
          specialtyMissing: "Hunyanzvi hahusati hwaiswa",
          about:
            "Chiremba ane ruzivo rwakadzama anoshanda nehutano hwemurwere nerudo nekuchenjerera.",
          notSet: "Hazvisati zvaiswa",
          facility: "Nzvimbo",
          service: "Basa",
        },
        stats: {
          patients: "Varwere",
          experience: "Ruzivo",
          rating: "Chiyero",
        },
        values: {
          patientAge: {
            childrenAdults: "Vana nevanhu vakuru",
            adultsOnly: "Vanhu vakuru chete",
            childrenOnly: "Vana chete",
          },
          availability: {
            everyDay: "Mazuva ese",
            everyWeek: "Vhiki rega rega",
          },
          insurance: {
            accepting: "Kugamuchira",
          },
          paymentTerms: {
            cards: "Makadhi eDebit / Credit",
            creditCards: "Makadhi eCredit",
            debitCards: "Makadhi eDebit",
            bankTransfers: "Transfers dzebhangi",
            cashOnService: "Mari panguva yebasa",
          },
          days: {
            monday: "Muvhuro",
            tuesday: "Chipiri",
            wednesday: "Chitatu",
            thursday: "China",
            friday: "Chishanu",
            saturday: "Mugovera",
            sunday: "Svondo",
            mon: "Mvh",
            tue: "Chp",
            wed: "Cht",
            thu: "Chn",
            fri: "Chsh",
            sat: "Mgv",
            sun: "Svd",
          },
        },
        sections: {
          aboutYou: "Nezvako",
          about: "Nezvake",
          yourFocus: "Zvaunotarisisa",
          focus: "Zvaanotarisisa",
          consultancies: "Makiriniki",
          consultancy: "Kirinki",
          evisit: "E-Kuonana",
          gallery: "Garari",
          yourServices: "Masevhisi ako",
          services: "Masevhisi",
        },
        focus: {
          addFromSettings: "Wedzera nzvimbo dzako dzekutarisa kubva muzvirongwa.",
          empty: "Hapana nzvimbo dzekutarisa dzakawedzerwa parizvino.",
        },
        consultancies: {
          empty: "Hapana makiriniki parizvino.",
          addLocation: "Wedzera nzvimbo yekiriniki",
          options: "Sarudzo dzekiriniki",
          edit: "Gadzirisa kiriniki",
          remove: "Bvisa kiriniki",
          removeTitle: "Bvisa kiriniki iyi?",
          removeBody: "Kiriniki iyi ichabviswa paprofile yako.",
          removing: "Kuchibvisa...",
          labels: {
            patientsAge: "Zera revarwere",
            availability: "Kuwanikwa",
            insurancePolicy: "Mutemo weinishuwarenzi",
            paymentTerms: "Mitemo yekubhadhara",
          },
        },
        gallery: {
          addFromSettings: "Wedzera mifananidzo kubva kuzvirongwa zveGarari",
        },
        services: {
          empty: "Wedzera masevhisi nemitengo kubva muzvirongwa kuti varwere vaone zvaunopa.",
        },
        cover: {
          editTitle: "Gadzirisa mufananidzo wepamusoro",
          uploadImage: "Isa mufananidzo",
          takePhoto: "Tora mufananidzo",
          errors: {
            fileType: "Ndapota isa mufananidzo weJPG, PNG kana WebP.",
            fileSize: "Saizi huru yefaira remufananidzo wepamusoro i3MB.",
            fileRead: "Hatina kukwanisa kuverenga mufananidzo wasarudza.",
            save: "Hatina kukwanisa kuchengetedza mufananidzo wako wepamusoro. Edza zvakare.",
          },
        },
      },
      editIntro: {
        title: "Gadzirisa sumo",
        sections: {
          personalInfo: "Mashoko ako",
          specialty: "Hunyanzvi",
          about: "Nezvako",
          formation: "Dzidzo",
          languages: "Mitauro",
        },
        labels: {
          name: "Zita",
          dob: "Zuva rekuzvarwa",
          registrationNumber: "Nhamba yekunyoresa",
        },
        placeholders: {
          fullName: "Wedzera zita rako rakazara",
          dob: "Wedzera zuva rako rekuzvarwa",
          specialty: "Wedzera hunyanzvi hwako",
          registerNumber: "Wedzera nhamba yako yekunyoresa",
          about: "Udza varwere zvishoma nezveruzivo rwako nemaitiro ako.",
        },
        verification: {
          verified: "Yasimbiswa neID yepamutemo yawapa.",
          pending: "Kusimbisa kuchiri kuitika.",
        },
        signature: {
          title: "Siginicha",
          subtitle: "Wedzera / Gadzirisa siginicha yako",
          preview: "Muenzaniso wesiginicha",
          helper:
            "Siginicha iyi inochengetwa zvakachengeteka uye inoiswa pamushonga wese unopihwa.",
        },
        formation: {
          empty: "Wedzera dzidzo nekudzidziswa kwako kuti varwere vaone zvakabva kwauri.",
        },
        actions: {
          addFormation: "Wedzera dzidzo",
          addLanguage: "Wedzera mutauro",
        },
        languages: {
          editAria: "Gadzirisa {language}",
          empty: "Wedzera mitauro yaunotaura kuti varwere vasarudze zvinoenderana navo.",
        },
        photoSheet: {
          title: "Gadzirisa mufananidzo wako weprofile",
          uploadImage: "Isa mufananidzo",
          takePhoto: "Tora mufananidzo",
        },
        fallback: {
          education: "Dzidzo",
        },
        errors: {
          save: "Hatina kukwanisa kuchengetedza sumo yako. Edza zvakare.",
          photoSave: "Hatina kukwanisa kuchengetedza mufananidzo wako weprofile. Edza zvakare.",
        },
      },
    },
    settings: {
      home: {
        title: "Zvirongwa",
        profileSupport: "Gadzirisa mapeji, profile nezvimwe zvakawanda...",
        loginSecurity: "Kupinda nekuchengeteka",
        language: "Mutauro",
        availability: "Kuwanikwa",
        notifications: "Zviziviso",
        preferences: "Zvaunofarira",
        legal: "Zvemutemo",
        faq: "Mibvunzo inowanzo bvunzwa",
        userFallback: "Mushandisi",
        logoutDialog: {
          title: "Une chokwadi here kuti unoda kubuda?",
          body: "Uchafanira kupinda zvakare kuti urambe uchishandisa AfriMed.",
        },
      },
      welcome: {
        fallbackName: "shamwari",
        title: "Mauya {name} kuzvirongwa zvako",
        doctorBody:
          "Zvirongwa ndizvo zvaunoshandisa kugadzirisa mufananidzo wako, maawa ekuonana, kuchengetedzwa, kubhadhara nezvimwe.",
        patientBody:
          "Zvirongwa ndizvo zvaunoshandisa kugadzirisa mufananidzo wako, kuchengetedzwa, kubhadhara, zviziviso nezvimwe.",
      },
      subflows: {
        discardTitle: "Rasa shanduko here?",
        languages: {
          title: "Wedzera mitauro",
          searchPlaceholder: "Tsvaga kana wedzera",
          clearSearch: "Bvisa kutsvaga",
          addAria: "Wedzera mutauro",
          helperPatient: "Sarudza mitauro yose yaunotaura.",
          helperDoctor: "Wedzera mitauro yose yaunotaura.",
          catalogHidden:
            "Nyora mutauro wobva wadzvanya + kuti uwedzere. Mazita asati asimbiswa haasi kuratidzwa pano mumutauro uyu.",
          noResults: "Hapana mutauro waenderana nekutsvaga kwako.",
          discardBody:
            "Une shanduko dzemutauro dzisina kuchengetwa. Une chokwadi chekuda kuvhara usina kuchengetedza here?",
          saveError: "Hatina kukwanisa kuchengetedza shanduko dzako. Edza zvakare.",
        },
        bloodType: {
          title: "Rudzi rweropa",
          searchPlaceholder: "O+",
          clearSearch: "Bvisa kutsvaga",
          noResults: "Hapana zvawanikwa.",
          discardBody:
            "Une shanduko dzerudzi rweropa dzisina kuchengetwa. Une chokwadi chekuda kuvhara usina kuchengetedza here?",
        },
        allergies: {
          title: "Ma-allergy",
          searchPlaceholder: "Tsvaga kana wedzera",
          clearSearch: "Bvisa kutsvaga",
          addAria: "Wedzera allergy",
          catalogHidden:
            "Nyora allergy wobva wadzvanya + kuti uwedzere. Rondedzero isati yashandurwa haisi kuratidzwa pano mumutauro uyu.",
          noMatchesAdd: "Hapana zvakafanana. Tinya + kuti uwedzere “{query}”.",
          discardBody:
            "Une shanduko dzema-allergy dzisina kuchengetwa. Une chokwadi chekuda kuvhara usina kuchengetedza here?",
          saveError: "Hatina kukwanisa kuchengetedza shanduko dzako. Edza zvakare.",
        },
        conditions: {
          title: "Mamiro ezvehutano",
          searchPlaceholder: "Tsvaga kana wedzera",
          clearSearch: "Bvisa kutsvaga",
          addAria: "Wedzera mamiriro",
          catalogHidden:
            "Nyora mamiriro wobva wadzvanya + kuti uwedzere. Rondedzero isati yashandurwa haisi kuratidzwa pano mumutauro uyu.",
          noMatchesAdd: "Hapana zvakafanana. Tinya + kuti uwedzere “{query}”.",
          discardBody:
            "Une shanduko dzemamiriro ezvehutano dzisina kuchengetwa. Une chokwadi chekuda kuvhara usina kuchengetedza here?",
          saveError: "Hatina kukwanisa kuchengetedza shanduko dzako. Edza zvakare.",
        },
        medications: {
          title: "Mishonga",
          searchPlaceholder: "Tsvaga kana wedzera",
          clearSearch: "Bvisa kutsvaga",
          addAria: "Wedzera mushonga",
          loading: "Kurodha... imbomirai!",
          catalogHidden:
            "Nyora mushonga wobva wadzvanya + kuti uwedzere. Rondedzero isati yashandurwa haisi kuratidzwa pano mumutauro uyu.",
          noMatchesAdd: "Hapana zvakafanana. Tinya + kuti uwedzere “{query}”.",
          discardBody:
            "Une shanduko dzemishonga dzisina kuchengetwa. Une chokwadi chekuda kuvhara usina kuchengetedza here?",
          saveError: "Hatina kukwanisa kuchengetedza shanduko dzako. Edza zvakare.",
        },
        heightWeight: {
          title: "Kureba & huremu",
          subtitle:
            "Wedzera kureba nehuremu hwako hwazvino kuti profile yako yezveutano ibatsire pakutarisirwa.",
          heightLabel: "Kureba (cm)",
          weightLabel: "Huremu (kg)",
          heightPlaceholder: "175",
          weightPlaceholder: "72",
          clearHeight: "Bvisa kureba",
          clearWeight: "Bvisa huremu",
          saveError: "Hatina kukwanisa kuchengetedza kureba nehuremu. Edza zvakare.",
        },
        patientEhr: {
          title: "Rekodhi yangu yezveutano",
          subtitle:
            "Mishumo, maresiphi, makatadzirwa ekutumirwa nezvinyorwa zvekuonana — ndiwe unodzora anokwanisa kuzviona.",
          statusReady: "Kugadziridzwa kweEHR kwagadzirira",
          statusUpdating: "EHR iri kuvandudzwa…",
          statusPreparing: "Zvinyorwa zviri kugadzirirwa…",
          bodyReady:
            "Kuvandudzwa kwekuonana kwako kwagadzirira. Unogona kuona zvinyorwa zvekuonana nemarekodhi ane chekuita nazvo pano.",
          bodyUpdating:
            "VitalRep™ yapedza zvinyorwa uye iri kuvandudza EHR yako. Izvi zvinogona kutora chinguva.",
          bodyPreparing:
            "Kuonana kwako kuri kugadziriswa. Tichavandudza EHR yako munguva pfupi.",
          latestVisitTitle: "Kushanya kwazvino",
          appointmentReference: "Kuonana #{appointmentId}",
          latestVisitFallback:
            "Mamiro ekuonana kwako kwazvino neresiphi yakabudiswa zvinoonekwa pano kutanga.",
          prescriptionTitle: "Resiphi",
          prescriptionLoading: "Kurodha resiphi...",
          noPrescription: "Hakuna resiphi yabudiswa parizvino",
          prescriptionIssued: "Yabudiswa {date}",
          prescriptionHint:
            "Kana chiremba wako akabudisa mushonga, unogona kuuvhura kubva pano.",
          viewPrescription: "Vhura resiphi",
          backToAppointments: "Dzokera kukuonana",
          visitNotesTitle: "Zvinyorwa zvekuonana",
          visitNotesBody:
            "Parizvino haticharatidzi zvinyorwa zvizere zvekuonana nemishumo muEHR yemurwere. Parizvino skrini iyi inochengeta mamiro ekushanya kwako kwazvino neresiphi zviri pedyo.",
        },
      },
      loginSecurity: {
        password: {
          label: "Password",
          action: "Gadzira / Chinja",
          aria: "Chinja password",
        },
        passcode: {
          label: "Passcode",
          create: "Gadzira",
          createAfterPassword: "Gadzira mushure mepassword",
          aria: "Chinja passcode",
        },
        twoFactor: {
          label: "Two factor authentication",
          aria: "Two factor authentication",
        },
      },
      preferences: {
        title: "Zvaunofarira",
        language: {
          title: "Mutauro",
        },
        notifications: {
          title: "Zviziviso",
          subtitle: "Ronga chiyeuchidzo, push, email neSMS",
        },
        notificationInbox: {
          title: "Inbox yezviziviso",
          subtitle: "Ona zvitsva zvako zveAfriMed™ zvinobva pakutarisirwa",
        },
        sections: {
          app: "Chishandiso",
          account: "Akaunti",
        },
        appearance: {
          title: "Kutarisika",
          subtitle: "Shandisa theme yechishandiso, yakajeka kana yerima",
          selected: "{label} theme yasarudzwa",
          currentlyUsing: "Parizvino iri kushandisa AfriMed™ {theme}.",
          footer:
            "Shandisa marongerwo echishandiso kuti AfriMed ishande nemavara akajeka kana erima zvinoenderana nechishandiso chako.",
          options: {
            system: {
              label: "Shandisa marongerwo echishandiso",
              support:
                "Tevedza marongerwo echishandiso chako uchichengeta kutaridzika kweAfriMed™.",
            },
            light: {
              label: "Yakajeka",
              support: "Theme yeAfriMed™ yakajeka inoshandiswa muapp yose.",
            },
            dark: {
              label: "Yerima",
              support: "Theme yeAfriMed™ yerima yekuona nyore munzvimbo dzisina chiedza.",
            },
          },
        },
        news: {
          title: "Nhau nezvitsva",
          subtitle: "Imeyili nePush zvaunofarira",
        },
        accessibility: {
          title: "Kuwanikwa naFola",
          subtitle: "Sarudzo dzinobatsira nerutsigiro rwe-AI",
          heading: "Kuwanikwa",
          copy:
            "Ronga AfriMed™ kuti ive nyore kuverenga, yakadzikama kushandisa, uye nyore kufamba nayo.",
          folaAssistance: "Rubatsiro rweFola",
          folaAssistanceCopy:
            "Sarudza kuti Fola inokubatsira sei muAfriMed. Fola inogona kutsanangura kutarisirwa uye kukubatsira kugadzirira kuonana, asi haitongorapi kana kutsiva chiremba wako.",
          updated: "Kuwanikwa naFola zvagadziridzwa",
          rows: {
            summaries: {
              title: "Pfupisa manotsi ekuonana kwangu",
              support: "Tsananguro dziri nyore mushure mekuonana",
            },
            medicines: {
              title: "Tsanangura mishonga yangu",
              support: "Ndibatsire kunzwisisa maresiphi nemutauro uri nyore",
            },
            questions: {
              title: "Ndibatsire kugadzirira mibvunzo",
              support: "Ratidza mibvunzo inobatsira pamberi pekuonana",
            },
            largeText: {
              title: "Mavara nemifananidzo mikuru",
              support: "Wedzera kukura kwemavara akakosha uye nyore kubata",
            },
            reduceMotion: {
              title: "Deredza kufamba",
              support: "Deredza ma-animation nekufamba kweinterface",
            },
            highContrast: {
              title: "Maitiro ekusiyanisa kukuru",
              support: "Wedzera kuverengeka nekusiyanisa kwakasimba",
            },
          },
        },
        plans: {
          title: "Mapurani nekunyoresa",
          subtitle: "Purani yako yeAfriMed™ nezvakawedzerwa",
          loadError: "Hatina kukwanisa kurodha mamiriro ekubhadhara parizvino.",
          doctor: {
            subtitleLong:
              "Ronga nzira yekubhadhara yekutarisira kwakananga: komisheni yeAfriMed™, kugadzirira kubhadharwa, masevhisi, nezviratidzo zvinotsigira kubhuka kunobhadharwa.",
            businessModel: "Nzira yekubhadhara yekutarisira kwakananga",
            commissionTitle:
              "Vanachiremba vanoramba vakasununguka kupinda. AfriMed™ inotora 10% kana wabhadharwa.",
            commissionBody:
              "Iri harisi peji rekusarudza chirongwa chemwedzi. Varwere vanobhuka nekubhadhara masevhisi ako, AfriMed™ ichitarisira ruzivo rwemhando, uye komisheni inotorwa chete mushure mekuonana kwakabhadharwa kwabudirira.",
            servicesCta: "Masevhisi nemitengo",
            servicesReady: "Masevhisi ari kushanda",
            servicesHint: "Varwere vanokwanisa kubhuka chete zvawakaisa mutengo nekuzivisa.",
            locationsReady: "Nzvimbo dzekuonana",
            locationsHint: "Zvirongwa zveconsultancy ndizvo zvinodzora nzvimbo dzekuonana dziri pa-saiti.",
            paymentsReady: "Nzira dzekubhadhara",
            stripeStatus: "Kubatanidzwa kweStripe",
            stripeTitle: "Kubatanidza Stripe nekugadzirira kubhadharwa",
            stripeConnected: "Yakabatana",
            stripePending: "Haisati yabatana",
            stripeStatusHint:
              "Kubhadharwa kwechiremba kunofanira kutanga chete kana akaundi yakabatana yagadzirira.",
            stripeBody:
              "Pano ndipo panofanira kugara kugadzirira kubatanidza Stripe Connect yeAfriMed™. Chengetedza masevhisi, zvaunofarira zvekubhadharwa, chibvumirano, uye siginicha yekurapa zvakagadzirira kuti nzira yekubhadhara ibatane zvakanaka.",
            connectStripeCta: "Batanidza Stripe",
            connectStripeManage: "Ronga Stripe",
            payoutCta: "Nzira dzekubhadharwa",
            paymentMethodsPending:
              "Wedzera nzira dzekubhadhara neyekugamuchira mari kuti kugadzirira kubhadharwa kuperere.",
            agreementStatus: "Chibvumirano",
            agreementStatusHint:
              "Kubvuma chibvumirano kunofanira kusainwa uye kuva neshanduro kusati kwagoneswa kubhadharwa kweupenyu chaihwo.",
            agreementPending: "Zvakamirira",
            agreementSigned: "Chasainwa",
            agreementSignedVersion: "Chasainwa ({version})",
            logoReady: "Logo yepraxis yachengetwa",
            logoPending: "Logo yepraxis haisati yawedzerwa",
            diasporaSadc: "Misika yeSADC",
            diasporaDiaspora: "Misika yediaspora",
            diasporaReference: "Mari dzekureferensa",
            diasporaTitle: "Mabhadharo esponsor eSADC nediaspora",
            diasporaCoverage: "{count} misika",
            diasporaBody:
              "AfriMed™ haifaniri kufunga kuti murwere wese anobhanga muno. Kuonana kwakawanda kunotsigirwa nemhuri iri mudiaspora, saka zvikumbiro zvekubhadhara zvine sponsor uye malink anogoverwa zvinofanira kugara pano.",
            diasporaSadcHint:
              "Varwere nevatsigiri vemuno munyika dzose dzedunhu.",
            diasporaDiasporaHint:
              "UK, US, Gulf, Europe, Australia, nedzimwe nzira dzekubhadhara dzediaspora.",
            diasporaReferenceHint:
              "Mitengo yekuyambuka miganhu haifaniri kudonha kusvika pamari yemuno isina kusimba.",
            brandingTitle: "Kuvimbika, chimiro chepraxis nemagwaro",
            brandingBody:
              "Siginecha yako yakachengetwa yatove kushandiswazve pamaprescriptions. Logo yepraxis uye kubata zvibvumirano zvakavanzika zvino zvava kugara pano kuitira kuti mabhiri, maresiti, nemagwaro eAfriMed™ vashandise sosi imwe yakasimbiswa.",
            brandingPanelCta: "Chimiro chepraxis nechibvumirano",
            brandLogo: "Logo yepraxis",
            brandLogoReady: "Yagadzirira",
            brandLogoMissing: "Isipo",
            brandLogoHint:
              "Shandisa sosi imwe yelogo yemabhiri, maresiti, nemagwaro ane chimiro chepraxis.",
            brandSignature: "Siginecha yemuprescriber",
            brandSignatureReady: "Yagadzirira",
            brandSignatureMissing: "Isipo",
            brandSignatureHint:
              "Kusaina prescription kwatove kushandisa chinhu ichi.",
            brandAgreement: "Chibvumirano chakasainwa",
            brandAgreementHint:
              "Zvibvumirano zvakaiswa siginecha zvinofanira kuchengetwa zvakavanzika uye zvine version.",
            fxTitle: "Kururamisira kweFX kumiganhu",
            fxBody:
              "Kana sponsor akabhadhara kubva kuUK, US, kana imwe misika ine mari yakasimba, AfriMed™ inofanira kuchengetedza kugoverwa kwemari kwakarurama kune chiremba nepuratifomu. Izvi zvinoreva kushandisa gwara rakavimbika reFX uye kuratidza mutengo kunoenderana nemusikavo wemubhadhari.",
            fxFootnote:
              "Murayiro wechigadzirwa: sunga kukosha kwekuonana pamari yebasa, wobva washandisa malink ekubhadhara eStripe pamwe nekuratidza mutengo kunochinjika kana nzira yekumashure yava kugara.",
            brandingSheetTitle: "Chimiro chepraxis nechibvumirano chakasainwa",
            brandingSheetBody:
              "Chengeta siginecha yechiremba, logo yepraxis, uye nzvimbo yezvibvumirano zvakachengetwa zveAfriMed™ panzvimbo imwe kuti maprescriptions, magwaro, uye kugadzirira kubhadharwa zvigare zvakabatana.",
            brandingSheetSignature: "Siginecha yechiremba",
            brandingSheetLogo: "Logo yepraxis",
            brandingSheetAgreement: "Zvibvumirano zvakachengetwa",
            brandingSheetDoctorTerms: "Mitemo yechiremba",
            brandingSheetDoctorTermsHint:
              "Nzira yekuisa gwaro rine muridzi wechiremba inogara pano kuitira kuti varwere vaone mitemo yepraxis yakaparadzana neyeAfriMed™.",
            brandingSheetVersion: "Shanduro yechibvumirano",
            brandingSheetSignatureTitle:
              "Isa siginecha inoshandiswa pamaprescriptions nezvibvumirano zveAfriMed™",
            brandingSheetSignatureBody:
              "Bata siginecha yako pano nekukurumidza kuitira kuti AfriMed™ igone kuishandisazve pamaprescriptions uye paiiswa siginecha pachibvumirano pazvinenge zvichidiwa.",
            brandingSheetLogoTitle: "Logo yepraxis",
            brandingSheetLogoBody:
              "Shandisa logo yakazvipira yemapepa anoburitswa nachiremba. Isa PNG, JPG, kana WEBP. Izvi zvinoramba zvakaparadzana nechimiro chechibvumirano chepuratifomu yeAfriMed™.",
            brandingSheetAgreementTitle: "Chibvumirano chepuratifomu",
            brandingSheetAgreementBody:
              "Tarisa chibvumirano kutanga, isa siginecha, uye chengetedza kopi yakaiswa munzvimbo yezvibvumirano zvakachengetwa yeAfriMed™ pamberi pekuti kugadzirira kubhadharwa kuperere.",
            brandingDoctorTermsTitle: "Mitemo yachiremba kana yechipatara",
            brandingDoctorTermsBody:
              "Isa mitemo yemuchiremba iyo varwere vanofanira kuona panguva yekubhuka nekubhadhara. Izvi zvinoramba zvakaparadzana nemitemo yepuratifomu yeAfriMed™ uye zvinomirira mitemo yepraxis kana yechipatara.",
            brandingAddLogo: "Wedzera logo yepraxis",
            brandingUpdateLogo: "Gadziridza logo yepraxis",
            brandingAgreementDownloadError:
              "Hatina kukwanisa kudhawunirodha kopi yechibvumirano chakasainwa.",
            brandingAgreementError:
              "Hatina kukwanisa kusaina nekuisa chibvumirano.",
            brandingAgreementPreviewError:
              "Hatina kukwanisa kuvhura chitarisiko chekutanga chechibvumirano.",
            brandingAgreementSaved: "Chibvumirano chasainwa chikaiswa.",
            brandingAgreementSignedAt: "Chakasainwa pa",
            brandingAgreementSignedAtEmpty: "Hachisati chasainwa",
            brandingAgreementVersion: "Shanduro yechibvumirano",
            brandingAwaitingUpload: "Zvakamirira kuiswa",
            brandingComplete: "Yapera",
            brandingDoctorTermsEmpty: "Hakusati kwava nemitemo yachiremba yakaiswa.",
            brandingDoctorTermsError:
              "Hatina kukwanisa kuchengetedza gwaro remitemo yachiremba.",
            brandingDoctorTermsMissing: "Isa mitemo yachiremba kutanga.",
            brandingDoctorTermsSaved: "Mitemo yachiremba yachengetwa.",
            brandingDoctorTermsUpdated: "Yakagadziridzwa {date}",
            brandingDownloadAgreement: "Dhawunirodha chibvumirano",
            brandingHidePreview: "Viga chitarisiko chekutanga",
            brandingIncomplete: "Hazvina kukwana",
            brandingLoadError:
              "Hatina kukwanisa kurodha chimiro chekubhadhara parizvino.",
            brandingLogoAlt: "Chitarisiko chekutanga chelogo yepraxis",
            brandingLogoEmpty: "Hakusati kwava nelogo yepraxis yakachengetwa.",
            brandingLogoError: "Hatina kukwanisa kuchengetedza logo yepraxis.",
            brandingLogoSaved: "Logo yepraxis yachengetwa.",
            brandingMissing: "Isipo",
            brandingPending: "Zvakamirira",
            brandingPreviewAgreement: "Ona chibvumirano",
            brandingPreviewFrameTitle: "Chitarisiko chekutanga chechibvumirano cheAfriMed™",
            brandingPreviewSurfaceTitle: "Chitarisiko chekutanga chechibvumirano",
            brandingPreviewSurfaceBody:
              "Tarisa kuti siginecha, zita repraxis, uye shanduro yechibvumirano zvakarurama usati waisa munzvimbo yezvakachengetwa.",
            brandingPrivateStorage:
              "Zvinhu izvi zvinochengetwa zvakavanzika kuitira kugadzirira kubhadharwa nemagwaro ane chimiro chepraxis.",
            brandingRefreshError:
              "Hatina kukwanisa kuvandudza chimiro chekubhadhara.",
            brandingReplaceDoctorTerms: "Tsiva mitemo yachiremba",
            brandingSignAgreement: "Saina chibvumirano",
            brandingSignatureSaved: "Siginecha yechiremba yachengetwa.",
            brandingUploadDoctorTerms: "Isa mitemo yachiremba",
            brandingViewDoctorTerms: "Ona mitemo yachiremba",
          },
          patient: {
            subtitleLong:
              "Akaunti yemurwere yeAfriMed™ inoramba yakasununguka. Nhoroondo yekubhadhara, maresiti, nemabhadharo evatsigiri zvinogadzirirwa pano.",
            accountType: "Rudzi rweakaundi",
            freeTitle: "Akaunti yemurwere yemahara",
            freeBody:
              "Varwere havabhadhari mari yekuve nhengo muAfriMed™ muchikamu chekutarisira kwakananga. Mari dzinongooneka kana wabhuka masevhisi akabhadharwa nachiremba muAfriMed™.",
            transactions: "Mabhadharo",
            transactionsValue: "Ona maresiti",
            transactionsCta: "Mabhadharo nemabhiri",
            transactionsHint:
              "Vhura Mabhadharo nemabhiri kuti uone nhoroondo yekubhadhara uye zvaburitswa zvemareseiti.",
            downloads: "Madhawunirodhi",
            downloadsValue: "Zvaburitswa zvagadzirira",
            downloadsCta: "Madhawunirodhi",
            downloadsHint:
              "Mareseiti nekuburitsa mabhiri zviri kugadzirira muMadhawunirodhi apo maturusi ezvakachengetwa achiri kukura.",
          },
        },
        transactions: {
          title: "Mabhadharo nemabhiri",
          subtitle: "Ona nhoroondo wobva wadhawunirodha maresiti",
          subtitleLong:
            "Tevera nhoroondo yekubhadhara, mamiriro ekubhadhara, uye chengeta maresiti ari pedyo.",
          totalPaid: "Zvabhadharwa zvese",
          totalPaidMixed: "Mari dzakasiyana",
          totalPaidMixedHelper:
            "Zvinoratidzwa pamari imwe neimwe. Hapana kushandurwa kweFX kwakaiswa patotal iyi.",
          invoiceCount: "Mabhiri",
          open: "Zvakazaruka",
          refunded: "Zvadzoserwa",
          billingCenter: "Maresiti nezvaburitswa",
          billingCenterSubtitle:
            "Shandisa madhawunirodhi kune zvaburitswa nekukurumidza apo maturusi akazara ekubhadhara achiri kukura.",
          openCenter: "Vhura madhawunirodhi",
          downloadCsv: "Dhawunirodha CSV",
          sponsorTitle: "Kubatsirwa kwemhuri nediaspora",
          sponsorBody:
            "Varwere vazhinji vanobhadharirwa nenhengo dzemhuri dziri muSADC nediaspora. Gadzirira ruzivo rwemutsigiri kamwe chete, wobva wagadzira link yakachengeteka yekubhadhara yekugovera nyore.",
          sponsorSadc: "Misika yeSADC",
          sponsorDiaspora: "Misika yediaspora",
          sponsorTotal: "Coverage yese",
          sponsorAction: "Gadzirira mubhadharo wemutsigiri",
          sponsorLearnMore: "Mashandiro anoita mubhadharo weAfriMed™",
          recentInvoices: "Mabhiri achangoburwa",
          recentInvoicesSubtitle:
            "Maresiti, zvitupa zvekubhadhara, nezvigadziriso zvekubhadhara zvichaungana pano apo zviitiko zvitsva zvichiitika.",
          invoice: "Bhiri",
          empty: "Hakusati kwava nemarekodhi ekubhadhara",
          emptySubtitle:
            "Mabhadharo nemaresiti zvichaonekwa pano kana kubhadhara kwatanga.",
          states: {
            refundPending: "Yakanzurwa / Refund iri kufamba",
            refunded: "Yakanzurwa / Yadzoserwa",
            cancelled: "Yakanzurwa",
            paidPharmacy: "Yabhadharwa / Pharmacy",
            paid: "Yabhadharwa",
            pending: "Zvakamirira",
          },
          errors: {
            refreshSponsorPayments:
              "Hatina kukwanisa kuvandudza marekodhi e sponsor payment parizvino.",
            load:
              "Hatina kukwanisa kurodha nhoroondo yako yekubhadhara parizvino.",
          },
          sponsorSellerDoctor: "chiremba",
          sponsorSellerPharmacy: "furumasi",
          sponsorFx: "Chiratidzo cheFX",
          sponsorSheetTitle: "Gadzirira mubhadharo wemutsigiri",
          sponsorSheetBody:
            "Shandisa izvi kugadzira chikumbiro chakachengeteka chekubhadhara cheAfriMed™ chemhuri kana vatsigiri vari muSADC nediaspora. Gadzira link yekubhadhara yakachengeteka kutanga, wobva waigovera nemutsigiri zviri nyore.",
          sponsorContact: "Mashoko emutsigiri",
          sponsorName: "Zita remutsigiri",
          sponsorNamePlaceholder: "Zita rizere",
          sponsorRelationship: "Ukama",
          sponsorRelationshipPlaceholder: "Mukoma, hanzvadzi, mubereki, muchengeti",
          sponsorCountry: "Nyika yemutsigiri",
          sponsorEmail: "Imeyili yemutsigiri",
          sponsorEmailPlaceholder: "zita@example.com",
          sponsorRequest: "Chikumbiro chekubhadhara",
          sponsorPaymentFor: "Mubhadharo we",
          sponsorPaymentForPlaceholderDoctor: "Kuonana kuri kure",
          sponsorPaymentForPlaceholderPharmacy: "Kuzadzikisa recipe",
          sponsorPaymentForPlaceholder: "Kuonana kuri kure",
          sponsorPaymentForPatient: "{service} kwa {name}",
          sponsorPaymentForDefaultDoctor: "Kuonana kuri kure",
          sponsorPaymentForDefaultPharmacy: "Kuzadzikisa recipe",
          sponsorAmount: "Mari",
          sponsorCurrency: "Mari yenyika",
          sponsorFxNote:
            "Shandisa mutemo wakarurama weFX kuvabhadhari vediaspora. Mutsigiri ari kuUK kana kuUS haafaniri kumanikidzwa pamutengo usina kusimba kana mari iri kusimba.",
          sponsorFxRefreshing: "Tiri kuvandudza mari yakarurama yemutsigiri yeFX…",
          sponsorFxReference: "Chiratidzo cheFX",
          sponsorFxSentence: "Mari yebasa {amount} {currency} • {reference}.",
          sponsorPreview: "Muenzaniso wekugovera",
          sponsorLiveLinkTitle: "Link yakachengeteka yemubhadharo wemutsigiri",
          sponsorLiveLinkBody:
            "Gadzira link yakachengeteka yekubhadhara inobatwa neStripe inogona kugoverwa nemhuri kana vatsigiri. Izvi zvinoita kuti rwendo rwemutsigiri ruve nyore kupfuura kuronga mubhadharo nemaoko.",
          sponsorGenerateLink: "Gadzira link yekubhadhara",
          sponsorCopied: "Pfupiso yakopiwa",
          sponsorCopy: "Kopa pfupiso yemutsigiri",
          sponsorEmailAction: "Vhura draft reimeyili rine link",
          sponsorLinkRequired:
            "Gadzira link yekubhadhara yakachengeteka usati wavhura draft reimeyili kuitira kuti mutsigiri agamuchire chiito chechokwadi chekuchouta.",
          sponsorGeneratedUrl: "Link yagadzirwa",
          sponsorPlatformSummary: "Muenzaniso wekugoverwa kwemari",
          sponsorPlatformSummaryEmpty:
            "Muenzaniso wekugoverwa kwemari unoonekwa pano kana nzvimbo yekugamuchira mari yabatanidzwa.",
          sponsorSettlementFee: "{amount} {currency} mubhadharo weAfriMed™",
          sponsorSettlementNet:
            "{amount} {currency} zvasara mushure memubhadharo wepuratifomu kuakaunti yakabatana yekugamuchira mari ye{seller}.",
          sponsorSettlementMissingDestination:
            "Link iyi yashanda, asi nzvimbo yakabatana yekugamuchira mari ye{seller} haisati yabatanidzwa. Batanidzazve Stripe usati waiisa mhenyu.",
          sponsorRecoveryError:
            "Mubhadharo wemutsigiri waonekwa, asi AfriMed haina kukwanisa kupedzisa booking parizvino.",
          sponsorAmountPlaceholder: "50.00",
          sponsorCurrencyPlaceholder: "ZAR",
          sponsorCurrencyPlaceholderUsd: "USD",
          sponsorBriefTitle: "Chikumbiro chemubhadharo wemutsigiri cheAfriMed™",
          sponsorBriefSponsor: "Mutsigiri",
          sponsorBriefRelationship: "Ukama",
          sponsorBriefCountry: "Nyika yemutsigiri",
          sponsorBriefPhone: "Runhare rwemutsigiri",
          sponsorBriefEmail: "Imeyili yemutsigiri",
          sponsorBriefPaymentFor: "Mubhadharo we",
          sponsorBriefAmount: "Mari yakumbirwa",
          sponsorBriefLink: "Link yakachengeteka yekubhadhara",
          sponsorBriefReady:
            "Link yakachengeteka iyi yeAfriMed™ yagadzirira mubhadharo wemutsigiri.",
          sponsorBriefNeedLink:
            "Gadzira link yakachengeteka yekubhadhara yeAfriMed™ usati watumira chikumbiro ichi kuitira kuti mutsigiri agamuchire chiito chechokwadi chekubhadhara.",
          sponsorBriefFx:
            "Mabhadharo anoyambuka miganhu anofanira kuremekedza kuratidzwa kweFX kwakaringana kuitira kuti kuonana kubhadharwe zvakanaka.",
          sponsorMailSubject: "Chikumbiro chemubhadharo cheAfriMed™",
          sponsorCreateLinkError:
            "Hatina kukwanisa kugadzira link yemubhadharo wemutsigiri parizvino.",
          },
        downloads: {
          title: "Madhawunirodhi",
          subtitle: "Buritsa data, mishumo nezvipfupiso",
          subtitleLong:
            "Chengetedza zvaburitswa zveAfriMed™ zviri pedyo apo maturusi akazara ezvakachengetwa achiri kukura.",
          billingSnapshot: "Pfupiso yemabhiri",
          billingSnapshotSubtitle:
            "Dhawunirodha mitsara yemabhiri seCSV yemareseiti, kudzosera mari, kana basa respredishiti.",
          subscriptionSnapshot: "Pfupiso yekugadzirira kubhadharwa",
          subscriptionSnapshotSubtitle:
            "Dhawunirodha pfupiso yePDF yemamiriro ekubhadhara, kugadzirira kubhadharwa, kana nzira yako yekukumbira mubhadharo.",
          profileSnapshot: "Mbiri yezvirongwa",
          profileSnapshotSubtitle:
            "Dhawunirodha pfupiso yePDF yemamiriro ako aripo embiri yako nezvirongwa.",
          comingNext: "Zvinotevera",
          comingNextSubtitle:
            "Mabundle akazara ezvakachengetwa, maPDF emabhiri, uye kuburitswa kwemarekodhi akapfuma eAfriMed™ zvichawedzerwa pano pasina kuchinja navigation iyi.",
        },
        closeAccount: {
          title: "Vhara, Misa kana Bvisa Akaunti",
          subtitle: "Tichakutungamira kutanga kuburikidza nesarudzo dzakachengeteka",
          subtitleLong:
            "Tinotanga nesarudzo dzakachengeteka uye dzekudzorera kuitira kuti usarasikirwe nekupinda netsaona.",
          pauseTitle: "Misa kuwana akaunti",
          pauseBody:
            "Zvakanakira kana uchida zororo renguva pfupi. Izvi zvinofanira kuchengetedza nhoroondo yako asi kumisa chiitiko chemazuva ose.",
          pauseAction: "Misa akaunti",
          pauseListOne: "Nhoroondo inoramba iripo",
          pauseListOneBody: "Chengetedza rekodhi reakaunti asi mira zviitiko zvinotarisana nemushandisi.",
          pauseListTwo: "Nhanho yakachengeteka kutanga",
          pauseListTwoBody: "Shandisa izvi kutanga kana chido chiri chechinguvana.",
          closeTitle: "Vhara akaunti",
          closeBody:
            "Zvakanakira kana uchida kumira kushandisa AfriMed™ asi uchiri kuda nzira yakarongeka yekubuda uye kujeka kwekuchengetwa kwerekodhi.",
          closeAction: "Vhara akaunti",
          closeListOne: "Ongororo yekubuda",
          closeListOneBody:
            "Tinofanira kuratidza kuchengetwa kwerekodhi, maresiti, uye gwara rekutevedzera tisati taita kuvhara kwekupedzisira.",
          closeListTwo: "Nzira yekudzorera inokosha",
          closeListTwoBody:
            "Kuvhara kunofanira kuva kushoma kuparadza kupfuura kubvisa zvachose.",
          deleteTitle: "Kumbira kubviswa",
          deleteBody:
            "Iyi ndiyo sarudzo ine hanya zvikuru. AfriMed™ inobata zvikumbiro zvekubvisa nemaoko kuti kuzivikanwa, kubhadhara, uye kuchengetedzwa kweutano zviongororwe tisati tabvisa zvachose.",
          deleteAction: "Kumbira kubviswa",
          deleteListOne: "Maitiro ekukumbira kubviswa",
          deleteListOneBody:
            "Tumira email ku info@afrimedtelehealth.com uchishandisa email yakabatana neakaunti yako yeAfriMed™. Kana email isipo, shandisa nzira dzerubatsiro dzeAfriMed™ uchipa ruzivo rwakakwana rwekusimbisa.",
          deleteListTwo: "Isa ruzivo urwu",
          deleteListTwoBody:
            "Zita rizere, email kana nhamba yefoni yeakaunti, basa rako, uye mashoko anoti: “Ndiri kukumbira kubviswa kwedata reakaunti yangu yeAfriMed.”",
          nextStepTitle: "Nguva yekugadzirisa",
          nextStepBody:
            "AfriMed™ inosimbisa zvikumbiro zvekubvisa uye inotanga ongororo nekugadzirisa nekukurumidza zvinobvira. Nguva yekupedzisa inoenderana nekusimbiswa kwekuzivikanwa nezvisungo zvemutemo, asi kazhinji zvinopera mukati memazuva 30.",
        },
        notificationPreferences: {
          appointmentReminders: {
            title: "Zviyeuchidzo zvekuonana",
            introDoctor:
              "AfriMed inotumira chiyeuchidzo chimwe chisati chatanga maawa rimwe pamberi pekuonana kwabvumidzwa. Pfupiso yemangwanani ichauya gare gare.",
            introPatient:
              "AfriMed inotumira zviyeuchidzo maawa 24 nemaawa 2 pamberi pekuonana kwabvumidzwa. SMS inobatsira kunyange usina kupinda.",
            timingDoctor: "Nguva yazvino: awa rimwe pamberi pekuonana.",
            timingPatient: "Nguva yazvino: maawa 24 nemaawa 2 pamberi pekuonana.",
          },
          channels: {
            inApp: {
              title: "Zviyeuchidzo muapp",
              support: "Zvinoratidzwa muinbox yezviziviso zveAfriMed nepamascreen ekuonana.",
            },
            sms: {
              title: "Zviyeuchidzo zveSMS",
              support: "Ndezvakanakisa kana usina kupinda muapp.",
            },
            email: {
              title: "Zviyeuchidzo zveemail",
              support: "Tumira zvakare zvakakosha kubhokisi rako reemail.",
            },
            push: {
              title: "Zviziviso zvepush",
              support: "Zvinoda kubvumidzwa kwebhurawuza kana mudziyo.",
            },
          },
        },
      },
      notificationPreferences: {
        appointmentReminders: {
          title: "Zviyeuchidzo zvekuonana",
          introDoctor:
            "AfriMed inotumira chiyeuchidzo chimwe chisati chatanga maawa rimwe pamberi pekuonana kwabvumidzwa. Pfupiso yemangwanani ichauya gare gare.",
          introPatient:
            "AfriMed inotumira zviyeuchidzo maawa 24 nemaawa 2 pamberi pekuonana kwabvumidzwa. SMS inobatsira kunyange usina kupinda.",
          timingDoctor: "Nguva yazvino: awa rimwe pamberi pekuonana.",
          timingPatient: "Nguva yazvino: maawa 24 nemaawa 2 pamberi pekuonana.",
        },
        channels: {
          inApp: {
            title: "Zviyeuchidzo muapp",
            support:
              "Zvinoratidzwa muinbox yezviziviso zveAfriMed nepamascreen ekuonana.",
          },
          sms: {
            title: "Zviyeuchidzo zveSMS",
            support: "Ndezvakanakisa kana usina kupinda muapp.",
          },
          email: {
            title: "Zviyeuchidzo zveemail",
            support: "Tumira zvakare zvakakosha kubhokisi rako reemail.",
          },
          push: {
            title: "Zviziviso zvepush",
            supportGranted:
              "Shandisa zviziviso zvebhurawuza kune zviziviso zvakakosha nezvekukurumidza kana AfriMed isiri kumberi.",
            supportPrompt:
              "Bvumira zviziviso zvebhurawuza kana wabvunzwa kuti uwane zviziviso zvakakosha nezvekukurumidza.",
            unsupported:
              "Bhurawuza iri haritsigire zvikumbiro zvezviziviso pano.",
          },
          sound: {
            title: "Ruzha rwechiziviso chinokurumidza",
            support:
              "Rinoridza ruzha pfupi kune zviziviso zvekukurumidza apo AfriMed yakavhurika.",
          },
          vibration: {
            title: "Kudedera kwekukurumidza",
            support:
              "Kunowedzera kudedera kupfupi pamidziyo inotsigirwa kune zviziviso zvekukurumidza.",
          },
        },
        footer:
          "Zviziviso zvebhurawuza zvino zvava kufukidza zviziviso zvekutarisirwa panguva yesession kana mvumo yabvumidzwa. Kuendeswa kwepush kwakazara usina kupinda kuchiri kuda hwaro hwekunyoresa hweworker yeservice uye kuchiri kunonotswa.",
      },
      patientsAge: {
        title: "Zera revarwere",
        subtitle: "Sarudza mapoka ezera revarwere aunobvuma parizvino.",
        infants: "Vacheche",
        infantsSupport: "0-2 makore",
        children: "Vana",
        childrenShort: "Vana",
        adults: "Vakuru",
      },
      services: {
        title: "Masevhisi nemitengo",
        emptyTitle: "Hakusati kwava nemasevhisi",
        emptySupport:
          "Wedzera sevhisi yako yekutanga kuti utange kupa nguva dzinobhukika nemitengo.",
        add: "Wedzera sevhisi",
        addTitle: "Wedzera sevhisi",
        searchPlaceholder: "Tsvaga kana wedzera",
        clearSearch: "Bvisa kutsvaga",
        addCustom: "Wedzera sevhisi yako",
        catalogTitle: "Rondedzero yemasevhisi",
        setPricingTitle: "Isa mitengo",
        editPrice: "Chinja mutengo",
        deleteService: "Bvisa sevhisi",
        deleteNamedTitle: "Bvisa sevhisi ye{name}?",
        deleteBody: "Sevhisi iyi ichabviswa kubva kumasevhisi eprofile yako.",
        added: "Sevhisi yawedzerwa",
        duplicate:
          "Sevhisi ine nguva imwe chete yatovepo. Chinja iripo panzvimbo payo.",
        moreOptions: "Dzimwe sarudzo dze{name}",
        price: "Mutengo",
        pricePlaceholder: "0.00",
        clearPrice: "Bvisa mutengo",
        duration: "Nguva",
        durationMinutes: "maminitsi {minutes}",
        durationShort: "maminitsi {minutes}",
        signInRequired: "Unofanira kunge wapinda.",
        catalog: {
          eczemaCare: "Kutarisirwa kweeczema",
          rosaceaTreatments: "Kurapwa kwerosacea nenyanzvi",
          moleAssessment: "Kuongorora mamoles nenyanzvi",
          allergyTesting: "Kuyedzwa nekutarisirwa kweallergy",
        },
      },
      specialtyEdit: {
        title: "Hunyanzvi",
        subtitle: "Sarudza kana kugadzirisa hunyanzvi hwako hukuru.",
        search: "Tsvaga",
        clearSearch: "Bvisa kutsvaga",
        updated: "Hunyanzvi hwagadziridzwa",
        saveFailed: "Hatina kukwanisa kuchengetedza shanduko. Edza zvakare.",
        catalogHidden:
          "Rondedzero yehunyanzvi haisati yaiswa mumutauro uyu.",
        empty: "Hapana zvinowirirana.",
      },
      consultancyEdit: {
        title: "Chinja nzvimbo yekuonana",
        addLocation: "Wedzera imwe nzvimbo",
        locationFallback: "Nzvimbo {count}",
        facilityNameFallback: "Zita renzvimbo",
        facilityAddressFallback: "Wedzera nzvimbo yako huru nekero yayo",
        availability: "Kuwanikwa",
        bookingHorizon: "Nguva yekubhuka mberi",
        cancellationNotice: "Nguva yekuzivisa kukanzura",
        patientsAge: "Zera revarwere",
        paymentMethods: "Nzira dzekubhadhara",
        insurancePolicy: "Mutemo weinishuwarenzi",
        servicesPricing: "Masevhisi nemitengo",
        setInAvailability: "Isa muKuwanikwa",
        managedInAvailability: "Zvinotongwa muKuwanikwa",
        everyDay: "Mazuva ose",
        everyDays: "Mazuva ose ega ega {count}",
        everyWeek: "Vhiki rega rega",
        everyWeeks: "Mavhiki ose ega ega {count}",
        everyWeekDays: "{base}, {days}",
        twentyFourHours: "Maawa 24",
        daysAhead: "mazuva {count} mberi",
        hoursNotice: "chiziviso chemaawa {count}",
        accepting: "Kugamuchira",
        permissionsBlocked:
          "Nzvimbo dzekuonana hadzigoni kugadziridzwa parizvino nekuda kwemvumo. Data harina kuchengetwa mudhatabhesi.",
        saveFailed: "Hatina kukwanisa kuchengetedza nzvimbo yekuonana. Edza zvakare.",
      },
      evisit: {
        title: "E-Kuonana",
        toggleTitle: "E-Kuonana",
        helper: "Bvumira varwere kuronga kuonana kwepaindaneti mukarenda yako.",
        availabilityHelp:
          "E-Kuonana inoshandisa kuwanikwa kwechiremba wako pamitemo yenguva nekuvhurwa kwebhuku. Ronga kureba kwemasevhisi nemitengo zvakasiyana muMasevhisi nemitengo.",
        availability: "Kuwanikwa",
        servicesPricing: "Masevhisi nemitengo",
        patientsAge: "Zera revarwere",
        paymentMethods: "Nzira dzekubhadhara",
        insurancePolicy: "Mutemo weinishuwarenzi",
        price: "Mutengo weE-Kuonana",
        setPrice: "Isa mutengo",
        priceLabel: "Mutengo",
        pricePlaceholder: "0.00",
        clearPrice: "Bvisa mutengo",
        priceDialogTitle: "E-Kuonana",
        selectTime: "Sarudza nguva",
        closeTimePicker: "Vhara sarudzo yenguva",
        switchTimeField: "Chinja chikamu chenguva",
        cancel: "Kanzura",
        confirm: "Zvakanaka",
        dialogConfirm: "Simbisa",
        manage: "Ronga",
        manageInAvailability: "Ronga muKuwanikwa",
        accepting: "Kugamuchira",
        summary: {
          regularDaysAhead: "mazuva {count} anowanzoitwa • mazuva {days} mberi",
          regularDaysOnly: "mazuva {count} anowanzoitwa",
          daysAheadOnly: "mazuva {days} mberi",
        },
        servicesCount: "masevhisi {count}",
      },
      registrationUpload: {
        title: "Gwaro rekunyoresa",
        subtitle: "Isa kopi yakajeka yekunyoreswa kwako kukanzuru",
        addTitle: "Wedzera gwaro rako rekunyoresa",
        addAction: "Wedzera",
        changeFile: "Chinja faira",
        addMore: "Wedzera zvimwe",
        existingNotice:
          "Pane gwaro rekunyoresa riripo kubva {date}. Isa idzva kuti ritore nzvimbo yaripo.",
        status: {
          done: "Kunyoresa kwazivikanwa",
          failed: "Kusimbisa kwatadza",
        },
        saveOcrFailed: "Hatina kukwanisa kuchengetedza zvabuda muOCR.",
        analysisFailed: "Kuongorora gwaro kwatadza. Edza rimwe faira.",
        statusReadFailed: "Hatina kukwanisa kuverenga mamiriro ekusimbisa.",
        summary: {
          registrationNumber: "Nhamba yekunyoresa",
          practitionerName: "Zita rakazara (chiremba)",
          specialty: "Hunyanzvi / chikamu",
          practitionerFallback: "Chiremba",
        },
        retention: "Rakachengetwa kusvika {date}.",
        consent:
          "Nekuisa izvi, unobvuma kuti AfriMed ichengete gwaro iri rakavharidzirwa kubva kumagumo kusvika kumagumo kuti riitwe kusimbisa. Richachengetwa kusvika pamazuva 90.",
        alt: {
          verified: "Yasimbiswa",
          documentPreview: "Muenzaniso wegwaro rekunyoresa",
        },
      },
      registrationDetails: {
        title: "Zvinyorwa zvekunyoresa",
        subtitle: "Chengeta mashoko ako ekunyoresa ari matsva.",
        expiryPast:
          "Zuva rekupera harigoni kuva rapfuura. Sarudza nhasi kana ramangwana.",
        signInRequired: "Unofanira kupinda kuti urambe uchienderera.",
        saveFailed: "Hatina kukwanisa kuchengetedza zvinyorwa zvekunyoresa.",
        savedPending: "Shanduko dzachengetwa (kusimbisa kuchakamirira)",
        yearsRange: "Isa kukosha kuri pakati pe1 ne60.",
        suggestions: {
          botswana: "Botswana Health Professions Council",
          hpcsa: "Health Professions Council of South Africa (HPCSA)",
          namibia: "Namibia Health Professions Council",
          zambia: "Zambia Medical Council",
          zimbabwe: "Zimbabwe Medical & Dental Practitioners Council",
          lesotho: "Lesotho Medical, Dental and Pharmacy Council",
          eswatini: "Eswatini Medical & Dental Council",
          mozambique: "Mozambique Ministry of Health",
          angola: "Angola Ordem dos Médicos",
          other: "Zvimwe",
        },
        fields: {
          number: {
            label: "Nhamba yekunyoresa",
            placeholder: "semuenzaniso BMD 12345",
          },
          issuer: {
            label: "Sangano rinopa",
            placeholder: "Sarudza kana nyora sangano rinopa",
          },
          issueDate: {
            label: "Zuva rakabudiswa",
          },
          expiryDate: {
            label: "Zuva rekupera",
          },
          datePlaceholder: "DD MMM, YYYY",
          yearsExperience: {
            label: "Makore eruzivo",
            placeholder: "semuenzaniso 12",
          },
        },
      },
      availability: {
        pageTitle: "Kuronga nguva kwechiremba",
        pageSubtitle:
          "Ronga maitiro ekiriniki ako anowanzoitwa, wobva waronga shanduko dziri kumberi.",
        loadError: "Hatina kukwanisa kurodha kuwanikwa izvozvi.",
        saveError: "Hatina kukwanisa kuchengetedza shanduko dzekuwanikwa.",
        overrideDateRequired: "Sarudza zuva rekutanga nerokupedzisira.",
        overrideDateOrder: "Zuva rekupedzisira rinofanira kufanana kana kuuya mushure.",
        overrideTimeOrder: "Maawa akasarudzika anoda nguva yekuvhara iri mushure mekuvhura.",
        unavailableLabel: "Hazviwanikwe pakubhuka",
        toggleOn: "Yakabatidzwa",
        toggleOff: "Yakadzimwa",
        optionMinutes5: "maminitsi 5",
        optionMinutes10: "maminitsi 10",
        optionMinutes15: "maminitsi 15",
        optionMinutes20: "maminitsi 20",
        optionMinutes30: "maminitsi 30",
        optionMinutes45: "maminitsi 45",
        optionHour1: "awa 1",
        optionNoBuffer: "Hapana nguva yekuzorora",
        optionWeeks2: "mavhiki 2",
        optionMonth1: "mwedzi 1",
        optionMonths2: "mwedzi 2",
        optionMonths3: "mwedzi 3",
        optionMonths6: "mwedzi 6",
        optionMonths12: "mwedzi 12",
        optionHours2: "maawa 2",
        optionHours12: "maawa 12",
        optionHours24: "maawa 24",
        optionHours48: "maawa 48",
        optionHours72: "maawa 72",
        weeklyCard: "Template yevhiki",
        weeklyCardValue: "mazuva {count} anowanzoitwa",
        weeklyCardSupport:
          "Iyi ndiyo purogiramu yekutanga inoonekwa nevarwere vhiki rega rega.",
        bookingCard: "Nguva yekubhuka mberi",
        bookingCardValue: "mazuva {count} mberi",
        bookingCardSupport:
          "Inobatsira varwere kuchengetedza maappointment kare.",
        overrideCard: "Shanduko dzakarongwa",
        overrideCardValue: "{count} dzakaiswa",
        overrideCardSupport:
          "{away} mabhuroko ekusavapo uye {special} mashandiro emaawa akasarudzika.",
        calendarTitle: "Karenda yekuwanikwa",
        calendarSupport:
          "Shandisa karenda kuona zuva riri kumberi, wobva waronga maawa anowanzoitwa uye shanduko dzakarongwa pairi.",
        calendarMode: "Kuronga kunotungamirirwa nemwedzi",
        selectedDate: "Zuva rasarudzwa",
        focusedDateLabel: "{weekday}, {day} {month} {year}",
        dayActiveCompact: "Yakazarukira kubhuka",
        dayInactiveCompact: "Yakavharwa nekusarudzika",
        specialHours: "Maawa akasarudzika",
        timeAway: "Nguva yausipo",
        selectedDateOpen: "Varwere vanogona kubhuka pakati pe{start} ne{end}.",
        selectedDateClosed:
          "Zuva iri rakavharwa parizvino kunze kwekunge wawedzera maawa akasarudzika.",
        overrideAppliedAvailable:
          "Shanduko yemaawa akasarudzika iri kushanda pazuva iri.",
        overrideAppliedUnavailable:
          "Shanduko yenguva yausipo iri kushanda pazuva iri.",
        upcomingChanges: "Shanduko dziri kuuya dzakarongwa",
        upcomingChangesSupport:
          "Maonero anokurumidza ezvichachinja diary yako mberi.",
        specialHoursFallback: "Maawa akasarudzika ekiriniki",
        timeAwayFallback: "Hazviwanikwe pakubhuka",
        removeOverride: "Bvisa",
        overrideEmptyTitle: "Hapana shanduko dziri kuuya",
        overrideEmptySupport:
          "Karenda yako yeramangwana parizvino iri kutungamirirwa netemplate yevhiki chete.",
        weeklyTitle: "Kuwanikwa kwevhiki kunowanzoitwa",
        weeklySupport:
          "Tanga nemaawa anodzokororwa mavhiki mazhinji. Kusavapo kwezuva chairo kana makiriniki akasarudzika zvinoiswa muchikamu chiri pazasi.",
        copyWeekdays: "Shandisa Muvhuro kumazuva ebasa",
        copyAll: "Shandisa Muvhuro kumazuva ose",
        weeklyHint:
          "Chengeta template yevhiki iri nyore. Shandisa shanduko dzemazuva dziri pazasi pazororo, leave, uye makiriniki akasarudzika.",
        days: {
          monday: "Muvhuro",
          tuesday: "Chipiri",
          wednesday: "Chitatu",
          thursday: "China",
          friday: "Chishanu",
          saturday: "Mugovera",
          sunday: "Svondo",
        },
        opens: "Kuvhura",
        closes: "Kuvhara",
        applyWeekdays: "Mazuva ebasa",
        applyAllDays: "Mazuva ose",
        overrideTitle: "Shanduko dzemazuva dzakarongwa",
        overrideSupport:
          "Shandisa izvi kuzororo regore, mazororo eruzhinji, misangano, kana makiriniki akawedzerwa kamwe chete. Zvinokunda template yevhiki yakajairwa.",
        overrideHint: "Zvakanaka pakuronga mwedzi iri kumberi",
        addOverride: "Wedzera shanduko yakarongwa",
        addOverrideSupport:
          "Unogona kuvhara nguva yese yekusavapo kana kuvhura zuva rimwe rekiriniki rine maawa akasiyana.",
        overrideSetup: "Mashoko eshanduko",
        overrideSetupSupport:
          "Isa rudzi rwechanduko kutanga, wozopa zita rakajeka richazivikanwa nechikwata chako gare gare.",
        overrideTypeAvailable: "Maawa akasarudzika",
        overrideTypeUnavailable: "Nguva yausipo",
        overrideType: "Rudzi rwechanduko",
        overrideLabel: "Chiratidzo",
        overrideLabelPlaceholder:
          "Zororo regore, musangano, kiriniki yeMugovera yakawedzerwa...",
        overrideTiming: "Nguva yazvinobata",
        overrideTimingSupportAvailable:
          "Sarudza mazuva, wobva waisa maawa ekuvhura ehwindo iri rekiriniki rimwe chete.",
        overrideTimingSupportUnavailable:
          "Sarudza nguva yaunoda kuvhara kune leave, mazororo, kana imwe nguva yausipo.",
        overrideDatePending: "Sarudza mazuva",
        startsOn: "Inotanga pa",
        endsOn: "Inopera pa",
        specialOpens: "Inovhura",
        specialCloses: "Inovhara",
        addChange: "Wedzera shanduko yakarongwa",
        overrideEmpty:
          "Hapana shanduko dzemazuva parizvino. Purogiramu yevhiki ndiyo iri kushanda yose.",
        servicesTitle: "Masevhisi nemitengo",
        servicesSupport:
          "Kuwanikwa kunodzora nguva dzinogona kubhukwa nevarwere. Masevhisi nemitengo zvinodzora zvavanogona kubhuka, kwenguva yakareba sei, uye nemutengo upi.",
        manageServices: "Ronga masevhisi nemitengo",
        servicesConfigured: "Masevhisi akagadziriswa",
        servicesConfiguredValue: "{count} anoshanda",
        servicesConfiguredSupport:
          "Varwere vanosarudza rimwe rawo vasati vabhuka zuva nenguva.",
        serviceDurations: "Sarudzo dzenguva",
        serviceDurationsValue: "nguva {count}",
        serviceDurationsSupport:
          "Kubhuka kunoshandisa kureba kwebasa kwakasarudzwa pakugadzira nguva dzinowanikwa.",
        serviceCurrencies: "Mari dzemitengo",
        serviceCurrenciesValue: "{count} dzakagoneswa",
        serviceCurrenciesSupport:
          "Ronga mitengo yeSADC neUSD mumasevhisi, kwete mukuwanikwa kwevhiki.",
        rulesTitle: "Mitemo yekubhuka",
        rulesSupport:
          "Mitemo iyi inoronga maitiro anoitwa kubhuka kwevarwere kubva kupurogiramu yako.",
        headerRule: "Mutemo",
        headerChoice: "Sarudzo yazvino",
        slotDuration: "Nguva yeappointment",
        slotDurationSupport:
          "Nguva huru yekuonana inopihwa kuvarwere.",
        bufferTime: "Nguva yekuzorora pakati pemaappointment",
        bufferTimeSupport:
          "Nguva yekufema usati warongera murwere anotevera.",
        advanceBooking: "Varwere vanogona kubhuka mberi",
        advanceBookingSupport:
          "Zvakakosha kuvanyanzvi uye pakuronga mazororo akareba.",
        cancellationPolicy: "Nguva yekuzivisa kukanzura",
        cancellationPolicySupport:
          "Nguva yekuti varwere vazivise vasati vakanzura.",
        autoConfirm: "Simbisa maappointment otomatiki",
        autoConfirmSupport:
          "Mabhuku matsva anopinda mudhayari rako pasina kubvumidzwa nemaoko.",
        allowRescheduling: "Bvumira varwere kuchinjazve nguva",
        allowReschedulingSupport:
          "Varwere vanogona kushandura nguva yavo kana ichiri kutevera mitemo yako.",
        unsaved: "Shanduko dzekuwanikwa dzisina kuchengetwa",
        saved: "Kuwanikwa kwacho kwazvino",
        unsavedSupport:
          "Chengeta kana wagadzirira kuitira kuti kubhuka kwevarwere kuratidze purogiramu itsva.",
        savedSupport:
          "Maawa ako evhiki, shanduko dzemazuva, uye mitemo yekubhuka zvayenderana.",
        reset: "Dzorerazve",
        save: "Chengeta kuwanikwa",
      },
      edit: {
        common: {
          saveFailed: "Hatina kukwanisa kuchengetedza shanduko dzako parizvino.",
          sessionNotReady: "Session haisati yagadzirira. Ndapota pinda zvakare.",
        },
        idPassport: {
          awaitingReview: "Gwaro ragamuchirwa. Rakamirira kuongororwa.",
          signInRequired: "Unofanira kupinda kuti urambe uchienderera.",
          submitted: "ID / Pasipoti yatumirwa kuti isimbiswe",
          processingFailedSnackbar:
            "Kugadzirisa gwaro kwatadza. Ndapota edzazve.",
          statusReadFailed:
            "Hatina kukwanisa kuverenga mamiriro ekusimbisa.",
        },
        name: {
          title: "Chinja zita",
          subtitle: "Gadzirisa mashoko anoratidzwa paprofile yako.",
          firstLabel: "Zita rekutanga",
          firstPlaceholder: "Nyora zita rekutanga",
          lastLabel: "Zita rekupedzisira",
          lastPlaceholder: "Nyora zita rekupedzisira",
          clearFirst: "Bvisa zita rekutanga",
          clearLast: "Bvisa zita rekupedzisira",
          updated: "Zita ravandudzwa",
        },
        phone: {
          title: "Chinja foni",
          subtitle: "Wedzera nhamba inoshanda kuitira zviziviso nekusimbisa.",
          invalidPhone: "Isa nhamba yefoni yakarurama.",
          mustAccept: "Unofanira kubvuma kuti urambe uchienderera.",
          invalidPhoneLength: "Nhamba yefoni ipfupi kana kureba zvisiri izvo.",
          timeout: "Nguva yekusimbisa yapera. Edzazve.",
          saveFailed: "Hatina kukwanisa kuchengetedza nhamba yako yefoni.",
          updated: "Nhamba yefoni yavandudzwa",
          verified: "Yasimbiswa",
          termsLead: "Nekuenderera mberi unobvuma",
          termsLink: "Mitemo",
          termsJoiner: "uye",
          privacyLink: "Mutemo wekuvanzika",
          termsEnd: ".",
          verifyLater: "",
        },
        dob: {
          title: "Chinja zuva rekuzvarwa",
          subtitle: "Chengeta zuva rako rekuzvarwa rakarurama pamarekodhi.",
          label: "Zuva rekuzvarwa",
          placeholder: "Sarudza zuva",
          updated: "Zuva rekuzvarwa ravandudzwa",
        },
        about: {
          title: "Nezvako",
          subtitle: "Nyora sumo pfupi inobatsira vanhu kukuziva.",
          label: "Sumo",
          placeholder: "Nyora zvishoma nezvako",
          counter: "{count} mavara",
          minimum: "Svikira pavara shoma rinodiwa kuti urambe uchienderera.",
          updated: "Sumo yavandudzwa",
          folaTitle: "Rubatsiro rweFola",
          folaLoading: "Fola iri kunyora...",
          folaApply: "Shandisa chinyorwa chaFola",
        },
        organDonor: {
          title: "Mupi wenhengo",
          subtitle: "Ratidza kana uchida kunyorwa semupi wenhengo.",
          discardTitle: "Rasa shanduko here?",
          discardBody: "Pane shanduko dzisina kuchengetwa pamamiriro emupi wenhengo.",
        },
        email: {
          title: "Chinja imeyili",
          subtitleLink: "Tichakutumira chinongedzo chekusimbisa kune imeyili itsva.",
          subtitleCode: "Isa kodhi yakatumirwa kune imeyili yako.",
          label: "Imeyili",
          placeholder: "iwe@example.com",
          verified: "Yasimbiswa",
          enterEmail: "Isa imeyili kutanga.",
          invalidEmail: "Isa imeyili yakarurama.",
          sendFailed: "Hatina kukwanisa kutumira chinongedzo chekusimbisa.",
          linkSent: "Chinongedzo chekusimbisa chatumirwa.",
          checkInbox: "Tarisa bhokisi rako remameseji kuti usimbise.",
          sendVerificationLink: "Tumira chinongedzo chekusimbisa",
          sending: "Kutumira...",
          checking: "Kutarisisa...",
          verifiedAction: "Yasimbiswa",
          updated: "Imeyili yavandudzwa",
          notVerifiedYet: "Imeyili haisati yasimbiswa.",
          notAuthenticated: "Unofanira kupinda zvakare.",
          sendCodeFailed: "Hatina kukwanisa kutumira kodhi.",
          codeSent: "Tatumira kodhi ine manhamba matanhatu ku {email}",
          forbidden: "Hauna mvumo yekuita izvi.",
          enterCode: "Isa kodhi",
          codeExpired: "Kodhi yapera nguva.",
          incorrectCode: "Kodhi haina kururama.",
          verifyFailed: "Hatina kukwanisa kusimbisa kodhi.",
          stillSending: "Tichiri kutumira kodhi, mirira zvishoma.",
          waitBeforeResend: "Mirira usati watumirazve.",
          sendDisabled: "Kutumira kwambomiswa kwechinguvana.",
          resendIn: "Tumirazve mu {seconds}s",
          sendCode: "Tumira kodhi",
          enterSentCode: "Isa kodhi yatakutumira",
          codeLabel: "Kodhi",
          codePlaceholder: "000000",
          verifying: "Kusimbisa...",
          verify: "Simbisa",
        },
        address: {
          title: "Chinja kero",
          subtitle: "Chengeta kero yako yemazuva ose iri yazvino.",
          locationUnavailable: "Nzvimbo haina kuwanikwa pano.",
          lookupFailed: "Hatina kukwanisa kutsvaga kero yako.",
          permissionDenied: "Mvumo yenzvimbo yarambwa.",
          locationTimeout: "Kutora nzvimbo kwatora nguva yakawandisa.",
          locationFailed: "Hatina kukwanisa kuwana nzvimbo yako.",
          signInRequired: "Unofanira kupinda kuti uchengete kero.",
          saveFailed: "Hatina kukwanisa kuchengetedza kero yako.",
          locating: "Kutsvaga nzvimbo...",
          useCurrentLocation: "Shandisa nzvimbo yangu yazvino",
          line1Label: "Kero",
          line1Placeholder: "Nyora kero",
          line2Label: "Kero (mutsara 2)",
          line2Placeholder: "Furati, yuniti kana chivako",
          postalLabel: "Kodhi yepositi",
          postalPlaceholder: "0000",
          countryLabel: "Nyika",
          quickFillTitle: "Shandisa ruzivo urwu here?",
          quickFillBodyPatient: "AfriMed yawana kero inofungidzirwa kubva panzvimbo yako.",
          quickFillBodyDoctor: "AfriMed yawana kero inofungidzirwa yekiriniki yako kubva panzvimbo yako.",
          quickFillAction: "Ehe, zadza nekukurumidza",
          locationNameLabel: "Zita renzvimbo",
          locationNamePlaceholder: "Semuenzaniso imba kana kliniki",
          buildingTypeLabel: "Rudzi rwechivako",
          buildingTypePlaceholder: "Sarudza rudzi rwechivako",
          doctorLine1Placeholder: "Nyora kero yekiriniki",
          cityLabel: "Guta",
          cityPlaceholder: "Nyora guta",
          buildingTypeDescription: "Tibatsire kunzwisisa nzvimbo iyi.",
          addBuildingTypeLabel: "Wedzera rudzi rwechivako",
          addBuildingTypePlaceholder: "Tsika rudzi rwechivako",
          permissionsBlocked: "Mvumo yenzvimbo yakavharwa pachishandiso ichi.",
          distanceAway: "{distance} km kure",
        },
        formation: {
          title: "Chinja dzidzo",
          catalogHidden:
            "Mazano ezvikoro, zvidzidzo, uye minda yekudzidza achiri mudata remutauro mumwe chete uye avanzwa mumutauro uyu parizvino.",
          loadFailed: "Hatina kukwanisa kurodha dzidzo iyi.",
          saveFailed: "Hatina kukwanisa kuchengetedza shanduko. Edza zvakare.",
          deleteFailed: "Hatina kukwanisa kubvisa dzidzo iyi. Edza zvakare.",
          institutionLabel: "Sangano redzidzo*",
          institutionPlaceholder: "Nyora sangano rako redzidzo",
          degreeLabel: "Chitupa chedzidzo*",
          degreePlaceholder: "Nyora chitupa chako",
          fieldLabel: "Munda wekudzidza*",
          fieldPlaceholder: "Nyora munda wako wekudzidza",
          startLabel: "Zuva rekutanga*",
          endLabel: "Zuva rekupedza*",
          datePlaceholder: "DD MMM, YYYY",
          achievementLabel: "Mhedzisiro yedzidzo",
          achievementPlaceholder: "Nyora mhedzisiro yako yedzidzo",
          clearInstitution: "Bvisa sangano redzidzo",
          clearDegree: "Bvisa chitupa",
          clearField: "Bvisa munda wekudzidza",
          clearAchievement: "Bvisa mhedzisiro yedzidzo",
          delete: "Bvisa dzidzo",
          deleting: "Kuri kubviswa…",
        },
      },
      profile: {
        title: "Mbiri yako",
        common: {
          editName: "Chinja zita",
          editDob: "Chinja zuva rekuzvarwa",
          editPhone: "Chinja foni",
          editEmail: "Chinja imeyili",
          idPassport: "ID yenyika • Pasipoti",
          address: "Kero",
          languages: "Mitauro",
        },
        patient: {
          editIntro: "Chinja sumo",
          healthRecord: "Rekodhi yangu yezveutano",
          bloodType: "Rudzi rweropa",
          allergies: "Ma-allergy",
          conditions: "Mamiro ezvehutano",
          medications: "Mishonga",
          organDonor: "Mupi wenhengo",
          heightWeight: "Kureba & huremu",
        },
        doctor: {
          editIntro: "Chinja sumo",
          specialty: "Hunyanzvi",
          registrationDetails: "Zvinyorwa zvekunyoresa",
          uploadRegistration: "Isa gwaro rekunyoresa",
          consultancies: "Nzvimbo dzekuonana",
          eVisit: "E-Kuonana",
          availability: "Kuwanikwa",
          focus: "Zvaunotarisisa",
          editFormation: "Chinja dzidzo",
          about: "Nezvako",
        },
      },
      focus: {
        title: "Zvaunotarisisa",
        subtitle: "Ratidza ruzivo rwaunoda kuzivikanwa narwo.",
        empty: "Hausati wawedzera nzvimbo dzaunotarisisa parizvino.",
        add: "Wedzera",
        addTitle: "Wedzera nzvimbo yekutarisisa",
        searchPlaceholder: "Tsvaga",
        clearSearch: "Bvisa kutsvaga",
        addCustom: "Wedzera “{query}” senzvimbo yekutarisisa",
        catalog: {
          acne: "Mapundu echiso",
          rosacea: "Kutsvuka kwechiso kusingaperi",
          hairLoss: "Kudonha kwebvudzi",
          psoriasis: "Chirwere cheganda chine makwapa",
          moleMonitoring: "Kuongorora mamoles",
          eczema: "Kuzvimba kweganda",
          skinCancer: "Gomarara reganda",
          dermatitis: "Kupisa kweganda",
          cosmeticDermatology: "Kurapwa kweganda kwerunako",
          surgery: "Kuvhiya",
        },
      },
      insurancePolicy: {
        title: "Mutemo weinishuwarenzi",
        subtitle: "Wedzera makambani einishuwarenzi aunogamuchira kubva kuvarwere",
        addCompany: "Wedzera kambani yeinishuwarenzi",
      },
      insuranceCompanies: {
        title: "Wedzera kambani yeinishuwarenzi",
        searchPlaceholder: "Tsvaga kana wedzera",
        clearSearch: "Bvisa kutsvaga",
        addCompany: "Wedzera kambani yeinishuwarenzi",
        heading: "Makambani einishuwarenzi",
        noMatches: "Hapana zvakafanana. Tinya + kuti uwedzere “{query}”.",
      },
    },
    prescriptions: {
      fulfillment: {
        title: "Kuzadzikiswa kwefamasi",
        currentState: "Mamiro azvino",
        awaitingSelection: "Kusarudzwa kuchakamirira",
        pharmacy: "Famasi",
        mode: "Nzira",
        nextStepLabel: "Danho rinotevera",
        openThread: "Vhura thread yeMeseji",
        openThreadShort: "Fola",
        payNow: "Bhadhara famasi zvino",
        requestQuote: "Kumbira quote yefamasi",
        selectPharmacy: "Sarudza famasi",
        selectingPharmacy: "Kusarudza famasi…",
        sendingRequest: "Kutumira chikumbiro…",
        cancelOrder: "Kanzura odha yefamasi",
        cancellingOrder: "Kukanzura odha…",
        feedbackSubtitle:
          "Bata danho iri rekuzadzikisa resiphi kuti AfriMed ikwanise kuvhura zvakare mamiriro chaiwo emuedzi.",
        state: {
          awaitingPayment: "Kumirira mubhadharo",
          issued: "Yabudiswa",
          needsNewPharmacy: "Inoda imwe famasi",
          notStarted: "Hazvisati zvatanga",
          paid: "Yabhadharwa",
          paymentReady: "Mubhadharo wagadzirira",
          preparing: "Kugadzirira",
          preparingAccepted: "Yagamuchirwa nefamasi",
          ready: "Yagadzirira kutorwa",
          refundPending: "Refund yakamirira",
          refunded: "Yadzoserwa",
          requestSent: "Chikumbiro chatumirwa",
          reviewing: "Famasi iri kuongorora",
          reviewingShort: "Kuongorora",
        },
        nextStep: {
          chooseAnother: "Sarudza imwe famasi yakanyoresa kuti uenderere mberi.",
          collect: "Tora resiphi pafamasi yakasarudzwa.",
          notStarted: "Sarudza famasi yakanyoresa kuti kutange kuzadzikiswa.",
          pay: "Pedzisa kubhadhara kuti famasi iburitse odha.",
          preparing:
            "Famasi iri kugadzirira odha uye ichakumbira mubhadharo kana kuimaka seyagadzirira.",
          refundPending:
            "Dzokera kuresiphi iyi kuti usimbise kana kudzoserwa kwapera.",
          refunded:
            "Sarudza imwe famasi kana uchiri kuda kuzadzikisa resiphi iyi.",
          requestSent: "Mirira kuti famasi yakasarudzwa igadzirire quote.",
          reviewing:
            "Mirira kuti famasi igamuchire resiphi kana iise mutengo.",
          waitRelease:
            "Mirira kuti famasi iite kuti odha yagadzirira kutorwa.",
        },
        description: {
          notStarted: "Resiphi iyi haisati yatumirwa kufamasi.",
          paid: "Mubhadharo wasimbiswa na {pharmacy}.",
          paymentReady:
            "{pharmacy} yagadzira quote inobhadharika yeresiphi iyi.",
          preparing:
            "{pharmacy} yagamuchira resiphi iyi uye iri kuigadzirira.",
          ready: "{pharmacy} yamaka resiphi iyi seyakagadzirira kutorwa.",
          refundPending: "Oda iyi yefamasi iri kudzorerwa.",
          refunded: "Oda iyi yefamasi yadzoserwa.",
          rejected: "{pharmacy} yatadza kuzadzisa resiphi iyi.",
          requestSent: "{pharmacy} yakumbirwa kugadzirira quote ye{mode}.",
          reviewing: "{pharmacy} iri kuongorora resiphi iyi.",
        },
        errors: {
          request:
            "Hatina kukwanisa kukumbira kuzadzikiswa kwefamasi pari zvino.",
        },
      },
      qrOverlay: {
        fallbackHelp:
          "Unoda imwe nzira here? Vhura nzira yekusimbisa pano kana kukopa link kuti uiongorore nemaoko.",
        openVerifyPage: "Vhura peji rekusimbisa",
        linkCopied: "Link yakopiwa",
        copyFailed: "Kukopa kwatadza",
        copyLink: "Kopa link",
      },
      wizard: {
        reviewPrescription: "Ongorora resiphi",
        reviewSubtitle: "Pane mishonga {count} yakagadzirira kuongororwa.",
        signSubtitle: "Simbisa pfupiso yeresiphi, wobva wasaina kamwe chete usati waburitsa.",
        savedMedicines: "Mishonga yakachengetwa",
        builderHelp: "Pedzisa mashoko emushonga, wobva waichengeta muresiphi.",
        medicationNote: "Chinyorwa chemushonga",
        medicationNotePlaceholder: "Chinyorwa chemushonga ichi kana chiripo",
        medicines: "Mishonga",
        medicinesCount: "Mishonga {count}",
        addAnotherMedicine: "Wedzera mumwe mushonga",
        moveUp: "Endesa kumusoro",
        moveDown: "Endesa pasi",
        saveMedicine: "Chengeta mushonga",
        diagnosisPlaceholder: "Isa chirwere chiri kurapwa",
        referenceImages: "Mifananidzo yereferensi",
        referenceImagesSubtitle:
          "Chengeta mifananidzo yepakeji kana label yakabatanidzwa nesession iyi yemushonga.",
      },
    },
  },
  zu: {
    common: {
      back: "Buyela",
      continue: "Qhubeka",
      discard: "Lahla",
      changesSaved: "Izinguquko zilondoloziwe",
      keepEditing: "Qhubeka nokuhlela",
      saving: "Kuyalondolozwa...",
      loading: "Iyalayisha...",
      edit: "Hlela",
      add: "Engeza",
      all: "Konke",
      showAll: "Bonisa konke",
      manage: "Phatha",
      open: "Vula",
      change: "Shintsha",
      save: "Londoloza",
      delete: "Susa",
      cancel: "Khansela",
      close: "Vala",
      map: "Imephu",
      yes: "Yebo",
      no: "Cha",
      on: "Kuvuliwe",
      off: "Kuvaliwe",
      verified: "Kuqinisekisiwe",
      pendingVerification: "Ukuqinisekiswa kusalindile",
      download: "Landa",
      clear: "Sula",
      review: "Buyekeza",
      refresh: "Vuselela",
      done: "Kuphelile",
      selectPlaceholder: "Khetha...",
      comingSoon: "Kuyeza maduze",
      availableSoon: "Kuyeza maduze",
      reviewRequired: "Kudingeka ukubuyekezwa",
      logout: "Phuma",
      loggingOut: "Kuyaphunywa...",
      cookies: {
        title: "Izinketho zama-cookie ze-AfriMed™",
        intro:
          "Khetha indlela ama-cookie angaphoqelekile azosetshenziswa ngayo. Lezi zigaba zivaliwe ekuqaleni. Hlela ama-cookie akho ngezansi.",
        support:
          "Kwakhelwe ukusekela amazinga e-GDPR nemigomo ye-African Union yokuvikela idatha ukuze kube nokucaca, inhloso ecacile, nokulawula komsebenzisi.",
        close: "Vala izinketho zama-cookie",
        alwaysActive: "Kuhlala kusebenza",
        rejectOptional: "Yenqaba okungaphoqelekile",
        saveChoices: "Londoloza okukhethile",
        acceptAll: "Vuma konke",
        categories: {
          strictly: {
            label: "Ama-cookie abalulekile kuphela",
            body:
              "Ayadingeka ekusebenzeni okuyisisekelo njengokuvikeleka, ukuqhubeka kweseshini, kanye nokufinyeleleka. Lawa ma-cookie ahlala esebenza.",
          },
          statistics: {
            label: "Ama-cookie ezibalo",
            body:
              "Alinganisa ukusetshenziswa okuhlanganisiwe ukuze sithuthukise ikhwalithi yesevisi nokuqonda kwezempilo yomphakathi. Asidayisi idatha yomuntu.",
          },
          performance: {
            label: "Ama-cookie okusebenza",
            body:
              "Asisiza ukulandelela isivinini, ukuzinza, nokwethembeka kwesevisi kumadivayisi namanethiwekhi ahlukene.",
          },
          functionality: {
            label: "Ama-cookie wokusebenza komuntu",
            body:
              "Akhumbula okuthandayo njengolimi nesifunda ukuze ukubuyela kwakho kube lula kakhulu.",
          },
          targeting: {
            label: "Ama-cookie okukhankasa",
            body:
              "Asetshenziswa ukuletha noma ukunciphisa ukubaluleka kwemikhankaso. Asidayisi idatha yomuntu.",
          },
          social: {
            label: "Ama-cookie ezinkundla zokuxhumana",
            body:
              "Enza i-social login nokuqukethwe kwezinkundla zokuxhumana kusebenze. Abahlinzeki bangaphandle bangase basebenzise imigomo yabo.",
          },
          beacons: {
            label: "Ama-web beacon",
            body:
              "Amathegi amancane asetshenziswa ukuqonda ukulethwa nokuzibandakanya kwemiyalezo lapho kudingeka khona.",
          },
        },
      },
      datePicker: {
        enterDate: "Faka usuku",
        today: "Namuhla",
        yesterday: "Izolo",
        last7Days: "Izinsuku ezi-7 ezedlule",
        selectDate: "Khetha usuku",
        selectMonth: "Khetha inyanga",
        selectYear: "Khetha unyaka",
        toggleCalendar: "Shintsha ikhalenda",
        calendar: "Ikhalenda",
        previousMonth: "Inyanga edlule",
        nextMonth: "Inyanga elandelayo",
        displayDate: "{day} {month} {year}",
        headlineDate: "{weekday}, {day} {month}",
        monthYear: "{month} {year}",
        months: {
          january: "Januwari",
          february: "Febhuwari",
          march: "Mashi",
          april: "Ephreli",
          may: "Meyi",
          june: "Juni",
          july: "Julayi",
          august: "Agasti",
          september: "Septhemba",
          october: "Okthoba",
          november: "Novemba",
          december: "Disemba",
        },
        weekdays: {
          sunday: "Is",
          monday: "Ms",
          tuesday: "Bi",
          wednesday: "Th",
          thursday: "Si",
          friday: "Hl",
          saturday: "Mg",
        },
      },
      signature: {
        title: "Isiginesha",
        subtitle:
          "Sayina kanye. I-AfriMed™ izosebenzisa lesi siginesha kuwo wonke amarisiphi akhishwayo.",
        signInToAdd: "Sicela ungene ukuze ungeze isiginesha yakho.",
        signInToSave: "Sicela ungene ukuze ulondoloze isiginesha yakho.",
        loadFailed: "Asikwazanga ukulayisha isiginesha.",
        readFailed: "Asikwazanga ukufunda isithombe.",
        addFirst: "Sicela ungeze isiginesha yakho kuqala.",
        saveFailed: "Asikwazanga ukulondoloza isiginesha.",
        saved: "Isiginesha ilondoloziwe.",
        current: "Isiginesha yamanje",
        alt: "Isiginesha kadokotela",
        storedAt: "Igciniwe ku",
        none: "Akukabi khona siginesha elondoloziwe.",
        draw: "Dweba isiginesha yakho",
        upload: "Layisha",
        camera: "Ikhamera",
        save: "Londoloza isiginesha",
        tip:
          "Icebo: i-stylus noma ipeni eline-rubber tip livame ukusebenza kangcono kunomunwe. Pressure iyasekelwa uma idivayisi iyinikeza.",
        imageLoadFailed: "Asikwazanga ukulayisha isithombe.",
      },
      legal: {
        terms: "Imigomo nemibandela",
        privacy: "Inqubomgomo yobumfihlo",
        dataDeletion: "Ukusulwa kwedatha",
        copyright:
          "© 2026 AfriMed™ Telehealth. I-HIPAA ne-GDPR zihambisana nengqalasizinda yedijithali yezempilo.",
      },
    },
    routeFallback: {
      loadingPatientSettings: "Kulayishwa izilungiselelo zesiguli...",
      loadingDoctorSettings: "Kulayishwa izilungiselelo zikadokotela...",
    },
    onboarding: {
      idPassport: {
        title: "I-ID kazwelonke • Iphasipoti",
        subtitle:
          "Faka inombolo yakho ye-ID/iphasipoti noma umugqa we-MRZ (izinhlamvu ezingu-30).",
        continue: "Qhubeka",
        verifying: "Kuyaqinisekiswa...",
        label: "I-ID / Iphasipoti",
        placeholder: "A12345678  ·  noma umugqa we-MRZ",
        done: "Kuphelile",
        invalidHint:
          "Sebenzisa inombolo yepasipoti noma unamathisele umugqa we-MRZ onezinhlamvu ezingu-30.",
        processingTitle: "Ukucutshungulwa kwe-ID",
        processingPending: "I-doc yakho iyahlaziywa...",
        processingIdle: "Thatha isithombe noma ulayishe iskena esicacile",
        working: "Kuyasebenza...",
        retake: "Phinda uthwebule",
        camera: "Ikhamera",
        uploadFile: "Layisha ifayela",
        aiProcessing: "Ukucutshungulwa kwe-AI",
        sessionNotReady: "Iseshini ayikalungi. Sicela ungene futhi.",
        fileTooLarge:
          "Ifayela likhulu kakhulu. Sicela ulayishe isithombe esingaphansi kuka-5 MB.",
        uploading: "Kulayishwa idokhumenti...",
        receivedExtracting: "Idokhumenti itholiwe. Kukhishwa imininingwane...",
        uploadFailed: "Ukulayisha kuhlulekile. Sicela uzame futhi.",
        retention: "Ubumfihlo: isitoreji esibethelwe siphela ngo-{date}.",
        picker: {
          files: "Layisha kumafayela",
          camera: "Thatha isithombe",
          remove: "Susa",
        },
        status: {
          pending: "I-doc yakho iyahlaziywa...",
          done: "Ubuwena butholiwe",
          failed: "Ukucutshungulwa kuhlulekile",
          received: "Idokhumenti itholiwe",
          idle: "Iyacutshungulwa...",
        },
        alt: {
          passed: "Kuphasile",
          failed: "Kuhlulekile",
          received: "Kutholiwe",
        },
        summary: {
          surname: "Isibongo",
          idNumber: "Inombolo ye-ID",
          dob: "Usuku lokuzalwa",
          expiry: "Usuku lokuphelelwa",
          givenNames: "Amagama okuqala",
        },
      },
    },
    legal: {
      common: {
        category: "Ezomthetho",
        effectiveLabel: "Iqala ukusebenza",
        effectiveDate: "5 Mashi 2026",
        empty:
          "Okuqukethwe kwezomthetho akutholakali okwamanje. Sicela uzame futhi maduze.",
      },
      terms: {
        title: "Imigomo nemibandela",
        subtitle:
          "Imithetho yokusebenzisa izinsiza ze-AfriMed™, okubalwa ukusetshenziswa kwe-akhawunti, ukufinyelela enkonzweni, ukuziphatha kupulatifomu, kanye nezibopho zomthetho.",
        sections: {
          acceptanceOfTerms: {
            title: "Ukwamukela imigomo",
            body: {
              1: "Ngokufinyelela noma ngokusebenzisa izinsiza ze-AfriMed™, uyavuma le Migomo nemibandela kanye nanoma yimiphi imigomo efanele ekhonjwe lapha.",
              2: "Uma ungavumelani nayo, ungayisebenzisi le sevisi.",
            },
          },
          eligibilityAndAccountRegistration: {
            title: "Ukufaneleka nokubhaliswa kwe-akhawunti",
            body: {
              1: "Kufanele unikeze imininingwane eqondile lapho ubhalisa futhi ugcine imininingwane yokungena kwi-akhawunti yakho iphephile.",
              2: "Unesibopho semisebenzi eyenzeka ngaphansi kwe-akhawunti yakho ngaphandle uma ibangelwe ukwehluleka kokuvikeleka kwethu.",
            },
          },
          serviceScope: {
            title: "Ububanzi bensizakalo",
            body: {
              1: "I-AfriMed™ inikeza ingqalasizinda yedijithali yezempilo, okubalwa ukuxhumana kwe-telehealth kanye nezinqubo zokuhlela ukunakekelwa.",
              2: "I-AfriMed™ ayithathi indawo yezinsiza eziphuthumayo. Ezimeni eziphuthumayo noma ezisongela impilo, xhumana nezinsiza eziphuthumayo zendawo ngokushesha.",
            },
          },
          appointmentsBookingsAndCancellations: {
            title: "Ama-aphoyintimenti, ukubhukha, nokukhansela",
            body: {
              1: "Ukutholakala kwe-aphoyintimenti, ukuhlela, ukushintsha, namafasitela okukhansela kungancika kudokotela, esikhungweni, nasezinqubomgomo zokusebenza.",
              2: "Abasebenzisi kufanele banikeze imininingwane efanele yokubhukha futhi benze ngobuqotho lapho behlela kabusha noma bekhansela.",
            },
          },
          paymentsAndBilling: {
            title: "Izinkokhelo nokukhokhisa",
            body: {
              1: "Lapho izinsiza ezikhokhelwayo zisebenza khona, izimali, imigomo yokukhokhisa, nezindlela zokukhokha zizovezwa ezindaweni zomkhiqizo ezifanele noma kuzilungiselelo zomnikezeli.",
              2: "Uyagunyaza ukukhokhiswa kwemali okuhambisana nezinsiza ozikhethayo nozisebenzisayo.",
            },
          },
          userResponsibilitiesAndAcceptableUse: {
            title: "Izibopho zomsebenzisi nokusetshenziswa okwamukelekayo",
            body: {
              1: "Uyavuma ukuthi awusoze wasebenzisa kabi ipulatifomu, uphazamise izilawuli zokuphepha, uthumele imininingwane yomgunyathi, noma usebenzise i-AfriMed™ ngezinto ezingekho emthethweni.",
              2: "Abasebenzisi bezempilo kufanele bagcine amazinga afanele omsebenzi nobumfihlo.",
            },
          },
          intellectualProperty: {
            title: "Impahla yengqondo",
            body: {
              1: "Izimpawu zokuhweba ze-AfriMed™, umklamo womkhiqizo, okuqukethwe, kanye nesoftware yepulatifomu kuvikelwe yimithetho efanele yempahla yengqondo.",
              2: "Awuvunyelwe ukukopisha, ukuhlehla uhlaziye, noma ukusabalalisa kabusha izinto ezivikelekile ngaphandle kwalokho okuvunyelwe ngumthetho noma ngokugunyazwa okubhaliwe.",
            },
          },
          thirdPartyServices: {
            title: "Izinsiza zabanye abantu",
            body: {
              1: "Ezinye izindlela zingathembela ezinsizeni ezithembekile zabanye abantu, isibonelo ukuqinisekiswa kobuwena, ingqalasizinda, ukuxhumana, noma abahlinzeki bamamephu.",
              2: "Izinsiza zabo zingase zilawulwe imigomo yabo kanye nezaziso zabo zobumfihlo.",
            },
          },
          privacyAndDataHandling: {
            title: "Ubumfihlo nokuphathwa kwedatha",
            body: {
              1: "Ukusetshenziswa kwedatha yomuntu kulawulwa Inqubomgomo yobumfihlo ye-AfriMed™.",
              2: "Ngokusebenzisa ipulatifomu, uyavuma ukucutshungulwa kwedatha okudingekayo ekuqinisekisweni kobuwena, ekulethweni kwezinsiza zezempilo, kanye nasezinqubweni zokuhambisana nomthetho.",
            },
          },
          disclaimersAndLimitationOfLiability: {
            title: "Izixwayiso nemingcele yesibopho",
            body: {
              1: "I-AfriMed™ inikezwa 'njengoba injalo', ngaphansi kokulungiswa, imingcele yokusebenza, kanye nokuncika ezintweni zangaphandle.",
              2: "Kuze kufike lapho umthetho uvumela khona, isibopho silinganiselwe ngomonakalo ongathinteki ngqo, ongazelelwe, noma olandelanayo ovela ekusetshenzisweni kwepulatifomu.",
            },
          },
          suspensionAndTermination: {
            title: "Ukumiswa nokuqedwa",
            body: {
              1: "Singamisa noma siqede ukufinyelela lapho kudingeka khona ngenxa yokuphepha, ukuhambisana nomthetho, ukwephulwa kwenqubomgomo, noma ukuvimbela ukuhlukunyezwa.",
              2: "Ungayeka ukusebenzisa insiza noma kunini, ngokuya ngezibopho zomthetho nezivumelwano ezisasebenza.",
            },
          },
          governingLawAndUpdates: {
            title: "Umthetho olawulayo nezibuyekezo",
            body: {
              1: "Le migomo ilawulwa yimithetho nemigomo esebenza ekusebenzeni kwe-AfriMed™ nasezindaweni lapho izinsiza zinikezwa khona.",
              2: "Singabuyekeza le migomo; inguqulo yakamuva nosuku oluqala ngalo ukusebenza kushicilelwa kuleli khasi.",
            },
          },
          contact: {
            title: "Ukuxhumana",
            body: {
              1: "AfriMed™ Telehealth",
              2: "I-imeyili: info@afrimedtelehealth.com",
              3: "Iwebhusayithi: https://afrimedtelehealth.com",
            },
          },
        },
      },
      privacy: {
        title: "Inqubomgomo yobumfihlo",
        subtitle:
          "Indlela i-AfriMed™ eqoqa ngayo, esebenzisa ngayo, evikela ngayo, futhi elawula ngayo idatha yomuntu kuwo wonke amapulatifomu ethu e-telehealth.",
        sections: {
          whoWeAre: {
            title: "Singobani",
            body: {
              1: "I-AfriMed™ Telehealth inikeza izinsiza zezempilo zedijithali ezigulini nasezisebenzi zezempilo, okubalwa izinqubo ze-teleconsultation, ukuhlela ama-aphoyintimenti, imiyalezo evikelekile, ukusethwa kwephrofayela ekuqaleni, kanye nemisebenzi yezempilo esekelwa ukuqinisekiswa.",
              2: "Le Nqubomgomo yobumfihlo isebenza kudatha yomuntu eqoqwa ngewebhu ne-app ye-AfriMed™, okubalwa amadomain okuhlola nawokukhiqiza.",
            },
          },
          informationWeCollect: {
            title: "Ulwazi esiluqoqayo",
            body: {
              1: "Siqoqa idatha ye-akhawunti (igama, i-imeyili, ucingo, indima), idatha yephrofayela nokusethwa kokuqala (isibonelo isimo sabantu kanye nomongo wezokwelapha owunikezayo), idatha yomsebenzi wensiza (ama-aphoyintimenti, imiyalezo, izilungiselelo), kanye nedatha yobuchwepheshe (metadata yedivayisi ne-browser, amalogi, nezimpawu zokuphepha).",
              2: "Lapho ulayisha amadokhumenti ezempilo noma okuqinisekisa, sicubungula lawo mafayela nemetadata ehambisana nawo ukuze kuqinisekiswe i-akhawunti, izinqubo zokuhambisana nomthetho, nokulethwa kwensiza.",
            },
          },
          howWeUseYourInformation: {
            title: "Indlela esisebenzisa ngayo ulwazi lwakho",
            body: {
              1: "Sisebenzisa idatha yomuntu ukuze sakhe futhi sivikele ama-akhawunti, siqinisekise abasebenzisi, silethe izici ze-telehealth, sisekele ama-aphoyintimenti nokuxhumana, sithuthukise ukwethembeka, sithole ukuhlukunyezwa, futhi sihambisane nezibopho zomthetho nezempilo.",
              2: "Singasebenzisa i-operational analytics ukuthuthukisa ikhwalithi yensiza nokuhlangenwe nakho komsebenzisi ngenkathi sisebenzisa izindlela zokunciphisa idatha nokulawula ukufinyelela.",
            },
          },
          howWeProtectYourInformation: {
            title: "Indlela esivikela ngayo ulwazi lwakho",
            body: {
              1: "I-AfriMed™ isebenzisa izivikelo zobuchwepheshe nezokuphatha ezinezendlalelo, okubalwa izilawuli zokufinyelela, ukubethelwa kwedatha ngesikhathi sokudlulisa, ukugunyazwa okususelwa endimeni, ukuqapha, kanye nezinqubo ezilungele ukubuyekeza.",
              2: "Alukho uhlelo lwe-inthanethi olungenabungozi ngokuphelele; siqhubeka sithuthukisa izilawuli zokunciphisa ubungozi bokuphepha nobumfihlo.",
            },
          },
          howWeShareInformation: {
            title: "Indlela esabelana ngayo ngolwazi",
            body: {
              1: "Sabelana ngedatha kuphela lapho kudingeka khona ekulethweni kwensiza, ekuhambisaneni nomthetho, ezinqubweni eziqondiswe ngumsebenzisi, noma kubasebenzisi abathembekile abangaphansi kwezibopho zobumfihlo nokuvikelwa kwedatha.",
              2: "Asithengisi ulwazi lomuntu.",
            },
          },
          dataRetention: {
            title: "Ukugcinwa kwedatha",
            body: {
              1: "Sigcina idatha yomuntu isikhathi esidingekayo ukuze silethe izinsiza, sihlangabezane nezibopho zomthetho nezokulawula, sixazulule izingxabano, siphoqelele izivumelwano, futhi sigcine amarekhodi okubuyekeza.",
              2: "Izikhathi zokugcinwa zingahluka ngokuya ngohlobo lwedatha nezidingo zendawo.",
            },
          },
          cookiesAndSimilarTechnologies: {
            title: "Amakhukhi nobuchwepheshe obufanayo",
            body: {
              1: "Sisebenzisa amakhukhi abalulekile nobuchwepheshe obufanayo ukuze kube nokuphepha, ukuqhubeka kweseshini, nokusebenza komkhiqizo.",
              2: "Lapho kusebenza khona, izigaba ezingakhethwa zilawulwa ngezilawuli zokhetho lwamakhukhi ezitholakala kuhlelo lokusebenza.",
            },
          },
          yourDataRights: {
            title: "Amalungelo akho edatha",
            body: {
              1: "Ngokwezomthetho wendawo okuyo, ungaba namalungelo okufinyelela, ukulungisa, ukususa, ukuvimba, ukuphikisa, noma ukudlulisa idatha ethile yomuntu.",
              2: "Ungaphinde ucele ukwesekwa kwe-akhawunti nobumfihlo ngeziteshi zethu zokuxhumana ezibalwe ngezansi.",
            },
          },
          childrensPrivacy: {
            title: "Ubumfihlo bezingane",
            body: {
              1: "I-AfriMed™ ayihloselwe ukusetshenziswa okungagadiwe izingane lapho umthetho ukwenqabela khona.",
              2: "Uma ukholwa ukuthi idatha inikezwe ngendlela engafanele, xhumana nathi futhi sizobuyekeza bese senza ngokuhambisana nezibopho zomthetho.",
            },
          },
          internationalDataUse: {
            title: "Ukusetshenziswa kwedatha kwamazwe ngamazwe",
            body: {
              1: "I-AfriMed™ ingacubungula idatha ezindaweni ezahlukene njengoba kudingeka ukuze insiza isebenze ngokuphepha.",
              2: "Lapho kudingeka ukudluliswa kwemingcele yamazwe, sisebenzisa izivikelo ezifanele ezihambisana nomthetho osebenzayo.",
            },
          },
          changesToThisPolicy: {
            title: "Izinguquko kule nqubomgomo",
            body: {
              1: "Singabuyekeza le Nqubomgomo yobumfihlo njengoba ipulatifomu, imithetho, noma izidingo zokuphepha ziguquka.",
              2: "Izibuyekezo ezibalulekile zizobonakala ngokubuyekezwa kosuku lokuqala ukusebenza nokushicilela inguqulo ebuyekeziwe kuleli khasi.",
            },
          },
          contactUs: {
            title: "Xhumana nathi",
            body: {
              1: "AfriMed™ Telehealth",
              2: "I-imeyili: info@afrimedtelehealth.com",
              3: "Iwebhusayithi: https://afrimedtelehealth.com",
            },
          },
        },
      },
      dataDeletion: {
        title: "Imiyalelo yokusulwa kwedatha",
        subtitle:
          "Indlela abasebenzisi be-AfriMed™ abangacela ngayo ukusulwa kwedatha yepulatifomu exhunywe ku-akhawunti, okubalwa ukuqinisekiswa kobuwena, ububanzi, kanye nokwehlukaniswa okususelwa ekuhambisaneni nomthetho.",
        sections: {
          purposeAndScope: {
            title: "Inhloso nobubanzi",
            body: {
              1: "Leli khasi lichaza indlela abasebenzisi be-AfriMed™ abangacela ngayo ukusulwa kwedatha yepulatifomu exhunywe ku-akhawunti yabo.",
              2: "Idatha yepulatifomu kulokhu ihlanganisa amarekhodi e-akhawunti nephrofayela, amarekhodi okusetshenziswa kwensiza, kanye nemetadata ekhiqizwe uhlelo i-AfriMed™ eliyilawulayo ezinhlelweni zayo.",
            },
          },
          howToRequestDeletion: {
            title: "Indlela yokucela ukusulwa",
            body: {
              1: "Abasebenzisi bangacela ukusulwa ngokuthumela i-imeyili ku info@afrimedtelehealth.com besebenzisa i-imeyili exhunywe ku-akhawunti yabo ye-AfriMed™.",
              2: "Uma i-imeyili ingatholakali, abasebenzisi bangathumela isicelo ngeziteshi zokwesekwa ze-AfriMed™ bese befaka imininingwane yokubona umuntu ukuze kuqinisekiswe.",
              3: "Ukuze kusheshiswe ukucutshungulwa, faka igama lakho eligcwele, i-imeyili ye-akhawunti noma inombolo yocingo, indima yakho (isiguli noma udokotela), kanye nesitatimende esithi: 'Ngicela ukusulwa kwedatha ye-akhawunti yami ye-AfriMed™.'",
            },
          },
          identityVerificationBeforeDeletion: {
            title: "Ukuqinisekiswa kobuwena ngaphambi kokusula",
            body: {
              1: "Ngokuphepha nangokuvikela ubumfihlo bezempilo, i-AfriMed™ iqinisekisa ubuwena bomfakisicelo ngaphambi kokucubungula ukusulwa.",
              2: "Ukuqinisekiswa kungabandakanya ukuqinisekiswa nge-email noma inombolo yocingo ebhalisiwe kanye nezinye izivivinyo lapho kudingeka khona ukuvimbela ukusulwa kwe-akhawunti ngaphandle kwemvume.",
            },
          },
          whatWeDelete: {
            title: "Esikusulayo",
            body: {
              1: "Ngemva kokuqinisekiswa okuyimpumelelo, i-AfriMed™ isula noma isuse ubuwena ngokungenakubuyiselwa kudatha yepulatifomu exhunywe ku-akhawunti engasadingeki ngezibopho zomthetho, zokulawula, zokuphepha, noma zokuvimbela ukukhwabanisa.",
              2: "Izibonelo zingabandakanya imininingwane yephrofayela, amafayela wemidiya angasadingeki, kanye namarekhodi omsebenzi wohlelo ahlobene ne-akhawunti.",
            },
          },
          whatMayBeRetained: {
            title: "Okungase kugcinwe",
            body: {
              1: "Ezinhlelweni zezicelo zokusulwa, i-AfriMed™ ingagcina amarekhodi ambalwa kuze kufike ezinsukwini ezingama-90 ukuze kuqedelwe ukuqinisekiswa, ukuphathwa kwezingxabano, ukuhlolwa kokuphepha, kanye nezivikelo zokusebenza.",
              2: "Ngemva kwalelo windi, ukusulwa kusetshenziswa ngokuzenzakalela ngaphandle uma kukhona ukubamba okuthile komthetho, kokulawula, kokuvimbela ukukhwabanisa, noma kokubuyekeza okudinga ukugcinwa isikhathi eside.",
              3: "Noma iyiphi idatha egciniwe ivinjelwe ukufinyeleleka futhi igcinwa kuphela ngesikhathi sokugcinwa esidingekayo.",
            },
          },
          processingTimeline: {
            title: "Isikhathi sokucutshungulwa",
            body: {
              1: "I-AfriMed™ iyazivuma izicelo zokusulwa futhi iqale ukuqinisekisa nokucubungula ngokushesha okungenzeka ngendlela enengqondo.",
              2: "Izikhathi zokuqedwa zincike ekuqinisekisweni kobuwena nasezibophweni zomthetho, kodwa izicelo ngokuvamile ziqedwa phakathi kwezinsuku ezingama-30.",
            },
          },
          outcomeConfirmation: {
            title: "Ukuqinisekiswa komphumela",
            body: {
              1: "Uma ukucubungula sekuphelile, i-AfriMed™ ithumela impendulo yokuqinisekisa kumfakisicelo.",
              2: "Uma kukhona idatha engenakususwa ngenxa yezibopho zomthetho, i-AfriMed™ ichaza isizathu kanye nesisekelo sokugcinwa esisebenzayo.",
            },
          },
          contactForDataDeletion: {
            title: "Ukuxhumana ngokusulwa kwedatha",
            body: {
              1: "AfriMed™ Telehealth",
              2: "I-imeyili: info@afrimedtelehealth.com",
              3: "I-URL yereferensi: https://staging.afrimed.tech/legal/data-deletion",
            },
          },
        },
      },
    },
    feedback: {
      trigger: "Bika inkinga",
      dialog: {
        title: "Bika inkinga",
        subtitle:
          "Gcina lo mongo we-AfriMed ukuze ithimba likwazi ukubuyela kule ndawo futhi.",
        sending: "Kuyathunyelwa...",
        send: "Thumela umbiko",
        context: "Umongo wamanje",
        role: "Indima",
        section: "Ingxenye",
        entity: "Into",
        note: "Kwenzekeni?",
        placeholder:
          "Bhala kafushane obukulindele nokuthi i-AfriMed ikubonise ini lapha.",
        sent: "Umbiko ugcinwe kanye nomongo wamanje we-AfriMed.",
      },
      errors: {
        save: "Asikwazanga ukugcina umbiko wakho okwamanje.",
      },
    },
    appointments: {
      common: {
        yearsExperience: "iminyaka engu-{count}",
      },
      details: {
        ehr: {
          title: "EHR",
        },
        deleteDialog: {
          title: "Ufuna ukususa le rekhodi ye-aphoyintimenti?",
          description:
            "Uma usususa le rekhodi, ngeke ukwazi ukuyibuyisela.",
          confirm: "Susa",
        },
      },
      book: {
        title: "Khetha usuku, uhlobo nesikhathi",
        doctorMissing:
          "Udokotela akatholakali. Buyela emuva ukhethe udokotela futhi.",
        backToDoctors: "Buyela kodokotela",
        sections: {
          service: "Khetha isevisi",
          type: "Khetha uhlobo lwe-aphoyintimenti",
          location: "Indawo",
          date: "Usuku",
          time: "Isikhathi",
          payment: "Khetha indlela yokukhokha",
        },
        fallbackServices: {
          evisit: "Virtual",
          consultation: "Ukuhlolwa",
        },
        mode: {
          virtual: "Okubonakalayo",
          inPerson: "Ubuso nobuso",
        },
        selectDate: "Khetha usuku",
        minutesShort: "{count} imiz",
        loading: {
          services: "Kulayishwa amasevisi kadokotela...",
          type: "Kulayishwa izinhlobo zama-aphoyintimenti...",
          dates: "Kulayishwa izinsuku ezitholakalayo...",
          times: "Kulayishwa izikhathi ezitholakalayo...",
          payments: "Kulayishwa izindlela zokukhokha zikadokotela...",
        },
        empty: {
          services: "Lo dokotela akakange engeze amasevisi okwamanje.",
          dates: "Lo dokotela akakavuli izinsuku zama-aphoyintimenti okwamanje.",
          unavailableDay: "Lo dokotela akatholakali ngosuku olukhethiwe.",
          noTimes: "Azisekho izikhathi zama-aphoyintimenti ezisele kulolu suku.",
          payments: "Lo dokotela akakabeki izindlela zokukhokha okwamanje.",
          onlinePaymentsOnly:
            "Lo dokotela akakavuli ukukhokha ngekhadi okuphephile ku-inthanethi okwamanje. Ukubhuka kuhlala kuvaliwe kuze kutholakale indlela yokukhokha yangempela.",
        },
        errors: {
          loadServices: "Asikwazanga ukulayisha amasevisi kadokotela.",
          loadDates: "Asikwazanga ukulayisha izinsuku ezingabhukeka.",
          loadTimes: "Asikwazanga ukulayisha izikhathi ezitholakalayo.",
          loadPayments: "Asikwazanga ukulayisha izindlela zokukhokha.",
        },
        evisitUnavailable:
          "Ama-aphoyintimenti akude awakakavulwa kulo dokotela, ngakho ukubhuka kuzohlala kuhambisana nokutholakala kobuso nobuso.",
        virtualVisitNotice:
          "Lokhu kubhuka kuzoba yi-aphoyintimenti ebonakalayo ukuze kube indlela esheshayo elungele isiguli.",
        inpersonFallback:
          "I-aphoyintimenti yobuso nobuso endaweni yokubonana kadokotela.",
        confirm: "Qinisekisa",
      },
      edit: {
        title: "Hlela ama-aphoyintimenti",
        notFound:
          "I-aphoyintimenti ayitholakali. Buyela emuva uyivule futhi.",
        backToAppointments: "Buyela kuma-aphoyintimenti",
        paymentMethodFallback: "Amakhadi edebhithi / esikweletu",
        chooseFacility: "Khetha indawo",
        chooseFacilityError:
          "Sicela ukhethe indawo ye-aphoyintimenti yobuso nobuso.",
        virtualVisitNotice: "Le aphoyintimenti izoba ukuvakasha okubonakalayo.",
        selectDate: "Khetha usuku",
        confirm: "Qinisekisa",
        mode: {
          inPerson: "Ubuso nobuso",
          eVisit: "Virtual",
        },
        sections: {
          type: "Uhlobo",
          location: "Indawo",
          date: "Usuku",
          time: "Isikhathi",
          payment: "Indlela yokukhokha",
        },
        loading: {
          dates: "Kulayishwa izinsuku ezitholakalayo...",
          times: "Kulayishwa izikhathi ezitholakalayo...",
        },
        empty: {
          dates: "Lo dokotela akakavuli izinsuku zama-aphoyintimenti okwamanje.",
          unavailableDay: "Lo dokotela akatholakali ngosuku olukhethiwe.",
          noTimes: "Azisekho izikhathi zama-aphoyintimenti ezisele kulolu suku.",
        },
        errors: {
          loadDates: "Asikwazanga ukulayisha izinsuku ezitholakalayo.",
          loadTimes: "Asikwazanga ukulayisha izikhathi ezitholakalayo.",
        },
      },
      editLocation: {
        title: "Shintsha indawo",
        searchPlaceholder: "Sesha izibhedlela noma amakliniki",
        evisitSubtitle:
          "Shintsha le aphoyintimenti ibe ukuvakasha kwevidiyo okubonakalayo",
        selectEvisit: "Khetha Virtual",
        selectFacility: "Khetha u-{name}",
        empty: "Azikho izindawo ezitholakele.",
        facilityFallback: "Indawo",
        evisitUnavailable:
          "Virtual ayikavulwa kulo dokotela okwamanje, ngakho ukubhuka kuhlala kuhambisana nezindawo zokubonana kobuso nobuso.",
        inpersonUnavailable:
          "Lo dokotela akanayo indawo yokubonana yobuso nobuso etholakalayo yokubhuka okwamanje.",
        errors: {
          loadCsv: "Asikwazanga ukulayisha i-CSV yezindawo",
          emptyCsv: "I-CSV yezindawo ayinalutho",
          load: "Asikwazanga ukulayisha izindawo",
        },
      },
      confirm: {
        title: "Isifinyezo se-aphoyintimenti",
        continueToPayment: "Qhubeka uye ekukhokheni",
        continueToPaymentOptions: "Qhubeka uye kokukhethwa kokukhokha",
        schedule: "Uhlelo",
        patientFallback: "Isiguli",
        doctorFallback: "Udokotela",
        serviceFallback: "Ukuhlolwa",
        firstConsultancy: "Ukuhlolwa kokuqala",
        videoConsultation: "Ukubonana ngevidiyo",
        facilityFallback: "Isibhedlela esizimele",
        inPersonLower: "ubuso nobuso",
        visitTypeChange: "Ukushintsha uhlobo lokuvakasha",
        newTimeRequest: "Isicelo sesikhathi esisha",
        confirming: "Kuyaqinisekiswa...",
        confirm: "Qinisekisa",
        payment: {
          credit: "Amakhadi edebhithi / esikweletu",
          bank: "Ukudluliselwa ebhange",
          cash: "Imali ngesikhathi sesevisi",
          eft: "I-EFT esheshayo",
          wallet: "Ama-e-wallet",
          fallback: "Indlela yokukhokha",
        },
        fields: {
          doctor: "Udokotela",
          service: "Isevisi",
          date: "Usuku",
          time: "Isikhathi",
          amountPaid: "Inani elikhokhwayo",
        },
        errors: {
          invalidDateTime: "Usuku noma isikhathi se-aphoyintimenti asivumelekile.",
          update: "Asikwazanga ukubuyekeza i-aphoyintimenti.",
        },
        notifications: {
          newAppointmentTitle: "I-aphoyintimenti entsha",
          newTimeRequestTitle: "Isicelo sesikhathi esisha",
          visitTypeChangeTitle: "Ukushintsha uhlobo lokuvakasha",
          appointmentUpdateTitle: "Ukuvuselelwa kwe-aphoyintimenti",
          appointmentUpdateMessage:
            "Udokotela wakho uphakamise isikhathi esisha se-aphoyintimenti. Buyekeza bese uqinisekisa ushintsho.",
          modeChangeLead: "ucele ukushintsha kusuka ku",
          modeChangeTo: "kuye ku",
          newTimeLead: "ucele isikhathi esisha se-aphoyintimenti.",
          pendingApproval: "Kulindwe ukuvunywa.",
        },
      },
      past: {
        common: {
          doctorFallback: "Udokotela",
          unknownValue: "—",
          unknownDate: "—",
          monthFallback: "Inyanga {year}",
        },
        list: {
          title: "Ama-aphoyintimenti adlulile",
          searchPlaceholder: "Sesha",
          clearSearch: "Sula",
          loading: "Kulayishwa...",
          empty: "Awekho ama-aphoyintimenti adlulile atholakele.",
          reviewSubmitted: "Isibuyekezo sithunyelwe",
          reviewPending: "Linganisela udokotela nolwazi lwakho",
          tabs: {
            upcoming: "Azayo ({count})",
            past: "Adlulile ({count})",
            pending: "Alindele ukuvunywa ({count})",
          },
        },
        details: {
          title: "Ama-aphoyintimenti adlulile",
          loading: "Kulayishwa i-aphoyintimenti edlulile...",
          notFound: "I-aphoyintimenti edlulile ayitholakali.",
          descriptionFallbackMissed:
            "Le aphoyintimenti iphuthelwe. Akukho ukuhlonza noma amanothi angeziwe.",
          descriptionFallback:
            "Akukho ukuhlonza okubhaliwe kule aphoyintimenti.",
          videoConsultation: "Ukubonana ngevidiyo",
          locationFallback: "Indawo yokubonana",
          paymentFallback: "Amakhadi edebhithi / esikweletu",
          paymentPending: "Inkokhelo ilindile",
          bookAgain: "Bhuka futhi",
          mode: {
            inPerson: "Ubuso nobuso",
            virtual: "Okubonakalayo",
          },
          sections: {
            time: "Isikhathi se-aphoyintimenti",
            status: "Isimo",
            description: "Incazelo ye-aphoyintimenti",
            payment: "Indlela yokukhokha",
          },
          status: {
            completed: "Kuqediwe",
            missed: "Kuphuthelwe",
            rescheduled: "Kuhlelwe kabusha",
            declined: "Kwenqatshiwe",
            cancelled: "Kukhanseliwe",
            confirmed: "Kuqinisekisiwe",
            scheduled: "Kuhleliwe",
            pending: "Kulindwe",
            past: "Kudlulile",
            completedHelp: "Lokhu kubonisana kuqediwe.",
            missedHelp:
              "Le aphoyintimenti iphuthelwe. Iwindi lokubonana ngevidiyo livalekile ngaphambi kokuba ukuvakashelwa kuqale.",
            rescheduledHelp:
              "Lesi sikhala se-aphoyintimenti sathuthelwa kwesinye isikhathi esisha esivunyelwe.",
            declinedHelp:
              "Le aphoyintimenti yenqatshiwe ngaphambi kokuba ukubonisana kwenzeke.",
            cancelledHelp:
              "Le aphoyintimenti ikhanseliwe ngaphambi kokuba ukubonisana kwenzeke.",
            pendingCloseoutHelp:
              "Le aphoyintimenti isidlulile kodwa ayikakaphawulwa njengeqediwe.",
            rescheduleHistory:
              "Yaqale yabhalelwa u {oldDate}. Kamuva yahlelelwa kabusha ku {newDate}.",
          },
          errors: {
            load: "Asikwazanga ukulayisha i-aphoyintimenti edlulile.",
          },
        },
        review: {
          section: "Isibuyekezo sikadokotela",
          title: "Linganisela udokotela nolwazi lwakho",
          subtitle:
            "Impendulo yakho isiza ezinye iziguli zenze izinqumo ezinolwazi ngemva kwama-aphoyintimenti aqediwe.",
          submitted: "Isibuyekezo sithunyelwe",
          lastUpdated: "Kugcine ukubuyekezwa ngo {date}",
          loading: "Kulayishwa isibuyekezo sakho...",
          doctorPrompt: "Ungamlinganisa kanjani udokotela wakho?",
          doctorHint: "Cabanga ngokucaca, ukunakekela, nobungcweti.",
          experiencePrompt: "Ungakulinganisa kanjani konke okwenzekile kuwe?",
          experienceHint:
            "Cabanga ngokubhuka, isikhathi, nokuthi ukuvakasha kuhambe kahle kangakanani.",
          textPrompt: "Sitshele okwengeziwe ngolwazi lwakho",
          textPlaceholder:
            "Yabelana ngalokho okugqame kakhulu ngale aphoyintimenti. Kungaba kufushane.",
          textHelper: "{count}/1200 izinhlamvu",
          savedHint:
            "Isibuyekezo sakho silondoloziwe. Shintsha izilinganiso noma incazelo uma ufuna ukusihlela.",
          reset: "Setha kabusha",
          updateCta: "Buyekeza isibuyekezo",
          savedCta: "Isibuyekezo silondoloziwe",
          submitCta: "Thumela isibuyekezo",
          updated: "Isibuyekezo sakho sibuyekeziwe.",
          saved: "Isibuyekezo sakho sithunyelwe.",
          unavailableMissed:
            "Isibuyekezo sikadokotela asitholakali ku-aphoyintimenti ephuthelwe.",
          unavailableRescheduled:
            "Isibuyekezo sikadokotela asitholakali esikhathini esihlelwe kabusha.",
          unavailableCancelled:
            "Isibuyekezo sikadokotela asitholakali ku-aphoyintimenti ekhanseliwe.",
          unavailablePendingCloseout:
            "Isibuyekezo sikadokotela sitholakala kuphela ngemva kokuba i-aphoyintimenti imakwe njengeqediwe.",
          errors: {
            load:
              "Asikwazanga ukulayisha isibuyekezo sakho sikadokotela sale aphoyintimenti.",
            save: "Asikwazanga ukulondoloza isibuyekezo sakho okwamanje.",
          },
        },
      },
      checkout: {
        title: "Ukukhokha okuphephile",
        notFound:
          "Imininingwane yokubhuka ayitholakali. Buyela emuva uqale futhi.",
        steps: {
          appointment: "I-aphoyintimenti",
          payAndBook: "Khokha ubhuke",
          paymentOptions: "Izinketho zokukhokha",
          sponsorPayment: "Inkokhelo yomxhasi",
        },
        paymentDetails: "Imininingwane yokukhokha",
        cardTitle: "Ukukhokha ngekhadi",
        cardIntro:
          "Faka imininingwane yekhadi ukuze uqinisekise i-aphoyintimenti ngokuphepha. Isikhala osikhethile sihlala sigciniwe ngenkathi uqeda ukukhokha noma ushintshela ekuxhasweni.",
        savedCards: "Amakhadi agciniwe",
        loadingSavedCards: "Kulayishwa amakhadi akho agciniwe...",
        savedCardLabel: "{brand} ephela ngo {last4} · iphela {month}/{year}",
        cardBrandFallback: "Ikhadi",
        useSavedCardHint:
          "Sebenzisa leli khadi eligciniwe ngaphandle kokuthayipha inombolo futhi.",
        useDifferentCard: "Sebenzisa elinye ikhadi",
        loadingCardForm: "Kulayishwa ifomu lekhadi eliphephile...",
        rememberCard:
          "Khumbula imininingwane yekhadi lesikweletu esikhathini esizayo",
        processing: "Kuyacutshungulwa...",
        payAndBook: "Khokha ubhuke",
        chooseDifferentMethod: "Khetha enye indlela yokukhokha",
        refresh: "Vuselela imininingwane yokukhokha",
        summary: "Isifinyezo",
        summaryHelp:
          "Gcina isikhala osikhethile siqinile ngenkathi uqinisekisa inkokhelo.",
        total: "Ingqikithi",
        fields: {
          doctor: "Udokotela",
          service: "Isevisi",
          appointment: "I-aphoyintimenti",
          paymentMethod: "Indlela yokukhokha",
        },
        doctorFallback: "Udokotela wase-AfriMed",
        chooseSlot: "Khetha isikhala",
        sponsorOption: "Inketho yomxhasi",
        sponsorTitle: "Sebenzisa isixhumanisi esivikelekile somxhasi",
        sponsorHelp:
          "Umndeni, abagcini, noma abaxhasi basemazweni angaphandle bangakhokhela ukuvakasha ngaphandle kokuthi isiguli siqale kabusha ukubhuka.",
        prepareSponsorPayment: "Lungisa inkokhelo yomxhasi",
        selectedAppointment: "I-aphoyintimenti ekhethiwe",
        hideDetails: "Fihla imininingwane",
        viewDetails: "Buka imininingwane",
        sponsorFinalising:
          "Kuqedwa inkokhelo yomxhasi futhi kubuyiselwa i-aphoyintimenti...",
        retrySponsorSync: "Zama futhi ukuvumelanisa inkokhelo yomxhasi",
        methodNotSupported:
          "Le ndlela ayikaqoqi inkokhelo ngaphakathi kwe-AfriMed okwamanje, ngakho i-aphoyintimenti ayiqinisekiswa lapha. Khetha ukukhokha ngekhadi okuphephile noma lungisa inkokhelo yomxhasi ngaphandle kokulahlekelwa isikhala esibhukhiwe.",
        preparing: "Kulungiswa ukukhokha kwekhadi okuphephile...",
        doctorTerms: {
          title: "Imigomo kadokotela / yesikhungo",
          available: "Imigomo ehlukile kadokotela iyatholakala",
          none: "Ayikho imigomo ehlukile kadokotela efakiwe",
          availableHelp:
            "Le migomo ingeyodokotela noma yesikhungo futhi ihlukile emigomeni yenkundla ye-AfriMed.",
          noneHelp:
            "Imigomo yenkundla ye-AfriMed isasebenza. Lo dokotela akakalayishi elinye idokhumenti lemigomo yesikhungo okwamanje.",
          updated: "Kubuyekezwe ngo {date}",
          ready: "Sekulungele ukubuyekezwa",
          view: "Buka i-T&Cs kadokotela",
        },
        notifications: {
          newAppointmentSecured:
            "{name} ucele i-aphoyintimenti, ngo {datetime}. Inkokhelo isivele ivikelekile.",
        },
        errors: {
          intentMissing:
            "I-Stripe ayitholanga le payment intent yokuphuma kulesi siphequluli. Vuselela imininingwane yokukhokha uzame futhi.",
          doctorReconnect:
            "Ukusethwa kwemali kadokotela kudinga ukuxhunywa futhi ngaphambi kokuthi ukukhokha ngekhadi kuqhubeke. Cela udokotela axhume kabusha i-Stripe kokuthandwayo kwezinkokhelo, bese uvuselela imininingwane yokukhokha.",
          cardFormLoading:
            "Ifomu lekhadi eliphephile lisalayisha. Sicela ulinde kancane bese uzama futhi.",
          cardFormSlow:
            "Ifomu lekhadi eliphephile lithatha isikhathi eside kunokulindelekile. Vuselela imininingwane yokukhokha uzame futhi.",
          prepare:
            "Ukukhokha okuphephile akukwazanga ukulungiswa kahle. Vuselela imininingwane yokukhokha uzame futhi.",
          processing:
            "Inkokhelo yakho isacutshungulwa. Sicela ulinde kancane bese uzama futhi.",
          finalize:
            "Inkokhelo kungenzeka idlulile, kodwa asikwazanga ukuqedela ukubhuka. Sicela uxhumane nosekelo lwe-AfriMed.",
          intentMismatch:
            "I-Stripe ayitholanga le payment intent yokuphuma kulesi simo sefomu lekhadi esisebenzayo. Vuselela imininingwane yokukhokha uzame futhi.",
          intentMismatchWithAccount:
            "I-Stripe ayitholanga le payment intent yokuphuma kulesi simo sefomu lekhadi esisebenzayo. I-AfriMed iyidale ku-akhawunti yepulatifomu {account}. Vuselela imininingwane yokukhokha uzame futhi.",
          bookingIncomplete: "Imininingwane yokubhuka ayiphelele.",
          bookingIncompleteRetry:
            "Imininingwane yokubhuka ayiphelele. Vuselela imininingwane yokukhokha uzame futhi.",
          stripeNotConfigured:
            "I-Stripe checkout ayikakamiselwa kule ndawo okwamanje.",
          stripeLoad:
            "I-Stripe checkout ayikwazanga ukulayisha okwamanje.",
          sponsorRestore:
            "Inkokhelo yomxhasi iphelile, kodwa i-aphoyintimenti ayikwazanga ukubuyiselwa.",
          sponsorSync:
            "Inkokhelo yomxhasi iphelile, kodwa i-AfriMed ayikwazanga ukubuyisela i-aphoyintimenti okwamanje.",
          missingAmount:
            "Inani lokubonana alikho kulokhu kubhuka.",
          cardFormRefresh:
            "Ifomu lekhadi eliphephile lisalayisha. Vuselela imininingwane yokukhokha.",
          stagingKey:
            "I-staging checkout ikhomba ku-Stripe secret ephilayo. Shintsha i-STRIPE_SECRET_KEY ye-staging ibe ukhiye we-sk_test bese uvuselela imininingwane yokukhokha.",
          prepareIntent:
            "I-Stripe ayikwazanga ukulungisa le payment intent.",
          paymentFailed: "Inkokhelo ayikwazanga ukuqedwa.",
          paymentReferenceMissing:
            "Inkokhelo iqediwe kodwa ireferensi yokukhokha ibingekho.",
          invalidTime:
            "Inkokhelo iphumelele kodwa isikhathi se-aphoyintimenti besingavumelekile.",
          createAppointment:
            "Inkokhelo iphumelele kodwa i-aphoyintimenti ayikwazanga ukudalwa.",
        },
      },
      index: {
        title: "Ama-aphoyintimenti",
        more: "Okunye",
        pastAppointments: "Ama-aphoyintimenti adlulile",
        tabs: {
          upcoming: "Azayo",
          past: "Adlulile",
          pending: "Alindele ukuvunywa",
        },
        upcomingEmpty: {
          title: "Awekho ama-aphoyintimenti azayo",
          body: "Awunawo ama-aphoyintimenti ahleliwe okwamanje.\nBhuka ukubonana kwakho okulandelayo ukuze kuvele lapha.",
        },
        pendingEmpty: {
          title: "Awekho ama-aphoyintimenti alindile",
          body: "Akukho okulindele ukuvunywa okwamanje.",
        },
        empty: {
          title: "Awekho ama-aphoyintimenti azoboniswa",
          body: "Uhlu lwakho alunalutho okwamanje. Buya futhi kamuva.",
        },
        toast: {
          deleted: "I-aphoyintimenti yakho isusiwe",
        },
        bookNow: "Bhuka manje",
        nearby: "Ukunakekelwa okuseduze nawe",
      },
    },
    home: {
      patient: {
        welcome: "Siyakwamukela, {firstName}!",
        actions: {
          openProfile: "Vula iphrofayela",
          notifications: "Izaziso",
          viewAll: "Buka konke",
        },
        appointments: {
          title: "Ama-aphoyintimenti akho",
          open: "Vula i-aphoyintimenti",
          tabs: {
            upcoming: "Azayo",
            past: "Adlulile",
            pending: "Alindele ukuvunywa",
          },
          empty: {
            title: "Awunawo ama-aphoyintimenti okwamanje",
            body: "Bhuka i-aphoyintimenti entsha ukuze ukunakekelwa kwakho kuqale lapha.",
          },
          bookAction: "Bhuka i-aphoyintimenti",
          error: "Asikwazanga ukulayisha ama-aphoyintimenti akho.",
          status: {
            pending: "Kulindwe ukuvunywa",
            confirmed: "Kuqinisekisiwe",
            inProgress: "Kuyaqhubeka",
            completed: "Kuqediwe",
            cancelled: "Kukhanseliwe",
          },
          defaults: {
            doctor: "Udokotela",
            appointment: "I-aphoyintimenti",
          },
        },
        serviceCategories: {
          title: "Izigaba zesevisi",
          doctors: "Odokotela",
          hospitals: "Izibhedlela",
          pharmacies: "Amakhemisi",
          ambulances: "Ama-ambulensi",
        },
        popularServices: {
          title: "Amasevisi adumile",
          action: "Wonke amasevisi",
          labels: {
            generalPractitioner: "Udokotela ojwayelekile",
            telemedicine: "Ukubonana ukude",
            childHealthPediatrics: "Impilo yezingane",
            maternalCareAntenatal: "Ukunakekelwa kokukhulelwa",
            dermatologist: "Udokotela wesikhumba",
            hivCare: "Ukunakekelwa kwe-HIV",
            hypertension: "Umfutho wegazi ophezulu",
            tbScreening: "Ukuhlolwa kwe-TB",
            diabetes: "Isifo sikashukela",
            malaria: "Umalaleveva",
            stiScreening: "Ukuhlolwa kwe-STI",
            dental: "Amazinyo",
            mentalHealth: "Impilo yengqondo",
          },
        },
        nearby: {
          title: "Ukunakekelwa kwezempilo eduze kwakho",
          action: "Thola ukunakekelwa kwezempilo eduze nami",
          selectedFacility: "Indawo ekhethiwe",
          seeDoctors: "Bona odokotela kule ndawo",
          loading: "Kulayishwa izindawo eziseduze...",
          findDoctors: "Thola odokotela",
          searchPlaceholder: "Sesha indawo",
          clearSearch: "Sula usesho",
          showingNear: "Kubukwa izindawo eziseduze no",
          clearSelectedFacility: "Susa indawo ekhethiwe",
          defaultFacilityName: "Isikhungo sezempilo",
        },
        verifyIdentity: {
          title: "Qinisekisa ubuwena",
          body: "Qedela ukuqinisekisa ukuze uthole ukufinyelela okuphephile ku-AfriMed™.",
          pending: "Kulindwe ukubuyekezwa",
          action: "Qinisekisa manje",
        },
        fallbackName: "Umsebenzisi we-AfriMed",
      },
    },
    navigation: {
      tabs: {
        dashboard: "Ikhaya",
        appointments: "Ama-aphoyintimenti",
        messages: "Imilayezo",
        profile: "Mina",
      },
      menu: {
        viewProfile: "Buka iphrofayela",
        dashboard: "Ikhaya",
        ePrescriptions: "Ama-ePrescription",
        settings: "Izilungiselelo",
        signOut: "Phuma",
        logOut: "Phuma",
        loggingOut: "Kuyaphunywa...",
        logoutConfirmTitle: "Uqinisekile ukuthi ufuna ukuphuma?",
        logoutConfirmBody: "Uzodinga ukungena futhi ukuze uqhubeke.",
      },
      search: "Sesha",
      globalSearch: "Sesha ku-AfriMed",
      me: "Mina",
    },
    fields: {
      country: {
        label: "Izwe",
        requiredLabel: "Izwe*",
        placeholder: "Khetha izwe",
        searchPlaceholder: "Sesha amazwe",
        selected: "Kukhethiwe",
        chooseMarket: "Khetha imakethe yakho",
        current: "Okwamanje",
        noMatchesLong: "Ayikho imakethe efana nalokho useshe khona.",
        noMatchesShort: "Akukho okufanayo",
      },
    },
    doctors: {
      appointmentDetails: {
        attachments: {
          untitled: "Ifayela",
          byDoctor: "NgoDokotela",
          addedSingle: "Ifayela lengeziwe.",
          addedMultiple: "Amafayela angu-{count} engeziwe.",
        },
      },
      diagnosisNotes: {
        title: "Ukuhlonza namanothi",
        subtitle: "Engeza ukuhlonza kokubonana namanothi asekelayo.",
        fields: {
          diagnosis: "Ukuhlonza",
          diagnosisPlaceholder: "Isibonelo: Isifo sikashukela",
          notes: "Amanothi",
        },
      },
    },
    messages: {
      composeModal: {
        title: "Ingxoxo entsha",
        addParticipants: "Engeza ababambiqhaza",
        close: "Vala",
        types: {
          doctorPatient: "Udokotela-Isiguli",
          doctorDoctor: "Udokotela-Udokotela",
          group: "Ingxoxo yeqembu",
        },
        typeDescriptions: {
          doctorPatient: "Xoxa ngqo nesiguli noma udokotela.",
          doctorDoctor: "Ingxoxo phakathi kochwepheshe bezempilo.",
          group: "Engeza abantu abaningi engxoxweni eyodwa.",
        },
        conversationTitle: "Isihloko sengxoxo",
        conversationTitlePlaceholder: "Engeza isihloko uma kudingeka",
        autoGenerateHint: "Uma ungafaki isihloko, i-AfriMed izosikhiqiza.",
        searchUsers: "Sesha abasebenzisi",
        searchPlaceholder: "Sesha ngamagama noma i-imeyili",
        selectedParticipants: "Abakhethiwe",
        searchResults: "Imiphumela yosesho",
        unknownUserInitial: "A",
        unknownUser: "Umsebenzisi ongaziwa",
        noUsersFound: "Akekho umsebenzisi otholakele",
        startTyping: "Qala ukubhala ukuze useshe",
        back: "Buyela",
        cancel: "Khansela",
        nextAddParticipants: "Okulandelayo: engeza ababambiqhaza",
        createConversation: "Dala ingxoxo",
        chatWith: "Xoxa no {name}",
        groupChatMembers: "Ingxoxo yeqembu ({count} amalungu)",
      },
      inbox: {
        search: "Sesha",
        searchRecipients: "Sesha abantu",
        clearSearch: "Sula usesho",
        openFola: "Vula i-Fola",
        newMessage: "Umyalezo omusha",
        composeTitle: "Qala umyalezo omusha",
        loadingMessagesTitle: "Kulayishwa imilayezo",
        loadingMessagesBody: "Sicela ulinde sisalanda izingxoxo zakho zakamuva.",
        loadingRecipientsTitle: "Kulandwa abemukeli",
        loadingRecipientsBody: "Sicela ulinde njengoba sithola abantu ongabathumela umlayezo.",
        fallbackTitle: "Ingxoxo",
        noRecipientsTitle: "Akukho bantu abatholakele",
        noRecipientsMatch: "Akekho umuntu ofana nosesho lwakho",
        noRecipientsBody: "Zama elinye igama, i-imeyili noma inombolo.",
        noConversationsMatch: "Ayikho ingxoxo etholakele",
        noMessagesPreview: "Ayikho imilayezo okwamanje.",
        new: "Okusha",
        opening: "Kuyavuleka...",
      },
      conversation: {
        now: "Manje",
        deletedForMe: "Usuyisusile le ngosi kuwe",
        deletedBySender: "Lo myalezo ususwe ngumthumeli",
        deleteDialog: {
          close: "Vala ibhokisi lokususa",
          body:
            "Susa lo myalezo kuwe kuphela noma uwususe kubo bonke. Lokhu akunakuhlehliswa.",
          deleting: "Kuyasuswa...",
        },
        toast: {
          dismiss: "Kulungile",
          undo: "Hlehlisa",
          deletedForMe: "Umlayezo ususiwe kuwe",
          deletedForBoth: "Umlayezo ususiwe kubo bonke",
          deleteError: "Asikwazanga ukususa lo myalezo okwamanje.",
        },
        composer: {
          all: "Konke",
          attach: "Namathisela isithombe",
          selectedAttachmentAlt: "Isithombe esikhethiwe",
        },
        topBar: {
          back: "Buyela emilayezweni",
          openFola: "Vula i-Fola",
          compose: "Bhala umlayezo",
        },
      },
    },
    auth: {
      callback: {
        failed: "Ukungena kuhlulekile. Zama futhi.",
        linkVerificationFailed:
          "Ikhodi yokungena ayiqinisekiswanga. Cela esinye isixhumanisi.",
        requestAnotherLink: "Cela esinye isixhumanisi sokungena",
      },
    },
    notifications: {
      title: "Izaziso",
      filters: {
        all: "Konke",
        urgent: "Okuphuthumayo",
        unread: "Okungakafundwa",
        appointments: "Ama-aphoyintimenti",
      },
      common: {
        title: "Izaziso",
        loading: "Kulayishwa izaziso...",
        emptyTitle: "Azikho izaziso",
        emptyBody: "Okwamanje azikho izaziso.",
      },
      loadingSingle: "Kulayishwa isaziso...",
      singleTitle: "Isaziso",
      headers: {
        newAppointment: "I-aphoyintimenti entsha",
        appointmentUpdate: "I-aphoyintimenti ibuyekeziwe",
        appointmentConfirmed: "I-aphoyintimenti ivunyiwe",
        appointmentDeclined: "I-aphoyintimenti inqatshiwe",
        upcomingAppointment: "I-aphoyintimenti ezayo",
        newTimeRequestApproved: "Isikhathi esisha sivunyiwe",
        newTimeRequestDeclined: "Isikhathi esisha sinqatshiwe",
      },
      messages: {
        bookingApprovedForDate:
          "Isicelo sika-{name} se-aphoyintimenti sango-{date} sivunyiwe.",
        bookingApprovedGeneric:
          "Isicelo sika-{name} se-aphoyintimenti sivunyiwe.",
        yourAppointmentAcceptedForDate:
          "I-aphoyintimenti yakho yango-{date} ivunyiwe.",
        yourAppointmentAcceptedGeneric:
          "Isicelo sakho se-aphoyintimenti sivunyiwe.",
        bookingDeclinedForDate:
          "Isicelo sika-{name} se-aphoyintimenti sango-{date} sinqatshiwe.",
        bookingDeclinedGeneric:
          "Isicelo sika-{name} se-aphoyintimenti sinqatshiwe.",
        yourAppointmentDeclinedForDate:
          "I-aphoyintimenti yakho yango-{date} inqatshiwe.",
        yourAppointmentDeclinedGeneric:
          "Isicelo sakho se-aphoyintimenti sinqatshiwe.",
        upcomingAppointmentForDate:
          "I-aphoyintimenti yakho ihlelelwe u-{date}.",
        upcomingAppointmentGeneric: "I-aphoyintimenti yakho ihleliwe.",
        rescheduleApprovedGeneric:
          "Isikhathi esisha se-aphoyintimenti sivunyiwe.",
        yourRescheduleApprovedGeneric:
          "Isicelo sakho sesikhathi esisha sivunyiwe.",
        rescheduleDeclinedGeneric:
          "Isikhathi esisha se-aphoyintimenti sinqatshiwe.",
        yourRescheduleDeclinedGeneric:
          "Isicelo sakho sesikhathi esisha sinqatshiwe.",
      },
      errors: {
        notFound: "Isaziso asitholakalanga.",
        load: "Asikwazanga ukulayisha isaziso.",
      },
    },
    patient: {
      editIntro: {
        title: "Hlela isingeniso",
        sections: {
          personalInfo: "Ulwazi lwakho",
          contactData: "Ulwazi lokuxhumana",
          residenceAddress: "Ikheli lasekhaya",
          medicalInfo: "Ulwazi lwezempilo",
        },
        verification: {
          verified: "Kuqinisekiswe nge-ID esemthethweni oyinikezile.",
        },
        actions: {
          addLanguage: "Engeza ulimi",
        },
        placeholders: {
          fullName: "Engeza igama lakho eligcwele",
          dob: "Engeza usuku lwakho lokuzalwa",
          phone: "Engeza inombolo yakho yocingo",
          email: "Engeza ikheli lakho le-imeyili",
          idPassport: "Engeza i-ID yakho noma iphasipoti",
          street: "Engeza ikheli lomgwaqo wakho",
          postalCode: "Engeza ikhodi yakho yeposi",
          country: "Engeza izwe lakho",
          bloodType: "Engeza uhlobo lwegazi lwakho",
          allergies: "Engeza ukungezwani komzimba kwakho",
          conditions: "Engeza izimo zakho zezempilo",
          organDonor: "Sitshele uma ungumnikeli wezitho",
          heightWeight: "Engeza ubude nesisindo sakho",
        },
        languages: {
          empty:
            "Engeza izilimi ozikhulumayo ukuze odokotela bakhethe okukufanele kahle.",
        },
        photoSheet: {
          title: "Hlela isithombe sakho sephrofayela",
          uploadImage: "Layisha isithombe",
          takePhoto: "Thatha isithombe",
        },
        errors: {
          loadProfile: "Asikwazanga ukulayisha iphrofayela yakho.",
          fileType: "Sicela ulayishe isithombe seJPG, PNG noma WebP.",
          fileSize: "Usayizi omkhulu wefayela ngu-5MB.",
          signInToUpload: "Kufanele ungene ukuze ulayishe isithombe sephrofayela.",
          uploadPhoto: "Asikwazanga ukulayisha isithombe sakho.",
          signInToSave: "Kufanele ungene ukuze ulondoloze izinguquko.",
          savePhoto:
            "Asikwazanga ukulondoloza isithombe sakho sephrofayela. Zama futhi kamuva.",
        },
      },
      profile: {
        mock: {
          specialtyDermatologist: "Udokotela wesikhumba",
          yearsExperience: "iminyaka engu-14",
          serviceLabel: "Isevisi",
          serviceNameFirstConsultancy: "Ukuhlolwa kokuqala",
          languagesEnglishTswana: "IsiNgisi, Setswana",
          distanceFromCityCentre: "5.2 km ukusuka enkabeni yedolobha",
          chronicConditionsHypertensionDiabetes:
            "Umfutho wegazi ophezulu, isifo sikashukela",
        },
        fullName: "Igama eligcwele",
        dateOfBirth: "Usuku lokuzalwa",
        verified: "Kuqinisekisiwe",
        pendingVerification: "Ukuqinisekiswa kusalindile",
        notProvided: "Akunikezwanga",
        notConfigured: "Akukakamiselwa",
        unknown: "Akwaziwa",
        noneRecorded: "Akukho okubhaliwe",
        notRecorded: "Akubhaliwe",
        dependantFallback: "Othembele kuwe",
        dependantsCount: "{count} abathembele kuwe",
        insuranceSupport:
          "Engeza umhlinzeki wakho nemininingwane yobulungu ukuze izimangalo nokukhokha kusheshe.",
        notificationsSupport:
          "Phatha indlela othola ngayo izibuyekezo ngama-aphoyintimenti, amarisiphi nezindaba.",
        medicalHistoryLocked:
          "Umlando wezempilo ukhiyiwe kuze kube isiguli sinike amandla okufinyelela ku-EHR.",
        sections: {
          appointments: "Umlando wama-aphoyintimenti",
          contact: "Imininingwane yokuxhumana",
          insurance: "Umshwalense wezempilo",
          dependants: "Abathembele kuwe",
          address: "Ikheli lokuhlala",
          medicalHistory: "Umlando wezempilo",
          photos: "Izithombe zesiguli",
          languages: "Izilimi",
          notifications: "Izaziso",
        },
        labels: {
          phone: "Ucingo",
          email: "I-imeyili",
          nationalId: "I-ID kazwelonke / Iphasipoti",
          street: "Umgwaqo",
          postalCode: "Ikhodi yeposi",
          country: "Izwe",
          bloodType: "Uhlobo lwegazi",
          allergies: "Ukungezwani komzimba",
          chronicConditions: "Izimo ezingapheli",
          organDonor: "Umnikeli wezitho",
          heightWeight: "Ubude nesisindo",
        },
        placeholders: {
          phone: "Inombolo yakho yocingo",
          email: "wena@imeyili.com",
          nationalId: "I-ID yakho / Iphasipoti",
          street: "Umgwaqo wakho, Idolobha",
          postalCode: "00000",
          country: "Izwe",
          languages: "Izilimi ozikhethayo",
        },
        actions: {
          addEdit: "Engeza / Hlela",
          addDependant: "Engeza othembele kuwe",
          addFavorite: "Engeza kokuthandwayo",
          removeFavorite: "Susa kokuthandwayo",
        },
        history: {
          visit: "Ukuvakashela",
          visitAt: "Ukuvakashela · {day}, {time}",
        },
        consultType: {
          private: "Ukuhlolwa kwangasese",
          moh: "Ukuhlolwa kwe-MoH",
          evisit: "Virtual",
          default: "Ukuhlolwa",
        },
        fallback: {
          addressNotProvided: "Ikheli alinikezwanga",
          consultationLocation: "Indawo ye-aphoyintimenti",
          generalPractice: "Udokotela ojwayelekile",
          notSpecified: "Akucaciswanga",
        },
        notifications: {
          title: "Izindaba nezibuyekezo",
          off: "Kuvaliwe",
          emailPush: "I-imeyili nePush",
          emailPushLocalNews: "I-imeyili nePush · Izindaba nemithetho yasendaweni",
        },
        empty: {
          appointments: "Awukabi nawo ama-aphoyintimenti.",
          dependants: "Akekho othembele kuwe obhaliwe",
          photos: "Azikho izithombe okwamanje.",
        },
      },
    },
    doctor: {
      profile: {
        fallback: {
          doctor: "Udokotela",
          specialtyMissing: "Ubuchwepheshe abukangezwe okwamanje",
          about:
            "Udokotela onesipiliyoni onakekela iziguli ngobumnene nangokucophelela.",
          notSet: "Akukakamiselwa",
          facility: "Isikhungo",
          service: "Isevisi",
        },
        stats: {
          patients: "Iziguli",
          experience: "Isipiliyoni",
          rating: "Isilinganiso",
        },
        values: {
          patientAge: {
            childrenAdults: "Izingane nabantu abadala",
            adultsOnly: "Abantu abadala kuphela",
            childrenOnly: "Izingane kuphela",
          },
          availability: {
            everyDay: "Nsuku zonke",
            everyWeek: "Isonto ngalinye",
          },
          insurance: {
            accepting: "Kwamukelwa",
          },
          paymentTerms: {
            cards: "Amakhadi eDebit / Credit",
            creditCards: "Amakhadi eCredit",
            debitCards: "Amakhadi eDebit",
            bankTransfers: "Ukudlulisa ebhange",
            cashOnService: "Imali ngesikhathi sesevisi",
          },
          days: {
            monday: "UMsombuluko",
            tuesday: "ULwesibili",
            wednesday: "ULwesithathu",
            thursday: "ULwesine",
            friday: "ULwesihlanu",
            saturday: "UMgqibelo",
            sunday: "ISonto",
            mon: "Mso",
            tue: "Bil",
            wed: "Tha",
            thu: "Ne",
            fri: "Hla",
            sat: "Mgq",
            sun: "Son",
          },
        },
        sections: {
          aboutYou: "Ngawe",
          about: "Ngaye",
          yourFocus: "Okugxilwe kukho",
          focus: "Ukugxila",
          consultancies: "Izindawo zokubonisana",
          consultancy: "Indawo yokubonisana",
          evisit: "Virtual",
          gallery: "Igalari",
          yourServices: "Izinsiza zakho",
          services: "Izinsiza",
        },
        focus: {
          addFromSettings: "Engeza izindawo okugxilwe kuzo kusuka kuzilungiselelo.",
          empty: "Azikho izindawo okugxilwe kuzo ezingeziwe okwamanje.",
        },
        consultancies: {
          empty: "Azikho izindawo zokubonisana okwamanje.",
          addLocation: "Engeza indawo yokubonisana",
          options: "Izinketho zendawo yokubonisana",
          edit: "Hlela indawo yokubonisana",
          remove: "Susa indawo yokubonisana",
          removeTitle: "Susa le ndawo yokubonisana?",
          removeBody: "Le ndawo yokubonisana izosuswa kuphrofayela yakho.",
          removing: "Kuyasuswa...",
          labels: {
            patientsAge: "Iminyaka yeziguli",
            availability: "Ukutholakala",
            insurancePolicy: "Inqubomgomo yomshwalense",
            paymentTerms: "Imigomo yokukhokha",
          },
        },
        gallery: {
          addFromSettings: "Engeza izithombe kusuka kuzilungiselelo zeGalari",
        },
        services: {
          empty: "Engeza izinsiza namanani kusuka kuzilungiselelo ukuze iziguli zibone okunikezayo.",
        },
        cover: {
          editTitle: "Hlela isithombe sekhava",
          uploadImage: "Layisha isithombe",
          takePhoto: "Thatha isithombe",
          errors: {
            fileType: "Sicela ulayishe isithombe se-JPG, PNG noma i-WebP.",
            fileSize: "Usayizi omkhulu wesithombe sekhava uyi-3MB.",
            fileRead: "Asikwazanga ukufunda isithombe osikhethile.",
            save: "Asikwazanga ukugcina isithombe sakho sekhava. Zama futhi.",
          },
        },
      },
      editIntro: {
        title: "Hlela isingeniso",
        sections: {
          personalInfo: "Ulwazi lomuntu siqu",
          specialty: "Ubuchwepheshe",
          about: "Ngawe",
          formation: "Imfundo",
          languages: "Izilimi",
        },
        labels: {
          name: "Igama",
          dob: "Usuku lokuzalwa",
          registrationNumber: "Inombolo yokubhalisa",
        },
        placeholders: {
          fullName: "Engeza igama lakho eligcwele",
          dob: "Engeza usuku lwakho lokuzalwa",
          specialty: "Engeza ubuchwepheshe bakho",
          registerNumber: "Engeza inombolo yakho yokubhalisa",
          about: "Tshela iziguli kafushane ngesizinda sakho, isipiliyoni nendlela osebenza ngayo.",
        },
        verification: {
          verified: "Kuqinisekiswe nge-ID esemthethweni oyinikezile.",
          pending: "Ukuqinisekiswa kusaqhubeka.",
        },
        signature: {
          title: "Isiginesha",
          subtitle: "Engeza / Buyekeza isiginesha yakho",
          preview: "Ukubuka isiginesha",
          helper:
            "Le siginesha igcinwa ngokuphepha futhi izofakwa kuyo yonke imithi ekhishiwe.",
        },
        formation: {
          empty: "Engeza imfundo nokuqeqeshwa kwakho ukuze iziguli zibone isizinda sakho.",
        },
        actions: {
          addFormation: "Engeza imfundo",
          addLanguage: "Engeza ulimi",
        },
        languages: {
          editAria: "Hlela {language}",
          empty: "Engeza izilimi ozikhulumayo ukuze iziguli zikhethe okubafanele.",
        },
        photoSheet: {
          title: "Hlela isithombe sakho sephrofayela",
          uploadImage: "Layisha isithombe",
          takePhoto: "Thatha isithombe",
        },
        fallback: {
          education: "Imfundo",
        },
        errors: {
          save: "Asikwazanga ukugcina isingeniso sakho. Zama futhi.",
          photoSave: "Asikwazanga ukugcina isithombe sakho sephrofayela. Zama futhi.",
        },
      },
    },
    settings: {
      home: {
        title: "Izilungiselelo",
        profileSupport: "Hlela amakhasi, iphrofayela nokunye okuningi...",
        loginSecurity: "Ukungena nokuphepha",
        language: "Ulimi",
        availability: "Ukutholakala",
        notifications: "Izaziso",
        preferences: "Okuncanyelwayo",
        legal: "Ezomthetho",
        faq: "Imibuzo evame ukubuzwa",
        userFallback: "Umsebenzisi",
        logoutDialog: {
          title: "Uqinisekile ukuthi ufuna ukuphuma?",
          body: "Uzodinga ukungena futhi ukuze uqhubeke usebenzisa i-AfriMed.",
        },
      },
      welcome: {
        fallbackName: "mngani",
        title: "Siyakwamukela {name} ku-Zilungiselelo zakho",
        doctorBody:
          "Izilungiselelo ziyisikhungo sakho sokulawula lapho ulungisa khona isithombe, amahora okuvakashela, ukuphepha, ukukhokha nokunye.",
        patientBody:
          "Izilungiselelo ziyisikhungo sakho sokulawula lapho ulungisa khona isithombe, ukuphepha, ukukhokha, izaziso nokunye.",
      },
      subflows: {
        discardTitle: "Lahla izinguquko?",
        languages: {
          title: "Engeza izilimi",
          searchPlaceholder: "Sesha noma engeza",
          clearSearch: "Sula usesho",
          addAria: "Engeza ulimi",
          helperPatient: "Khetha zonke izilimi ozikhulumayo.",
          helperDoctor: "Engeza zonke izilimi ozikhulumayo.",
          catalogHidden:
            "Thayipha ulimi bese ucindezela u-+ ukuze ulwengeze. Izinketho ezingakahunyushwa aziboniswa lapha ngalolu limi.",
          noResults: "Azikho izilimi ezihambisana nosesho lwakho.",
          discardBody:
            "Unezinguquko zolimi ezingakalondolozwa. Uqinisekile ukuthi ufuna ukuvala ngaphandle kokulondoloza?",
          saveError: "Asikwazanga ukulondoloza izinguquko zakho. Sicela uzame futhi.",
        },
        bloodType: {
          title: "Uhlobo lwegazi",
          searchPlaceholder: "O+",
          clearSearch: "Sula usesho",
          noResults: "Ayikho imiphumela.",
          discardBody:
            "Unezinguquko zohlobo lwegazi ezingakalondolozwa. Uqinisekile ukuthi ufuna ukuvala ngaphandle kokulondoloza?",
        },
        allergies: {
          title: "Ukungezwani komzimba",
          searchPlaceholder: "Sesha noma engeza",
          clearSearch: "Sula usesho",
          addAria: "Engeza ukungezwani komzimba",
          catalogHidden:
            "Thayipha ukungezwani komzimba bese ucindezela u-+ ukuze ukwengeze. Uhlu olungakahunyushwa aluboniswa lapha ngalolu limi.",
          noMatchesAdd: "Akukho okufanayo. Thepha u-+ ukuze ungeze “{query}”.",
          discardBody:
            "Unezinguquko zokungezwani komzimba ezingakalondolozwa. Uqinisekile ukuthi ufuna ukuvala ngaphandle kokulondoloza?",
          saveError: "Asikwazanga ukulondoloza izinguquko zakho. Sicela uzame futhi.",
        },
        conditions: {
          title: "Izimo zezempilo",
          searchPlaceholder: "Sesha noma engeza",
          clearSearch: "Sula usesho",
          addAria: "Engeza isimo",
          catalogHidden:
            "Thayipha isimo bese ucindezela u-+ ukuze usengeze. Uhlu olungakahunyushwa aluboniswa lapha ngalolu limi.",
          noMatchesAdd: "Akukho okufanayo. Thepha u-+ ukuze ungeze “{query}”.",
          discardBody:
            "Unezinguquko zezimo zezempilo ezingakalondolozwa. Uqinisekile ukuthi ufuna ukuvala ngaphandle kokulondoloza?",
          saveError: "Asikwazanga ukulondoloza izinguquko zakho. Sicela uzame futhi.",
        },
        medications: {
          title: "Imithi",
          searchPlaceholder: "Sesha noma engeza",
          clearSearch: "Sula usesho",
          addAria: "Engeza umuthi",
          loading: "Kulayishwa... sicela ume kancane!",
          catalogHidden:
            "Thayipha umuthi bese ucindezela u-+ ukuze uwengeze. Uhlu olungakahunyushwa aluboniswa lapha ngalolu limi.",
          noMatchesAdd: "Akukho okufanayo. Thepha u-+ ukuze ungeze “{query}”.",
          discardBody:
            "Unezinguquko zemithi ezingakalondolozwa. Uqinisekile ukuthi ufuna ukuvala ngaphandle kokulondoloza?",
          saveError: "Asikwazanga ukulondoloza izinguquko zakho. Sicela uzame futhi.",
        },
        heightWeight: {
          title: "Ubude nesisindo",
          subtitle:
            "Engeza ubude nesisindo sakho sakamuva ukuze iphrofayela yakho yezempilo ibe usizo kakhulu ekunakekelweni.",
          heightLabel: "Ubude (cm)",
          weightLabel: "Isisindo (kg)",
          heightPlaceholder: "175",
          weightPlaceholder: "72",
          clearHeight: "Sula ubude",
          clearWeight: "Sula isisindo",
          saveError: "Asikwazanga ukulondoloza ubude nesisindo. Sicela uzame futhi.",
        },
        patientEhr: {
          title: "Irekhodi lami lezempilo",
          subtitle:
            "Imibiko, amarisiphi, ama-referral namanothi okuvakasha — nguwe olawula ukuthi ubani ongawafinyelela.",
          statusReady: "Ukuvuselelwa kwe-EHR sekulungele",
          statusUpdating: "I-EHR iyavuselelwa…",
          statusPreparing: "Amanothi ayalungiswa…",
          bodyReady:
            "Ukuvuselelwa kokubonana kwakho sekulungele. Ungabuyekeza amanothi okuvakasha namanye amarekhodi ahlobene lapha.",
          bodyUpdating:
            "I-VitalRep™ isiqedile amanothi futhi ivuselela i-EHR yakho. Lokhu kungathatha isikhashana.",
          bodyPreparing:
            "Ukubonana kwakho kuyacutshungulwa. Sizovuselela i-EHR yakho maduze.",
          latestVisitTitle: "Ukuvakasha kwakamuva",
          appointmentReference: "I-aphoyintimenti #{appointmentId}",
          latestVisitFallback:
            "Isimo sokubonana kwakho kwakamuva neresiphi ekhishiwe kuvela lapha kuqala.",
          prescriptionTitle: "Iresiphi",
          prescriptionLoading: "Kulayishwa iresiphi...",
          noPrescription: "Akukakhishwa iresiphi okwamanje",
          prescriptionIssued: "Ikhishwe {date}",
          prescriptionHint:
            "Uma udokotela wakho ekhiphe umuthi, ungawuvula kusuka lapha.",
          viewPrescription: "Buka iresiphi",
          backToAppointments: "Buyela kuma-aphoyintimenti",
          visitNotesTitle: "Amanothi okuvakasha",
          visitNotesBody:
            "Okwamanje asikabonisi amanothi aphelele okuboniswana nemibiko ngaphakathi kwe-EHR yesiguli. Okwamanje, lesi sikrini sigcina isimo sokuvakasha kwakho kwakamuva neresiphi kufinyeleleka kalula.",
        },
      },
      loginSecurity: {
        password: {
          label: "Iphasiwedi",
          action: "Dala / Shintsha",
          aria: "Shintsha iphasiwedi",
        },
        passcode: {
          label: "Ikhodi yokungena",
          create: "Dala",
          createAfterPassword: "Dala ngemva kwephasiwedi",
          aria: "Shintsha ikhodi yokungena",
        },
        twoFactor: {
          label: "Ukuqinisekisa ngezinyathelo ezimbili",
          aria: "Ukuqinisekisa ngezinyathelo ezimbili",
        },
      },
      preferences: {
        title: "Okuncanyelwayo",
        language: {
          title: "Ulimi",
        },
        notifications: {
          title: "Izaziso",
          subtitle: "Phatha izikhumbuzi, push, i-imeyili ne-SMS",
        },
        notificationInbox: {
          title: "Ibhokisi lezaziso",
          subtitle: "Bona izibuyekezo zakho ze-AfriMed™ ezivela ekunakekelweni",
        },
        sections: {
          app: "Uhlelo lokusebenza",
          account: "I-akhawunti",
        },
        appearance: {
          title: "Ukubukeka",
          subtitle: "Sebenzisa itimu yedivayisi, ekhanyayo noma emnyama",
          selected: "Itimu ethi {label} ikhethiwe",
          currentlyUsing: "Njengamanje kusebenzisa i-AfriMed™ {theme}.",
          footer:
            "Sebenzisa uhlelo lwedivayisi ukuze i-AfriMed isebenzise umbala okhanyayo noma omnyama ngokuya ngokuthandwa yidivayisi yakho.",
          options: {
            system: {
              label: "Sebenzisa uhlelo lwedivayisi",
              support:
                "Landela uhlelo lwedivayisi yakho ugcine ukubukeka kwe-AfriMed™.",
            },
            light: {
              label: "Okukhanyayo",
              support: "Itimu ekhanyayo ye-AfriMed™ esetshenziswa kuyo yonke i-app.",
            },
            dark: {
              label: "Okumnyama",
              support: "Itimu emnyama ye-AfriMed™ yokubuka ezimeni zokukhanya okuphansi.",
            },
          },
        },
        news: {
          title: "Izindaba nezibuyekezo",
          subtitle: "Okuncanyelwayo kwe-imeyili ne-push",
        },
        accessibility: {
          title: "Ukufinyeleleka noFola",
          subtitle: "Izinketho zosizo kanye nokwesekwa kwe-AI",
          heading: "Ukufinyeleleka",
          copy:
            "Lungisa i-AfriMed™ ukuze kube lula ukuyifunda, ithule ukuyisebenzisa, futhi icace ukuyihamba.",
          folaAssistance: "Usizo lwe-Fola",
          folaAssistanceCopy:
            "Khetha ukuthi i-Fola ikusiza kanjani ku-AfriMed. I-Fola ingachaza ukunakekelwa futhi ikusize ulungiselele ama-aphoyintimenti, kodwa ayihlonzi isifo noma ithathe indawo kadokotela wakho.",
          updated: "Ukufinyeleleka noFola kubuyekeziwe",
          rows: {
            summaries: {
              title: "Fingqa amanothi okuvakasha kwami",
              support: "Izincazelo ezilula zokulandela ngemva kwama-aphoyintimenti",
            },
            medicines: {
              title: "Chaza imithi yami",
              support: "Ngisize ngiqonde amarisiphi ngolimi olulula",
            },
            questions: {
              title: "Ngisize ngilungise imibuzo",
              support: "Phakamisa imibuzo ewusizo ngaphambi kokubonana",
            },
            largeText: {
              title: "Umbhalo nezithonjana ezinkulu",
              support: "Khulisa osayizi bombhalo obalulekile nokunethezeka kokuthinta",
            },
            reduceMotion: {
              title: "Nciphisa ukunyakaza",
              support: "Nciphisa ama-animation nokunyakaza kwe-interface",
            },
            highContrast: {
              title: "Imodi yokugqama okuphezulu",
              support: "Khulisa ukufundeka ngokugqama okuqinile",
            },
          },
        },
        plans: {
          title: "Amapulani nokubhalisa",
          subtitle: "Uhlelo lwakho lwe-AfriMed™ nezandiso",
          loadError: "Asikwazanga ukulayisha ukulungela kokukhokha manje.",
          doctor: {
            subtitleLong:
              "Phatha indlela yokukhokha yokunakekelwa okuqondile: ikhomishini ye-AfriMed™, ukulungela ukukhokhelwa, izinsiza, nezimpawu zokwethembeka ezisekela ukubhukha okukhokhelwayo.",
            businessModel: "Imodeli yokukhokha yokunakekelwa okuqondile",
            commissionTitle:
              "Odokotela bahlala bekhululekile ukujoyina. I-AfriMed™ ithatha u-10% uma usukhokhelwe.",
            commissionBody:
              "Leli akusilo ikhasi lokukhetha uhlelo lwenyanga. Iziguli ziyabhuka futhi zikhokhele izinsiza zakho, i-AfriMed™ iphathe isipiliyoni somkhiqizo, kanti ikhomishini ithathwa kuphela ngemva kokubonana okukhokhelwe ngempumelelo.",
            servicesCta: "Izinsiza namanani",
            servicesReady: "Izinsiza ezisebenzayo",
            servicesHint: "Iziguli zingabhuka kuphela okushicilelwe futhi okunentengo.",
            locationsReady: "Izindawo zokubonana",
            locationsHint: "Izilungiselelo zeconsultancy zisabusa lapho ukubhukha komzimba kwenzeka khona.",
            paymentsReady: "Izindlela zokukhokha",
            stripeStatus: "Ukuxhumeka kweStripe",
            stripeTitle: "Ukuxhumeka kweStripe nokulungela ukukhokhelwa",
            stripeConnected: "Kuxhunyiwe",
            stripePending: "Akukaxhunywa",
            stripeStatusHint:
              "Ukukhokhelwa kodokotela kufanele kuqale kuphela lapho i-akhawunti exhunyiwe isilungile.",
            stripeBody:
              "Leli yikhaya elifanele lokulungisa ukuxhumeka kwe-AfriMed™ Stripe Connect. Gcina izinsiza, izintandokazi zokukhokhelwa, isimo sesivumelwano, nesignesha yezokwelapha kulungile ukuze indlela yokukhokha ixhume kahle.",
            connectStripeCta: "Xhuma i-Stripe",
            connectStripeManage: "Phatha i-Stripe",
            payoutCta: "Izindlela zokukhokhelwa",
            paymentMethodsPending:
              "Engeza izindlela zokukhokha nezokwamukela imali ukuze ukulungela ukukhokhelwa kuqedeleke.",
            agreementStatus: "Isivumelwano",
            agreementStatusHint:
              "Ukwamukelwa kwesivumelwano kufanele kusayinwe futhi kube nenguqulo ngaphambi kokuthi ukukhokhelwa bukhoma kuvunyelwe.",
            agreementPending: "Kulindile",
            agreementSigned: "Kusayinwe",
            agreementSignedVersion: "Kusayinwe ({version})",
            logoReady: "Ilogo yomsebenzi igciniwe",
            logoPending: "Ilogo yomsebenzi ayikangezwanga",
            diasporaSadc: "Izimakethe ze-SADC",
            diasporaDiaspora: "Izimakethe ze-diaspora",
            diasporaReference: "Izimali zokubhekisela",
            diasporaTitle: "Izinkokhelo zoxhaso ze-SADC ne-diaspora",
            diasporaCoverage: "{count} izimakethe",
            diasporaBody:
              "I-AfriMed™ akufanele icabange ukuthi zonke iziguli zibhange lapha ekhaya. Ukuvakasha okuningi kuxhaswa umndeni ose-diaspora, ngakho izicelo zenkokhelo ezilungele oxhasayo nezixhumanisi zokukhokha ezabiwayo kufanele zihlale lapha.",
            diasporaSadcHint:
              "Iziguli nabaxhasi bendawo kuwo wonke amazwe esifunda.",
            diasporaDiasporaHint:
              "UK, US, Gulf, Europe, Australia, nezinye izindlela zokuxhasa ze-diaspora.",
            diasporaReferenceHint:
              "Intengo yokuwela imingcele akufanele iwe ibe yizinga lemali lendawo elibuthakathaka.",
            brandingTitle: "Ukuthembeka, isithombe somsebenzi nemibhalo",
            brandingBody:
              "Isignesha yakho egciniwe isivele isetshenziswa futhi emarisiphini. Ukulayisha ilogo yomsebenzi nokugcina izivumelwano ezisayiniwe ngasese sekukhona lapha ukuze amabhili, amarisidi, nemibhalo ye-AfriMed™ kusebenzise umthombo owodwa oqinisekisiwe womkhiqizo kadokotela.",
            brandingPanelCta: "Isithombe somsebenzi nesivumelwano",
            brandLogo: "Ilogo yomsebenzi",
            brandLogoReady: "Kulungele",
            brandLogoMissing: "Kulahlekile",
            brandLogoHint:
              "Sebenzisa umthombo owodwa welogo yamabhili, amarisidi, nemibhalo yesithombe somsebenzi.",
            brandSignature: "Isignesha somnikezeli",
            brandSignatureReady: "Kulungele",
            brandSignatureMissing: "Kulahlekile",
            brandSignatureHint:
              "Ukusayina amarisiphi sekusebenzisa le nto kakade.",
            brandAgreement: "Isivumelwano esisayiniwe",
            brandAgreementHint:
              "Izivumelwano ezisayiniwe kufanele zigcinwe ngasese futhi zibe ne-version.",
            fxTitle: "Ukulunga kwe-FX emingceleni",
            fxBody:
              "Uma oxhasayo ekhokha evela e-UK, e-US, noma kwenye imakethe enohlobo lwemali oluqinile, i-AfriMed™ kufanele ivikele ukukhokhelwa okulungile kudokotela nakupuratifomu. Lokho kusho ukusebenzisa isiqondiso se-FX esithembekile nokwethulwa kwenani okuhambisana nemakethe yomkhokhi.",
            fxFootnote:
              "Ukuqondisa komkhiqizo: bamba inani lokubonana ngemali yesevisi, bese usebenzisa izixhumanisi zokukhokha ze-Stripe kanye nokwethulwa kwenani okuguqukayo lapho indlela yokukhokha yangemuva isizinzile.",
            brandingSheetTitle: "Isithombe somsebenzi nesivumelwano esisayiniwe",
            brandingSheetBody:
              "Gcina isignesha kadokotela, ilogo yomsebenzi, kanye nendawo yezivumelwano ezigciniwe ze-AfriMed™ endaweni eyodwa ukuze amarisiphi, imibhalo, nokulungela ukukhokhelwa kuhlale kuhambisana.",
            brandingSheetSignature: "Isignesha kadokotela",
            brandingSheetLogo: "Ilogo yomsebenzi",
            brandingSheetAgreement: "Izivumelwano ezigciniwe",
            brandingSheetDoctorTerms: "Imigomo kadokotela",
            brandingSheetDoctorTermsHint:
              "Indlela yokulayisha idokhumenti elinomnikazi kadokotela ihlala lapha ukuze iziguli zibuke imigomo yomsebenzi ehlukene kweye-AfriMed™.",
            brandingSheetVersion: "Inguqulo yesivumelwano",
            brandingSheetSignatureTitle:
              "Layisha isignesha yakho esisetshenziswa emarisiphini nasezivumelwaneni ze-AfriMed™",
            brandingSheetSignatureBody:
              "Bamba isignesha yakho lapha kusenesikhathi ukuze i-AfriMed™ ikwazi ukuyisebenzisa futhi emarisiphini nasekusayineni isivumelwano lapho kudingeka.",
            brandingSheetLogoTitle: "Ilogo yomsebenzi",
            brandingSheetLogoBody:
              "Sebenzisa ilogo ezinikele yemibhalo ekhishwa udokotela. Layisha i-PNG, JPG, noma i-WEBP. Lokhu kuhlala kuhlukene nesithombe sesivumelwano sepuratifomu ye-AfriMed™.",
            brandingSheetAgreementTitle: "Isivumelwano sepuratifomu",
            brandingSheetAgreementBody:
              "Buka isivumelwano kuqala, usayine, bese ugcina ikhophi efakwe endaweni yesivumelwano esigciniwe se-AfriMed™ ngaphambi kokuthi ukulungela ukukhokhelwa kuqedwe.",
            brandingDoctorTermsTitle: "Imigomo kadokotela noma yesikhungo",
            brandingDoctorTermsBody:
              "Layisha imigomo kadokotela okufanele iziguli ziyibuke ngesikhathi sokubhuka nokukhokha. Lokhu kuhlala kuhlukene nemigomo yepuratifomu ye-AfriMed™ futhi kumela imithetho yesikhungo noma kadokotela.",
            brandingAddLogo: "Engeza ilogo yomsebenzi",
            brandingUpdateLogo: "Buyekeza ilogo yomsebenzi",
            brandingAgreementDownloadError:
              "Asikwazanga ukulanda ikhophi yesivumelwano esisayiniwe.",
            brandingAgreementError:
              "Asikwazanga ukusayina nokulayisha isivumelwano.",
            brandingAgreementPreviewError:
              "Asikwazanga ukuvula ukubuka kusengaphambili kwesivumelwano.",
            brandingAgreementSaved: "Isivumelwano sisayiniwe futhi silayishiwe.",
            brandingAgreementSignedAt: "Sisayiniwe ngo",
            brandingAgreementSignedAtEmpty: "Asikakasayinwa",
            brandingAgreementVersion: "Inguqulo yesivumelwano",
            brandingAwaitingUpload: "Kulindwe ukulayishwa",
            brandingComplete: "Kuphelele",
            brandingDoctorTermsEmpty:
              "Akukabi khona migomo kadokotela ebhekiswe ezigulini elayishiwe.",
            brandingDoctorTermsError:
              "Asikwazanga ukulondoloza idokhumenti yemigomo kadokotela.",
            brandingDoctorTermsMissing: "Layisha imigomo kadokotela kuqala.",
            brandingDoctorTermsSaved: "Imigomo kadokotela ilondoloziwe.",
            brandingDoctorTermsUpdated: "Kubuyekezwe ngo-{date}",
            brandingDownloadAgreement: "Landa isivumelwano",
            brandingHidePreview: "Fihla ukubuka kusengaphambili",
            brandingIncomplete: "Akuphelele",
            brandingLoadError:
              "Asikwazanga ukulayisha isithombe sokukhokha okwamanje.",
            brandingLogoAlt: "Ukubuka kusengaphambili kwelogo yomsebenzi",
            brandingLogoEmpty: "Akukho logo yomsebenzi esalondoloziwe okwamanje.",
            brandingLogoError:
              "Asikwazanga ukulondoloza ilogo yomsebenzi.",
            brandingLogoSaved: "Ilogo yomsebenzi ilondoloziwe.",
            brandingMissing: "Kulahlekile",
            brandingPending: "Kulindile",
            brandingPreviewAgreement: "Buka isivumelwano",
            brandingPreviewFrameTitle: "Ukubuka kusengaphambili kwesivumelwano se-AfriMed™",
            brandingPreviewSurfaceTitle: "Ukubuka kusengaphambili kwesivumelwano",
            brandingPreviewSurfaceBody:
              "Hlola ukuthi isignesha, igama lomsebenzi, nenguqulo yesivumelwano kulungile ngaphambi kokuyigcina endaweni egciniwe.",
            brandingPrivateStorage:
              "Lezi zinto zigcinwa ngasese ukuze kusekelwe ukulungela ukukhokhelwa nemibhalo yesithombe somsebenzi.",
            brandingRefreshError:
              "Asikwazanga ukuvuselela isithombe sokukhokha.",
            brandingReplaceDoctorTerms: "Shintsha imigomo kadokotela",
            brandingSignAgreement: "Sayina isivumelwano",
            brandingSignatureSaved: "Isignesha kadokotela ilondoloziwe.",
            brandingUploadDoctorTerms: "Layisha imigomo kadokotela",
            brandingViewDoctorTerms: "Buka imigomo kadokotela",
          },
          patient: {
            subtitleLong:
              "I-akhawunti yesiguli ye-AfriMed™ ihlala imahhala. Umlando wokukhokha, amarisidi, nezinkokhelo zoxhaso kugcinwa lapha.",
            accountType: "Uhlobo lwe-akhawunti",
            freeTitle: "I-akhawunti yesiguli yamahhala",
            freeBody:
              "Iziguli azikhokhi imali yobulungu ku-AfriMed™ endleleni yokunakekelwa okuqondile. Izindleko zivele kuphela uma ubhukha izinsiza ezikhokhelwayo nodokotela ku-AfriMed™.",
            transactions: "Ukuthengiselana",
            transactionsValue: "Bona amarisidi",
            transactionsCta: "Izinkokhelo namabhili",
            transactionsHint:
              "Vula Izinkokhelo namabhili ukuze ubone umlando wokukhokha nokuthekeliswa kwamarisidi.",
            downloads: "Okulandiwayo",
            downloadsValue: "Okuthekelisiwe kulungile",
            downloadsCta: "Okulandiwayo",
            downloadsHint:
              "Amarisidi nokuthekelisa kokukhokha sekumi ku-Okulandiwayo ngenkathi amathuluzi ezigciniwe ekhula.",
          },
        },
        transactions: {
          title: "Izinkokhelo namabhili",
          subtitle: "Bona umlando bese ulanda amarisidi",
          subtitleLong:
            "Landelela umlando wokukhokha, izimo zokukhokha, futhi ugcine amarisidi eseduze.",
          totalPaid: "Konke okukhokhiwe",
          totalPaidMixed: "Izimali ezixubile",
          totalPaidMixedHelper:
            "Kubukwa ngemali ngayinye. Akukho ukuguqulwa kwe-FX okufakiwe kulokhu konke.",
          invoiceCount: "Amabhili",
          open: "Kuvulekile",
          refunded: "Kubuyisiwe",
          billingCenter: "Amarisidi nokulandiwe",
          billingCenterSubtitle:
            "Sebenzisa Okulandiwayo ukuthola okuthekelisiwe ngokushesha ngenkathi amathuluzi aphelele okukhokha esakhula.",
          openCenter: "Vula okulandiwayo",
          downloadCsv: "Landa i-CSV",
          sponsorTitle: "Izinkokhelo zoxhaso zomndeni ne-diaspora",
          sponsorBody:
            "Iziguli eziningi zixhaswa amalungu omndeni ase-SADC nase-diaspora. Lungisa imininingwane yoxhaso kanye, bese ukhiqiza isixhumanisi sokukhokha esivikelekile ukuze sabelwane kalula.",
          sponsorSadc: "Izimakethe ze-SADC",
          sponsorDiaspora: "Izimakethe ze-diaspora",
          sponsorTotal: "Ukuhlanganiswa konke",
          sponsorAction: "Lungisa inkokhelo yoxhaso",
          sponsorLearnMore: "I-AfriMed™ iphatha kanjani izinkokhelo",
          recentInvoices: "Amabhili akamuva",
          recentInvoicesSubtitle:
            "Amarisidi, ukuqinisekiswa kokukhokha, nezibuyekezo zokukhokha zizoqoqeka lapha njengoba umsebenzi omusha ufika.",
          invoice: "Ibhili",
          empty: "Awekho amarekhodi okukhokha okwamanje",
          emptySubtitle:
            "Izinkokhelo namarisidi kuzovela lapha lapho ukukhokha kuqala.",
          states: {
            refundPending: "Kukhanseliwe / Imbuyiselo iyaqhubeka",
            refunded: "Kukhanseliwe / Kubuyisiwe",
            cancelled: "Kukhanseliwe",
            paidPharmacy: "Kukhokhiwe / Ikhemisi",
            paid: "Kukhokhiwe",
            pending: "Kulindile",
          },
          errors: {
            refreshSponsorPayments:
              "Asikwazanga ukuvuselela amarekhodi akho enkokhelo yoxhaso okwamanje.",
            load:
              "Asikwazanga ukulayisha umlando wakho wokukhokha okwamanje.",
          },
          sponsorSellerDoctor: "udokotela",
          sponsorSellerPharmacy: "ikhemisi",
          sponsorFx: "Inkomba ye-FX",
          sponsorSheetTitle: "Lungisa inkokhelo yoxhaso",
          sponsorSheetBody:
            "Sebenzisa lokhu ukulungisa isicelo sokukhokha esivikelekile se-AfriMed™ somndeni noma sabaxhasi abase-SADC nase-diaspora. Dala isixhumanisi sokukhokha esivikelekile kuqala, bese usabelana ngaso nomxhaso kahle.",
          sponsorContact: "Imininingwane yomxhaso",
          sponsorName: "Igama lomxhaso",
          sponsorNamePlaceholder: "Igama eligcwele",
          sponsorRelationship: "Ubudlelwano",
          sponsorRelationshipPlaceholder: "Umfowethu, udadewethu, umzali, umnakekeli",
          sponsorCountry: "Izwe lomxhaso",
          sponsorEmail: "I-imeyili yomxhaso",
          sponsorEmailPlaceholder: "igama@example.com",
          sponsorRequest: "Isicelo sokukhokha",
          sponsorPaymentFor: "Kukhokhelwa ini",
          sponsorPaymentForPlaceholderDoctor: "Ukubonana nge-telemedicine",
          sponsorPaymentForPlaceholderPharmacy: "Ukugcwaliswa kweresiphi",
          sponsorPaymentForPlaceholder: "Ukubonana nge-telemedicine",
          sponsorPaymentForPatient: "{service} ka {name}",
          sponsorPaymentForDefaultDoctor: "Ukubonana nge-telemedicine",
          sponsorPaymentForDefaultPharmacy: "Ukugcwaliswa kweresiphi",
          sponsorAmount: "Inani",
          sponsorCurrency: "Uhlobo lwemali",
          sponsorFxNote:
            "Sebenzisa umthetho olungile we-FX kubakhokhi be-diaspora. Umxhaso ose-UK noma e-US akufanele aphoqwe esilinganisweni esibuthakathaka uma imali yokuxhasa iqinile.",
          sponsorFxRefreshing: "Kuvuselelwa inani elifanele le-FX lomxhaso…",
          sponsorFxReference: "Inkomba ye-FX",
          sponsorFxSentence: "Inani lesevisi {amount} {currency} • {reference}.",
          sponsorPreview: "Ukuhlola okuzokwabelwana",
          sponsorLiveLinkTitle: "Isixhumanisi sokukhokha esivikelekile somxhaso",
          sponsorLiveLinkBody:
            "Dala isixhumanisi sokukhokha esivikelekile esibanjwe yi-Stripe esingabelwana nomndeni noma abaxhasi. Lokhu kwenza uhambo lomxhaso luhlanzeke kunokucela ukuthi bahlele inkokhelo mathupha.",
          sponsorGenerateLink: "Dala isixhumanisi sokukhokha",
          sponsorCopied: "Isifinyezo sikopishiwe",
          sponsorCopy: "Kopisha isifinyezo somxhaso",
          sponsorEmailAction: "Vula idrafti ye-imeyili enesixhumanisi",
          sponsorLinkRequired:
            "Dala isixhumanisi sokukhokha esivikelekile ngaphambi kokuvula idrafti ye-imeyili ukuze umxhaso athole isenzo sangempela sokukhokha.",
          sponsorGeneratedUrl: "Isixhumanisi esidalwe",
          sponsorPlatformSummary: "Ukuhlola kokwabiwa kwemali",
          sponsorPlatformSummaryEmpty:
            "Ukuhlola kokwabiwa kwemali kuzovela lapha uma indawo yokwamukela inkokhelo isixhunywe.",
          sponsorSettlementFee: "{amount} {currency} imali ye-AfriMed™",
          sponsorSettlementNet:
            "{amount} {currency} kusasele ngemva kwemali yepuratifomu ku-akhawunti exhunyiwe yokukhokhelwa ye-{seller}.",
          sponsorSettlementMissingDestination:
            "Lesi sixhumanisi siyasebenza, kodwa indawo yokukhokhelwa exhunyiwe ye-{seller} ayikaxhunywa okwamanje. Xhuma kabusha i-Stripe ngaphambi kokusisebenzisa bukhoma.",
          sponsorRecoveryError:
            "Inkokhelo yomxhasi itholakele, kodwa i-AfriMed ayikwazanga ukuqedela ukubhukha okwamanje.",
          sponsorAmountPlaceholder: "50.00",
          sponsorCurrencyPlaceholder: "ZAR",
          sponsorCurrencyPlaceholderUsd: "USD",
          sponsorBriefTitle: "Isicelo senkokhelo yoxhaso se-AfriMed™",
          sponsorBriefSponsor: "Umxhaso",
          sponsorBriefRelationship: "Ubudlelwano",
          sponsorBriefCountry: "Izwe lomxhaso",
          sponsorBriefPhone: "Ucingo lomxhaso",
          sponsorBriefEmail: "I-imeyili yomxhaso",
          sponsorBriefPaymentFor: "Kukhokhelwa ini",
          sponsorBriefAmount: "Inani eliceliwe",
          sponsorBriefLink: "Isixhumanisi sokukhokha esivikelekile",
          sponsorBriefReady:
            "Lesi sixhumanisi esivikelekile se-AfriMed™ sesilungele inkokhelo yomxhaso.",
          sponsorBriefNeedLink:
            "Dala isixhumanisi sokukhokha esivikelekile se-AfriMed™ ngaphambi kokuthumela lesi sicelo ukuze umxhaso athole isenzo sangempela sokukhokha.",
          sponsorBriefFx:
            "Izinkokhelo ezinqamula imingcele kufanele zihloniphe ukuboniswa kwe-FX okufanele ukuze ukubonana kukhokhelwe kahle.",
          sponsorMailSubject: "Isicelo sokukhokha se-AfriMed™",
          sponsorCreateLinkError:
            "Asikwazanga ukudala isixhumanisi senkokhelo yoxhaso okwamanje.",
        },
        downloads: {
          title: "Okulandiwayo",
          subtitle: "Thumela idatha, imibiko nezifinyezo",
          subtitleLong:
            "Gcina okuthekelisiwe kwe-AfriMed™ kusondele ngenkathi amathuluzi aphelele ezigciniwe esaqhubeka nokukhula.",
          billingSnapshot: "Isifinyezo sokukhokha",
          billingSnapshotSubtitle:
            "Landa imigqa yokukhokha njenge-CSV yamarisidi, ukubuyiselwa kwemali, noma umsebenzi weshidi ledatha.",
          subscriptionSnapshot: "Isifinyezo sokulungela ukukhokhelwa",
          subscriptionSnapshotSubtitle:
            "Landa isifinyezo se-PDF sokulungela ukukhokhelwa noma indlela yakho yokukhokha.",
          profileSnapshot: "Iphrofayela yezilungiselelo",
          profileSnapshotSubtitle:
            "Landa isifinyezo se-PDF sesimo sakho samanje sephrofayela nezilungiselelo.",
          comingNext: "Okulandelayo",
          comingNextSubtitle:
            "Amabundle agcwele ezinqolobaneni, ama-PDF wamabhili, kanye nokuthekeliswa kwamarekhodi acebile e-AfriMed™ kuzokwengezwa lapha ngaphandle kokushintsha le navigation.",
        },
        closeAccount: {
          title: "Vala, Misa noma Susa I-akhawunti",
          subtitle: "Sizokuqondisa ngezinketho eziphephile kuqala",
          subtitleLong:
            "Siveza izinketho zokumisa eziphephile nezendlela yokubuyela kuqala ukuze ungalahlekelwa ukufinyelela ngengozi.",
          pauseTitle: "Misa ukufinyelela ku-akhawunti",
          pauseBody:
            "Kuhle uma ufuna ikhefu lesikhashana. Lokhu kufanele kugcine umlando wakho kodwa kumise umsebenzi ojwayelekile.",
          pauseAction: "Misa i-akhawunti",
          pauseListOne: "Umlando uhlala uphephile",
          pauseListOneBody: "Gcina umlando we-akhawunti ngenkathi umsebenzi ojwayelekile umi.",
          pauseListTwo: "Isinyathelo sokuqala esiphephile",
          pauseListTwoBody: "Sebenzisa lokhu kuqala uma isidingo siyisikhashana.",
          closeTitle: "Vala i-akhawunti",
          closeBody:
            "Kuhle uma ufuna ukuyeka ukusebenzisa i-AfriMed™ kodwa usadinga indlela ehlelekile yokuphuma nokuqonda ukugcinwa kwamarekhodi.",
          closeAction: "Vala i-akhawunti",
          closeListOne: "Ukubuyekezwa kokuphuma",
          closeListOneBody:
            "Kudingeka siveze ukugcinwa kwamarekhodi, amarisidi, kanye nomhlahlandlela wokuhambisana ngaphambi kokuba ukuvalwa kube kokugcina.",
          closeListTwo: "Indlela yokubuyela ibalulekile",
          closeListTwoBody:
            "Ukuvalwa kufanele kungabi yingozi njengokususwa ngokuphelele.",
          deleteTitle: "Cela ukususwa",
          deleteBody:
            "Lena inketho ezwela kakhulu. I-AfriMed™ iphatha izicelo zokususa ngesandla ukuze ubuwena, ukukhokha, nezivikelo zempilo kuqinisekiswe ngaphambi kokususwa okungunaphakade.",
          deleteAction: "Cela ukususwa",
          deleteListOne: "Uyicela kanjani ukusulwa",
          deleteListOneBody:
            "Thumela i-imeyili ku info@afrimedtelehealth.com usebenzisa i-imeyili exhunywe ku-akhawunti yakho ye-AfriMed™. Uma i-imeyili ingatholakali, sebenzisa iziteshi zosizo ze-AfriMed™ futhi unikeze imininingwane eyanele yokuqinisekisa.",
          deleteListTwo: "Faka le mininingwane",
          deleteListTwoBody:
            "Igama eligcwele, i-imeyili noma inombolo yocingo ye-akhawunti, indima yakho, kanye nesisho esithi: “Ngicela ukusulwa kwedatha ye-akhawunti yami ye-AfriMed.”",
          nextStepTitle: "Isikhathi sokucubungula",
          nextStepBody:
            "I-AfriMed™ yamukela izicelo zokusulwa bese iqala ukuqinisekisa nokucubungula ngokushesha okungenzeka. Isikhathi sokuqeda sincike ekuqinisekisweni kobuwena nasezibophweni zomthetho, kodwa izicelo ngokuvamile ziqedwa kungakapheli izinsuku ezingama-30.",
        },
        notificationPreferences: {
          appointmentReminders: {
            title: "Izikhumbuzi zama-aphoyintimenti",
            introDoctor:
              "I-AfriMed ithumela isikhumbuzi esisodwa eseduze nesikhathi sokuqala ihora elilodwa ngaphambi kokuvakashelwa okuqinisekisiwe. I-digest yasekuseni iza kamuva.",
            introPatient:
              "I-AfriMed ithumela izikhumbuzi emahoreni angu-24 nasehoreni eli-2 ngaphambi kokuvakashelwa okuqinisekisiwe. I-SMS isiza noma ungakangeni.",
            timingDoctor: "Isikhathi samanje: ihora elilodwa ngaphambi kwe-aphoyintimenti.",
            timingPatient: "Isikhathi samanje: amahora angu-24 kanye nama-2 ngaphambi kwe-aphoyintimenti.",
          },
          channels: {
            inApp: {
              title: "Izikhumbuzi ngaphakathi kwe-app",
              support: "Zivela ebhokisini lezaziso le-AfriMed nasemakhasini ama-aphoyintimenti.",
            },
            sms: {
              title: "Izikhumbuzi ze-SMS",
              support: "Indlela engcono uma ungangenanga ku-app.",
            },
            email: {
              title: "Izikhumbuzi ze-imeyili",
              support: "Thumela futhi izinto ezibalulekile ebhokisini lakho le-imeyili.",
            },
            push: {
              title: "Izaziso ze-push",
              support: "Kudinga imvume yesiphequluli noma yedivayisi.",
            },
          },
        },
      },
      notificationPreferences: {
        appointmentReminders: {
          title: "Izikhumbuzi zama-aphoyintimenti",
          introDoctor:
            "I-AfriMed ithumela isikhumbuzi esisodwa eseduze nesikhathi sokuqala ihora elilodwa ngaphambi kokuvakashelwa okuqinisekisiwe. I-digest yasekuseni iza kamuva.",
          introPatient:
            "I-AfriMed ithumela izikhumbuzi emahoreni angu-24 nasehoreni eli-2 ngaphambi kokuvakashelwa okuqinisekisiwe. I-SMS isiza noma ungakangeni.",
          timingDoctor: "Isikhathi samanje: ihora elilodwa ngaphambi kwe-aphoyintimenti.",
          timingPatient: "Isikhathi samanje: amahora angu-24 kanye nama-2 ngaphambi kwe-aphoyintimenti.",
        },
        channels: {
          inApp: {
            title: "Izikhumbuzi ngaphakathi kwe-app",
            support:
              "Zivela ebhokisini lezaziso le-AfriMed nasemakhasini ama-aphoyintimenti.",
          },
          sms: {
            title: "Izikhumbuzi ze-SMS",
            support: "Indlela engcono uma ungangenanga ku-app.",
          },
          email: {
            title: "Izikhumbuzi ze-imeyili",
            support: "Thumela futhi izinto ezibalulekile ebhokisini lakho le-imeyili.",
          },
          push: {
            title: "Izaziso ze-push",
            supportGranted:
              "Sebenzisa izaziso zesiphequluli kwezibalulekile nezisheshayo zokunakekelwa uma i-AfriMed ingekho phambili.",
            supportPrompt:
              "Vumela izaziso zesiphequluli lapho ucelwa ukuze uthole izaziso ezibalulekile nezisheshayo zokunakekelwa.",
            unsupported:
              "Lesi siphequluli asisekeli izicelo zezaziso lapha.",
          },
          sound: {
            title: "Umsindo wesaziso esiphuthumayo",
            support:
              "Kudlala umsindo omfushane wezaziso zokunakekelwa eziphuthumayo ngesikhathi i-AfriMed ivuliwe.",
          },
          vibration: {
            title: "Ukudlidliza okuphuthumayo",
            support:
              "Kungeza ukudlidliza okufushane kumadivayisi asekelwayo ngezaziso zokunakekelwa eziphuthumayo.",
          },
        },
        footer:
          "Izaziso zesiphequluli manje zimboza izaziso zokunakekelwa ngesikhathi iseshini ivuliwe uma imvume ivunyelwe. Ukulethwa kwe-push okuphelele ngaphandle kokungena kusadinga isisekelo sokubhalisa se-service worker futhi kusahlehlisiwe.",
      },
      patientsAge: {
        title: "Iminyaka yeziguli",
        subtitle: "Khetha amaqembu eminyaka eziguli ozamukelayo njengamanje.",
        infants: "Izinsana",
        infantsSupport: "iminyaka engu-0-2",
        children: "Izingane",
        childrenShort: "Izingane",
        adults: "Abantu abadala",
      },
      services: {
        title: "Izinsiza namanani",
        emptyTitle: "Azikabi khona izinsiza",
        emptySupport:
          "Engeza insiza yakho yokuqala ukuze uqale ukunikeza izikhathi ezibhukekayo namanani.",
        add: "Engeza insiza",
        addTitle: "Engeza insiza",
        searchPlaceholder: "Sesha noma ungeze",
        clearSearch: "Sula usesho",
        addCustom: "Engeza insiza yakho",
        catalogTitle: "Ikhathalogi yezinsiza",
        setPricingTitle: "Setha amanani",
        editPrice: "Hlela intengo",
        deleteService: "Susa insiza",
        deleteNamedTitle: "Susa insiza ethi {name}?",
        deleteBody: "Le nsiza izosuswa ezinsizeni zephrofayela yakho.",
        added: "Insiza yengeziwe",
        duplicate:
          "Insiza enobude besikhathi obufanayo isivele ikhona. Hlela ekhona esikhundleni salokho.",
        moreOptions: "Ezinye izinketho ze-{name}",
        price: "Intengo",
        pricePlaceholder: "0.00",
        clearPrice: "Sula intengo",
        duration: "Ubude besikhathi",
        durationMinutes: "imizuzu engu-{minutes}",
        durationShort: "imizuzu engu-{minutes}",
        signInRequired: "Kufanele ungene ngemvume.",
        catalog: {
          eczemaCare: "Ukunakekelwa kwe-eczema",
          rosaceaTreatments: "Imithi ye-rosacea yochwepheshe",
          moleAssessment: "Ukuhlolwa kwamamole ochwepheshe",
          allergyTesting: "Ukuhlolwa nokuphathwa kwe-allergy",
        },
      },
      specialtyEdit: {
        title: "Ubuchwepheshe",
        subtitle: "Khetha noma ubuyekeze ubuchwepheshe bakho obukhulu.",
        search: "Sesha",
        clearSearch: "Sula usesho",
        updated: "Ubuchwepheshe bubuyekeziwe",
        saveFailed: "Asikwazanga ukulondoloza izinguquko. Zama futhi.",
        catalogHidden:
          "Ikhathalogi yobuchwepheshe ayikahunyushwa kulolu limi okwamanje.",
        empty: "Akukho okufanayo.",
      },
      consultancyEdit: {
        title: "Hlela indawo yokubonisana",
        addLocation: "Engeza enye indawo",
        locationFallback: "Indawo {count}",
        facilityNameFallback: "Igama lendawo",
        facilityAddressFallback: "Engeza indawo yakho eyinhloko nekheli layo",
        availability: "Ukutholakala",
        bookingHorizon: "Ibanga lokubhuka",
        cancellationNotice: "Isaziso sokukhansela",
        patientsAge: "Iminyaka yeziguli",
        paymentMethods: "Izindlela zokukhokha",
        insurancePolicy: "Inqubomgomo yomshwalense",
        servicesPricing: "Izinsiza namanani",
        setInAvailability: "Setha ku-Ukutholakala",
        managedInAvailability: "Kuphethwe ku-Ukutholakala",
        everyDay: "Nsuku zonke",
        everyDays: "Njalo ezinsukwini ezingu-{count}",
        everyWeek: "Njalo ngesonto",
        everyWeeks: "Njalo emavikini angu-{count}",
        everyWeekDays: "{base}, {days}",
        twentyFourHours: "Amahora angu-24",
        daysAhead: "izinsuku ezingu-{count} ngaphambili",
        hoursNotice: "isaziso samahora angu-{count}",
        accepting: "Yamukela",
        permissionsBlocked:
          "Izindawo zokubonisana azikwazi ukubuyekezwa okwamanje ngenxa yezimvume. Idatha ayigcinwanga kudathabhesi.",
        saveFailed:
          "Asikwazanga ukulondoloza indawo yokubonisana. Zama futhi.",
      },
      evisit: {
        title: "Virtual consultations",
        toggleTitle: "Virtual consultations",
        helper:
          "Vumela iziguli zihlele ukuvakasha okubonakalayo ekhalendeni lakho.",
        availabilityHelp:
          "Virtual consultations isebenzisa ukutholakala kukadokotela wakho emithethweni yeshejuli nokubhuka. Phatha ubude bezinsiza namanani ngokwehlukana ku-Izinsiza namanani.",
        availability: "Ukutholakala",
        servicesPricing: "Izinsiza namanani",
        patientsAge: "Iminyaka yeziguli",
        paymentMethods: "Izindlela zokukhokha",
        insurancePolicy: "Inqubomgomo yomshwalense",
        price: "Virtual consultation price",
        setPrice: "Setha inani",
        priceLabel: "Inani",
        pricePlaceholder: "0.00",
        clearPrice: "Sula inani",
        priceDialogTitle: "Virtual consultations",
        selectTime: "Khetha isikhathi",
        closeTimePicker: "Vala isikhethi sesikhathi",
        switchTimeField: "Shintsha inkambu yesikhathi",
        cancel: "Khansela",
        confirm: "Kulungile",
        dialogConfirm: "Qinisekisa",
        manage: "Phatha",
        manageInAvailability: "Phatha ku-Ukutholakala",
        accepting: "Yamukela",
        summary: {
          regularDaysAhead:
            "izinsuku ezijwayelekile ezingu-{count} • izinsuku ezingu-{days} ngaphambili",
          regularDaysOnly: "izinsuku ezijwayelekile ezingu-{count}",
          daysAheadOnly: "izinsuku ezingu-{days} ngaphambili",
        },
        servicesCount: "izinsiza ezingu-{count}",
      },
      registrationUpload: {
        title: "Idokhumenti yokubhalisa",
        subtitle:
          "Layisha ikhophi ecacile yokubhalisa kwakho emkhandlwini",
        addTitle: "Engeza idokhumenti yakho yokubhalisa",
        addAction: "Engeza",
        changeFile: "Shintsha ifayela",
        addMore: "Engeza okunye",
        existingNotice:
          "Kunomqulu wokubhalisa osevele ukhona kusukela ngo-{date}. Layisha omusha ukuze uwushintshe.",
        status: {
          done: "Ukubhalisa kuqashelwe",
          failed: "Ukuqinisekisa kwehlulekile",
        },
        saveOcrFailed: "Asikwazanga ukulondoloza imiphumela ye-OCR.",
        analysisFailed:
          "Ukuhlaziywa kwedokhumenti kwehlulekile. Sicela uzame elinye ifayela.",
        statusReadFailed:
          "Asikwazanga ukufunda isimo sokuqinisekisa.",
        summary: {
          registrationNumber: "Inombolo yokubhalisa",
          practitionerName: "Igama eligcwele (udokotela)",
          specialty: "Ubuchwepheshe / isigaba",
          practitionerFallback: "Udokotela",
        },
        retention: "Kugcinwe kuze kube ngu-{date}.",
        consent:
          "Ngokulayisha lokhu, uyavuma ukuthi i-AfriMed igcine le dokhumenti ngokubethela kusukela ekugcineni kuya ekugcineni ukuze iqinisekise. Izogcinwa kuze kube yizinsuku ezingu-90.",
        alt: {
          verified: "Kuqinisekisiwe",
          documentPreview: "Ukubuka kuqala idokhumenti yokubhalisa",
        },
      },
      registrationDetails: {
        title: "Imininingwane yokubhalisa",
        subtitle: "Gcina imininingwane yakho yokubhalisa isesikhathini.",
        expiryPast:
          "Usuku lokuphelelwa alukwazi ukuba sesikhathini esedlule. Khetha namuhla noma usuku oluzayo.",
        signInRequired: "Kufanele ungene ukuze uqhubeke.",
        saveFailed: "Asikwazanga ukulondoloza imininingwane yokubhalisa.",
        savedPending: "Izinguquko zilondoloziwe (ukuqinisekisa kusalindile)",
        yearsRange: "Faka inani eliphakathi kuka-1 no-60.",
        suggestions: {
          botswana: "Botswana Health Professions Council",
          hpcsa: "Health Professions Council of South Africa (HPCSA)",
          namibia: "Namibia Health Professions Council",
          zambia: "Zambia Medical Council",
          zimbabwe: "Zimbabwe Medical & Dental Practitioners Council",
          lesotho: "Lesotho Medical, Dental and Pharmacy Council",
          eswatini: "Eswatini Medical & Dental Council",
          mozambique: "Mozambique Ministry of Health",
          angola: "Angola Ordem dos Médicos",
          other: "Okunye",
        },
        fields: {
          number: {
            label: "Inombolo yokubhalisa",
            placeholder: "isb. BMD 12345",
          },
          issuer: {
            label: "Isiphathimandla esikhiphayo",
            placeholder: "Khetha noma bhala isiphathimandla esikhiphayo",
          },
          issueDate: {
            label: "Usuku lokukhishwa",
          },
          expiryDate: {
            label: "Usuku lokuphelelwa",
          },
          datePlaceholder: "DD MMM, YYYY",
          yearsExperience: {
            label: "Iminyaka yesipiliyoni",
            placeholder: "isb. 12",
          },
        },
      },
      availability: {
        pageTitle: "Ishejuli kadokotela",
        pageSubtitle:
          "Setha indlela yakho evamile yekliniki, bese uhlela okuhlukile kusenesikhathi.",
        loadError: "Asikwazanga ukulayisha ukutholakala okwamanje.",
        saveError: "Asikwazanga ukulondoloza lezi zinguquko zokutholakala.",
        overrideDateRequired:
          "Khetha usuku lokuqala nolokuphela kokubili.",
        overrideDateOrder:
          "Usuku lokuphela kufanele lufane noma lube ngemva kosuku lokuqala.",
        overrideTimeOrder:
          "Amahora akhethekile adinga isikhathi sokuvala esingemva kwesokuvula.",
        unavailableLabel: "Akutholakali ukubhukha",
        toggleOn: "Kuvuliwe",
        toggleOff: "Kuvaliwe",
        optionMinutes5: "imizuzu emi-5",
        optionMinutes10: "imizuzu eyi-10",
        optionMinutes15: "imizuzu eyi-15",
        optionMinutes20: "imizuzu eyi-20",
        optionMinutes30: "imizuzu engu-30",
        optionMinutes45: "imizuzu engu-45",
        optionHour1: "ihora eli-1",
        optionNoBuffer: "Ayikho ibhafa",
        optionWeeks2: "amasonto ama-2",
        optionMonth1: "inyanga eyi-1",
        optionMonths2: "izinyanga ezi-2",
        optionMonths3: "izinyanga ezi-3",
        optionMonths6: "izinyanga ezi-6",
        optionMonths12: "izinyanga ezi-12",
        optionHours2: "amahora ama-2",
        optionHours12: "amahora ayi-12",
        optionHours24: "amahora angu-24",
        optionHours48: "amahora angu-48",
        optionHours72: "amahora angu-72",
        weeklyCard: "Iphethini yesonto",
        weeklyCardValue: "izinsuku ezijwayelekile ezingu-{count}",
        weeklyCardSupport:
          "Lena yishejuli eyisisekelo ebonwa iziguli njalo ngesonto.",
        bookingCard: "Ibanga lokubhuka",
        bookingCardValue: "izinsuku ezingu-{count} ngaphambili",
        bookingCardSupport:
          "Kusiza iziguli ziqinisekise ama-aphoyintimenti kusenesikhathi.",
        overrideCard: "Izinguquko ezihleliwe",
        overrideCardValue: "ezingu-{count} zikhona",
        overrideCardSupport:
          "amabhulokhi okungabi khona angu-{away} kanye nokungenela kwamahora akhethekile angu-{special}.",
        calendarTitle: "Ikhalenda yokutholakala",
        calendarSupport:
          "Sebenzisa ikhalenda ukuhlola usuku oluzayo, bese ulungisa amahora akho avamile nezinguquko ezihleliwe nxazonke zalo.",
        calendarMode: "Ukuhlela okuholwa yinyanga",
        selectedDate: "Usuku olukhethiwe",
        focusedDateLabel: "{weekday}, {day} {month} {year}",
        dayActiveCompact: "Kuvulekele ukubhukha",
        dayInactiveCompact: "Kuvaliwe ngokuzenzakalelayo",
        specialHours: "Amahora akhethekile",
        timeAway: "Isikhathi sokungabi khona",
        selectedDateOpen:
          "Iziguli zingabhuka phakathi kuka-{start} no-{end}.",
        selectedDateClosed:
          "Lolu suku luvaliwe okwamanje ngaphandle kokuthi ungeze amahora akhethekile.",
        overrideAppliedAvailable:
          "Ukungenela kwamahora akhethekile kuyasebenza ngalolu suku.",
        overrideAppliedUnavailable:
          "Ukungenela kwesikhathi sokungabi khona kuyasebenza ngalolu suku.",
        upcomingChanges: "Izinguquko ezihleliwe ezizayo",
        upcomingChangesSupport:
          "Umbono osheshayo wezinguquko ezilandelayo ezithinta idayari yakho.",
        specialHoursFallback: "Amahora akhethekile ekiliniki",
        timeAwayFallback: "Akutholakali ukubhukha",
        removeOverride: "Susa",
        overrideEmptyTitle: "Azikho izinguquko ezizayo",
        overrideEmptySupport:
          "Ikhalenda yakho yesikhathi esizayo okwamanje iqhutshwa yiphethini yesonto kuphela.",
        weeklyTitle: "Ukutholakala okuvamile kwesonto",
        weeklySupport:
          "Qala ngamahora aphindaphindeka emasontweni amaningi. Ikhefu losuku oluthile noma amakliniki akhethekile afakwa esigabeni esingezansi.",
        copyWeekdays: "Sebenzisa uMsombuluko ngezinsuku zomsebenzi",
        copyAll: "Sebenzisa uMsombuluko kuzo zonke izinsuku",
        weeklyHint:
          "Gcina iphethini yesonto ilula. Sebenzisa izinguquko zezinsuku ezingezansi ngamaholidi, ikhefu, namakliniki akhethekile angaphindi.",
        days: {
          monday: "uMsombuluko",
          tuesday: "uLwesibili",
          wednesday: "uLwesithathu",
          thursday: "uLwesine",
          friday: "uLwesihlanu",
          saturday: "uMgqibelo",
          sunday: "iSonto",
        },
        opens: "Kuvula",
        closes: "Kuvala",
        applyWeekdays: "Izinsuku zomsebenzi",
        applyAllDays: "Zonke izinsuku",
        overrideTitle: "Izinguquko zezinsuku ezihleliwe",
        overrideSupport:
          "Sebenzisa lokhu ngekhefu lonyaka, amaholidi omphakathi, izingqungquthela, noma amakliniki anwetshiwe angaba kanye. Lokhu kudlula iphethini yesonto ejwayelekile.",
        overrideHint: "Kuhle ekuhleleni izinyanga ezizayo",
        addOverride: "Engeza ushintsho oluhleliwe",
        addOverrideSupport:
          "Ungavimba isikhathi sonke sekhefu noma uvule usuku lweklinikhi olungokwehlukile olunamahora ahlukile.",
        overrideSetup: "Imininingwane yoshintsho",
        overrideSetupSupport:
          "Setha uhlobo loshintsho kuqala, bese unikeza ilebula ecacile ezokwaziwa ithimba lakho kamuva.",
        overrideTypeAvailable: "Amahora akhethekile",
        overrideTypeUnavailable: "Isikhathi sokungabi khona",
        overrideType: "Uhlobo loshintsho",
        overrideLabel: "Ilebula",
        overrideLabelPlaceholder:
          "Ikhefu lonyaka, ingqungquthela, ikliniki eyengeziwe yangoMgqibelo...",
        overrideTiming: "Isikhathi esisebenzayo",
        overrideTimingSupportAvailable:
          "Khetha uhla lwezinsuku, bese usetha amahora okuvula aleli windi leklinikhi elingokwehlukile.",
        overrideTimingSupportUnavailable:
          "Khetha uhla lwezinsuku ofuna luvalwe ngenxa yekhefu, amaholidi, noma okunye ukungabi khona.",
        overrideDatePending: "Khetha izinsuku",
        startsOn: "Kuqala ngo",
        endsOn: "Kuphela ngo",
        specialOpens: "Kuvula",
        specialCloses: "Kuvala",
        addChange: "Engeza ushintsho oluhleliwe",
        overrideEmpty:
          "Azikho izinguquko zezinsuku okwamanje. Ishejuli yakho yeviki yenza wonke umsebenzi okwamanje.",
        servicesTitle: "Izinsiza namanani",
        servicesSupport:
          "Ukutholakala kulawula ukuthi iziguli zingabhuka nini. Izinsiza namanani kulawula ukuthi zingabhuka ini, isikhathi esingakanani, nangayiphi intengo.",
        manageServices: "Phatha izinsiza namanani",
        servicesConfigured: "Izinsiza ezimisiwe",
        servicesConfiguredValue: "ezingu-{count} ziyasebenza",
        servicesConfiguredSupport:
          "Iziguli zikhetha okukodwa kulokhu ngaphambi kokubhuka usuku nesikhathi.",
        serviceDurations: "Izinketho zesikhathi",
        serviceDurationsValue: "ubude obungu-{count}",
        serviceDurationsSupport:
          "Ukubhuka kusebenzisa ubude bensiza ekhethiwe lapho kwakhiwa izikhala zesikhathi ezitholakalayo.",
        serviceCurrencies: "Izinhlobo zemali zamanani",
        serviceCurrenciesValue: "ezingu-{count} zivuliwe",
        serviceCurrenciesSupport:
          "Phatha amanani e-SADC ne-USD endleleni yezinsiza, hhayi ngaphakathi kokutholakala kweviki.",
        rulesTitle: "Imithetho yokubhuka",
        rulesSupport:
          "Le mithetho ilungisa indlela ukubhukha kweziguli okukhiqizwa ngayo kusuka eshejulini yakho.",
        headerRule: "Umthetho",
        headerChoice: "Inketho yamanje",
        slotDuration: "Ubude be-aphoyintimenti",
        slotDurationSupport:
          "Ubude obuyisisekelo bokubonisana obunikezwa iziguli.",
        bufferTime: "Ibhafa phakathi kwama-aphoyintimenti",
        bufferTimeSupport:
          "Isikhala esengeziwe ngaphambi kokuba isiguli esilandelayo sibhuke.",
        advanceBooking: "Iziguli zingabhuka kusenesikhathi",
        advanceBookingSupport:
          "Kubalulekile kongoti nasekuhleleni amaholidi esikhathi eside.",
        cancellationPolicy: "Isaziso sokukhansela",
        cancellationPolicySupport:
          "Isaziso okufanele iziguli zisinike ngaphambi kokukhansela.",
        autoConfirm: "Qinisekisa ama-aphoyintimenti ngokuzenzakalela",
        autoConfirmSupport:
          "Ukubhuka okusha kuya ngqo edayarini yakho ngaphandle kwemvume yesandla.",
        allowRescheduling: "Vumela iziguli zihlele kabusha",
        allowReschedulingSupport:
          "Iziguli zingashintsha ukubhuka kwazo uma kusahambisana nemithetho yakho.",
        unsaved: "Izinguquko zokutholakala ezingakalondolozwa",
        saved: "Ukutholakala kusezingeni lamanje",
        unsavedSupport:
          "Londoloza uma usulungile ukuze ukubhukha kweziguli kubonise ishejuli entsha.",
        savedSupport:
          "Amahora akho eviki, izinguquko zezinsuku, nemithetho yokubhuka kuvunyelanisiwe.",
        reset: "Setha kabusha",
        save: "Londoloza ukutholakala",
      },
      edit: {
        common: {
          saveFailed: "Asikwazanga ukulondoloza izinguquko zakho okwamanje.",
          sessionNotReady: "Iseshini ayikalungi. Sicela ungene futhi.",
        },
        idPassport: {
          awaitingReview: "Idokhumenti itholiwe. Kulindwe ukubuyekezwa.",
          signInRequired: "Kufanele ungene ukuze uqhubeke.",
          submitted: "I-ID / Iphasipoti ithunyelwe ukuze iqinisekiswe",
          processingFailedSnackbar:
            "Ukucutshungulwa kwedokhumenti kuhlulekile. Sicela uzame futhi.",
          statusReadFailed:
            "Asikwazanga ukufunda isimo sokuqinisekisa.",
        },
        name: {
          title: "Hlela igama",
          subtitle: "Buyekeza imininingwane ebonakala kuphrofayela yakho.",
          firstLabel: "Igama lokuqala",
          firstPlaceholder: "Faka igama lokuqala",
          lastLabel: "Isibongo",
          lastPlaceholder: "Faka isibongo",
          clearFirst: "Sula igama lokuqala",
          clearLast: "Sula isibongo",
          updated: "Igama libuyekeziwe",
        },
        phone: {
          title: "Hlela ifoni",
          subtitle: "Engeza inombolo esebenzayo yezaziso nokuqinisekisa.",
          invalidPhone: "Faka inombolo yocingo efanele.",
          mustAccept: "Kufanele uvume ngaphambi kokuqhubeka.",
          invalidPhoneLength: "Ubude benombolo yocingo abulungile.",
          timeout: "Isikhathi sokuqinisekisa siphelile. Zama futhi.",
          saveFailed: "Asikwazanga ukulondoloza inombolo yakho yocingo.",
          updated: "Inombolo yocingo ibuyekeziwe",
          verified: "Kuqinisekisiwe",
          termsLead: "Ngokuqhubeka uyavuma i-",
          termsLink: "Migomo",
          termsJoiner: "kanye ne-",
          privacyLink: "Inqubomgomo yobumfihlo",
          termsEnd: ".",
          verifyLater: "",
        },
        dob: {
          title: "Hlela usuku lokuzalwa",
          subtitle: "Gcina usuku lwakho lokuzalwa luyiqiniso kumarekhodi.",
          label: "Usuku lokuzalwa",
          placeholder: "Khetha usuku",
          updated: "Usuku lokuzalwa lubuyekeziwe",
        },
        about: {
          title: "Ngawe",
          subtitle: "Bhala isingeniso esifushane esisiza abantu bakwazi kangcono.",
          label: "Isingeniso",
          placeholder: "Bhala kafushane ngawe",
          counter: "{count} izinhlamvu",
          minimum: "Finyelela inani elincane lezinhlamvu ukuze uqhubeke.",
          updated: "Isingeniso sibuyekeziwe",
          folaTitle: "Usizo lwe-Fola",
          folaLoading: "UFola uyabhala...",
          folaApply: "Sebenzisa okusalungiswa kukaFola",
        },
        organDonor: {
          title: "Umnikeli wezitho",
          subtitle: "Khombisa ukuthi ufuna ukubhaliswa njengomnikeli wezitho yini.",
          discardTitle: "Lahla izinguquko?",
          discardBody: "Kunezinguquko ezingakalondolozwa esimweni somnikeli wezitho.",
        },
        email: {
          title: "Hlela i-imeyili",
          subtitleLink: "Sizothumela isixhumanisi sokuqinisekisa ku-imeyili entsha.",
          subtitleCode: "Faka ikhodi ethunyelwe ku-imeyili yakho.",
          label: "I-imeyili",
          placeholder: "wena@example.com",
          verified: "Kuqinisekisiwe",
          enterEmail: "Faka i-imeyili kuqala.",
          invalidEmail: "Faka i-imeyili efanele.",
          sendFailed: "Asikwazanga ukuthumela isixhumanisi sokuqinisekisa.",
          linkSent: "Isixhumanisi sokuqinisekisa sithunyelwe.",
          checkInbox: "Hlola ibhokisi lakho lokungenayo ukuze uqinisekise.",
          sendVerificationLink: "Thumela isixhumanisi sokuqinisekisa",
          sending: "Kuyathunyelwa...",
          checking: "Kuyahlolwa...",
          verifiedAction: "Kuqinisekisiwe",
          updated: "I-imeyili ibuyekeziwe",
          notVerifiedYet: "I-imeyili ayikaqinisekiswa.",
          notAuthenticated: "Kufanele ungene futhi.",
          sendCodeFailed: "Asikwazanga ukuthumela ikhodi.",
          codeSent: "Sithumele ikhodi enamadijithi ayisithupha ku-{email}",
          forbidden: "Awuvunyelwe ukwenza lokhu.",
          enterCode: "Faka ikhodi",
          codeExpired: "Ikhodi isiphelelwe yisikhathi.",
          incorrectCode: "Ikhodi ayilungile.",
          verifyFailed: "Asikwazanga ukuqinisekisa ikhodi.",
          stillSending: "Sisathumela ikhodi, sicela ulinde.",
          waitBeforeResend: "Linda ngaphambi kokuphinda uthumele.",
          sendDisabled: "Ukuthumela kuvinjelwe okwesikhashana.",
          resendIn: "Phinda uthumele ngemizuzwana engu-{seconds}",
          sendCode: "Thumela ikhodi",
          enterSentCode: "Faka ikhodi esiyithumele",
          codeLabel: "Ikhodi",
          codePlaceholder: "000000",
          verifying: "Kuyaqinisekiswa...",
          verify: "Qinisekisa",
        },
        address: {
          title: "Hlela ikheli",
          subtitle: "Gcina ikheli lakho lansuku zonke livuselelwe.",
          locationUnavailable: "Indawo ayitholakali lapha.",
          lookupFailed: "Asikwazanga ukubheka ikheli lakho.",
          permissionDenied: "Imvume yendawo yenqatshiwe.",
          locationTimeout: "Ukuthola indawo kuthathe isikhathi eside.",
          locationFailed: "Asikwazanga ukuthola indawo yakho.",
          signInRequired: "Kufanele ungene ukuze ulondoloze ikheli.",
          saveFailed: "Asikwazanga ukulondoloza ikheli lakho.",
          locating: "Kutholwa indawo...",
          useCurrentLocation: "Sebenzisa indawo yami yamanje",
          line1Label: "Ikheli",
          line1Placeholder: "Faka ikheli",
          line2Label: "Ikheli (ulayini 2)",
          line2Placeholder: "Ifulethi, iyunithi noma isakhiwo",
          postalLabel: "Ikhodi yeposi",
          postalPlaceholder: "0000",
          countryLabel: "Izwe",
          quickFillTitle: "Sebenzisa le mininingwane?",
          quickFillBodyPatient: "I-AfriMed ithole ikheli elingaba khona kusuka endaweni yakho.",
          quickFillBodyDoctor: "I-AfriMed ithole ikheli elingaba khona lesikhungo sakho kusuka endaweni yakho.",
          quickFillAction: "Yebo, gcwalisa ngokushesha",
          locationNameLabel: "Igama lendawo",
          locationNamePlaceholder: "Njengesakhiwo noma ikliniki",
          buildingTypeLabel: "Uhlobo lwesakhiwo",
          buildingTypePlaceholder: "Khetha uhlobo lwesakhiwo",
          doctorLine1Placeholder: "Faka ikheli lekliniki",
          cityLabel: "Idolobha",
          cityPlaceholder: "Faka idolobha",
          buildingTypeDescription: "Sisize siqonde le ndawo.",
          addBuildingTypeLabel: "Engeza uhlobo lwesakhiwo",
          addBuildingTypePlaceholder: "Uhlobo lwesakhiwo olungokwezifiso",
          permissionsBlocked: "Imvume yendawo ivinjiwe kule divayisi.",
          distanceAway: "{distance} km ukusuka lapha",
        },
        formation: {
          title: "Hlela ukuqeqeshwa",
          catalogHidden:
            "Iziphakamiso zezikole, iziqu, nemikhakha yokufunda zisadla idatha yolimi olulodwa futhi zifihlwe kulolu limi okwamanje.",
          loadFailed: "Asikwazanga ukulayisha lokhu kuqeqeshwa.",
          saveFailed: "Asikwazanga ukulondoloza izinguquko. Zama futhi.",
          deleteFailed: "Asikwazanga ukususa lokhu kuqeqeshwa. Zama futhi.",
          institutionLabel: "Isikhungo semfundo*",
          institutionPlaceholder: "Faka isikhungo sakho semfundo",
          degreeLabel: "Isiqu semfundo*",
          degreePlaceholder: "Faka isiqu sakho",
          fieldLabel: "Umkhakha wokufunda*",
          fieldPlaceholder: "Faka umkhakha wakho wokufunda",
          startLabel: "Usuku lokuqala*",
          endLabel: "Usuku lokuqeda*",
          datePlaceholder: "DD MMM, YYYY",
          achievementLabel: "Impumelelo yemfundo",
          achievementPlaceholder: "Faka impumelelo yakho yemfundo",
          clearInstitution: "Sula isikhungo semfundo",
          clearDegree: "Sula isiqu",
          clearField: "Sula umkhakha wokufunda",
          clearAchievement: "Sula impumelelo yemfundo",
          delete: "Susa ukuqeqeshwa",
          deleting: "Kuyasuswa…",
        },
      },
      profile: {
        title: "Iphrofayela",
        common: {
          editName: "Hlela igama",
          editDob: "Hlela usuku lokuzalwa",
          editPhone: "Hlela ifoni",
          editEmail: "Hlela i-imeyili",
          idPassport: "I-ID kazwelonke • Iphasipoti",
          address: "Ikheli",
          languages: "Izilimi",
        },
        patient: {
          editIntro: "Hlela isingeniso",
          healthRecord: "Irekhodi lami lezempilo",
          bloodType: "Uhlobo lwegazi",
          allergies: "Ukungezwani komzimba",
          conditions: "Izimo zezempilo",
          medications: "Imithi",
          organDonor: "Umnikeli wezitho",
          heightWeight: "Ubude nesisindo",
        },
        doctor: {
          editIntro: "Hlela isingeniso",
          specialty: "Ubuchwepheshe",
          registrationDetails: "Imininingwane yokubhalisa",
          uploadRegistration: "Layisha ukubhalisa",
          consultancies: "Izindawo zokubonisana",
          eVisit: "Ukuvakasha ku-inthanethi",
          availability: "Ukutholakala",
          focus: "Okuqondwe nguwe",
          editFormation: "Hlela ukuqeqeshwa",
          about: "Ngawe",
        },
      },
      focus: {
        title: "Okuqondwe nguwe",
        subtitle: "Bonisa ulwazi ofuna ukwaziwa ngalo.",
        empty: "Awukafaki izindawo zakho zokugxila okwamanje.",
        add: "Engeza",
        addTitle: "Engeza indawo yokugxila",
        searchPlaceholder: "Sesha",
        clearSearch: "Sula usesho",
        addCustom: "Engeza “{query}” njengendawo yokugxila",
        catalog: {
          acne: "Induna",
          rosacea: "Ukubomvu kobuso okuqhubekayo",
          hairLoss: "Ukuqothuka kwezinwele",
          psoriasis: "Isifo sesikhumba esinamaxolo",
          moleMonitoring: "Ukuqapha ama-mole",
          eczema: "Ukuvuvukala kwesikhumba",
          skinCancer: "Umdlavuza wesikhumba",
          dermatitis: "Ukuqubuka kwesikhumba",
          cosmeticDermatology: "Ukunakekelwa kobuhle besikhumba",
          surgery: "Ukuhlinzwa",
        },
      },
      insurancePolicy: {
        title: "Inqubomgomo yomshwalense",
        subtitle: "Engeza izinkampani zomshwalense ozamukela ezigulini",
        addCompany: "Engeza inkampani yomshwalense",
      },
      insuranceCompanies: {
        title: "Engeza inkampani yomshwalense",
        searchPlaceholder: "Sesha noma engeza",
        clearSearch: "Sula usesho",
        addCompany: "Engeza inkampani yomshwalense",
        heading: "Izinkampani zomshwalense",
        noMatches: "Akukho okufanayo. Thepha + ukuze ungeze “{query}”.",
      },
    },
    prescriptions: {
      fulfillment: {
        title: "Ukugcwaliswa kwefamasi",
        currentState: "Isimo samanje",
        awaitingSelection: "Ukukhetha kusalindile",
        pharmacy: "Ifamasi",
        mode: "Indlela",
        nextStepLabel: "Isinyathelo esilandelayo",
        openThread: "Vula uchungechunge lwemilayezo",
        openThreadShort: "Fola",
        payNow: "Khokha ifamasi manje",
        requestQuote: "Cela ikhotheshini yefamasi",
        selectPharmacy: "Khetha ifamasi",
        selectingPharmacy: "Kukhethwa ifamasi…",
        sendingRequest: "Kuthunyelwa isicelo…",
        cancelOrder: "Khansela i-oda lefamasi",
        cancellingOrder: "Kukhanselwa i-oda…",
        feedbackSubtitle:
          "Bamba lesi sinyathelo sokugcwaliswa kweresiphi ukuze i-AfriMed ikwazi ukuvula kabusha umongo oqondile womhloli.",
        state: {
          awaitingPayment: "Kulindwe inkokhelo",
          issued: "Ikhishiwe",
          needsNewPharmacy: "Idinga enye ifamasi",
          notStarted: "Akuqaliwe",
          paid: "Ikhokhiwe",
          paymentReady: "Inkokhelo isilungile",
          preparing: "Kuyalungiswa",
          preparingAccepted: "Yamukelwe yifamasi",
          ready: "Ilungele ukulandwa",
          refundPending: "Imbuyiselo ilindile",
          refunded: "Ibuyisiwe",
          requestSent: "Isicelo sithunyelwe",
          reviewing: "Ifamasi iyabuyekeza",
          reviewingShort: "Iyabuyekeza",
        },
        nextStep: {
          chooseAnother: "Khetha enye ifamasi ebhalisiwe ukuze uqhubeke.",
          collect: "Landa iresiphi efamasi ekhethiwe.",
          notStarted:
            "Khetha ifamasi ebhalisiwe ukuze kuqale ukugcwaliswa.",
          pay: "Qedela ukukhokha ukuze ifamasi ikhulule i-oda.",
          preparing:
            "Ifamasi ilungisa i-oda futhi izocela inkokhelo noma ilimake njengelilungile ngokulandelayo.",
          refundPending:
            "Buyela kule resiphi uqinisekise uma ukubuyiselwa sekuphelile.",
          refunded:
            "Khetha enye ifamasi uma usadinga le resiphi igcwaliswe.",
          requestSent:
            "Lindela ifamasi ekhethiwe ilungise ikhotheshini.",
          reviewing:
            "Lindela ifamasi yamukele iresiphi noma ibeke intengo.",
          waitRelease:
            "Lindela ifamasi imake i-oda njengelilungele ukulandwa.",
        },
        description: {
          notStarted: "Le resiphi ayikathunyelwa efamasi okwamanje.",
          paid: "Inkokhelo iqinisekisiwe no-{pharmacy}.",
          paymentReady:
            "{pharmacy} ilungise ikhotheshini ekhokhekayo yale resiphi.",
          preparing:
            "{pharmacy} yamukele le resiphi futhi iyayilungisa.",
          ready:
            "{pharmacy} imake le resiphi njengelungele ukulandwa.",
          refundPending: "Leli oda lefamasi liyabuyiselwa.",
          refunded: "Leli oda lefamasi selibuyisiwe.",
          rejected: "{pharmacy} ayikwazanga ukugcwalisa le resiphi.",
          requestSent:
            "{pharmacy} iceliwe ukuba ilungise ikhotheshini ye-{mode}.",
          reviewing: "{pharmacy} ibuyekeza le resiphi.",
        },
        errors: {
          request:
            "Asikwazanga ukucela ukugcwaliswa yifamasi manje.",
        },
      },
      qrOverlay: {
        fallbackHelp:
          "Udinga enye indlela? Vula indlela yokuqinisekisa lapha noma ukopishe isixhumanisi ukuze usihlole ngesandla.",
        openVerifyPage: "Vula ikhasi lokuqinisekisa",
        linkCopied: "Isixhumanisi sikopishiwe",
        copyFailed: "Ukukopa kwehlulekile",
        copyLink: "Kopisha isixhumanisi",
      },
      wizard: {
        reviewPrescription: "Buyekeza iresiphi",
        reviewSubtitle: "Kunemithi engu-{count} elungele ukubuyekezwa.",
        signSubtitle: "Qinisekisa isifinyezo seresiphi, bese usayina kanye ngaphambi kokuyikhipha.",
        savedMedicines: "Imithi egciniwe",
        builderHelp: "Qedela imininingwane yomuthi, bese uwugcina kuresiphi.",
        medicationNote: "Inothi lomuthi",
        medicationNotePlaceholder: "Inothi elengeziwe lalo muthi uma kudingeka",
        medicines: "Imithi",
        medicinesCount: "Imithi engu-{count}",
        addAnotherMedicine: "Engeza omunye umuthi",
        moveUp: "Yisa phezulu",
        moveDown: "Yisa phansi",
        saveMedicine: "Londoloza umuthi",
        diagnosisPlaceholder: "Faka ukuxilongwa",
        referenceImages: "Izithombe zesethenjwa",
        referenceImagesSubtitle:
          "Gcina izithombe zephakheji noma ilebula zihlotshaniswa nalesi sikhathi somuthi.",
      },
    },
  },
};


// FRANCO_DOCTOR_ACTIVATION_APPENDIX
// Focused French coverage for the clinician activation journey. This deep-merge
// keeps the patch additive and avoids shadowing existing French sections above.
function deepMergeLanguagePatch(target, source) {
  for (const [key, value] of Object.entries(source || {})) {
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      target[key] &&
      typeof target[key] === "object" &&
      !Array.isArray(target[key])
    ) {
      deepMergeLanguagePatch(target[key], value);
    } else {
      target[key] = value;
    }
  }
  return target;
}

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  auth: {
    loginRequired: "Vous devez être connecté.",
  },
  common: {
    dismiss: "Fermer",
    verified: "Vérifié",
    pendingVerification: "Vérification en attente",
    edit: "Modifier",
    delete: "Supprimer",
    add: "Ajouter",
    logout: "Se déconnecter",
    all: "Tout",
    map: "Carte",
    loading: "Chargement...",
    on: "Activé",
    off: "Désactivé",
    showAll: "Tout afficher",
    selectPlaceholder: "Sélectionner...",
    datePicker: {
      displayDate: "{day} {month} {year}",
    },
    actions: {
      clear: "Effacer",
      save: "Enregistrer",
    },
  },
  navigation: {
    menu: {
      logOut: "Se déconnecter",
    },
  },
  settings: {
    consultancyEdit: {
      setInAvailability: "À définir dans les disponibilités",
      managedInAvailability: "Géré dans les disponibilités",
    },
    editIntro: {
      saveProfile: "Enregistrer le profil",
      profileSaved: "Profil enregistré",
    },
    availability: {
      optionMinutes5: "5 minutes",
      optionMinutes10: "10 minutes",
      optionMinutes15: "15 minutes",
      optionMinutes20: "20 minutes",
      optionMinutes30: "30 minutes",
      optionMinutes45: "45 minutes",
      overrideLabel: "Libellé",
      overrideLabelPlaceholder: "Congé annuel, conférence, clinique spéciale du samedi...",
      overrideTiming: "Période concernée",
      overrideTimingSupportAvailable: "Choisissez la période, puis indiquez les heures d’ouverture pour cette plage clinique ponctuelle.",
      overrideTimingSupportUnavailable: "Choisissez la période à bloquer pour congé, jour férié ou autre absence.",
      overrideDatePending: "Choisir les dates",
      startsOn: "Débute le",
      endsOn: "Se termine le",
      specialOpens: "Ouvre",
      specialCloses: "Ferme",
      addChange: "Ajouter le changement planifié",
      overrideEmpty: "Aucun changement de date pour le moment. Votre planning hebdomadaire reste la référence.",
      timeAway: "Absence",
      specialHoursFallback: "Horaires cliniques spéciaux",
      timeAwayFallback: "Indisponible à la réservation",
      removeOverride: "Retirer",
      servicesTitle: "Services et tarifs",
      servicesSupport: "Les disponibilités déterminent quand les patients peuvent réserver. Les services et tarifs déterminent ce qu’ils peuvent réserver, pour quelle durée et à quel prix.",
      manageServices: "Gérer les services et tarifs",
      servicesConfigured: "Services configurés",
      servicesConfiguredValue: "{count} actifs",
      servicesConfiguredSupport: "Les patients choisissent l’un de ces services avant de réserver une date et une heure.",
      serviceDurations: "Options de durée",
      serviceDurationsValue: "{count} durées",
      serviceDurationsSupport: "La réservation utilise la durée du service sélectionné pour générer les créneaux disponibles.",
      serviceCurrencies: "Devises tarifaires",
      serviceCurrenciesValue: "{count} activées",
      serviceCurrenciesSupport: "Gérez les tarifs SADC et USD dans Services et tarifs, pas dans les disponibilités hebdomadaires.",
      advancedControls: "Contrôles avancés",
      advancedRulesTitle: "Règles de réservation avancées",
      rulesSupport: "Ces règles déterminent comment les réservations patient sont générées à partir de votre planning.",
      headerRule: "Règle",
      headerChoice: "Choix actuel",
      slotDuration: "Durée du rendez-vous",
      slotDurationSupport: "Durée de consultation de base proposée aux patients.",
      bufferTime: "Pause entre rendez-vous",
      bufferTimeSupport: "Temps de respiration optionnel avant la réservation du patient suivant.",
      advanceBooking: "Réservation à l’avance",
      advanceBookingSupport: "Important pour les spécialistes et la planification des congés à long terme.",
      cancellationPolicy: "Préavis d’annulation",
      cancellationPolicySupport: "Délai souhaité pour qu’un patient annule son rendez-vous.",
      autoConfirm: "Confirmation automatique",
      autoConfirmSupport: "Les nouvelles réservations entrent directement dans votre agenda sans approbation manuelle.",
      allowRescheduling: "Autoriser le report par le patient",
      allowReschedulingSupport: "Les patients peuvent déplacer leur rendez-vous si vos règles restent respectées.",
      unsaved: "Disponibilités non enregistrées",
      saved: "Disponibilités à jour",
      unsavedSupport: "Enregistrez lorsque vous êtes prêt afin que la réservation patient reflète le nouveau planning.",
      savedSupport: "Vos horaires hebdomadaires, changements de dates et règles de réservation sont synchronisés.",
      reset: "Réinitialiser",
      save: "Enregistrer les disponibilités",
    },
  },
  appointments: {
    reminders: {
      active: {
        title: "Appointment reminders are on",
        copy: "AfriMed™ will send reminders before your appointment. You can change this at any time in Notifications.",
        action: "Got it",
      },
    },
  },
  doctor: {
    editIntro: {
      errors: {
        save: "Impossible d’enregistrer votre présentation. Veuillez réessayer.",
        photoSave: "Impossible d’enregistrer votre photo de profil. Veuillez réessayer plus tard.",
      },
      placeholders: {
        fullName: "Ajouter votre nom complet",
        dob: "Ajouter votre date de naissance",
        specialty: "Ajouter votre spécialité",
        registerNumber: "Ajouter votre numéro d’inscription",
        about: "Présentez brièvement votre parcours, votre expérience et votre approche clinique.",
      },
      fallback: {
        education: "Formation",
      },
      sections: {
        personalInfo: "Informations personnelles",
        specialty: "Spécialité",
        about: "À propos de vous",
        formation: "Formation",
        languages: "Langues",
      },
      labels: {
        name: "Nom",
        dob: "Date de naissance",
        registrationNumber: "Numéro d’inscription",
      },
      verification: {
        verified: "Vérifié à partir de la pièce d’identité officielle fournie.",
        pending: "Vérification en cours.",
      },
      signature: {
        title: "Signature",
        subtitle: "Ajouter / mettre à jour votre signature",
        preview: "Aperçu de la signature",
        helper: "Cette signature est conservée de manière sécurisée et sera figée dans chaque prescription émise.",
      },
      formation: {
        empty: "Ajoutez votre formation afin que les patients voient votre parcours.",
      },
      actions: {
        addFormation: "Ajouter une formation",
        addLanguage: "Ajouter une langue",
      },
      languages: {
        editAria: "Modifier {language}",
        empty: "Ajoutez les langues que vous parlez afin que les patients choisissent celle qui leur convient.",
      },
      photoSheet: {
        title: "Modifier votre photo de profil",
        uploadImage: "Téléverser une image",
        takePhoto: "Prendre une photo",
      },
    },
    profile: {
      values: {
        days: {
          monday: "Lundi",
          tuesday: "Mardi",
          wednesday: "Mercredi",
          thursday: "Jeudi",
          friday: "Vendredi",
          saturday: "Samedi",
          sunday: "Dimanche",
          mon: "Lun",
          tue: "Mar",
          wed: "Mer",
          thu: "Jeu",
          fri: "Ven",
          sat: "Sam",
          sun: "Dim",
        },
        patientAge: {
          childrenAdults: "Enfants et adultes",
          adultsOnly: "Adultes uniquement",
          childrenOnly: "Enfants uniquement",
        },
        availability: {
          everyDay: "Tous les jours",
          everyWeek: "Chaque semaine",
          regularDay: "jour régulier",
          regularDays: "jours réguliers",
          daysAhead: "jours à l’avance",
        },
        insurance: {
          accepting: "Acceptée",
          accepts: "Accepte",
        },
        paymentTerms: {
          cards: "Cartes de débit / crédit",
          creditCards: "Cartes de crédit",
          debitCards: "Cartes de débit",
          bankTransfers: "Virements bancaires",
          cashOnService: "Paiement sur place",
          selfPay: "Paiement direct",
        },
      },
      fallback: {
        doctor: "Médecin",
        specialtyMissing: "Spécialité non ajoutée",
        about: "Médecin dédié aux soins cliniques, avec une approche centrée sur le patient.",
        notSet: "Non renseigné",
        facility: "Établissement",
        service: "Service",
      },
      sections: {
        virtualConsultations: "Consultations virtuelles",
        aboutYou: "À propos de vous",
        about: "À propos",
        yourFocus: "Vos domaines d’intérêt",
        focus: "Domaines d’intérêt",
        yourServices: "Vos services",
        services: "Services",
        consultancies: "Lieux de consultation",
        consultancy: "Lieu de consultation",
        gallery: "Galerie",
      },
      cover: {
        editTitle: "Modifier la photo de couverture",
        uploadImage: "Téléverser une image",
        takePhoto: "Prendre une photo",
        errors: {
          fileType: "Veuillez téléverser une image JPG, PNG ou WebP.",
          fileSize: "La taille maximale de la photo de couverture est de 3 Mo.",
          fileRead: "Impossible de lire l’image sélectionnée.",
          save: "Impossible d’enregistrer votre photo de couverture. Veuillez réessayer.",
        },
      },
      share: {
        intent: "Retrouvez-moi sur AfriMed™ pour réserver votre consultation.",
        label: "Partager le profil",
        copyLink: "Copier le lien du profil",
      },
      stats: {
        patients: "Patients",
        experience: "Expérience",
        rating: "Note",
      },
      focus: {
        addFromSettings: "Ajoutez vos domaines d’intérêt depuis les réglages.",
        empty: "Aucun domaine d’intérêt ajouté pour le moment.",
      },
      consultancies: {
        empty: "Aucun lieu de consultation pour le moment.",
        addLocation: "Ajouter un lieu de consultation",
        options: "Options du lieu de consultation",
        labels: {
          patientsAge: "Âge des patients",
          availability: "Disponibilités",
          insurancePolicy: "Politique d’assurance",
          paymentTerms: "Conditions de paiement",
        },
        edit: "Modifier le lieu de consultation",
        remove: "Retirer le lieu de consultation",
        removeTitle: "Retirer ce lieu de consultation ?",
        removeBody: "Ce lieu de consultation sera retiré de votre profil.",
        removing: "Retrait...",
      },
      virtualConsultations: {
        labels: {
          price: "Tarif de consultation virtuelle",
        },
      },
      gallery: {
        addFromSettingsPrefix: "Ajoutez des images depuis vos",
        gallerySettingsLink: "réglages de galerie",
        emptyPublic: "Aucune image de galerie pour le moment.",
      },
      services: {
        empty: "Ajoutez vos services et tarifs depuis les réglages pour montrer aux patients ce que vous proposez.",
      },
    },
  },
  appointments: {
    common: {
      yearsExperience: "{count} ans",
    },
    discovery: {
      loading: "Chargement...",
      noFacilityMatches: "Aucun clinicien trouvé pour {facilityName}.",
      noFilterMatches: "Aucun clinicien ne correspond à vos filtres.",
      tryDifferentFacility:
        "Essayez un autre établissement ou effacez l’établissement sélectionné pour parcourir l’annuaire complet.",
      tryClearingFilters: "Essayez d’effacer les filtres ou d’élargir votre recherche.",
    },
    doctor: {
      insuranceAccepted: "Assurance acceptée",
      insuranceGeneric: "Assurance acceptée — confirmez l’organisme pendant la réservation.",
    },
  },
  patient: {
    editIntro: {
      title: "Modifier l'intro",
    },
    profile: {
      fullName: "Nom complet",
      dateOfBirth: "Date de naissance",
      sections: { languages: "Langues" },
      languages: { otherLabel: "Autres langues" },
      placeholders: { languages: "Langues préférées" },
      actions: {
        addPhoto: "Ajouter",
        editPhotos: "Modifier",
      },
    },
  },
  fola: {
    onboarding: {
      patient: {
        identityStartCta: "Identité",
        profileReview: "Vérifiez votre profil.",
        editIntro: "Construisons votre profil afin que les cliniciens comprennent qui ils accompagnent.",
        editIntroSupport:
          "Ajoutez votre photo de profil et complétez les sections utiles ci-dessous. Une fois terminé, enregistrez le profil et suivez l’étape suivante.",
        profileReady: "Vérifiez votre profil",
        profileReadySupport:
          "Voici comment vos informations apparaissent dans AfriMed™. Relisez vos détails et mettez à jour tout élément nécessaire avant de réserver ou d’honorer un rendez-vous.",
        saveProfileCta: "Enregistrer le profil",
        homeAppointments: "Vos rendez-vous à venir et passés sont regroupés ici.",
        homeAppointmentsSupport:
          "Pour rechercher et réserver un clinicien, ouvrez Médecins. Vous arriverez sur une carte. Pour de meilleurs résultats, autorisez AfriMed™ à utiliser votre position actuelle.",
        findCliniciansCta: "Trouver un clinicien",
      },
    },
    readiness: {
      patient: {
        welcome: "Bienvenue {addressName}",
        identityStartDetail:
          "Commençons par confirmer votre identité afin que votre profil de soins reste correctement rattaché à votre compte.",
        identityStartCta: "Identité",
        dobCurrent: "Complétez votre date de naissance.",
        dobCurrentDetail:
          "Ajoutez votre date de naissance, enregistrez, puis poursuivez l’étape suivante.",
        idPassportCurrent: "Ajoutez votre pièce d’identité ou votre passeport.",
        idPassportCurrentDetail:
          "Téléversez ou saisissez votre document d’identité pour sécuriser votre profil.",
        languagesCurrent: "Ajoutez vos langues de soins.",
        languagesCurrentDetail:
          "Indiquez la langue dans laquelle vous préférez recevoir vos soins et communiquer avec les cliniciens.",
        addressCurrent: "Complétez votre adresse.",
        addressCurrentDetail:
          "Votre adresse aide AfriMed™ à contextualiser les soins, les pharmacies et les rendez-vous.",
        bloodTypeCurrent: "Sélectionnez votre groupe sanguin.",
        bloodTypeCurrentDetail:
          "Si vous le connaissez, enregistrez-le afin qu’il soit facile à retrouver en cas de besoin.",
        allergiesCurrent: "Ajoutez vos allergies.",
        allergiesCurrentDetail:
          "Gardez vos allergies visibles avant les rendez-vous et les prescriptions.",
        conditionsCurrent: "Ajoutez vos antécédents médicaux.",
        conditionsCurrentDetail:
          "Ajoutez les problèmes de santé importants afin que les cliniciens aient le bon contexte.",
        medicationsCurrent: "Ajoutez vos médicaments.",
        medicationsCurrentDetail:
          "Tenez vos traitements actuels à jour pour des consultations plus sûres.",
        heightWeightCurrent: "Ajoutez votre taille et votre poids.",
        heightWeightCurrentDetail:
          "Ces informations peuvent soutenir certaines décisions de soins et de prescription.",
        ehrCurrent: "Créez ou saisissez votre mot de passe.",
        ehrCurrentDetail:
          "Votre dossier de santé est protégé et conserve vos informations de soins au même endroit.",
        prescriptionsCurrent: "Vérifiez vos prescriptions.",
        prescriptionsCurrentDetail:
          "Retrouvez ici les prescriptions émises pendant vos consultations.",
        dob: "Ajoutez votre date de naissance.",
        dobDetail:
          "Cela aide AfriMed™ à afficher les informations de soins adaptées à votre âge.",
        dobCta: "Ajouter la date de naissance",
        idPassport: "Ajoutez votre pièce d’identité ou votre passeport.",
        idPassportDetail:
          "Cela permet de garder votre profil de soins lié à la bonne personne.",
        idPassportCta: "Ajouter une pièce d’identité",
        languages: "Choisissez vos langues de soins.",
        languagesDetail:
          "Les cliniciens peuvent mieux vous accompagner lorsque votre langue préférée est claire.",
        languagesCta: "Choisir les langues",
        address: "Ajoutez votre adresse.",
        addressDetail:
          "Votre adresse aide pour le contexte de soins, la livraison en pharmacie et l’organisation des rendez-vous.",
        addressCta: "Ajouter l’adresse",
        bloodType: "Ajoutez votre groupe sanguin.",
        bloodTypeDetail:
          "Si vous le connaissez, enregistrez-le afin qu’il soit facile à retrouver en cas de besoin.",
        bloodTypeCta: "Ajouter le groupe sanguin",
        allergies: "Ajoutez vos allergies.",
        allergiesDetail:
          "Gardez vos allergies visibles avant les rendez-vous et les prescriptions.",
        allergiesCta: "Ajouter les allergies",
        conditions: "Ajoutez vos antécédents médicaux.",
        conditionsDetail:
          "Partagez les affections suivies afin que les cliniciens voient plus vite les informations importantes.",
        conditionsCta: "Ajouter les antécédents",
        medications: "Ajoutez vos médicaments.",
        medicationsDetail:
          "Gardez vos traitements actuels visibles pour des consultations plus sûres.",
        medicationsCta: "Ajouter les médicaments",
        heightWeight: "Ajoutez votre taille et votre poids.",
        heightWeightDetail:
          "Ces informations peuvent soutenir certaines décisions de soins et de prescription.",
        heightWeightCta: "Ajouter taille et poids",
        editIntro: "Construisons votre profil patient.",
        editIntroDetail:
          "Ajoutez votre photo de profil et complétez les sections utiles ci-dessous. Une fois terminé, enregistrez le profil et poursuivez.",
        editIntroCta: "Modifier l’intro",
        profileReview: "Vérifiez votre profil.",
        profileReviewDetail:
          "Relisez vos informations avant de réserver ou d’honorer un rendez-vous.",
        ehr: "Consultez votre dossier de santé.",
        ehrDetail:
          "Votre dossier de santé garde vos informations de soins au même endroit.",
        ehrCta: "Ouvrir le dossier de santé",
        appointmentLists: "Listes de rendez-vous",
        appointmentListsDetail:
          "Vos rendez-vous à venir, passés et en attente sont regroupés ici.",
        appointmentListsCta: "Voir les rendez-vous",
        messagesFola: "Rencontrez Fola",
        messagesFolaDetail:
          "Fola peut vous aider à comprendre les prochaines étapes et les actions importantes.",
        messagesFolaCta: "Commencer avec Fola",
        nextStepCta: "Étape suivante",
      },
    },
  },
  prescriptions: {
    wizard: {
      medType: {
        capsule: "Capsule",
        tablet: "Comprimé",
        injectionSolution: "Solution injectable",
        oralSolution: "Solution orale",
        topical: "Topique",
        device: "Dispositif",
        drops: "Gouttes",
        inhaler: "Inhalateur",
      },
      frequency: {
        daily: "Quotidien",
        cyclical: "Schéma cyclique",
        specificDays: "Un jour précis de la semaine",
        asNeeded: "Si nécessaire",
      },
      days: {
        mon: "Lun",
        tue: "Mar",
        wed: "Mer",
        thu: "Jeu",
        fri: "Ven",
        sat: "Sam",
        sun: "Dim",
        label: "Jours",
      },
      select: "Sélectionner",
      medication: "Médicament",
      referenceImages: "Images de référence",
      referenceImagesSubtitle: "Conservez les images de boîte ou d’étiquette jointes à cette prescription.",
      errors: {
        appointmentNotFound: "Rendez-vous introuvable pour cette prescription.",
        recordLinkageMissing: "Lien dossier médecin/patient manquant pour émettre la prescription.",
        authLinkageMissing: "Lien d’authentification médecin/patient manquant pour émettre la prescription.",
        idMissing: "Prescription émise, mais aucun identifiant n’a été retourné.",
        issueFailed: "Échec de l’émission de la prescription.",
      },
      header: {
        addPrescription: "Ajouter une prescription",
        subtitle: "Vous pouvez l’ajouter manuellement ou utiliser votre caméra pour la joindre.",
      },
      reviewSubtitle: "{count} médicament(s) prêts à vérifier.",
      signSubtitle: "Confirmez le résumé de la prescription, puis signez une seule fois avant émission.",
      addMedication: "Ajouter un médicament",
      uploadImage: "Téléverser une image",
      searchOrAdd: "Rechercher ou ajouter",
      possibleMatches: "Correspondances possibles",
      loadingMatches: "Chargement... un instant s’il vous plaît !",
      noMatchesAdd: "Aucun résultat. Touchez + pour ajouter « {query} ».",
      chooseMedicationType: "Choisir le type de médicament",
      addStrength: "Ajouter le dosage du médicament",
      dose: "Dose",
      every: "Toutes les",
      timeAndDoses: "Heures et doses",
      time: "Heure",
      cyclicalShort: "Cyclique",
      doses: "Doses",
      duration: "Durée",
      day: "Jour",
      cycle: "Cycle",
      useFor: "Utiliser pendant",
      pauseFor: "Pause de",
      schedule: "Schéma",
      dosage: "Posologie",
      starts: "Début",
      ends: "Fin",
      optionalNotes: "Notes facultatives pour le patient",
      diagnosis: "Diagnostic",
      diagnosisPlaceholder: "Ajouter le diagnostic",
      notes: "Notes",
      savedMedicines: "Médicaments enregistrés",
      builderHelp: "Complétez les détails du médicament, puis enregistrez-le dans la prescription.",
      medicationNote: "Note sur le médicament",
      medicationNotePlaceholder: "Note facultative pour ce médicament",
      medicines: "Médicaments",
      addAnotherMedicine: "Ajouter un autre médicament",
      moveUp: "Monter",
      moveDown: "Descendre",
    },
  },
});

// INSTITUTION_MOBILE_FRENCH_ENUM_CLOSURE_2026_08
// These labels are presentation-only mappings. Option identifiers and stored
// values remain unchanged across Institution routes.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES["en-GB"], {
  institutionControlled: {
    member: "Member",
    clinician: "Clinician",
    facility_staff: "Facility staff",
    nurse: "Nurse",
  },
  institutionServices: {
    selectOption: "Select an option",
    searchOptions: "Search options…",
  },
  institutionPatients: {
    status: {
      draft: "Draft",
      invitationNotSent: "Invitation not sent",
      invitationPending: "Invitation Pending",
      provisional: "Provisional",
      active: "Active",
      declined: "Declined",
      expired: "Expired",
      revoked: "Revoked",
      suspended: "Suspended",
      removed: "Removed",
    },
  },
  institutionPatientCreate: {
    searchOptions: "Search options…",
    options: {
      title: {
        Mr: "Mr.",
        Mrs: "Mrs.",
        Ms: "Ms.",
        Miss: "Miss",
      },
    },
  },
  regionalHealthSignals: {
    detail: {
      validationNarrative: "Aggregate evaluation signal for internal workflow validation. No patient-identifying information.",
    },
  },
});

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  institutionControlled: {
    member: "Membre",
    clinician: "Clinicien",
    facility_staff: "Personnel de l’établissement",
    nurse: "Personnel infirmier",
  },
  institutionServices: {
    selectOption: "Sélectionner une option",
    searchOptions: "Rechercher des options…",
  },
  institutionPatients: {
    status: {
      draft: "Brouillon",
      invitationNotSent: "Invitation non envoyée",
      invitationPending: "Invitation en attente",
      provisional: "Provisoire",
      active: "Actif",
      declined: "Refusé",
      expired: "Expiré",
      revoked: "Révoqué",
      suspended: "Suspendu",
      removed: "Retiré",
    },
  },
  institutionPatientCreate: {
    searchOptions: "Rechercher des options…",
    options: {
      title: {
        Mr: "Monsieur (M.)",
        Mrs: "Madame (Mme)",
        Ms: "Madame (Mme)",
        Miss: "Mademoiselle (Mlle)",
      },
    },
  },
  regionalHealthSignals: {
    detail: {
      validationNarrative: "Signal d’évaluation agrégé destiné à la validation du flux de travail interne. Aucune information permettant d’identifier un patient.",
    },
  },
});

// INSTITUTION_DETAIL_CONTROLLED_LABELS_2026_08
// Detail routes keep user-entered names, clinical data, facility data, and
// free-text values verbatim. Only the surrounding controlled UI is localised.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES["en-GB"], {
  institutionMedicalInstitutionDetail: {
    title: "Medical Institutions",
    actions: { back: "Back to Medical Institutions", cancel: "Cancel", save: "Save", edit: "Edit", add: "Add", addMember: "+ Add member", searchOrAdd: "Search or add" },
    sections: { basicInformation: "Basic Information", member: "Member", specialties: "Specialties", address: "Address", availability: "Availability", domain: "Domain" },
    fields: { institutionName: "Institution Name*", institutionType: "Institution type *", selectInstitutionType: "Select institution type", registrationTaxId: "Registration / VAT ID", facilityOwnership: "Facility ownership", description: "Facility description", descriptionHelp: "Optional concise description", address: "Address", zipCode: "ZIP Code", city: "City", country: "Country", domain: "Domain" },
    types: { hospital: "Hospital", clinic: "Clinic", pharmacy: "Pharmacy", laboratory: "Laboratory", imagingCentre: "Imaging centre", medicalCentre: "Medical centre", specialistCentre: "Specialist centre", otherAuthorisedHealthcareFacility: "Other authorised healthcare facility" },
    ownership: { private: "Private", public: "Public" },
    member: { accountableCreator: "Accountable creator" },
    availability: { openingHours: "Opening hours", openEveryDay: "Open 24 hours · Every day", setupPending: "Setup pending", allDays: "S,M,T,W,T,F,S", weekdays: "Mon – Fri", daysConfigured: "{count} days configured", days: { sunday: "S", monday: "M", tuesday: "T", wednesday: "W", thursday: "T", friday: "F", saturday: "S" } },
    availabilitySheet: { open24Hours: "Open 24 hours", openContinuously: "Open continuously every day", weekdays: "Weekdays", days: { sunday: "Sunday", monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday", thursday: "Thursday", friday: "Friday", saturday: "Saturday" }, enableOpen24Hours: "Enable open 24 hours", disableOpen24Hours: "Disable open 24 hours" },
    media: { actions: "{title} actions", logo: "Medical Institution logo", editLogo: "Edit Institution logo", editCover: "Edit Institution cover", moreCoverActions: "More Institution cover actions", coverActions: "Institution cover actions", uploadImage: "Upload image", takePhoto: "Take photo", removeImage: "Remove image", uploadCover: "Upload cover", changeCover: "Change cover", removeCover: "Remove cover", unavailable: "Cover management is unavailable until the protected media service is released.", removeLogoTitle: "Remove Institution logo?", removeLogoBody: "Your current Institution logo will be removed.", removeCoverTitle: "Remove Institution cover?", removeCoverBody: "Your current Institution cover will be removed." },
  },
  institutionPatientDetail: {
    ariaLabel: "Patient detail", patient: "Patient", avatar: "Patient avatar", image: "Patient image", notSupplied: "Not supplied", notRecorded: "Not Recorded", idDocumentOnFile: "ID document on file", healthProfileUnavailable: "Health profile access is not available for this relationship.",
    actions: { cancel: "Cancel", save: "Save", more: "More Patient actions", relationship: "Patient relationship actions", copyLink: "Copy link", openInNewTab: "Open in new tab", refresh: "Refresh Patient profile", refreshing: "Refreshing Patient profile…", manage: "Manage relationship" },
    notices: { refreshed: "Patient profile refreshed.", linkCopied: "Patient link copied.", linkCopyFallback: "Copy the Patient relationship URL from your browser address bar.", relationshipUpdated: "Institution relationship updated." },
    errors: { refresh: "Global Patient profile details could not be refreshed. Please try again.", relationshipUpdate: "The Institution relationship could not be updated. Please try again." },
    sections: { basicInformation: "Basic Information", physicalAddress: "Physical Address", relationship: "Institution relationship", healthProfile: "Health profile" },
    fields: { title: "Title", firstName: "First name", lastName: "Last name", dateOfBirth: "Date of birth", gender: "Gender", countryCode: "Country code", phoneNumber: "Phone number", email: "Email", nationalId: "National ID / Passport", patientId: "Patient ID", idVerification: "ID verification", address: "Address", city: "City", postalCode: "Postal code", country: "Country", assignedFacilities: "Assigned facilities", status: "Status", relationshipSource: "Relationship source", lawfulBasis: "Lawful basis", languages: "Languages", bloodType: "Blood type", allergies: "Allergies", medicalConditions: "Medical conditions", medications: "Medications", organDonor: "Organ donor" },
    status: { active: "Active", draft: "Draft", removed: "Removed" },
    idVerification: { pending: "Pending", verified: "Verified", rejected: "Rejected", not_recorded: "Not recorded" },
    relationshipSources: { existing_patient_link: "Existing Patient Link", manual: "Manual relationship" },
    lawfulBasis: { patient_present_intake_authorised: "Patient present and authorised", direct_care_registration: "Direct care registration", referral_documentation_received: "Referral documentation received", emergency_provisional_intake: "Emergency provisional intake", programme_enrolment_authority: "Public health programme authority" },
  },
  institutionClinicians: {
    relationshipStatus: { invitation_not_sent: "Invitation not sent", invitation_pending: "Invitation pending", active: "Active", declined: "Declined", expired: "Expired", revoked: "Revoked", suspended: "Suspended", removed: "Removed" },
    relationship: { title: "Institution relationship", loading: "Checking the current clinician relationship.", active: "{clinician} has accepted the {facility} clinician relationship.", pending: "{clinician} has not yet accepted the {facility} clinician relationship.", declined: "{clinician} declined the {facility} clinician relationship.", expired: "The invitation for {clinician} to review the {facility} clinician relationship expired.", revoked: "The invitation for {clinician} to review the {facility} clinician relationship was revoked.", suspended: "{clinician}’s {facility} clinician relationship is suspended.", removed: "{clinician}’s {facility} clinician relationship was removed.", notSent: "Send an invitation to {clinician} so they can review and accept the {facility} clinician relationship.", expires: "Expires {date}.", resend: "Resend", revoke: "Revoke", send: "Send invitation to clinician" },
  },
});

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  institutionMedicalInstitutionDetail: {
    title: "Établissements médicaux", notSupplied: "Non fourni",
    actions: { back: "Retour aux établissements médicaux", cancel: "Annuler", save: "Enregistrer", edit: "Modifier", add: "Ajouter", addMember: "+ Ajouter un membre", searchOrAdd: "Rechercher ou ajouter" },
    sections: { basicInformation: "Informations de base", member: "Membre", specialties: "Spécialités", address: "Adresse", availability: "Disponibilité", domain: "Domaine" },
    fields: { institutionName: "Nom de l’établissement*", institutionType: "Type d’établissement *", selectInstitutionType: "Sélectionner le type d’établissement", registrationTaxId: "Numéro d’enregistrement / TVA", facilityOwnership: "Propriété de l’établissement", description: "Description de l’établissement", descriptionHelp: "Brève description facultative", address: "Adresse", zipCode: "Code postal", city: "Ville", country: "Pays", domain: "Domaine" },
    types: { hospital: "Hôpital", clinic: "Clinique", pharmacy: "Pharmacie", laboratory: "Laboratoire", imagingCentre: "Centre d’imagerie", medicalCentre: "Centre médical", specialistCentre: "Centre spécialisé", otherAuthorisedHealthcareFacility: "Autre établissement de santé autorisé" },
    ownership: { private: "Privé", public: "Public" },
    member: { accountableCreator: "Créateur responsable" },
    availability: { openingHours: "Heures d’ouverture", openEveryDay: "Ouvert 24 h/24 · Tous les jours", setupPending: "Configuration en attente", allDays: "D,L,M,M,J,V,S", weekdays: "Lun. – ven.", daysConfigured: "{count} jours configurés", days: { sunday: "D", monday: "L", tuesday: "M", wednesday: "M", thursday: "J", friday: "V", saturday: "S" } },
    availabilitySheet: { open24Hours: "Ouvert 24 h/24", openContinuously: "Ouvert en continu tous les jours", weekdays: "Jours ouvrables", days: { sunday: "Dimanche", monday: "Lundi", tuesday: "Mardi", wednesday: "Mercredi", thursday: "Jeudi", friday: "Vendredi", saturday: "Samedi" }, enableOpen24Hours: "Activer l’ouverture 24 h/24", disableOpen24Hours: "Désactiver l’ouverture 24 h/24" },
    media: { actions: "Actions : {title}", logo: "Logo de l’établissement médical", editLogo: "Modifier le logo de l’établissement", editCover: "Modifier la couverture de l’établissement", moreCoverActions: "Autres actions pour la couverture de l’établissement", coverActions: "Actions pour la couverture de l’établissement", uploadImage: "Téléverser une image", takePhoto: "Prendre une photo", removeImage: "Supprimer l’image", uploadCover: "Téléverser une couverture", changeCover: "Modifier la couverture", removeCover: "Supprimer la couverture", unavailable: "La gestion de la couverture n’est pas disponible tant que le service média protégé n’est pas publié.", removeLogoTitle: "Supprimer le logo de l’établissement ?", removeLogoBody: "Le logo actuel de l’établissement sera supprimé.", removeCoverTitle: "Supprimer la couverture de l’établissement ?", removeCoverBody: "La couverture actuelle de l’établissement sera supprimée." },
  },
  institutionPatientDetail: {
    ariaLabel: "Détails du patient", patient: "Patient", avatar: "Avatar du patient", image: "Image du patient", notSupplied: "Non fourni", notRecorded: "Non enregistré", idDocumentOnFile: "Document d’identité au dossier", healthProfileUnavailable: "L’accès au profil de santé n’est pas disponible pour cette relation.",
    actions: { cancel: "Annuler", save: "Enregistrer", more: "Autres actions du patient", relationship: "Actions de la relation patient", copyLink: "Copier le lien", openInNewTab: "Ouvrir dans un nouvel onglet", refresh: "Actualiser le profil du patient", refreshing: "Actualisation du profil du patient…", manage: "Gérer la relation" },
    notices: { refreshed: "Profil du patient actualisé.", linkCopied: "Lien du patient copié.", linkCopyFallback: "Copiez l’URL de la relation patient depuis la barre d’adresse de votre navigateur.", relationshipUpdated: "Relation Institution mise à jour." },
    errors: { refresh: "Les détails du profil global du patient n’ont pas pu être actualisés. Réessayez.", relationshipUpdate: "La relation Institution n’a pas pu être mise à jour. Réessayez." },
    sections: { basicInformation: "Informations de base", physicalAddress: "Adresse physique", relationship: "Relation Institution", healthProfile: "Profil de santé" },
    fields: { title: "Titre", firstName: "Prénom", lastName: "Nom", dateOfBirth: "Date de naissance", gender: "Genre", countryCode: "Indicatif pays", phoneNumber: "Numéro de téléphone", email: "E-mail", nationalId: "Pièce d’identité nationale / Passeport", patientId: "Identifiant Patient", idVerification: "Vérification d’identité", address: "Adresse", city: "Ville", postalCode: "Code postal", country: "Pays", assignedFacilities: "Établissements attribués", status: "Statut", relationshipSource: "Source de la relation", lawfulBasis: "Base légale", languages: "Langues", bloodType: "Groupe sanguin", allergies: "Allergies", medicalConditions: "Antécédents médicaux", medications: "Médicaments", organDonor: "Donneur d’organes" },
    status: { active: "Actif", draft: "Brouillon", removed: "Retiré" },
    idVerification: { pending: "En attente", verified: "Vérifié", rejected: "Refusé", not_recorded: "Non enregistré" },
    relationshipSources: { existing_patient_link: "Lien avec un patient existant", manual: "Relation manuelle" },
    lawfulBasis: { patient_present_intake_authorised: "Patient présent et autorisé", direct_care_registration: "Enregistrement pour soins directs", referral_documentation_received: "Documentation d’orientation reçue", emergency_provisional_intake: "Admission provisoire d’urgence", programme_enrolment_authority: "Autorité du programme de santé publique" },
  },
  institutionClinicians: {
    relationshipStatus: { invitation_not_sent: "Invitation non envoyée", invitation_pending: "Invitation en attente", active: "Actif", declined: "Refusée", expired: "Expirée", revoked: "Révoquée", suspended: "Suspendu", removed: "Retiré" },
    relationship: { title: "Relation Institution", loading: "Vérification de la relation actuelle avec le clinicien.", active: "{clinician} a accepté la relation de clinicien avec {facility}.", pending: "{clinician} n’a pas encore accepté la relation de clinicien avec {facility}.", declined: "{clinician} a refusé la relation de clinicien avec {facility}.", expired: "L’invitation adressée à {clinician} pour examiner la relation de clinicien avec {facility} a expiré.", revoked: "L’invitation adressée à {clinician} pour examiner la relation de clinicien avec {facility} a été révoquée.", suspended: "La relation de clinicien de {clinician} avec {facility} est suspendue.", removed: "La relation de clinicien de {clinician} avec {facility} a été retirée.", notSent: "Envoyez une invitation à {clinician} afin qu’il puisse examiner et accepter la relation de clinicien avec {facility}.", expires: "Expire le {date}.", resend: "Renvoyer", revoke: "Révoquer", send: "Envoyer une invitation au clinicien" },
  },
});

// INSTITUTION_TOP_APP_BAR_AND_FRANCOPHONE_CLOSURE_2026_08
// Institution route copy is kept in the shared catalogue so the workspace
// never owns an English/French fork of its UI.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES["en-GB"], {
  institutionSettings: { title: "Settings", selectFacility: "Choose the facility whose settings you want to manage." },
  institutionFeedback: { title: "Staging Feedback", subtitle: "Share what worked, what felt unclear, or what needs attention in preparation for production." },
  institutionRegionalHealth: { title: "Regional Health" },
  institutionAccount: {
    title: "My account", subtitle: "Your AfriMed™ Institution identity, image, and role settings.",
    memberProfile: "Member profile", memberProfileCopy: "Keep your Institution identity clear and role-safe across AfriMed™.",
    fullName: "Full name", workEmail: "Work email", role: "Institution role", institution: "Institution",
    privilegeHandoff: "Privilege handoff", privilegeHandoffCopy: "Can manage Institution operations and approved member workflows within assigned permissions. Surveillance authority, facility authority and programme authority remain separately controlled and audited.",
    emailLifecycle: "Email changes use the existing verified sign-in lifecycle and are not managed through Institution governance.",
    saving: "Saving…", save: "Save changes", profileImage: "Profile image", profileImageCopy: "Stored in the established shared AfriMed profile image area and preferred by the header first.",
    imageSaved: "Profile image saved.", imageSaveFailed: "Profile image could not be saved.", imageSupport: "Supported: JPEG, PNG, WebP up to 5MB.",
    passwordTitle: "Password and sign-in", passwordCopy: "Use your password for normal Institution sign-in. Secure links remain available for first-time setup and recovery.", passwordSubmit: "Set or change password", recovery: "Send recovery link",
  },
  institutionPatients: { relationshipSources: { existingPatientLink: "Existing Patient Link", manual: "Manual relationship" } },
});

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  institutionShell: { actions: { back: "Retour" } },
  institutionSettings: { title: "Paramètres", selectFacility: "Choisissez l’établissement dont vous souhaitez gérer les paramètres." },
  institutionFeedback: { title: "Commentaires de préproduction", subtitle: "Indiquez ce qui a bien fonctionné, ce qui n’était pas clair ou ce qui nécessite une attention avant la production." },
  institutionRegionalHealth: { title: "Santé régionale" },
  institutionAccount: {
    title: "Mon compte", subtitle: "Votre identité, image et rôle AfriMed™ Institution.",
    memberProfile: "Profil du membre", memberProfileCopy: "Gardez votre identité Institution claire et adaptée à votre rôle dans AfriMed™.",
    fullName: "Nom complet", workEmail: "E-mail professionnel", role: "Rôle Institution", institution: "Institution",
    privilegeHandoff: "Transmission des privilèges", privilegeHandoffCopy: "Vous pouvez gérer les opérations Institution et les parcours de membres approuvés dans les limites de vos autorisations. Les autorités de surveillance, d’établissement et de programme restent contrôlées et auditées séparément.",
    emailLifecycle: "Les changements d’e-mail utilisent le cycle de connexion vérifié existant et ne sont pas gérés par la gouvernance Institution.",
    saving: "Enregistrement…", save: "Enregistrer les modifications", profileImage: "Image de profil", profileImageCopy: "Enregistrée dans l’espace d’images de profil AfriMed partagé établi et utilisée en priorité dans l’en-tête.",
    imageSaved: "Image de profil enregistrée.", imageSaveFailed: "L’image de profil n’a pas pu être enregistrée.", imageSupport: "Pris en charge : JPEG, PNG, WebP jusqu’à 5 Mo.",
    passwordTitle: "Mot de passe et connexion", passwordCopy: "Utilisez votre mot de passe pour la connexion Institution habituelle. Des liens sécurisés restent disponibles pour la première configuration et la récupération.", passwordSubmit: "Définir ou modifier le mot de passe", recovery: "Envoyer le lien de récupération",
    detailsSaved: "Les informations du compte ont été enregistrées.", detailsSaveFailed: "Les informations du compte n’ont pas pu être enregistrées.", recoverySent: "Le lien de récupération sécurisé a été envoyé.", recoveryFailed: "Le lien de récupération n’a pas pu être envoyé pour le moment. Réessayez.",
    imageActions: { alt: "Image de profil", upload: "Téléverser l’image", uploading: "Téléversement…", remove: "Supprimer l’image" },
    emailChange: { trigger: "Modifier l’adresse e-mail", close: "Fermer la modification de l’adresse e-mail", title: "Modifier l’adresse e-mail", intro: "Nous enverrons un lien de confirmation sécurisé à la nouvelle adresse. Votre adresse de connexion actuelle restera active jusqu’à la réussite de cette vérification.", newEmail: "Nouvelle adresse e-mail professionnelle", invalidEmail: "Saisissez une nouvelle adresse e-mail valide.", differentEmail: "Saisissez une adresse e-mail différente.", duplicateError: "Cette adresse e-mail est déjà utilisée par un autre compte AfriMed™ ou une adhésion active.", startError: "Nous n’avons pas pu démarrer la modification sécurisée de l’adresse e-mail. Réessayez.", sent: "Un lien de confirmation sécurisé a été envoyé. Votre adresse e-mail actuelle reste active jusqu’à la réussite de la vérification.", cancel: "Annuler", sending: "Envoi…", send: "Envoyer la confirmation" },
  },
  institutionPatients: { relationshipSources: { existingPatientLink: "Lien avec un patient existant", manual: "Relation manuelle" } },
});

// INSTITUTION_ACCOUNT_AND_SERVICES_FRENCH_CHROME_2026_08
// These messages localise only controlled UI chrome. Service, specialty, facility,
// clinician, and note values remain the stored source records.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  settings: {
    password: {
      strongHelpTitle: "Utilisez un mot de passe robuste", requirementUppercase: "Majuscule", requirementLowercase: "Minuscule", requirementNumber: "Chiffre", requirementSymbol: "Symbole", requirementMinLength: "Au moins 8 caractères", generateAction: "Générer un mot de passe robuste", newLabel: "Nouveau mot de passe", confirmLabel: "Confirmer le mot de passe",
    },
  },
  institutionServices: {
    title: "Services", catalogue: "Catalogue des services de l’Institution", new: "Nouveau service", search: "Rechercher des services", searchPlaceholder: "Rechercher des services", backToServices: "Retour aux services", loading: "Chargement des services…", retry: "Réessayer", missing: "Ce service n’est pas disponible dans l’Institution parente active.", archived: "Ce service est archivé et ne figure plus dans le catalogue actif.", archivedSuccess: "Service archivé", writeDenied: "Vous n’êtes pas autorisé à gérer ce catalogue de services parent.", notSupplied: "Non fourni", created: "Service créé", updated: "Service mis à jour", priceUnavailable: "Configuration en attente",
    columns: { service: "Service", specialty: "Spécialité", active: "Actif", notes: "Remarques", price: "Prix" },
    mobileFields: { service: "Service", specialty: "Spécialité", status: "Statut", price: "Prix", facility: "Établissement" },
    fields: { serviceName: "Nom du service", specialty: "Spécialité", price: "Prix", status: "Statut", currency: "Devise", notes: "Indications et remarques", facility: "Établissement médical", assignedFacilities: "Établissements attribués" },
    filters: { status: "Filtrer les services par statut", open: "Filtrer les services", title: "Filtrer les services", allSpecialties: "Toutes les spécialités", allFacilities: "Tous les établissements médicaux" },
    actions: { save: "Enregistrer", saving: "Enregistrement…", cancel: "Annuler", clear: "Effacer", apply: "Appliquer", done: "Terminé", discard: "Ignorer", copyLink: "Copier le lien", openInNewTab: "Ouvrir dans un nouvel onglet", delete: "Supprimer" },
    status: { all: "Tout", active: "Actif", inactive: "Inactif" },
    basicInformation: "Informations de base", selectSpecialty: "Sélectionner une spécialité", selectFacilities: "Sélectionner des établissements médicaux", facilityCount: "{count} sélectionné(s)", activeMedicalInstitution: "Établissement médical actif", select: "Sélectionner", selected: "Sélectionné", noFacilities: "Aucun établissement médical attribué", noFacilitiesTitle: "Créez d’abord un établissement médical", noFacilitiesBody: "Ajoutez un établissement médical avant d’ajouter des services.", emptyTitle: "Aucun service pour le moment", emptyBody: "Ajoutez les services que votre établissement médical fournit aux patients.", noResultsTitle: "Aucun service ne correspond à ce filtre", noResultsBody: "Essayez un autre filtre.", facilityAssignmentSupport: "Un service doit être attribué à un ou plusieurs établissements médicaux actifs dans cette Institution parente.", moreActions: "Autres actions relatives au service", linkCopied: "Lien du service Institution copié.", linkCopyFallback: "Copiez l’URL du service Institution depuis la barre d’adresse de votre navigateur.", discardTitle: "Ignorer les modifications ?", discardBody: "Voulez-vous vraiment ignorer les modifications apportées ? Cette action est irréversible.", archiveTitle: "Supprimer le service « {name} » ?", archiveBody: "Ce service sera supprimé de votre établissement médical. Cette action est irréversible.", createError: "Le service n’a pas pu être créé.", archiveError: "Le service n’a pas pu être archivé.", loadError: "Les services n’ont pas pu être chargés.", clearPrice: "Effacer le prix",
    delivery: { title: "Cliniciens attribués", description: "Les cliniciens sont autorisés à fournir ce service Institution dans l’établissement attribué.", assign: "Attribuer un clinicien", assignSupport: "Choisissez un établissement du service, puis un clinicien admissible déjà lié à cet établissement.", loading: "Chargement des attributions de prestation des cliniciens…", loadError: "Les attributions de prestation des cliniciens n’ont pas pu être chargées.", loadErrorBody: "Réessayez de charger les attributions de prestation des cliniciens.", unavailableTitle: "Les attributions de prestation ne sont pas encore disponibles", unavailableBody: "Le contrat de prestation du service n’est pas activé dans cet environnement.", emptyTitle: "Aucun clinicien attribué", emptyBody: "Attribuez un clinicien Institution admissible à un établissement autorisé avant que ce service puisse être fourni.", inactiveService: "Ce service est inactif et ne peut pas être fourni de manière opérationnelle.", facility: "Établissement", clinician: "Clinicien", selectFacility: "Sélectionner un établissement", selectClinician: "Sélectionner un clinicien admissible", selectFacilityFirst: "Choisissez d’abord un établissement", noneEligible: "Aucun clinicien Institution actif admissible n’est lié à cet établissement.", effectiveFrom: "Effectif à partir du", effectiveTo: "Effectif jusqu’au (facultatif)", selectDate: "Sélectionner une date", effectiveDateRange: "La date de fin ne peut pas être antérieure à la date de début.", assigned: "Clinicien attribué", createError: "Le clinicien n’a pas pu être attribué à ce service.", credentialWarning: "Justificatif de qualification : {state}", effective: "Effectif {from}{until}", unknownFacility: "Établissement autorisé", unknownClinician: "Clinicien lié", states: { actively_deliverable: "Fourni activement", catalogue_unassigned: "Aucun clinicien attribué", delivery_unavailable: "Prestation indisponible", inactive_service: "Service inactif", archived_service: "Service archivé", inactive_assignment: "Attribution inactive", inactive_clinician_relationship: "Relation clinicien inactive", facility_unavailable: "Établissement indisponible", facility_clinician_relationship_inactive: "Relation établissement-clinicien inactive", assignment_expired: "Attribution expirée", assignment_not_yet_effective: "Pas encore effectif", archived_assignment: "Attribution archivée" } },
  },
});

// INSTITUTION_VISIBLE_FRENCH_COMPLETION_2026_08
// Controlled labels are translated at their shared display boundary; names,
// addresses, emails, and other user-entered source data deliberately remain
// untouched.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES["en-GB"], {
  institutionControlled: { owner: "Owner", super_admin: "Super Admin", member: "Member", active: "Active", draft: "Draft", setup_pending: "Setup pending", not_assigned: "Not assigned", hospital: "Hospital" },
  institutionSurveillance: { historyEvents: { member_invited: "Member invited", member_suspended: "Member suspended", member_removed: "Member removed", authority_assigned: "Facility authority assigned", authority_revoked: "Facility authority removed", setup_pending: "Setup pending" } },
  institutionDashboard: {
    patients: "Patients", overallRating: "Overall rating", appointments: "Appointments", past30Days: "Past 30 Days", thisMonth: "This Month", clinicVisitors: "Clinic visitors", clinicVisitorsDateRange: "Clinic visitors date range", selectMonth: "Select month", previousMonth: "Previous month", nextMonth: "Next month", weekdays: "S,M,T,W,T,F,S", patientAppointments: "Patients Appointments", patientAppointmentsIntro: "An overview of the patient's journey and the assistance provided at the clinic.", noAppointments: "No linked appointments in this range.", appointmentCoverage: "Explicitly linked Institution appointments only", appointmentIncludedStatuses: "Scheduled, confirmed, in progress and completed; missed and cancelled excluded", appointmentLoading: "Loading scoped appointments…", appointmentReportingUnavailable: "Appointment reporting requires an authorised Institution data link.", setupPending: "Setup pending", unavailable: "Unavailable", visitorSourceUnavailable: "Visitor chart unavailable: no governed Institution check-in source is connected. Appointments are not visitor counts.", appointmentCalendar: "Institution appointments", calendarLinkedAppointments: "linked appointments in this month", calendarUnavailable: "Calendar unavailable until an authorised Institution appointment source is connected.", synthetic: "Synthetic", ageYears: "{count} years old", noPriorVisit: "No prior visit", noDiagnosis: "No diagnosis recorded", notRecorded: "Not recorded", notProvided: "Not provided", timeRange: "Time range",
    visitorRanges: { "3m": "Past 3 months", "6m": "Past 6 months", "12m": "Past Year" }, ranges: { today: "Today", week: "Week", month: "Month", year: "Year" }, months: { 1: "Jan", 2: "Feb", 3: "Mar", 4: "Apr", 5: "May", 6: "Jun", 7: "Jul", 8: "Aug", 9: "Sep", 10: "Oct", 11: "Nov", 12: "Dec" }, columns: { patientName: "Patient name", primaryCondition: "Primary condition", riskLevel: "Risk level", insurance: "Insurance", lastVisit: "Last visit", doctors: "Doctors" },
  },
  regionalHealthSignals: { errors: { activeMembershipRequired: "Active institution membership required" } },
});

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  institutionControlled: { owner: "Propriétaire", super_admin: "Super administrateur", member: "Membre", active: "Actif", draft: "Brouillon", setup_pending: "Configuration en attente", not_assigned: "Non attribué", hospital: "Hôpital" },
  institutionSurveillance: { noProgrammeAssigned: "Aucun programme attribué", allProgrammes: "Tous les programmes de l’Institution", historyEvents: { member_invited: "Membre invité", member_suspended: "Membre suspendu", member_removed: "Membre retiré", authority_assigned: "Autorité d’établissement attribuée", authority_revoked: "Autorité d’établissement retirée", setup_pending: "Configuration en attente" } },
  institutionDashboard: {
    patients: "Patients", overallRating: "Évaluation globale", appointments: "Rendez-vous", past30Days: "30 derniers jours", thisMonth: "Ce mois-ci", clinicVisitors: "Visiteurs de la clinique", clinicVisitorsDateRange: "Période des visiteurs de la clinique", selectMonth: "Sélectionner le mois", previousMonth: "Mois précédent", nextMonth: "Mois suivant", weekdays: "D,L,M,M,J,V,S", patientAppointments: "Rendez-vous des patients", patientAppointmentsIntro: "Vue d’ensemble du parcours du patient et de l’assistance fournie à la clinique.", noAppointments: "Aucun rendez-vous lié dans cette période.", appointmentCoverage: "Uniquement les rendez-vous explicitement liés à l’Institution", appointmentIncludedStatuses: "Planifiés, confirmés, en cours et terminés ; manqués et annulés exclus", appointmentLoading: "Chargement des rendez-vous autorisés…", appointmentReportingUnavailable: "Le suivi des rendez-vous nécessite un lien de données Institution autorisé.", setupPending: "Configuration en attente", unavailable: "Indisponible", visitorSourceUnavailable: "Graphique des visiteurs indisponible : aucune source de présence autorisée n’est liée à l’Institution. Les rendez-vous ne sont pas des visiteurs.", appointmentCalendar: "Rendez-vous de l’Institution", calendarLinkedAppointments: "rendez-vous liés ce mois-ci", calendarUnavailable: "Calendrier indisponible tant qu’aucune source de rendez-vous autorisée n’est liée à l’Institution.", synthetic: "Synthétique", ageYears: "{count} ans", noPriorVisit: "Aucune visite antérieure", noDiagnosis: "Aucun diagnostic enregistré", notRecorded: "Non renseigné", notProvided: "Non fourni", timeRange: "Période",
    visitorRanges: { "3m": "3 derniers mois", "6m": "6 derniers mois", "12m": "Année écoulée" }, ranges: { today: "Aujourd’hui", week: "Semaine", month: "Mois", year: "Année" }, months: { 1: "janv.", 2: "févr.", 3: "mars", 4: "avr.", 5: "mai", 6: "juin", 7: "juil.", 8: "août", 9: "sept.", 10: "oct.", 11: "nov.", 12: "déc." }, columns: { patientName: "Nom du patient", primaryCondition: "État principal", riskLevel: "Niveau de risque", insurance: "Assurance", lastVisit: "Dernière visite", doctors: "Cliniciens" },
  },
  regionalHealthSignals: { errors: { activeMembershipRequired: "Une adhésion Institution active est requise" } },
});

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES["en-GB"], {
  institutionRegistry: { columns: { name: "Institution Name", type: "Type", specialties: "Specialties", description: "Description", address: "Address", domain: "Domain", openingHours: "Opening hours" } },
  institutionFeedback: {
    feedbackType: "Feedback type", yourContext: "Your context", area: "Area", severity: "Severity", select: "Select", whatShouldWeKnow: "What should we know?", messagePlaceholder: "A few specific notes are more useful than a polished essay.", contextCaptured: "Context captured:", contactConsent: "AfriMed™ may contact me about this feedback.", cancel: "Cancel", sending: "Sending...", send: "Send feedback",
    options: { general_suggestion: "General suggestion", institution_moh: "Institution / MoH", general_platform: "General platform", medium: "Medium" },
  },
});

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  institutionRegistry: { columns: { name: "Nom de l’établissement", type: "Type", specialties: "Spécialités", description: "Description", address: "Adresse", domain: "Domaine", openingHours: "Heures d’ouverture" } },
  institutionFeedback: {
    feedbackType: "Type de retour", yourContext: "Votre contexte", area: "Domaine", severity: "Gravité", select: "Sélectionner", whatShouldWeKnow: "Que devons-nous savoir ?", messagePlaceholder: "Quelques notes précises sont plus utiles qu’un texte parfaitement rédigé.", contextCaptured: "Contexte capturé :", contactConsent: "AfriMed™ peut me contacter au sujet de ce retour.", cancel: "Annuler", sending: "Envoi…", send: "Envoyer le retour",
    options: { general_suggestion: "Suggestion générale", institution_moh: "Institution / ministère de la Santé", general_platform: "Plateforme générale", medium: "Moyenne" },
  },
});

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES["en-GB"], {
  institutionPatients: { title: "Patients", all: "All", filterByStatus: "Filter patients by status", importCsv: "Import CSV", addPatient: "Add Patient", notSupplied: "Not supplied", emptyTitle: "No patients yet", emptyBody: "Patients linked to this Institution will appear here.", relationshipSources: { notRecorded: "Not recorded" }, columns: { patient: "Patient", contact: "Contact", dateOfBirth: "Date of birth", assignedFacility: "Assigned facility", relationshipSource: "Relationship / source", status: "Status" } },
});

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  institutionPatients: { title: "Patients", all: "Tous", filterByStatus: "Filtrer les patients par statut", importCsv: "Importer un CSV", addPatient: "Ajouter un patient", notSupplied: "Non fourni", emptyTitle: "Aucun patient pour le moment", emptyBody: "Les patients liés à cette Institution apparaîtront ici.", relationshipSources: { notRecorded: "Non renseigné" }, columns: { patient: "Patient", contact: "Contact", dateOfBirth: "Date de naissance", assignedFacility: "Établissement attribué", relationshipSource: "Relation / source", status: "Statut" } },
});

// MAP_RENDER_RECOVERY_2026_07
// Keep the shared Mapbox failure surface understandable without exposing a
// rendering, token, or network implementation detail to a patient.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES["en-GB"], {
  maps: {
    loading: {
      title: "Loading map",
      body: "Preparing healthcare locations…",
    },
    unavailable: {
      title: "Map temporarily unavailable",
      body: "You can still browse the healthcare results below. Try loading the map again.",
    },
    unsupported: {
      title: "Interactive map unsupported",
      body: "You can still browse the healthcare results below.",
    },
    retry: "Try again",
  },
});

// Appointment reminder consent is one shared flow: booking establishes the
// visible default only when it is missing, then the same wording is used for
// an explicit opt-out in Settings or directly after booking.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES["en-GB"], {
  appointments: {
    reminders: {
      active: {
        title: "Appointment reminders are on",
        copy: "AfriMed™ will send reminders before your appointment. You can change this at any time in Notifications.",
        action: "Got it",
        turnOff: "Turn reminders off",
      },
      deactivate: {
        title: "Are you sure you want to switch off reminders for this appointment?",
        copy: "If you turn off reminders for this appointment, you may miss the appointment.",
        keep: "Keep reminders on",
        confirm: "Switch off reminders",
      },
    },
  },
});

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  appointments: {
    reminders: {
      active: {
        title: "Les rappels de rendez-vous sont activés",
        copy: "AfriMed™ enverra des rappels avant votre rendez-vous. Vous pouvez modifier ce choix à tout moment dans Notifications.",
        action: "Compris",
        turnOff: "Désactiver les rappels",
      },
      deactivate: {
        title: "Voulez-vous vraiment désactiver les rappels pour ce rendez-vous ?",
        copy: "Si vous désactivez les rappels pour ce rendez-vous, vous risquez de le manquer.",
        keep: "Garder les rappels activés",
        confirm: "Désactiver les rappels",
      },
    },
  },
});

// CONTROLLED_STAGING_ACCESS_2026_07
// Campaign eligibility and role resolution remain server-owned; the public
// contract makes the controlled staging-access lifecycle clear.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES["en-GB"], {
  stagingAccess: {
    title: "Private staging access",
    copy: "Enter the email to add you to the AfriMed™ staging allow list.",
    email: "Email",
    request: "Sign me up",
    requesting: "Requesting access...",
    confirmation: "Check your email to continue.",
  },
  firstUse: {
    password: {
      agreePrefix: "I agree to the",
      and: "and",
    },
  },
  settings: {
    notificationPreferences: {
      reminders: {
        deactivate: {
          title: "Are you sure you want to switch off reminders for this appointment?",
          copy: "If you turn off reminders for this appointment, you may miss the appointment.",
          keep: "Keep reminders on",
          confirm: "Switch off reminders",
        },
      },
    },
  },
  appointments: {
    reminders: {
      active: {
        title: "Les rappels de rendez-vous sont activés",
        copy: "AfriMed™ vous enverra des rappels avant votre rendez-vous. Vous pouvez modifier ce réglage à tout moment dans Notifications.",
        action: "Compris",
      },
    },
  },
});

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  stagingAccess: {
    title: "Accès privé à la préproduction",
    copy: "Saisissez l’adresse e-mail pour vous ajouter à la liste d’accès à la préproduction AfriMed™.",
    email: "E-mail",
    request: "M’inscrire",
    requesting: "Demande d’accès en cours...",
    confirmation: "Consultez votre e-mail pour continuer.",
  },
  firstUse: {
    password: {
      agreePrefix: "J’accepte les",
      and: "et la",
    },
  },
  settings: {
    notificationPreferences: {
      reminders: {
        deactivate: {
          title: "Voulez-vous vraiment désactiver les rappels ?",
          copy: "Si vous désactivez les rappels, vous risquez de manquer vos rendez-vous, car AfriMed™ ne pourra plus vous les rappeler.",
          keep: "Garder les rappels activés",
          confirm: "Désactiver les rappels",
        },
      },
    },
  },
});

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  maps: {
    loading: {
      title: "Chargement de la carte",
      body: "Préparation des établissements de santé à proximité…",
    },
    unavailable: {
      title: "Carte temporairement indisponible",
      body: "Vous pouvez toujours parcourir les résultats de santé ci-dessous. Essayez de recharger la carte.",
    },
    unsupported: {
      title: "Carte interactive non prise en charge",
      body: "Vous pouvez toujours parcourir les résultats de santé ci-dessous.",
    },
    retry: "Réessayer",
  },
});

// INSTITUTION_PATIENTS_SLICE_1_I18N_2026_07
// The governed Patients destination is available in every activated language.
// Keep its shared navigation and status-filter keys present in each bundle so
// the route never falls back through a development missing-key warning.
const INSTITUTION_PATIENTS_SHARED_EN = {
  navigation: { patients: "Patients" },
  filters: { all: "All" },
};

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES["en-GB"], INSTITUTION_PATIENTS_SHARED_EN);
for (const languageCode of ["pt", "de", "sn", "zu"]) {
  deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES[languageCode], INSTITUTION_PATIENTS_SHARED_EN);
}
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  navigation: { patients: "Patients" },
  filters: { all: "Tous" },
});

// WORLDLINE_HOSTED_SANDBOX_PAYMENT_FOUNDATION_2026_07
// Kept narrowly scoped to the controlled staging checkout and return state.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES["en-GB"], {
  worldline: {
    checkout: { title: "Pay securely with Worldline", notice: "No real charge will be made.", action: "Pay securely with Worldline", incomplete: "Booking details are incomplete for Worldline sandbox checkout.", unavailable: "Worldline sandbox checkout is unavailable." },
    return: { title: "Worldline sandbox payment", missing: "The payment reference is missing.", retry: "We are still verifying the payment. Please try again shortly.", success: "Payment verified. Your appointment is confirmed.", cancelled: "The sandbox checkout was cancelled. No appointment was confirmed.", declined: "The sandbox payment was declined. No appointment was confirmed.", expired: "The sandbox checkout expired. No appointment was confirmed.", failed: "We could not verify this payment. No appointment was confirmed.", pending: "Payment is still processing. No appointment is confirmed until Worldline verification succeeds.", verifying: "Verifying your sandbox payment with Worldline…", retryAction: "Check payment status", back: "Back to appointments" },
  },
});
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  worldline: {
    checkout: { title: "Payer en toute sécurité avec Worldline", notice: "Aucun paiement réel ne sera effectué.", action: "Payer en toute sécurité avec Worldline", incomplete: "Les détails de réservation sont incomplets pour le paiement Worldline en bac à sable.", unavailable: "Le paiement Worldline en bac à sable est indisponible." },
    return: { title: "Paiement Worldline en bac à sable", missing: "La référence de paiement est manquante.", retry: "Nous vérifions encore le paiement. Réessayez dans un instant.", success: "Paiement vérifié. Votre rendez-vous est confirmé.", cancelled: "Le paiement en bac à sable a été annulé. Aucun rendez-vous n’a été confirmé.", declined: "Le paiement en bac à sable a été refusé. Aucun rendez-vous n’a été confirmé.", expired: "Le paiement en bac à sable a expiré. Aucun rendez-vous n’a été confirmé.", failed: "Nous n’avons pas pu vérifier ce paiement. Aucun rendez-vous n’a été confirmé.", pending: "Le paiement est toujours en cours. Aucun rendez-vous n’est confirmé avant la vérification par Worldline.", verifying: "Vérification de votre paiement Worldline en bac à sable…", retryAction: "Vérifier l’état du paiement", back: "Retour aux rendez-vous" },
  },
});

// Regional Health facility reporting activation. These strings intentionally
// live in the established language patch rather than a route-specific store.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES["en-GB"], {
  regionalHealth: {
    countries: {
      angola: "Angola", botswana: "Botswana", comoros: "Comoros", drCongo: "DR Congo",
      eswatini: "Eswatini", lesotho: "Lesotho", madagascar: "Madagascar", malawi: "Malawi",
      mauritius: "Mauritius", mozambique: "Mozambique", namibia: "Namibia", seychelles: "Seychelles",
      southAfrica: "South Africa", tanzania: "Tanzania", zambia: "Zambia", zimbabwe: "Zimbabwe",
    },
  },
  regionalHealthSignals: {
    actions: { activateFacility: "Activate reporting facility", configureProfile: "Configure reporting profile", saveProfile: "Save reporting profile", increaseValue: "Increase value", decreaseValue: "Decrease value" },
    readiness: {
      title: "Reporting readiness", ready: "Ready to report", notReady: "Facility setup is incomplete", noReadyFacility: "Complete a facility reporting profile before reporting a signal.",
      facility_identity_incomplete: "Facility identity is incomplete.", facility_location_incomplete: "Geographic reporting location is incomplete.",
      aggregate_reporting_disabled: "Aggregate reporting is disabled.", reporting_cadence_missing: "Reporting cadence is missing.", programme_scope_missing: "Required programme scope is missing or no longer authorised for this facility.", authorised_reporting_member_missing: "No authorised reporting member or Super Admin entitlement is available.", reporting_profile_missing: "Facility reporting profile is missing.",
    },
    facilityActivation: {
      title: "Activate reporting facility", profileTitle: "Facility reporting profile", facilityName: "Facility name", facilityType: "Facility type", country: "Country", province: "Province/state", district: "District / health zone", latitude: "Latitude", longitude: "Longitude", create: "Create facility", identityHelp: "This creates an Institution facility through the governed AfriMed™ facility path. It is not a public or national registration.",
      reportingLanguage: "Reporting language", aggregateEnabled: "Enable aggregate reporting", cadence: "Reporting cadence", cadenceImmediate: "Immediate signal", cadenceWeekly: "Weekly aggregate", cadenceMonthly: "Monthly aggregate", programme: "Programme scope (where applicable)", programmeNotRequired: "Programme scope is not required for this facility", programmeRequired: "A programme scope is required for this facility", reportingMember: "Reporting contact (existing verified member)", noMember: "Use established Super Admin entitlement", recordMode: "Record mode", mappingTitle: "DHIS2-ready mapping", mappingHelp: "Preview-only contract. No live DHIS2 connection or external submission is available.", mappingVersion: "Mapping version", orgUnitMapping: "Organisation-unit mapping", dataElementMapping: "Data-element mapping", categoryMapping: "Category-option mapping", mappingNotConfigured: "Not configured", mappingPartial: "Partially mapped", mappingReady: "Ready for preview", mappingPreview: "Preview generated", externalNotConnected: "External submission not connected", profileHelp: "Aggregate information only. A facility becomes report-ready only when this server-backed checklist passes.", types: { clinic: "Clinic", hospital: "Hospital", laboratory: "Laboratory", other: "Other approved facility" },
    },
  },
});

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  regionalHealth: {
    countries: {
      angola: "Angola", botswana: "Botswana", comoros: "Comores", drCongo: "RDC",
      eswatini: "Eswatini", lesotho: "Lesotho", madagascar: "Madagascar", malawi: "Malawi",
      mauritius: "Maurice", mozambique: "Mozambique", namibia: "Namibie", seychelles: "Seychelles",
      southAfrica: "Afrique du Sud", tanzania: "Tanzanie", zambia: "Zambie", zimbabwe: "Zimbabwe",
    },
  },
  regionalHealthSignals: {
    actions: { activateFacility: "Activer l’établissement déclarant", configureProfile: "Configurer le profil de notification", saveProfile: "Enregistrer le profil de notification", increaseValue: "Augmenter la valeur", decreaseValue: "Diminuer la valeur" },
    readiness: {
      title: "État de préparation à la notification", ready: "Prêt à déclarer", notReady: "La configuration de l’établissement est incomplète", noReadyFacility: "Terminez le profil de notification d’un établissement avant de déclarer un signal.",
      facility_identity_incomplete: "L’identité de l’établissement est incomplète.", facility_location_incomplete: "La localisation géographique de notification est incomplète.", aggregate_reporting_disabled: "La notification agrégée est désactivée.", reporting_cadence_missing: "La cadence de notification est manquante.", programme_scope_missing: "Le périmètre de programme requis est manquant ou n’est plus autorisé pour cet établissement.", authorised_reporting_member_missing: "Aucun membre déclarant autorisé ni droit de Super Admin n’est disponible.", reporting_profile_missing: "Le profil de notification de l’établissement est manquant.",
    },
    facilityActivation: {
      title: "Activer l’établissement déclarant", profileTitle: "Profil de notification de l’établissement", facilityName: "Nom de l’établissement", facilityType: "Type d’établissement", country: "Pays", province: "Province/État", district: "District / zone de santé", latitude: "Latitude", longitude: "Longitude", create: "Créer l’établissement", identityHelp: "Cette action crée un établissement Institution via le parcours AfriMed™ gouverné. Il ne s’agit pas d’un enregistrement public ou national.",
      reportingLanguage: "Langue de notification", aggregateEnabled: "Activer la notification agrégée", cadence: "Cadence de notification", cadenceImmediate: "Signal immédiat", cadenceWeekly: "Agrégat hebdomadaire", cadenceMonthly: "Agrégat mensuel", programme: "Portée du programme (le cas échéant)", programmeNotRequired: "Un périmètre de programme n’est pas requis pour cet établissement", programmeRequired: "Un périmètre de programme est requis pour cet établissement", reportingMember: "Contact déclarant (membre vérifié existant)", noMember: "Utiliser le droit de Super Admin établi", recordMode: "Mode d’enregistrement", mappingTitle: "Correspondance prête pour DHIS2", mappingHelp: "Contrat d’aperçu uniquement. Aucune connexion DHIS2 active ni transmission externe n’est disponible.", mappingVersion: "Version de correspondance", orgUnitMapping: "Correspondance d’unité organisationnelle", dataElementMapping: "Correspondance d’élément de données", categoryMapping: "Correspondance d’option de catégorie", mappingNotConfigured: "Non configuré", mappingPartial: "Partiellement mappé", mappingReady: "Prêt pour l’aperçu", mappingPreview: "Aperçu généré", externalNotConnected: "Transmission externe non connectée", profileHelp: "Informations agrégées uniquement. Un établissement est prêt à déclarer uniquement lorsque cette liste de contrôle côté serveur est validée.", types: { clinic: "Clinique", hospital: "Hôpital", laboratory: "Laboratoire", other: "Autre établissement approuvé" },
    },
  },
});

// Institution Regional Health Heatmap v1: aggregate-only, authorised,
// approximate-only and neutral reporting-volume terminology.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES["en-GB"], {
  regionalHealthSignals: {
    tabs: { heatmap: "Map" },
    heatmap: {
      title: "Institution reporting map",
      body: "Approximate, authorised aggregate reporting geography only. Exact locations are never displayed.",
      approximateOnly: "Approximate areas only",
      legend: "Reporting volume",
      legendUnit: "Authorised aggregate reporting units per approximate area",
      legendClarification: "Colour intensity shows reporting volume, not severity or risk.",
      listTitle: "Accessible map results",
      loading: "Loading map…",
      filters: { label: "Map filters", facility: "Facility", programme: "Programme", period: "Reporting period", workflow: "Workflow state", quality: "Data-quality state", allFacilities: "All authorised facilities", allProgrammes: "All authorised programmes", allWorkflow: "All workflow states", allQuality: "All data-quality states" },
      period: { last_30_days: "Last 30 days", last_90_days: "Last 90 days", current_year: "Current year" },
      volume: { "5_19": "5–19", "20_49": "20–49", "50_plus": "50+" },
      quality: { clear: "No unresolved quality flags", needs_review: "Quality review required" },
      fields: { area: "Approved approximate area", programme: "Programme", period: "Reporting period", volume: "Reporting volume", workflow: "Workflow state", quality: "Data-quality state" },
      actions: { apply: "Apply filters", reset: "Reset view", openSignal: "Open signal", openAudit: "Open audit trail" },
      empty: {
        no_authorised_geographyTitle: "No eligible reporting geography",
        no_authorised_geography: "No approved approximate geography is available for the current selection.",
        incomplete_facility_mappingTitle: "Facility geography is incomplete",
        incomplete_facility_mapping: "An attestation exists, but its active facility, programme, provenance or approximate-location requirements are incomplete.",
        no_aggregate_dataTitle: "No aggregate data",
        no_aggregate_data: "No authorised aggregate reporting data is available for the selected filters.",
        all_cells_suppressedTitle: "Results protected by suppression",
        all_cells_suppressed: "The selected results are below the minimum reporting-unit threshold or require complementary suppression.",
        unavailableTitle: "Map unavailable",
        unavailable: "The authorised aggregate snapshot could not be loaded. Try again later.",
        mapUnavailableTitle: "Map unavailable",
        mapUnavailable: "The accessible result list remains available below.",
      },
    },
  },
});

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  regionalHealthSignals: {
    tabs: { heatmap: "Carte" },
    heatmap: {
      title: "Carte des déclarations de l’Institution",
      body: "Uniquement une géographie approximative et autorisée des déclarations agrégées. Les emplacements exacts ne sont jamais affichés.",
      approximateOnly: "Zones approximatives uniquement",
      legend: "Volume de déclarations",
      legendUnit: "Unités de déclaration agrégées autorisées par zone approximative",
      legendClarification: "L’intensité de la couleur indique le volume de déclarations, et non la gravité ni le risque.",
      listTitle: "Résultats accessibles de la carte",
      loading: "Chargement de la carte…",
      filters: { label: "Filtres de la carte", facility: "Établissement", programme: "Programme", period: "Période de déclaration", workflow: "État du flux de travail", quality: "État de la qualité des données", allFacilities: "Tous autorisés", allProgrammes: "Tous autorisés", allWorkflow: "Tous", allQuality: "Tous" },
      period: { last_30_days: "30 derniers jours", last_90_days: "90 derniers jours", current_year: "Année en cours" },
      volume: { "5_19": "5–19", "20_49": "20–49", "50_plus": "50+" },
      quality: { clear: "Aucun indicateur de qualité non résolu", needs_review: "Revue de qualité requise" },
      fields: { area: "Zone approximative approuvée", programme: "Programme", period: "Période de déclaration", volume: "Volume de déclarations", workflow: "État du flux de travail", quality: "État de la qualité des données" },
      actions: { apply: "Appliquer les filtres", reset: "Réinitialiser la vue", openSignal: "Ouvrir le signal", openAudit: "Ouvrir la piste d’audit" },
      empty: {
        no_authorised_geographyTitle: "Aucune géographie de déclaration éligible",
        no_authorised_geography: "Aucune géographie approximative approuvée n’est disponible pour la sélection actuelle.",
        incomplete_facility_mappingTitle: "La géographie de l’établissement est incomplète",
        incomplete_facility_mapping: "Une attestation existe, mais les exigences relatives à l’établissement actif, au programme, à la provenance ou à la localisation approximative sont incomplètes.",
        no_aggregate_dataTitle: "Aucune donnée agrégée",
        no_aggregate_data: "Aucune donnée agrégée autorisée n’est disponible pour les filtres sélectionnés.",
        all_cells_suppressedTitle: "Résultats protégés par suppression",
        all_cells_suppressed: "Les résultats sélectionnés sont inférieurs au seuil minimal d’unités déclarantes ou nécessitent une suppression complémentaire.",
        unavailableTitle: "Carte indisponible",
        unavailable: "L’instantané agrégé autorisé n’a pas pu être chargé. Réessayez plus tard.",
        mapUnavailableTitle: "Carte indisponible",
        mapUnavailable: "La liste accessible des résultats reste disponible ci-dessous.",
      },
    },
  },
});

// FRANCO_CLINICIAN_ACTIVATION_SPRINT_1_FOLA
// Formal clinical French for Fola doctor activation guidance only.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  fola: {
    onboarding: {
      doctor: {
        consultancyEdit: "Préparons votre profil afin que les patients puissent vous trouver et demander une consultation.",
        consultancyEditSupport:
          "Commencez par compléter les éléments cliniques essentiels : mode de pratique, services, tarifs et disponibilités. Ces informations permettent à AfriMed™ de présenter votre offre de soins de façon claire.",
        consultancyEditCta: "Compléter le profil",
        editIntro: "Présentez votre pratique de manière claire et rassurante.",
        editIntroSupport:
          "Ajoutez une photo professionnelle, une courte présentation clinique et, si possible, votre signature. Ces éléments renforcent la confiance et préparent les prescriptions émises depuis AfriMed™.",
        practiceModeContext: "Indiquez comment les patients peuvent vous consulter.",
        practiceModeContextSupport:
          "Choisissez si vous proposez des consultations virtuelles, des consultations en présentiel, ou les deux. Ce choix aide les patients à comprendre immédiatement votre mode de prise en charge.",
        servicesContext: "Ajoutez les services que les patients peuvent réserver.",
        servicesContextSupport:
          "Chaque service doit être compréhensible pour le patient, avec une durée et un tarif. Cela évite les échanges inutiles avant la prise de rendez-vous.",
        coverGuidance: "Ajoutez une image de couverture réaliste.",
        coverGuidanceSupport:
          "Choisissez de préférence une image dans un cadre médical ou professionnel. Elle améliore la crédibilité et l’attrait de votre profil public.",
        profileReady: "Votre profil clinique est prêt à recevoir des demandes de consultation.",
        viewAppointmentsCta: "Voir les rendez-vous",
        appointments: "Vos rendez-vous sont regroupés ici.",
        appointmentsSupport:
          "Consultez cette page pour suivre les demandes, les rendez-vous à venir et les informations nécessaires avant chaque consultation.",
        homeAppointments: "Vos rendez-vous à venir et en attente sont regroupés ici.",
        homeAppointmentsSupport:
          "Surveillez les demandes à approuver et les rendez-vous confirmés. Les alertes vous signalent les actions importantes afin de maintenir votre calendrier à jour.",
      },
    },
    readiness: {
      doctor: {
        profileReadinessReview: "Vérification finale du profil",
        profileReadinessReviewDetail:
          "Vos informations principales sont en place. Vérifiez votre profil public, votre photo, votre signature, vos services et vos disponibilités avant d’ouvrir pleinement la réservation aux patients.",
        profileReadinessReviewCta: "Vérifier le profil",
        identity: "Bienvenue {addressName}",
        identityDetail:
          "Préparons votre profil clinicien afin que les patients puissent vous identifier, vous trouver et demander une consultation. Commencez par confirmer vos informations d’identité.",
        identityCta: "Identité",
        registrationDetails: "Renseignez votre inscription professionnelle",
        registrationDetailsDetail:
          "Ajoutez votre numéro d’inscription, votre pays d’enregistrement et l’organisme professionnel concerné.",
        registrationDetailsCta: "Inscription professionnelle",
        registrationUpload: "Ajoutez votre justificatif professionnel",
        registrationUploadDetail:
          "Téléversez une copie lisible de votre document d’inscription ou de licence professionnelle.",
        registrationUploadCta: "Justificatif professionnel",
        languages: "Indiquez vos langues de consultation",
        languagesDetail:
          "Ajoutez votre langue principale ainsi que les autres langues dans lesquelles vous pouvez prendre en charge les patients.",
        languagesCta: "Langues",
        specialty: "Sélectionnez votre spécialité",
        specialtyDetail:
          "Choisissez ou ajoutez votre spécialité afin que les patients comprennent clairement votre domaine de soins.",
        specialtyCta: "Spécialité",
        subspecialty: "Ajoutez vos sous-spécialités",
        subspecialtyDetail:
          "Précisez vos domaines d’expertise lorsque cela aide les patients à choisir le bon clinicien.",
        subspecialtyCta: "Sous-spécialités",
        practiceMode: "Choisissez votre mode de pratique",
        practiceModeDetail:
          "Indiquez si vous consultez à distance, en présentiel, ou selon les deux modalités.",
        practiceModeCta: "Mode de pratique",
        evisit: "Activez les consultations virtuelles",
        evisitDetail:
          "Autorisez les consultations virtuelles afin que les patients puissent réserver une consultation en ligne selon vos disponibilités.",
        evisitCta: "Consultations virtuelles",
        services: "Ajoutez vos services et tarifs",
        servicesDetail:
          "Créez les services que les patients peuvent réserver, avec une durée, une devise et un tarif clairs.",
        servicesCta: "Services et tarifs",
        payout: "Choisissez vos modalités de paiement",
        payoutDetail:
          "Indiquez comment les patients peuvent régler vos services. Si les paiements en ligne ne sont pas encore disponibles dans votre pays, sélectionnez les modes de paiement manuels que vous acceptez.",
        payoutCta: "Configuration des paiements",
        consultation: "Ajoutez vos lieux de consultation en présentiel",
        consultationDetail:
          "Ajoutez uniquement les adresses où les patients peuvent se rendre pour une consultation physique. Si vous exercez uniquement en virtuel, poursuivez vers les disponibilités.",
        consultancyCta: "Lieux de consultation",
        availability: "Définissez vos disponibilités",
        availabilityDetail:
          "Ouvrez les jours et horaires pendant lesquels les patients peuvent demander un rendez-vous.",
        availabilityCta: "Disponibilités",
        editIntro: "Peaufinez votre profil public",
        editIntroDetail:
          "Ajoutez votre photo, votre présentation clinique, votre signature et les éléments qui renforcent la confiance du patient.",
        editIntroCta: "Modifier le profil",
        discoveryReview: "Excellent. Vérifions votre visibilité patient.",
        discoveryReviewDetail:
          "Votre profil principal est complet. AfriMed™ peut maintenant vérifier si les patients peuvent vous trouver et demander un rendez-vous.",
        discoveryReviewCta: "Vérifier le profil",
        ready: "Votre profil clinicien est prêt.",
        readyDetail:
          "Les patients peuvent maintenant découvrir votre profil et demander un rendez-vous lorsque des disponibilités sont ouvertes.",
        readyCta: "Voir le profil",
        nextStepCta: "Étape suivante",
        identityCurrent: "Complétez votre identité",
        identityCurrentDetail:
          "Ajoutez ou vérifiez les informations d’identité demandées, enregistrez, puis passez à l’étape suivante.",
        registrationDetailsCurrent: "Complétez votre inscription professionnelle",
        registrationDetailsCurrentDetail:
          "Ajoutez votre numéro, votre pays et votre organisme d’inscription, puis enregistrez.",
        registrationUploadCurrent: "Téléversez votre document professionnel",
        registrationUploadCurrentDetail:
          "Ajoutez une copie lisible de votre justificatif professionnel, puis enregistrez.",
        languagesCurrent: "Ajoutez vos langues de consultation",
        languagesCurrentDetail:
          "Indiquez votre langue principale et les autres langues parlées. Enregistrez ensuite pour poursuivre.",
        specialtyCurrent: "Sélectionnez votre spécialité",
        specialtyCurrentDetail:
          "Choisissez votre spécialité ou ajoutez-la manuellement si elle n’apparaît pas encore dans le catalogue.",
        subspecialtyCurrent: "Ajoutez vos sous-spécialités",
        subspecialtyCurrentDetail:
          "Ajoutez les domaines d’expertise qui décrivent réellement votre pratique clinique.",
        practiceModeCurrent: "Choisissez votre mode de pratique",
        practiceModeCurrentDetail:
          "Sélectionnez virtuel, présentiel ou hybride, puis enregistrez pour continuer.",
        evisitCurrent: "Activez les consultations virtuelles",
        evisitCurrentDetail:
          "Activez les consultations virtuelles, définissez le tarif correspondant et vérifiez que vos disponibilités permettent la réservation.",
        servicesCurrent: "Ajoutez vos services et tarifs",
        servicesCurrentDetail:
          "Ajoutez les services que vous proposez, avec leur durée et leur tarif. Enregistrez ensuite pour poursuivre.",
        payoutCurrent: "Choisissez vos modalités de paiement",
        payoutCurrentDetail:
          "Sélectionnez les modes de paiement que vous acceptez. Les paiements en ligne seront proposés uniquement lorsqu’ils sont disponibles pour votre pays.",
        consultancyCurrent: "Ajoutez vos lieux de consultation en présentiel",
        consultancyCurrentDetail:
          "Ajoutez les adresses où les patients peuvent vous consulter physiquement. Si votre pratique est virtuelle uniquement, passez ensuite aux disponibilités.",
        availabilityCurrent: "Ouvrez vos disponibilités",
        availabilityCurrentDetail:
          "Définissez soigneusement les jours et horaires réservables. Une fois enregistré, votre profil peut recevoir des demandes de consultation.",
        editIntroCurrent: "Finalisez votre profil public",
        editIntroCurrentDetail:
          "Vérifiez votre photo, votre présentation clinique, votre signature et les éléments qui renforcent la confiance du patient.",
        editDobCurrent: "Ajoutez votre date de naissance",
        editDobCurrentDetail:
          "Cette information fait partie de votre identité de compte. Complétez-la, enregistrez, puis poursuivez l’étape suivante.",
        identityComplete: "Identité enregistrée",
        identityCompleteDetail:
          "Votre identité de base est enregistrée. Ajoutez maintenant vos informations d’inscription professionnelle.",
        registrationDetailsComplete: "Inscription professionnelle enregistrée",
        registrationDetailsCompleteDetail:
          "Vos informations d’inscription sont enregistrées. Ajoutez maintenant votre justificatif professionnel.",
        registrationUploadComplete: "Justificatif professionnel reçu",
        registrationUploadCompleteDetail:
          "Votre document est enregistré. Choisissez maintenant vos langues de consultation.",
        languagesComplete: "Langues enregistrées",
        languagesCompleteDetail:
          "Vos langues de consultation sont enregistrées. Sélectionnez maintenant votre spécialité.",
        specialtyComplete: "Spécialité enregistrée",
        specialtyCompleteDetail:
          "Votre spécialité principale est enregistrée. Ajoutez maintenant vos sous-spécialités si nécessaire.",
        subspecialtyComplete: "Sous-spécialités enregistrées",
        subspecialtyCompleteDetail:
          "Vos domaines d’expertise sont enregistrés. Choisissez maintenant votre mode de pratique.",
        practiceModeComplete: "Mode de pratique enregistré",
        practiceModeCompleteDetail:
          "Votre modèle de consultation est enregistré. Activez maintenant les consultations virtuelles si vous les proposez.",
        evisitComplete: "Consultations virtuelles enregistrées",
        evisitCompleteDetail:
          "Les consultations virtuelles sont configurées. Ajoutez maintenant les services et tarifs que les patients peuvent réserver.",
        servicesComplete: "Services et tarifs enregistrés",
        servicesCompleteDetail:
          "Vos services réservables sont enregistrés. Ajoutez vos lieux de consultation en présentiel si vous en proposez.",
        payoutComplete: "Modalités de paiement enregistrées",
        payoutCompleteDetail:
          "Vos modalités de paiement sont enregistrées. Indiquez maintenant où les patients peuvent vous consulter si vous proposez le présentiel.",
        consultancyComplete: "Lieux de consultation enregistrés",
        consultancyCompleteDetail:
          "Vos lieux de consultation sont enregistrés. Définissez maintenant vos disponibilités afin que les patients puissent demander un rendez-vous.",
        availabilityComplete: "Disponibilités enregistrées",
        availabilityCompleteDetail:
          "Vos disponibilités sont configurées. Vérifiez maintenant votre profil tel qu’il sera vu par les patients.",
      },
    },
  },
});


// FRANCO_DOCTOR_ACTIVATION_FINAL_LABELS
// Small final labels surfaced in the appointment consultation flow.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  appointments: {
    edit: {
      paymentMethodFallback: "Cartes de débit / crédit",
    },
  },
  referrals: {
    entry: {
      postConsultActions: "Actions après consultation",
      postConsultActionsSubtitle: "Poursuivez le parcours de soins du patient.",
    },
    doctor: {
      title: "Orienter le patient",
    },
  },
});


// FRANCO_LEGAL_TRUST_SURFACE_2026_07
// French legal/trust copy for shared Terms, Privacy, and Data Deletion pages.
// English source legal copy remains unchanged in src/pages/legal/legalContent.js.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  common: {
    legal: {
      terms: "Conditions générales",
      privacy: "Politique de confidentialité",
      dataDeletion: "Suppression des données",
    },
  },
  legal: {
    common: {
      category: "Mentions légales",
      effectiveLabel: "En vigueur",
      effectiveDate: "5 mars 2026",
      empty: "Ce document juridique est en cours de préparation.",
    },
    terms: {
      title: "Conditions générales",
      subtitle:
        "Les règles d’utilisation des services AfriMed™, y compris l’usage du compte, l’accès aux services, la conduite sur la plateforme et les responsabilités légales.",
      sections: {
        acceptanceOfTerms: {
          title: "Acceptation des conditions",
          body: {
            1: "En accédant aux services AfriMed™ ou en les utilisant, vous acceptez les présentes Conditions générales ainsi que toute politique applicable qui y est mentionnée.",
            2: "Si vous n’acceptez pas ces conditions, n’utilisez pas le service.",
          },
        },
        eligibilityAndAccountRegistration: {
          title: "Éligibilité et création de compte",
          body: {
            1: "Vous devez fournir des informations exactes lors de votre inscription et garder vos identifiants de compte en sécurité.",
            2: "Vous êtes responsable de l’activité effectuée avec votre compte, sauf si elle résulte d’une défaillance de sécurité de notre part.",
          },
        },
        serviceScope: {
          title: "Portée du service",
          body: {
            1: "AfriMed™ fournit une infrastructure numérique de santé, notamment des flux de communication en télésanté et de coordination des soins.",
            2: "AfriMed™ ne remplace pas les services d’urgence. En cas d’urgence ou de situation mettant la vie en danger, contactez immédiatement les services d’urgence locaux.",
          },
        },
        appointmentsBookingsAndCancellations: {
          title: "Rendez-vous, réservations et annulations",
          body: {
            1: "La disponibilité des rendez-vous, la planification, les modifications et les délais d’annulation peuvent dépendre des politiques du praticien, de l’établissement et des opérations.",
            2: "Les utilisateurs doivent fournir des informations de réservation correctes et agir de bonne foi lorsqu’ils modifient ou annulent un rendez-vous.",
          },
        },
        paymentsAndBilling: {
          title: "Paiements et facturation",
          body: {
            1: "Lorsque des services payants s’appliquent, les frais, conditions de facturation et modes de paiement sont indiqués dans les parcours produit ou les paramètres du prestataire concernés.",
            2: "Vous autorisez les frais applicables aux services que vous sélectionnez et utilisez.",
          },
        },
        userResponsibilitiesAndAcceptableUse: {
          title: "Responsabilités de l’utilisateur et utilisation acceptable",
          body: {
            1: "Vous acceptez de ne pas détourner la plateforme de son usage, perturber les contrôles de sécurité, soumettre des informations frauduleuses ou utiliser AfriMed™ pour une activité illégale.",
            2: "Les utilisateurs professionnels de santé doivent respecter les normes professionnelles et de confidentialité applicables.",
          },
        },
        intellectualProperty: {
          title: "Propriété intellectuelle",
          body: {
            1: "Les marques AfriMed™, la conception du produit, le contenu et les logiciels de la plateforme sont protégés par les lois applicables en matière de propriété intellectuelle.",
            2: "Vous ne pouvez pas copier, faire de l’ingénierie inverse ou redistribuer des éléments protégés, sauf autorisation prévue par la loi ou accord écrit.",
          },
        },
        thirdPartyServices: {
          title: "Services tiers",
          body: {
            1: "Certains parcours peuvent s’appuyer sur des services tiers de confiance, par exemple pour l’authentification, l’infrastructure, les communications ou les cartes.",
            2: "Ces services peuvent être régis par leurs propres conditions et avis de confidentialité.",
          },
        },
        privacyAndDataHandling: {
          title: "Confidentialité et traitement des données",
          body: {
            1: "L’utilisation des données personnelles est régie par la Politique de confidentialité d’AfriMed™.",
            2: "En utilisant la plateforme, vous reconnaissez le traitement des données nécessaire à l’authentification, à la prestation des services de santé et aux flux de conformité.",
          },
        },
        disclaimersAndLimitationOfLiability: {
          title: "Avertissements et limitation de responsabilité",
          body: {
            1: "AfriMed™ est fourni selon disponibilité, sous réserve de maintenance, de limites opérationnelles et de dépendances externes.",
            2: "Dans la mesure permise par la loi, la responsabilité est limitée pour les dommages indirects, accessoires ou consécutifs découlant de l’utilisation de la plateforme.",
          },
        },
        suspensionAndTermination: {
          title: "Suspension et résiliation",
          body: {
            1: "Nous pouvons suspendre ou résilier l’accès lorsque cela est nécessaire pour la sécurité, la conformité légale, les violations de politiques ou la prévention des abus.",
            2: "Vous pouvez cesser d’utiliser le service à tout moment, sous réserve des obligations légales et contractuelles applicables.",
          },
        },
        governingLawAndUpdates: {
          title: "Droit applicable et mises à jour",
          body: {
            1: "Les présentes conditions sont régies par les lois et réglementations applicables aux opérations d’AfriMed™ et aux juridictions dans lesquelles les services sont proposés.",
            2: "Nous pouvons réviser ces conditions ; la dernière version et la date d’entrée en vigueur sont publiées sur cette page.",
          },
        },
        contact: {
          title: "Contact",
          body: {
            1: "AfriMed™ Telehealth",
            2: "Email : info@afrimedtelehealth.com",
            3: "Site web : https://afrimedtelehealth.com",
          },
        },
      },
    },
    privacy: {
      title: "Politique de confidentialité",
      subtitle:
        "Comment AfriMed™ collecte, utilise, protège et encadre les données personnelles sur notre plateforme de télésanté.",
      sections: {
        whoWeAre: {
          title: "Qui nous sommes",
          body: {
            1: "AfriMed™ Telehealth fournit des services de santé numériques aux patients et aux professionnels de santé, notamment des parcours de téléconsultation, la planification de rendez-vous, la messagerie sécurisée, l’onboarding des profils et des opérations de santé appuyées par la vérification.",
            2: "La présente Politique de confidentialité s’applique aux données personnelles collectées via les expériences web et app d’AfriMed™, y compris les domaines d’application staging et production.",
          },
        },
        informationWeCollect: {
          title: "Informations que nous collectons",
          body: {
            1: "Nous collectons des données de compte (nom, email, téléphone, rôle), des données de profil et d’onboarding (par exemple les informations démographiques et le contexte médical que vous fournissez), des données d’activité de service (rendez-vous, messages, réglages) et des données techniques (métadonnées d’appareil ou de navigateur, journaux et signaux de sécurité).",
            2: "Lorsque vous téléversez des documents de santé ou de vérification, nous traitons ces fichiers et leurs métadonnées associées pour la vérification de compte, les flux de conformité et la prestation du service.",
          },
        },
        howWeUseYourInformation: {
          title: "Comment nous utilisons vos informations",
          body: {
            1: "Nous utilisons les données personnelles pour créer et sécuriser les comptes, authentifier les utilisateurs, fournir les fonctionnalités de télésanté, soutenir les rendez-vous et communications, améliorer la fiabilité, détecter les abus et respecter les obligations de santé et légales.",
            2: "Nous pouvons utiliser des analyses opérationnelles pour améliorer la qualité du service et l’expérience utilisateur tout en appliquant des pratiques de minimisation et de contrôle d’accès.",
          },
        },
        howWeProtectYourInformation: {
          title: "Comment nous protégeons vos informations",
          body: {
            1: "AfriMed™ applique des garanties techniques et organisationnelles en couches, notamment des contrôles d’accès, le chiffrement en transit, l’autorisation fondée sur les rôles, la surveillance et des flux orientés audit.",
            2: "Aucun système en ligne n’est totalement exempt de risques ; nous améliorons continuellement les contrôles afin de réduire les risques de sécurité et de confidentialité.",
          },
        },
        howWeShareInformation: {
          title: "Comment nous partageons les informations",
          body: {
            1: "Nous partageons les données uniquement lorsque cela est nécessaire à la prestation du service, à la conformité légale, aux flux dirigés par l’utilisateur ou à des sous-traitants de confiance soumis à des obligations de confidentialité et de protection des données.",
            2: "Nous ne vendons pas les informations personnelles.",
          },
        },
        dataRetention: {
          title: "Conservation des données",
          body: {
            1: "Nous conservons les données personnelles aussi longtemps que nécessaire pour fournir les services, satisfaire aux obligations légales et réglementaires, résoudre les litiges, faire respecter les accords et maintenir les dossiers d’audit.",
            2: "Les durées de conservation peuvent varier selon le type de données et les exigences juridictionnelles.",
          },
        },
        cookiesAndSimilarTechnologies: {
          title: "Cookies et technologies similaires",
          body: {
            1: "Nous utilisons des cookies essentiels et des technologies similaires pour la sécurité, la continuité de session et les fonctionnalités du produit.",
            2: "Le cas échéant, les catégories optionnelles sont gérées via les contrôles de préférences de cookies disponibles dans l’expérience app.",
          },
        },
        yourDataRights: {
          title: "Vos droits sur les données",
          body: {
            1: "Selon votre juridiction, vous pouvez disposer de droits d’accès, de rectification, de suppression, de limitation, d’opposition ou de portabilité pour certaines données personnelles.",
            2: "Vous pouvez également demander une assistance concernant le compte et la confidentialité via les canaux de contact indiqués ci-dessous.",
          },
        },
        childrensPrivacy: {
          title: "Confidentialité des enfants",
          body: {
            1: "AfriMed™ n’est pas destiné à une utilisation non supervisée par des enfants lorsque la loi l’interdit.",
            2: "Si vous pensez que des données ont été fournies de manière inappropriée, contactez-nous ; nous examinerons la situation et agirons conformément aux obligations légales.",
          },
        },
        internationalDataUse: {
          title: "Utilisation internationale des données",
          body: {
            1: "AfriMed™ peut traiter des données dans différentes régions lorsque cela est nécessaire au fonctionnement sécurisé du service.",
            2: "Lorsqu’un transfert transfrontalier est requis, nous appliquons les garanties appropriées conformément au droit applicable.",
          },
        },
        changesToThisPolicy: {
          title: "Modifications de la présente politique",
          body: {
            1: "Nous pouvons mettre à jour cette Politique de confidentialité à mesure que la plateforme, les réglementations ou les exigences de sécurité évoluent.",
            2: "Les mises à jour importantes seront reflétées par la révision de la date d’entrée en vigueur et la publication de la version actualisée sur cette page.",
          },
        },
        contactUs: {
          title: "Nous contacter",
          body: {
            1: "AfriMed™ Telehealth",
            2: "Email : info@afrimedtelehealth.com",
            3: "Site web : https://afrimedtelehealth.com",
          },
        },
      },
    },
    dataDeletion: {
      title: "Instructions de suppression des données",
      subtitle:
        "Comment demander la suppression des données de compte et de plateforme contrôlées par AfriMed™.",
      sections: {
        purposeAndScope: {
          title: "Objectif et portée",
          body: {
            1: "Cette page explique comment les utilisateurs d’AfriMed™ peuvent demander la suppression des données de plateforme liées à leur compte.",
            2: "Dans ce contexte, les données de plateforme comprennent les dossiers de compte et de profil, les enregistrements d’utilisation du service et les métadonnées générées par l’app qu’AfriMed™ contrôle dans ses systèmes.",
          },
        },
        howToRequestDeletion: {
          title: "Comment demander la suppression",
          body: {
            1: "Les utilisateurs peuvent demander la suppression en envoyant un email à info@afrimedtelehealth.com depuis l’adresse email liée à leur compte AfriMed™.",
            2: "Si l’email n’est pas disponible, les utilisateurs peuvent soumettre la demande via les canaux d’assistance AfriMed™ et inclure les informations d’identification nécessaires à la vérification.",
            3: "Pour accélérer le traitement, indiquez votre nom complet, l’email ou le numéro de téléphone du compte, le rôle (patient ou médecin) et la phrase : « Je demande la suppression des données de mon compte AfriMed™. »",
          },
        },
        identityVerificationBeforeDeletion: {
          title: "Vérification d’identité avant suppression",
          body: {
            1: "Pour la sécurité et la protection de la confidentialité des données de santé, AfriMed™ vérifie l’identité du demandeur avant de traiter la suppression.",
            2: "La vérification peut inclure une confirmation depuis l’email ou le numéro de téléphone enregistré, ainsi que des contrôles supplémentaires si nécessaire pour éviter une suppression non autorisée du compte.",
          },
        },
        whatWeDelete: {
          title: "Ce que nous supprimons",
          body: {
            1: "Après vérification réussie, AfriMed™ supprime ou désidentifie irréversiblement les données de plateforme liées au compte qui ne sont plus requises pour des obligations légales, réglementaires, de sécurité ou de prévention de la fraude.",
            2: "Il peut s’agir, par exemple, de détails de profil, de fichiers médias non requis et d’enregistrements d’activité applicative associés au compte.",
          },
        },
        whatMayBeRetained: {
          title: "Ce qui peut être conservé",
          body: {
            1: "Pour les flux de demande de suppression, AfriMed™ peut conserver des enregistrements limités jusqu’à 90 jours afin de terminer la vérification, le traitement des litiges, les contrôles de sécurité et les protections opérationnelles.",
            2: "Après cette période, la suppression est appliquée automatiquement, sauf si une obligation légale, réglementaire, de prévention de la fraude ou d’audit impose une conservation plus longue.",
            3: "Toute donnée conservée est soumise à des restrictions d’accès et stockée uniquement pendant la durée de conservation requise.",
          },
        },
        processingTimeline: {
          title: "Délai de traitement",
          body: {
            1: "AfriMed™ accuse réception des demandes de suppression et commence la vérification et le traitement dès que raisonnablement possible.",
            2: "Les délais d’achèvement dépendent de la vérification d’identité et des obligations légales, mais les demandes sont généralement traitées dans un délai de 30 jours.",
          },
        },
        outcomeConfirmation: {
          title: "Confirmation du résultat",
          body: {
            1: "Une fois le traitement terminé, AfriMed™ envoie une réponse de confirmation au demandeur.",
            2: "Si certaines données ne peuvent pas être supprimées en raison d’obligations légales, AfriMed™ explique la raison et la base de conservation applicable.",
          },
        },
        contactForDataDeletion: {
          title: "Contact pour la suppression des données",
          body: {
            1: "AfriMed™ Telehealth",
            2: "Email : info@afrimedtelehealth.com",
            3: "URL de référence : https://staging.afrimed.tech/legal/data-deletion",
          },
        },
      },
    },
  },
});

// INSTITUTION_MOBILE_FRENCH_ENUM_CLOSURE_FINAL_2026_08
// Keep these last so legacy language patches cannot reintroduce mobile English
// fallbacks after the controlled mappings above.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES["en-GB"], {
  institutionPatientCreate: {
    select: "Select an option",
    searchOptions: "Search options…",
  },
  institutionServices: {
    selectOption: "Select an option",
    searchOptions: "Search options…",
  },
});

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  institutionPatientCreate: {
    select: "Sélectionner une option",
    searchOptions: "Rechercher des options…",
  },
  institutionServices: {
    selectOption: "Sélectionner une option",
    searchOptions: "Rechercher des options…",
  },
});

// INSTITUTION_CREATE_SELECTOR_RESOLVED_PATHS_2026_08
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  institutionClinicians: {
    searchOrAdd: "Rechercher ou ajouter",
    startTyping: "Commencez à saisir pour rechercher ou ajouter une spécialité.",
    completionDate: "Date d’obtention",
    addQualificationCertificate: "Ajouter un certificat de qualification",
    diplomaDraftOnly: "Sélectionné pour ce brouillon de personnel de l’Institution",
    formationEvidenceSupplied: "Sélectionné pour ce dossier de formation",
    replace: "Remplacer",
    remove: "Supprimer",
  },
  institutionMedicalInstitutionCreate: {
    memberSheet: {
      members: "Membres",
      member: "Membre",
      addMember: "Ajouter un membre",
      cancel: "Annuler",
      save: "Enregistrer",
      accountableCreator: "Créateur responsable",
      eligibleMembers: "Membres admissibles",
      loadingCandidates: "Chargement des membres admissibles…",
      noCandidates: "Aucun membre admissible ne correspond à cette recherche.",
      backToMembers: "Retour aux membres",
      fullName: "Nom complet",
      email: "E-mail",
      phone: "Téléphone",
      phoneNumber: "Numéro de téléphone",
      role: "Rôle",
      facilityMemberRole: "Rôle du membre de l’établissement",
      addRole: "Ajouter un rôle",
      roleName: "Nom du rôle",
      customStaff: "Personnel personnalisé",
      addThroughClinicians: "Ajouter via les cliniciens",
      clinicianHandoff: "Les cliniciens sont liés par le flux de travail gouverné des cliniciens de l’Institution.",
      clinicianHandoffSupport: "Continuez dans ce flux pour créer ou lier le clinicien et attribuer un établissement médical autorisé.",
      errors: {
        candidates: "Les candidats membres n’ont pas pu être chargés. Réessayez.",
        nameAndEmail: "Le nom et une adresse e-mail valide sont requis.",
        customRole: "Saisissez un nom de rôle personnalisé.",
        selectMember: "Sélectionnez un membre admissible ou ajoutez un nouveau membre.",
        save: "Le membre n’a pas pu être enregistré. Réessayez.",
      },
      roles: {
        admin: "Administrateur",
        pharmacist: "Pharmacien / Pharmacienne",
        nurse: "Personnel infirmier",
        laboratory_professional: "Professionnel de laboratoire",
        imaging_professional: "Professionnel de l’imagerie",
        receptionist: "Réceptionniste",
        operations_staff: "Personnel opérationnel",
        clinician: "Clinicien",
      },
    },
  },
});

// CONTROLLED_RESEARCH_PUBLIC_HEALTH_STAGING_2026_08
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES["en-GB"], {
  stagingAccess: {
    journeyLegend: "I’m exploring AfriMed™ as",
    patient: "Patient",
    clinician: "Clinician",
    research: "Research • Public health",
    errors: {
      journeyRequired: "Choose the Patient, Clinician, or Research • Public health journey.",
    },
  },
  auth: {
    callback: {
      takingResearchOnboarding: "Signed in. Taking you to Research • Public health onboarding...",
    },
  },
  researchOnboarding: {
    shared: {
      intro: "Tell us enough to orient your controlled AfriMed™ staging experience. These details do not grant product authority.",
      saving: "Saving...",
      continue: "Continue",
    },
    basic: { title: "Basic identity", firstName: "First name", lastName: "Last name", countryCode: "Country code", phone: "Phone" },
    organisation: { title: "Organisation • context", name: "Organisation • institution", department: "Department • unit", country: "Country" },
    work: {
      title: "Your work",
      intro: "Share as much as is useful for orientation. These optional details do not grant access or authority and can be completed later.",
      completeLater: "Complete later",
    },
    role: {
      heading: "Role",
      title: "Your Research • Public health role",
      other: "Describe your role",
      options: {
        researcher: "Researcher",
        postgraduate_student: "Postgraduate student",
        postdoctoral_fellow: "Postdoctoral fellow",
        programme_staff: "Programme staff",
        public_health_professional: "Public-health professional",
        community_health_worker: "Community health worker",
        research_field_staff: "Research • field staff",
        other: "Other Research • Public health role",
      },
    },
    interests: {
      heading: "Areas of interest",
      title: "Areas of interest",
      other: "Describe the other area",
      options: {
        digital_health: "Digital health",
        hiv_sti: "HIV • STI",
        sexual_reproductive_health: "Sexual & reproductive health",
        telehealth: "Telehealth",
        public_health: "Public health",
        population_longitudinal_research: "Population • longitudinal research",
        implementation_science: "Implementation science",
        data_governance: "Data • governance",
        community_field_workflows: "Community • field workflows",
        other: "Other",
      },
    },
    governance: {
      title: "Controlled staging acknowledgement",
      synthetic: "Use synthetic or evaluation data where applicable.",
      patientData: "This journey gives no automatic access to patient-identifying records.",
      membership: "Access remains governed by Institution and programme membership.",
      auditable: "Controlled staging activity is auditable.",
      acknowledge: "I understand and acknowledge these controlled staging conditions.",
    },
    pending: {
      title: "Research • Public health account ready",
      body: "Your AfriMed™ Research • Public health account is ready. Institution access will appear once your programme membership has been assigned.",
      boundary: "No Institution, clinical, Regional Health, programme-administration, or dataset authority has been assigned. Access is activated only through governed programme membership.",
      done: "Done and sign out",
      edit: "Review your details",
    },
    errors: {
      basic: "Enter your first and last name.",
      phone: "Enter a valid phone number with country code.",
      organisation: "Enter your organisation or institution.",
      department: "Enter your department or unit.",
      country: "Choose your country.",
      role: "Choose the Research • Public health role that best fits.",
      interests: "Choose at least one area of interest.",
      roleOther: "Describe your role to save the Other selection.",
      interestOther: "Describe the other area to save that selection.",
      governance: "Acknowledge the staging governance conditions to continue.",
      back: "AfriMed™ could not return to the previous step. Please try again.",
      save: "AfriMed™ could not save this onboarding step. Please try again.",
    },
  },
  researchSettings: {
    title: "Research • Public health profile",
    subtitle: "Orientation details only. Changes here never grant AfriMed™ product or Institution authority.",
    profile: {
      heading: "Profile",
      copy: "Your shared AfriMed™ identity and profile image.",
      fullName: "Full name",
      email: "Email",
      emailLifecycle: "Email is managed through AfriMed™’s verified account lifecycle.",
      imageSaved: "Profile image saved.",
      imageSaveFailed: "Profile image could not be saved.",
      imageSupport: "Supported: JPEG, PNG, WebP up to 5MB.",
      logOut: "Log out",
      loggingOut: "Logging out...",
      imageActions: {
        alt: "Profile image",
        upload: "Upload image",
        uploading: "Uploading…",
        remove: "Remove image",
      },
    },
    context: "Contact and organisation",
    authorityBoundary: "These details do not change Institution membership, programme authority, clinical access, Regional Health authority, user roles, or dataset access.",
    loading: "Loading Research • Public health profile...",
    saving: "Saving...",
    save: "Save changes",
    saved: "Research • Public health details saved.",
    unsaved: {
      title: "Unsaved changes",
      logoutBody: "Your changes have not been saved. If you log out now, those changes will be lost.",
      leaveBody: "Your changes have not been saved. If you leave now, those changes will be lost.",
      stayAndSave: "Stay and save",
      logoutWithoutSaving: "Log out without saving",
      leaveWithoutSaving: "Leave without saving",
    },
    errors: {
      unavailable: "This Research • Public health profile is not available for the signed-in account.",
      fullName: "Enter your full name.",
      phone: "Enter a valid phone number with country code.",
      organisation: "Enter your organisation or institution.",
      department: "Enter your department or unit.",
      country: "Choose your country.",
      roleOther: "Describe your role to save the Other selection.",
      interestOther: "Describe the other area to save that selection.",
      save: "AfriMed™ could not save these details. Please try again.",
    },
  },
  researchPending: {
    loading: "Loading Research • Public health...",
    title: "Research • Public health",
    ready: "Your Research • Public health profile is ready.",
    membership: "Institution and programme access will appear here when it has been assigned.",
    boundary: "No Institution, clinical, Regional Health, programme-administration, or dataset authority has been assigned. Access is activated only through governed programme membership.",
    reviewProfile: "Profile settings",
    signOut: "Sign out",
    signingOut: "Signing out...",
  },
});

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  stagingAccess: {
    journeyLegend: "J’explore AfriMed™ en tant que",
    patient: "Patient",
    clinician: "Clinicien",
    research: "Recherche • santé publique",
    errors: {
      journeyRequired: "Choisissez le parcours Patient, Clinicien ou Recherche • santé publique.",
    },
  },
  auth: {
    callback: {
      takingResearchOnboarding: "Connexion réussie. Accès à l’intégration Recherche • santé publique...",
    },
  },
  researchOnboarding: {
    shared: {
      intro: "Indiquez les informations nécessaires pour orienter votre expérience contrôlée dans la préproduction AfriMed™. Ces informations n’accordent aucune autorité dans le produit.",
      saving: "Enregistrement...",
      continue: "Continuer",
    },
    basic: { title: "Identité de base", firstName: "Prénom", lastName: "Nom", countryCode: "Indicatif pays", phone: "Téléphone" },
    organisation: { title: "Organisation • contexte", name: "Organisation • institution", department: "Département • unité", country: "Pays" },
    work: {
      title: "Votre activité",
      intro: "Partagez les informations utiles à l’orientation. Ces renseignements facultatifs n’accordent aucun accès ni aucune autorité et peuvent être complétés plus tard.",
      completeLater: "Compléter plus tard",
    },
    role: {
      heading: "Rôle",
      title: "Votre rôle en Recherche • santé publique",
      other: "Décrivez votre rôle",
      options: {
        researcher: "Chercheur ou chercheuse",
        postgraduate_student: "Étudiant ou étudiante de troisième cycle",
        postdoctoral_fellow: "Chercheur ou chercheuse postdoctorale",
        programme_staff: "Personnel de programme",
        public_health_professional: "Professionnel ou professionnelle de santé publique",
        community_health_worker: "Agent ou agente de santé communautaire",
        research_field_staff: "Personnel de recherche • terrain",
        other: "Autre rôle en Recherche • santé publique",
      },
    },
    interests: {
      heading: "Domaines d’intérêt",
      title: "Domaines d’intérêt",
      other: "Décrivez l’autre domaine",
      options: {
        digital_health: "Santé numérique",
        hiv_sti: "VIH • IST",
        sexual_reproductive_health: "Santé sexuelle et reproductive",
        telehealth: "Télésanté",
        public_health: "Santé publique",
        population_longitudinal_research: "Recherche populationnelle • longitudinale",
        implementation_science: "Science de la mise en œuvre",
        data_governance: "Données • gouvernance",
        community_field_workflows: "Flux communautaires • terrain",
        other: "Autre",
      },
    },
    governance: {
      title: "Reconnaissance des conditions de préproduction contrôlée",
      synthetic: "Utilisez des données synthétiques ou d’évaluation lorsque cela s’applique.",
      patientData: "Ce parcours ne donne aucun accès automatique aux données permettant d’identifier des patients.",
      membership: "L’accès reste régi par l’adhésion à une Institution et à un programme.",
      auditable: "L’activité dans la préproduction contrôlée est auditable.",
      acknowledge: "Je comprends et j’accepte ces conditions de préproduction contrôlée.",
    },
    pending: {
      title: "Compte Recherche • santé publique prêt",
      body: "Votre compte AfriMed™ Recherche • santé publique est prêt. L’accès à l’Institution apparaîtra lorsque votre adhésion au programme aura été attribuée.",
      boundary: "Aucune autorité Institution, clinique, Santé régionale, administration de programme ou jeu de données n’a été attribuée. L’accès est activé uniquement par une adhésion au programme gouvernée.",
      done: "Terminer et se déconnecter",
      edit: "Vérifier vos informations",
    },
    errors: {
      basic: "Saisissez votre prénom et votre nom.",
      phone: "Saisissez un numéro de téléphone valide avec l’indicatif du pays.",
      organisation: "Saisissez votre organisation ou institution.",
      department: "Saisissez votre département ou unité.",
      country: "Choisissez votre pays.",
      role: "Choisissez le rôle en Recherche • santé publique qui vous correspond le mieux.",
      interests: "Choisissez au moins un domaine d’intérêt.",
      roleOther: "Décrivez votre rôle pour enregistrer la sélection Autre.",
      interestOther: "Décrivez l’autre domaine pour enregistrer cette sélection.",
      governance: "Acceptez les conditions de gouvernance de la préproduction pour continuer.",
      back: "AfriMed™ n’a pas pu revenir à l’étape précédente. Veuillez réessayer.",
      save: "AfriMed™ n’a pas pu enregistrer cette étape. Veuillez réessayer.",
    },
  },
  researchSettings: {
    title: "Profil Recherche • santé publique",
    subtitle: "Informations d’orientation uniquement. Les modifications n’accordent jamais d’autorité AfriMed™ ou Institution.",
    profile: {
      heading: "Profil",
      copy: "Votre identité AfriMed™ et votre image de profil partagées.",
      fullName: "Nom complet",
      email: "E-mail",
      emailLifecycle: "L’e-mail est géré par le parcours de compte vérifié d’AfriMed™.",
      imageSaved: "Image de profil enregistrée.",
      imageSaveFailed: "L’image de profil n’a pas pu être enregistrée.",
      imageSupport: "Formats pris en charge : JPEG, PNG et WebP jusqu’à 5 Mo.",
      logOut: "Se déconnecter",
      loggingOut: "Déconnexion...",
      imageActions: {
        alt: "Image de profil",
        upload: "Importer une image",
        uploading: "Importation…",
        remove: "Supprimer l’image",
      },
    },
    context: "Contact et organisation",
    authorityBoundary: "Ces informations ne modifient ni l’adhésion à une Institution, ni l’autorité de programme, ni l’accès clinique, ni l’autorité Santé régionale, ni les rôles utilisateur, ni l’accès aux jeux de données.",
    loading: "Chargement du profil Recherche • santé publique...",
    saving: "Enregistrement...",
    save: "Enregistrer les modifications",
    saved: "Informations Recherche • santé publique enregistrées.",
    unsaved: {
      title: "Modifications non enregistrées",
      logoutBody: "Vos modifications n’ont pas été enregistrées. Si vous vous déconnectez maintenant, elles seront perdues.",
      leaveBody: "Vos modifications n’ont pas été enregistrées. Si vous quittez maintenant, elles seront perdues.",
      stayAndSave: "Rester et enregistrer",
      logoutWithoutSaving: "Se déconnecter sans enregistrer",
      leaveWithoutSaving: "Quitter sans enregistrer",
    },
    errors: {
      unavailable: "Ce profil Recherche • santé publique n’est pas disponible pour le compte connecté.",
      fullName: "Saisissez votre nom complet.",
      phone: "Saisissez un numéro de téléphone valide avec l’indicatif du pays.",
      organisation: "Saisissez votre organisation ou institution.",
      department: "Saisissez votre département ou unité.",
      country: "Choisissez votre pays.",
      roleOther: "Décrivez votre rôle pour enregistrer la sélection Autre.",
      interestOther: "Décrivez l’autre domaine pour enregistrer cette sélection.",
      save: "AfriMed™ n’a pas pu enregistrer ces informations. Veuillez réessayer.",
    },
  },
  researchPending: {
    loading: "Chargement de Recherche • santé publique...",
    title: "Recherche • santé publique",
    ready: "Votre profil Recherche • santé publique est prêt.",
    membership: "L’accès à l’Institution et au programme apparaîtra ici lorsqu’il aura été attribué.",
    boundary: "Aucune autorité Institution, clinique, Santé régionale, administration de programme ou jeu de données n’a été attribuée. L’accès est activé uniquement par une adhésion au programme gouvernée.",
    reviewProfile: "Paramètres du profil",
    signOut: "Se déconnecter",
    signingOut: "Déconnexion...",
  },
});

// INSTITUTION_CLINICIAN_EXACT_MATCH_2026_08
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES["en-GB"], {
  institutionClinicians: {
    exactMatch: {
      action: "Find existing clinician",
      help: "Search with a verified email address or exact professional registration number before starting a new clinician form.",
      clearRegistration: "Clear professional registration number",
      invalidIdentifier: "Enter a valid email address; add the professional registration number where available.",
      searching: "Checking the verified clinician identity…",
      noMatch: "No verified Global clinician matched exactly. Continue with the Institution draft below.",
      identifierMismatch: "The verified email and professional registration do not identify the same clinician.",
      ambiguous: "These identifiers cannot be resolved to one clinician. No relationship was created.",
      alreadyLinked: "This clinician is already linked to the active Institution.",
      pending: "A pending relationship for this clinician already exists in the active Institution.",
      denied: "You are not authorised to search or link clinicians for this Institution.",
      failed: "The protected clinician lookup could not be completed. Try again.",
      verifiedClinician: "Verified clinician",
      registrationEnding: "Registration ending {ending}",
      useClinician: "Use this clinician",
      selected: "Selected",
    },
  },
});

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  institutionClinicians: {
    exactMatch: {
      action: "Rechercher un clinicien existant",
      help: "Recherchez avec une adresse e-mail vérifiée ou un numéro d’inscription professionnelle exact avant de commencer une nouvelle fiche clinicien.",
      clearRegistration: "Effacer le numéro d’inscription professionnelle",
      invalidIdentifier: "Saisissez une adresse e-mail valide et ajoutez le numéro d’inscription professionnelle lorsqu’il est disponible.",
      searching: "Vérification de l’identité du clinicien…",
      noMatch: "Aucun clinicien Global vérifié ne correspond exactement. Continuez avec la fiche provisoire de l’Institution ci-dessous.",
      identifierMismatch: "L’adresse e-mail vérifiée et l’inscription professionnelle n’identifient pas le même clinicien.",
      ambiguous: "Ces identifiants ne permettent pas d’identifier un seul clinicien. Aucune relation n’a été créée.",
      alreadyLinked: "Ce clinicien est déjà lié à l’Institution active.",
      pending: "Une relation en attente existe déjà pour ce clinicien dans l’Institution active.",
      denied: "Vous n’êtes pas autorisé à rechercher ou lier des cliniciens pour cette Institution.",
      failed: "La recherche protégée du clinicien n’a pas abouti. Réessayez.",
      verifiedClinician: "Clinicien vérifié",
      registrationEnding: "Inscription se terminant par {ending}",
      useClinician: "Utiliser ce clinicien",
      selected: "Sélectionné",
    },
  },
});

// INSTITUTION_CSV_COMPACT_FRENCH_CTA_RELEASE_2026_08
// Registry actions intentionally use dedicated action keys: the corresponding
// noun keys are also used by page headings and create surfaces.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES["en-GB"], {
  institutionCsv: { importCta: "Import CSV" },
  institutionRegistryCtas: {
    addFacility: "Add",
    addClinician: "Add clinician",
    addMember: "Add member",
    addPatient: "Add patient",
    addService: "Add service",
  },
});

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  institutionCsv: { importCta: "Importer un CSV" },
  institutionRegistryCtas: {
    addFacility: "Ajouter",
    addClinician: "Ajouter un clinicien",
    addMember: "Ajouter un membre",
    addPatient: "Ajouter un patient",
    addService: "Ajouter un service",
  },
  institutionClinicians: { specialtyPending: "Spécialité non indiquée" },
});

// INSTITUTION_GOVERNED_CSV_FRENCH_RELEASE_2026_08
// Shared import chrome is deliberately centralised: every Institution registry
// resolves the same accessible, persisted-outcome vocabulary through LanguageContext.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES["en-GB"], {
  institutionCsv: {
    importCta: "Import CSV", chooseCsv: "Choose CSV", close: "Close", cancel: "Cancel", continue: "Continue", commit: "Commit import", importing: "Importing…", notMapped: "Not mapped", matchColumns: "Match CSV columns", patientUploadGuidance: "Drop a CSV file here or choose one from your device.", patientReviewComplete: "Review complete. Commit uses the governed patient CSV RPC.",
    titles: { facility: "Import Medical Institutions from CSV", member: "Import Institution members from CSV", surveillance: "Import surveillance authority from CSV", clinician: "Import clinicians from CSV", patient: "Import patients from CSV", service: "Import services from CSV" },
    headersGuidance: "Headers are matched by name. Required: {fields}.", row: "Row {row}", missingRequired: "Missing required values", required: "{field} is required", ready: "Ready to commit", readyReview: "Ready to review", identityRequired: "Identity required", emailOrPhoneRequired: "Email or phone is required", persisted: "Persisted", patientSummary: "{persisted} persisted; {notPersisted} not persisted.", summary: "{created} created; {rejected} rejected; {skipped} skipped.",
    outcomes: { created: "Created", rejected: "Rejected", skipped: "Skipped", duplicate: "Duplicate" }, errors: { commit: "The import could not be committed.", invalidCsv: "Choose a CSV with a header and at least one patient row.", patientFacilityExact: "Row {row}: facility must match exactly one active Medical Institution." },
    fields: { name: "Name", type: "Type", address: "Address", city: "City", country: "Country", postalCode: "Postal code", phone: "Phone", email: "Email", website: "Website", description: "Description", firstName: "First name", lastName: "Last name", profession: "Profession", facility: "Facility", phoneCountry: "Country code", dateOfBirth: "Date of birth", gender: "Gender", registrationNumber: "Registration number", specialtyId: "Specialty ID", role: "Role", programmeId: "Programme ID", startsAt: "Start date", endsAt: "End date", reason: "Reason", patientReference: "Patient ID", title: "Title", nationalId: "National ID / Passport", currency: "Currency", price: "Price", status: "Status", notes: "Notes" },
  },
});

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  institutionCsv: {
    importCta: "Importer un CSV", chooseCsv: "Choisir un fichier CSV", close: "Fermer", cancel: "Annuler", continue: "Continuer", commit: "Valider l’import", importing: "Importation…", notMapped: "Non associé", matchColumns: "Associer les colonnes CSV", patientUploadGuidance: "Déposez un fichier CSV ici ou choisissez-en un sur votre appareil.", patientReviewComplete: "Vérification terminée. La validation utilise le RPC CSV patient gouverné.",
    titles: { facility: "Importer des établissements médicaux depuis un CSV", member: "Importer des membres Institution depuis un CSV", surveillance: "Importer des autorisations de surveillance depuis un CSV", clinician: "Importer des cliniciens depuis un CSV", patient: "Importer des patients depuis un CSV", service: "Importer des services depuis un CSV" },
    headersGuidance: "Les en-têtes sont associés par leur nom. Obligatoires : {fields}.", row: "Ligne {row}", missingRequired: "Valeurs obligatoires manquantes", required: "{field} est obligatoire", ready: "Prêt à être validé", readyReview: "Prêt à être vérifié", identityRequired: "Identité obligatoire", emailOrPhoneRequired: "L’e-mail ou le téléphone est obligatoire", persisted: "Enregistré", patientSummary: "{persisted} enregistrés ; {notPersisted} non enregistrés.", summary: "{created} créés ; {rejected} rejetés ; {skipped} ignorés.",
    outcomes: { created: "Créé", rejected: "Rejeté", skipped: "Ignoré", duplicate: "Doublon" }, errors: { commit: "L’import n’a pas pu être validé.", invalidCsv: "Choisissez un CSV avec un en-tête et au moins une ligne de patient.", patientFacilityExact: "Ligne {row} : l’établissement doit correspondre exactement à un seul établissement médical actif." },
    fields: { name: "Nom", type: "Type", address: "Adresse", city: "Ville", country: "Pays", postalCode: "Code postal", phone: "Téléphone", email: "E-mail", website: "Site web", description: "Description", firstName: "Prénom", lastName: "Nom", profession: "Profession", facility: "Établissement", phoneCountry: "Indicatif pays", dateOfBirth: "Date de naissance", gender: "Genre", registrationNumber: "Numéro d’enregistrement", specialtyId: "ID de spécialité", role: "Rôle", programmeId: "ID de programme", startsAt: "Date de début", endsAt: "Date de fin", reason: "Motif", patientReference: "ID patient", title: "Titre", nationalId: "ID national / Passeport", currency: "Devise", price: "Prix", status: "Statut", notes: "Remarques" },
  },
});

// Final resolved-path overrides for the Institution clinician editor. The
// editor aliases action keys, so these must live under `actions`.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  institutionClinicians: {
    actions: {
      searchOrAdd: "Rechercher ou ajouter",
    },
    startTyping: "Commencez à saisir pour rechercher ou ajouter une spécialité.",
    completionDate: "Date d’obtention",
    addQualificationCertificate: "Ajouter un certificat de qualification",
  },
});

// Active final French overrides for the canonical Institution create selectors.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  institutionPatientCreate: {
    searchOptions: "Rechercher des options…",
    searchOptionsOrAdd: "Rechercher des options ou ajouter",
    loadingOptions: "Chargement des options…",
    noOptionsFound: "Aucune option trouvée.",
    narrowResults: "Saisissez une recherche pour affiner les résultats.",
  },
  institutionClinicians: {
    noTitle: "Sans titre",
    searchOrAdd: "Rechercher ou ajouter",
    startTyping: "Commencez à saisir pour rechercher ou ajouter une spécialité.",
    completionDate: "Date d’obtention",
    addQualificationCertificate: "Ajouter un certificat de qualification",
    salutation: { dr: "Dr", pharmd: "PharmD", prof: "Pr", rn: "Rn.", mr: "Monsieur (M.)", mrs: "Madame (Mme)", ms: "Madame (Mme)", miss: "Mademoiselle (Mlle)", mme: "Madame (Mme)" },
  },
  institutionControlled: {
    laboratory_officer: "Responsable de laboratoire",
    facility_surveillance_officer: "Agent de surveillance de l’établissement",
    facility_supervisor: "Superviseur de l’établissement",
    clinician: "Clinicien",
    nurse: "Personnel infirmier",
    facility_staff: "Personnel de l’établissement",
    member: "Membre",
  },
});

// INSTITUTION_CREATE_SELECTOR_REPAIR_2026_08
// Display-only vocabulary for the canonical Institution create sheets. These
// keys intentionally never alter option values, stored clinical data or names.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  institutionControlled: {
    laboratory_officer: "Responsable de laboratoire",
    facility_surveillance_officer: "Agent de surveillance de l’établissement",
    facility_supervisor: "Superviseur de l’établissement",
    clinician: "Clinicien",
    nurse: "Personnel infirmier",
    facility_staff: "Personnel de l’établissement",
    member: "Membre",
  },
  institutionPatientCreate: {
    searchOptions: "Rechercher des options…",
    searchOptionsOrAdd: "Rechercher des options ou ajouter",
    loadingOptions: "Chargement des options…",
    noOptionsFound: "Aucune option trouvée.",
    narrowResults: "Saisissez une recherche pour affiner les résultats.",
  },
  institutionClinicians: {
    noTitle: "Sans titre",
    searchOrAdd: "Rechercher ou ajouter",
    startTyping: "Commencez à saisir pour rechercher ou ajouter une spécialité.",
    completionDate: "Date d’obtention",
    addQualificationCertificate: "Ajouter un certificat de qualification",
    salutation: {
      dr: "Dr",
      pharmd: "PharmD",
      prof: "Pr",
      rn: "Rn.",
      mr: "Monsieur (M.)",
      mrs: "Madame (Mme)",
      ms: "Madame (Mme)",
      miss: "Mademoiselle (Mlle)",
      mme: "Madame (Mme)",
    },
  },
});

// INSTITUTION_FRENCH_CREATE_CLOSURE_2026_08
// These are controlled labels and templates only. Facility, patient and signal
// values remain data supplied by their respective records and are not translated.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES["en-GB"], {
  institutionMedicalInstitutionCreate: {
    navigation: "Medical Institutions", back: "Back to Medical Institutions", title: "New Medical Institution", editLogo: "Edit Medical Institution logo",
    actions: { cancel: "Cancel", save: "Save", add: "Add", addMember: "+ Add member", edit: "Edit", searchOrAdd: "Search or add" },
    sections: { basicInformation: "Basic Information", member: "Member", specialties: "Specialties", address: "Address", availability: "Availability", domain: "Domain" },
    fields: { institutionName: "Institution Name*", institutionType: "Institution type *", selectInstitutionType: "Select institution type", facilityDescription: "Facility description", optionalDescription: "Optional concise description", address: "Address", zipCode: "ZIP Code", city: "City", country: "Country", openingHours: "Opening hours", domain: "Domain" },
    types: { hospital: "Hospital", clinic: "Clinic", laboratory: "Laboratory", pharmacy: "Pharmacy", imagingCentre: "Imaging centre", medicalCentre: "Medical centre", specialistCentre: "Specialist centre", other: "Other", otherAuthorisedHealthcareFacility: "Other authorised healthcare facility" },
    availabilitySheet: { title: "Availability", open24Hours: "Open 24 hours", openContinuously: "Open continuously every day", weekdays: "Weekdays", days: { sunday: "Sunday", monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday", thursday: "Thursday", friday: "Friday", saturday: "Saturday" }, enableOpen24Hours: "Enable open 24 hours", disableOpen24Hours: "Disable open 24 hours", needsOpeningAndClosingTime: "{day} needs an opening and closing time.", overnightUnsupported: "Overnight opening hours are not supported in this phase.", open24HoursHelp: "Individual opening hours are not used while the facility is open 24 hours.", openDay: "Open {day}", closeDay: "Close {day}", chooseOpeningTime: "Choose {day} opening time", chooseClosingTime: "Choose {day} closing time", openingTime: "{day} opening time", closingTime: "{day} closing time", opensAt: "On {day}, opens at", closesAt: "On {day}, closes at" },
    accountableCreator: "Accountable creator", setupPending: "Setup pending",
  },
  institutionPatientCreate: {
    title: "New Patient", avatar: "Patient avatar", changeAvatar: "Change patient image", select: "Select", selectDate: "Select date", searchOptions: "Search options", searchOptionsOrAdd: "Search options or add", selectedValues: "Selected values",
    actions: { cancel: "Cancel", save: "Save", saving: "Saving…", searching: "Searching…", findExisting: "Find existing patient", locating: "Locating…", useCurrentLocation: "Use my current location", usePatient: "Use this patient" },
    findExistingHelp: "Search with a complete Patient ID, email address or phone number before starting a new patient form.",
    sections: { basicInformation: "Basic Information", physicalAddress: "Physical Address", institutionRelationship: "Institution relationship", healthProfile: "Health profile" },
    fields: { patientId: "Patient ID", email: "Email", countryCode: "Country code", phoneNumber: "Phone number", title: "Title", firstName: "First name", lastName: "Last name", dateOfBirth: "Date of birth", gender: "Gender", nationalId: "National ID / Passport", address: "Address", city: "City", postalCode: "Postal / ZIP code", country: "Country", assignedFacilities: "Assigned facilities", lawfulBasis: "Lawful basis", languages: "Languages", bloodType: "Blood type", allergies: "Allergies", medicalConditions: "Medical conditions", medications: "Medications", organDonor: "Organ donor" },
    options: { gender: { Female: "Female", Male: "Male", "Non-binary": "Non-binary", "Prefer not to say": "Prefer not to say" }, donor: { Yes: "Yes", No: "No", "Not known": "Not known" }, lawfulBasis: { patient_present_intake_authorised: "Patient present and authorised", direct_care_registration: "Direct care registration", referral_documentation_received: "Referral documentation received", emergency_provisional_intake: "Emergency provisional intake", programme_enrolment_authority: "Public health programme authority" } },
    idDocumentOnFile: "ID document on file", notSupplied: "Not supplied", healthProfileAvailable: "The global health profile becomes available after this authorised relationship is saved.", patientLinked: "Patient linked to this Institution.", alreadyLinked: "This patient is already linked to this Institution.", saveError: "The patient relationship could not be saved. Please try again.", locationError: "We couldn’t find your address. Enter it manually or try again.", exactMatch: "Exact patient match",
    match: { needsContact: "Enter a complete Patient ID, email address or phone number to search.", searching: "Searching for an exact AfriMed patient match…", noMatch: "No verified AfriMed patient matches those details.", identifierMismatch: "The supplied Patient ID and contact details identify different patients.", error: "The patient search could not be completed. Please try again.", emailVerified: "Email verified", emailNotVerified: "Email not verified", phoneVerified: "Telephone verified", phoneNotVerified: "Telephone not verified", noIdDocument: "No ID document", verificationNotRecorded: "Verification not recorded" },
  },
  institutionClinicians: {
    add: "Add",
    noTitle: "No title",
    selectOption: "Select an option",
    status: {
      invitation_not_sent: "Invitation not sent",
      invitation_pending: "Invitation pending",
      declined: "Declined",
      expired: "Expired",
      revoked: "Revoked",
    },
  },
  regionalHealthSignals: { audit: { awaiting_review: "Awaiting review", awaiting_reviewReason: "Submitted signal is awaiting internal review.", activity: "Activity recorded" } },
});

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  institutionMedicalInstitutionCreate: {
    navigation: "Établissements médicaux", back: "Retour aux établissements médicaux", title: "Nouvel établissement médical", editLogo: "Modifier le logo de l’établissement médical",
    actions: { cancel: "Annuler", save: "Enregistrer", add: "Ajouter", addMember: "+ Ajouter un membre", edit: "Modifier", searchOrAdd: "Rechercher ou ajouter" },
    sections: { basicInformation: "Informations de base", member: "Membre", specialties: "Spécialités", address: "Adresse", availability: "Disponibilité", domain: "Domaine" },
    fields: { institutionName: "Nom de l’établissement*", institutionType: "Type d’établissement *", selectInstitutionType: "Sélectionner un type d’établissement", facilityDescription: "Description de l’établissement", optionalDescription: "Brève description facultative", address: "Adresse", zipCode: "Code postal", city: "Ville", country: "Pays", openingHours: "Heures d’ouverture", domain: "Domaine" },
    types: { hospital: "Hôpital", clinic: "Clinique", laboratory: "Laboratoire", pharmacy: "Pharmacie", imagingCentre: "Centre d’imagerie", medicalCentre: "Centre médical", specialistCentre: "Centre spécialisé", other: "Autre", otherAuthorisedHealthcareFacility: "Autre établissement de santé autorisé" },
    availabilitySheet: { title: "Disponibilité", open24Hours: "Ouvert 24 h/24", openContinuously: "Ouvert en continu tous les jours", weekdays: "Jours ouvrables", days: { sunday: "Dimanche", monday: "Lundi", tuesday: "Mardi", wednesday: "Mercredi", thursday: "Jeudi", friday: "Vendredi", saturday: "Samedi" }, enableOpen24Hours: "Activer l’ouverture 24 h/24", disableOpen24Hours: "Désactiver l’ouverture 24 h/24", needsOpeningAndClosingTime: "{day} nécessite une heure d’ouverture et de fermeture.", overnightUnsupported: "Les horaires de nuit ne sont pas pris en charge dans cette phase.", open24HoursHelp: "Les horaires individuels ne sont pas utilisés lorsque l’établissement est ouvert 24 h/24.", openDay: "Ouvrir {day}", closeDay: "Fermer {day}", chooseOpeningTime: "Choisir l’heure d’ouverture du {day}", chooseClosingTime: "Choisir l’heure de fermeture du {day}", openingTime: "Heure d’ouverture du {day}", closingTime: "Heure de fermeture du {day}", opensAt: "Le {day}, ouverture à", closesAt: "Le {day}, fermeture à" },
    accountableCreator: "Créateur responsable", setupPending: "Configuration en attente",
  },
  institutionPatientCreate: {
    title: "Nouveau patient", avatar: "Avatar du patient", changeAvatar: "Modifier l’image du patient", select: "Sélectionner", selectDate: "Sélectionner une date", searchOptions: "Rechercher des options", searchOptionsOrAdd: "Rechercher des options ou ajouter", selectedValues: "Valeurs sélectionnées",
    actions: { cancel: "Annuler", save: "Enregistrer", saving: "Enregistrement…", searching: "Recherche…", findExisting: "Rechercher un patient existant", locating: "Localisation…", useCurrentLocation: "Utiliser ma position actuelle", usePatient: "Utiliser ce patient" },
    findExistingHelp: "Recherchez avec un identifiant Patient complet, une adresse e-mail ou un numéro de téléphone avant de commencer un nouveau formulaire patient.",
    sections: { basicInformation: "Informations de base", physicalAddress: "Adresse physique", institutionRelationship: "Relation Institution", healthProfile: "Profil de santé" },
    fields: { patientId: "Identifiant Patient", email: "E-mail", countryCode: "Indicatif pays", phoneNumber: "Numéro de téléphone", title: "Titre", firstName: "Prénom", lastName: "Nom", dateOfBirth: "Date de naissance", gender: "Genre", nationalId: "Pièce d’identité nationale / Passeport", address: "Adresse", city: "Ville", postalCode: "Code postal", country: "Pays", assignedFacilities: "Établissements attribués", lawfulBasis: "Base légale", languages: "Langues", bloodType: "Groupe sanguin", allergies: "Allergies", medicalConditions: "Antécédents médicaux", medications: "Médicaments", organDonor: "Donneur d’organes" },
    options: { gender: { Female: "Femme", Male: "Homme", "Non-binary": "Non binaire", "Prefer not to say": "Préfère ne pas répondre" }, donor: { Yes: "Oui", No: "Non", "Not known": "Inconnu" }, lawfulBasis: { patient_present_intake_authorised: "Patient présent et autorisé", direct_care_registration: "Inscription pour soins directs", referral_documentation_received: "Documentation de référence reçue", emergency_provisional_intake: "Admission provisoire d’urgence", programme_enrolment_authority: "Autorité du programme de santé publique" } },
    idDocumentOnFile: "Document d’identité enregistré", notSupplied: "Non fourni", healthProfileAvailable: "Le profil de santé global devient disponible après l’enregistrement de cette relation autorisée.", patientLinked: "Patient associé à cette Institution.", alreadyLinked: "Ce patient est déjà associé à cette Institution.", saveError: "La relation patient n’a pas pu être enregistrée. Veuillez réessayer.", locationError: "Nous n’avons pas pu trouver votre adresse. Saisissez-la manuellement ou réessayez.", exactMatch: "Correspondance exacte du patient",
    match: { needsContact: "Saisissez un identifiant Patient complet, une adresse e-mail ou un numéro de téléphone pour rechercher.", searching: "Recherche d’une correspondance exacte de patient AfriMed…", noMatch: "Aucun patient AfriMed vérifié ne correspond à ces informations.", identifierMismatch: "L’identifiant Patient et les coordonnées fournis correspondent à des patients différents.", error: "La recherche de patient n’a pas pu être effectuée. Veuillez réessayer.", emailVerified: "E-mail vérifié", emailNotVerified: "E-mail non vérifié", phoneVerified: "Téléphone vérifié", phoneNotVerified: "Téléphone non vérifié", noIdDocument: "Aucun document d’identité", verificationNotRecorded: "Vérification non enregistrée" },
  },
  institutionClinicians: {
    add: "Ajouter",
    noTitle: "Sans titre",
    selectOption: "Sélectionner une option",
    status: {
      invitation_not_sent: "Invitation non envoyée",
      invitation_pending: "Invitation en attente",
      declined: "Refusée",
      expired: "Expirée",
      revoked: "Révoquée",
    },
  },
  regionalHealthSignals: { audit: { awaiting_review: "Soumis pour examen", awaiting_reviewReason: "Le signal soumis attend un examen interne.", activity: "Activité enregistrée" } },
});

// INSTITUTION_PAGE_SHELL_SPACING_AND_DASHBOARD_FRENCH_2026_08
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES["en-GB"], {
  institutionDashboard: {
    title: "Dashboard",
    legend: { appointment: "Appointment", virtual: "Virtual", pending: "Pending" },
  },
});

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  institutionDashboard: {
    title: "Tableau de bord",
    legend: { appointment: "Rendez-vous", virtual: "Virtuel", pending: "En attente" },
  },
});

// Keep the final active French bundle explicit: this is the navigation branch
// consumed by the Institution shell after all legacy message patches load.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  institutionShell: {
    navigation: {
      dashboard: "Tableau de bord",
      clinicians: "Cliniciens",
      members: "Membres",
      surveillanceTeam: "Équipe de surveillance",
      services: "Services",
      regionalHealth: "Santé régionale",
      patients: "Patients",
      settings: "Paramètres",
      help: "Aide",
    },
  },
});

// PATIENT_PROFILE_IDENTITY_HEADER_2026_07
// A governed patient reference belongs to the profile identity group, not the
// settings action list. The route already governs when the profile is visible.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES["en-GB"], {
  patient: { profile: { identity: { patientId: "Patient ID" } } },
});
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  patient: { profile: { identity: { patientId: "Identifiant Patient" } } },
});

// INSTITUTION_FRENCH_PARITY_2026_08
// Keep Institution navigation, facility context, membership, and governed
// authority controls on the shared language path. This deliberately does not
// alter any authority, facility, or reporting behaviour.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES["en-GB"], {
  institutionShell: {
    searchMedicalInstitutions: "Search medical institutions",
    facilitySearchError: "Medical Institutions could not be loaded. Try again.",
    switchInstitution: "Switch Institution: {name}",
    navigation: { medicalInstitutions: "Medical Institutions" },
    accessibility: { currentSection: "Current Institution section" },
  },
  institutionWorkspace: {
    loading: "Loading Medical Institutions…",
    loadError: "Medical Institutions could not be loaded. Try again.",
    retry: "Retry",
    filterAll: "All",
    filterFacilityType: "Facility type filter",
    filterFacilitiesByType: "Filter facilities by type",
    noFacilitiesMatch: "No Medical Institutions match this type.",
    addMedicalInstitution: "Add new Medical Institution",
    facilityNetworkTitle: "Build your medical institution network",
    facilityNetworkBody: "Medical Institutions are the hospitals, clinics, pharmacies, laboratories and other facilities governed by this organisation. Registering them enables future member, service, patient, reporting-readiness and Regional Health facility mapping.",
    searchResults: "{label} search results",
    showLess: "Show less",
    viewAll: "View all",
    noSearchResults: "No Medical Institutions or Members match “{query}”.",
    facilityLabel: "Facility: {name}",
    accessUnavailable: "Institution access unavailable",
    accessUnavailableBody: "Your account is not currently linked to an active Institution Administrator or Owner membership.",
    workspaceLoadTitle: "Institution workspace could not load",
    workspaceLoadBody: "Your authenticated session was established, but a required workspace request did not complete.",
    failedRequest: "Failed request:",
    response: "Response:",
    workspaceRequest: "Institution workspace request",
    workspaceLoadFallback: "The Institution workspace could not be loaded.",
    setupPending: "Setup pending",
    setupBody: "A Super Admin must create or assign this Institution before you can start using the control centre.",
  },
  institutionMembers: {
    title: "Institution Members",
    description: "Authorised administrative and operational members of this Institution.",
    emptyTitle: "No Institution Members yet",
    emptyBody: "Authorised administrative and operational members will appear here.",
    member: "Member",
    role: "Role",
    email: "Email",
    status: "Status",
    notSupplied: "Not supplied",
    close: "Close",
    inviteEvaluator: "Invite evaluator",
    evaluationInviteSent: "Evaluation reviewer invitation sent. Provider delivery evidence has been recorded.",
    evaluationInviteError: "AfriMed™ could not send this invitation. No membership was granted.",
    evaluationInviteTitle: "Invite evaluation reviewer",
    evaluationInviteDescription: "Creates a pending, exact-email invitation to this Institution. No membership is active until the invited person accepts.",
    cancel: "Cancel",
    sending: "Sending…",
    sendInvitation: "Send invitation",
    institutionRole: "Institution role",
    evaluationReviewer: "Evaluation reviewer",
  },
  institutionSurveillance: {
    title: "Institution surveillance team",
    description: "Membership does not grant surveillance authority until a scoped assignment is approved here.",
    addExistingMember: "Add existing member",
    members: "Members",
    facilityMappings: "Facility mappings",
    activeAuthority: "Active authority",
    programmes: "Programmes",
    tableLabel: "Institution members and surveillance authority",
    profile: "Profile",
    profession: "Profession",
    facilities: "Facilities",
    authority: "Surveillance authority",
    status: "Status",
    actions: "Actions",
    member: "Member",
    institutionRole: "Profession / Institution role",
    facilityAuthority: "Facility authority",
    programmeScope: "Programme scope",
    authorityActions: "Authority actions",
    noFacilityAuthority: "No facility authority",
    notAssigned: "Not assigned",
    assignAuthority: "Assign authority",
    manageAuthority: "Manage authority",
    moreActions: "More actions for {name}",
    viewHistory: "View authority history",
    removeAuthority: "Remove authority",
    suspendMember: "Suspend member",
    removeMember: "Remove member",
    noMembersTitle: "No institution members yet",
    noMembersBody: "Invite members before assigning surveillance authority.",
    history: "Authority history",
    appendOnly: "Append-only",
    noHistory: "No surveillance-authority events have been recorded for this institution yet.",
    assignDescription: "{name} · Membership does not grant surveillance authority.",
    cancel: "Cancel",
    assigning: "Assigning…",
    facility: "Facility",
    authorityRole: "Authority role",
    effectiveDate: "Effective date",
    optionalExpiry: "Optional expiry",
    reason: "Reason",
    newMember: "New Member",
    adding: "Adding…",
    addMember: "Add member",
    countryCode: "Country code",
    number: "Number",
    role: "Role",
    existingAccountNotice: "This links an existing verified account. Surveillance authority is assigned separately.",
    phoneNotice: "Phone verification is temporarily unavailable. A phone number does not grant access or authority.",
    assignedNotice: "Surveillance authority assigned.",
    removedNotice: "Surveillance authority removed.",
    memberRemovedNotice: "Institution member removed and authority revoked.",
    memberSuspendedNotice: "Institution member suspended and authority revoked.",
    accountAlreadyMember: "This verified account is already an active member of this Institution. Surveillance authority remains separately assigned.",
    accountLinked: "Existing verified account linked to the Institution. Surveillance authority remains unassigned until approved separately.",
    assignError: "Unable to assign surveillance authority.",
    removeError: "Unable to remove surveillance authority.",
    updateError: "Unable to update institution membership.",
    removeReason: "Reason for removing this surveillance authority",
    memberReason: "Reason for {action} this institution member",
  },
});

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  institutionShell: {
    searchMedicalInstitutions: "Rechercher des établissements médicaux",
    facilitySearchError: "Les établissements médicaux n’ont pas pu être chargés. Réessayez.",
    switchInstitution: "Changer d’Institution : {name}",
    navigation: { medicalInstitutions: "Établissements médicaux" },
    accessibility: { currentSection: "Section Institution actuelle" },
  },
  institutionWorkspace: {
    loading: "Chargement des établissements médicaux…",
    loadError: "Les établissements médicaux n’ont pas pu être chargés. Réessayez.",
    retry: "Réessayer",
    filterAll: "Tous",
    filterFacilityType: "Filtrer par type d’établissement",
    filterFacilitiesByType: "Filtrer les établissements par type",
    noFacilitiesMatch: "Aucun établissement médical ne correspond à ce type.",
    addMedicalInstitution: "Ajouter un établissement médical",
    facilityNetworkTitle: "Construisez votre réseau d’établissements médicaux",
    facilityNetworkBody: "Les établissements médicaux sont les hôpitaux, cliniques, pharmacies, laboratoires et autres sites gouvernés par cette organisation. Leur enregistrement prépare les futurs rattachements de membres, services, patients, état de préparation à la déclaration et Santé régionale.",
    searchResults: "Résultats de recherche : {label}",
    showLess: "Afficher moins",
    viewAll: "Tout afficher",
    noSearchResults: "Aucun établissement médical ni membre ne correspond à « {query} ».",
    facilityLabel: "Établissement : {name}",
    accessUnavailable: "Accès Institution indisponible",
    accessUnavailableBody: "Votre compte n’est actuellement lié à aucune adhésion active d’administrateur ou de propriétaire d’Institution.",
    workspaceLoadTitle: "L’espace Institution n’a pas pu être chargé",
    workspaceLoadBody: "Votre session authentifiée a été établie, mais une requête d’espace requise ne s’est pas terminée.",
    failedRequest: "Requête ayant échoué :",
    response: "Réponse :",
    workspaceRequest: "Requête d’espace Institution",
    workspaceLoadFallback: "L’espace Institution n’a pas pu être chargé.",
    setupPending: "Configuration en attente",
    setupBody: "Un Super Admin doit créer ou attribuer cette Institution avant que vous puissiez utiliser le centre de contrôle.",
  },
  institutionMembers: {
    title: "Membres de l’Institution",
    description: "Membres administratifs et opérationnels autorisés de cette Institution.",
    emptyTitle: "Aucun membre Institution pour le moment",
    emptyBody: "Les membres administratifs et opérationnels autorisés apparaîtront ici.",
    member: "Membre",
    role: "Rôle",
    email: "E-mail",
    status: "Statut",
    notSupplied: "Non fourni",
    close: "Fermer",
    inviteEvaluator: "Inviter un évaluateur",
    evaluationInviteSent: "L’invitation de l’évaluateur a été envoyée. La preuve de livraison du fournisseur a été enregistrée.",
    evaluationInviteError: "AfriMed™ n’a pas pu envoyer cette invitation. Aucune adhésion n’a été accordée.",
    evaluationInviteTitle: "Inviter un évaluateur",
    evaluationInviteDescription: "Crée une invitation en attente liée exactement à cette adresse e-mail. Aucune adhésion n’est active avant l’acceptation par la personne invitée.",
    cancel: "Annuler",
    sending: "Envoi…",
    sendInvitation: "Envoyer l’invitation",
    institutionRole: "Rôle Institution",
    evaluationReviewer: "Évaluateur",
  },
  institutionSurveillance: {
    title: "Équipe de surveillance de l’Institution",
    description: "L’adhésion n’accorde pas d’autorité de surveillance tant qu’une attribution limitée n’est pas approuvée ici.",
    addExistingMember: "Ajouter un membre existant",
    members: "Membres",
    facilityMappings: "Rattachements d’établissements",
    activeAuthority: "Autorité active",
    programmes: "Programmes",
    tableLabel: "Membres de l’Institution et autorité de surveillance",
    profile: "Profil",
    profession: "Profession",
    facilities: "Établissements",
    authority: "Autorité de surveillance",
    status: "Statut",
    actions: "Actions",
    member: "Membre",
    institutionRole: "Profession / rôle Institution",
    facilityAuthority: "Autorité de l’établissement",
    programmeScope: "Périmètre de programme",
    authorityActions: "Actions d’autorité",
    noFacilityAuthority: "Aucune autorité d’établissement",
    notAssigned: "Non attribué",
    assignAuthority: "Attribuer l’autorité",
    manageAuthority: "Gérer l’autorité",
    moreActions: "Autres actions pour {name}",
    viewHistory: "Voir l’historique des autorités",
    removeAuthority: "Retirer l’autorité",
    suspendMember: "Suspendre le membre",
    removeMember: "Retirer le membre",
    noMembersTitle: "Aucun membre Institution pour le moment",
    noMembersBody: "Invitez des membres avant d’attribuer une autorité de surveillance.",
    history: "Historique des autorités",
    appendOnly: "Ajout uniquement",
    noHistory: "Aucun événement d’autorité de surveillance n’a encore été enregistré pour cette Institution.",
    assignDescription: "{name} · L’adhésion n’accorde pas d’autorité de surveillance.",
    cancel: "Annuler",
    assigning: "Attribution…",
    facility: "Établissement",
    authorityRole: "Rôle d’autorité",
    effectiveDate: "Date de prise d’effet",
    optionalExpiry: "Expiration facultative",
    reason: "Motif",
    newMember: "Nouveau membre",
    adding: "Ajout…",
    addMember: "Ajouter le membre",
    countryCode: "Indicatif pays",
    number: "Numéro",
    role: "Rôle",
    existingAccountNotice: "Cette action lie un compte vérifié existant. L’autorité de surveillance est attribuée séparément.",
    phoneNotice: "La vérification du téléphone est temporairement indisponible. Un numéro de téléphone n’accorde ni accès ni autorité.",
    assignedNotice: "Autorité de surveillance attribuée.",
    removedNotice: "Autorité de surveillance retirée.",
    memberRemovedNotice: "Membre Institution retiré et autorité révoquée.",
    memberSuspendedNotice: "Membre Institution suspendu et autorité révoquée.",
    accountAlreadyMember: "Ce compte vérifié est déjà un membre actif de cette Institution. L’autorité de surveillance reste attribuée séparément.",
    accountLinked: "Compte vérifié existant lié à l’Institution. L’autorité de surveillance reste non attribuée jusqu’à une approbation distincte.",
    assignError: "Impossible d’attribuer l’autorité de surveillance.",
    removeError: "Impossible de retirer l’autorité de surveillance.",
    updateError: "Impossible de mettre à jour l’adhésion Institution.",
    removeReason: "Motif du retrait de cette autorité de surveillance",
    memberReason: "Motif pour {action} ce membre Institution",
  },
});

// LOGOUT_FEEDBACK_LOCALISATION_2026_07
// The shared desktop and settings logout dialogs use this single feedback CTA
// key. Keep both active-language values explicit so switching languages updates
// the action without falling back to English.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES["en-GB"], {
  feedback: { logout: { leaveFeedback: "Leave feedback" } },
  navigation: {
    menu: {
      logoutConfirmTitle: "Before you go, would you like to leave quick feedback?",
      logoutConfirmBody:
        "It is optional, but staging notes help us improve AfriMed™ before production.",
    },
  },
  institutionShell: {
    logout: {
      title: "Are you sure you want to log out?",
      body: "You’ll need to sign in again to continue.",
      cancel: "Cancel",
      confirm: "Log out",
      loggingOut: "Logging out…",
    },
  },
});

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  feedback: { logout: { leaveFeedback: "Donner votre avis" } },
  navigation: {
    menu: {
      logoutConfirmTitle: "Voulez-vous vraiment vous déconnecter ?",
      logoutConfirmBody: "Vous devrez vous reconnecter pour continuer.",
    },
  },
  institutionShell: {
    logout: {
      title: "Voulez-vous vraiment vous déconnecter ?",
      body: "Vous devrez vous reconnecter pour continuer.",
      cancel: "Annuler",
      confirm: "Se déconnecter",
      loggingOut: "Déconnexion…",
    },
  },
});

// INSTITUTION_CLINICIAN_INVITATION_NOTIFICATION_2026_07
// Keep invitation lifecycle copy in the established language bundle so the
// canonical Notifications detail never falls back to JSX literals.
const INSTITUTION_CLINICIAN_INVITATION_EN = {
  institutionInvitation: {
    notification: {
      title: "Institution clinician invitation",
      summary: "{institution} has invited you to join its clinician team.",
      acceptedSummary: "You accepted {institution}'s invitation to join its clinician team.",
      declinedSummary: "You declined {institution}'s invitation to join its clinician team.",
      expiredSummary: "This invitation to join {institution}'s clinician team has expired.",
      revokedSummary: "{institution} revoked this invitation to join its clinician team.",
    },
    status: { pendingResponse: "Pending your response", accepted: "Accepted", declined: "Declined", expired: "Expired", revoked: "Revoked", unavailable: "Unavailable" },
    fields: { institution: "Institution", relationship: "Requested relationship", facility: "Facility", parentWorkspace: "Parent workspace", invitedBy: "Invited by", invitationDate: "Invitation date", expiry: "Expiry", status: "Status", unavailable: "Not available", noFacilities: "No facility relationship is included." },
    relationship: "Institution clinician relationship",
    actions: { accept: "Accept", decline: "Decline" },
    toast: { accepted: "Institution clinician relationship accepted", declined: "Institution clinician relationship declined" },
    errors: { signIn: "Please sign in again to review this invitation.", wrongRecipient: "This invitation is addressed to a different AfriMed™ account.", notFound: "This invitation is no longer available.", review: "The invitation could not be loaded. Please try again.", refresh: "The invitation was updated, but its latest details could not be refreshed. Please refresh this page." },
  },
};

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES["en-GB"], INSTITUTION_CLINICIAN_INVITATION_EN);
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  institutionInvitation: {
    notification: {
      title: "Invitation de clinicien Institution",
      summary: "{institution} vous a invité à rejoindre son équipe de cliniciens.",
      acceptedSummary: "Vous avez accepté l’invitation de {institution} à rejoindre son équipe de cliniciens.",
      declinedSummary: "Vous avez refusé l’invitation de {institution} à rejoindre son équipe de cliniciens.",
      expiredSummary: "Cette invitation à rejoindre l’équipe de cliniciens de {institution} a expiré.",
      revokedSummary: "{institution} a révoqué cette invitation à rejoindre son équipe de cliniciens.",
    },
    status: { pendingResponse: "En attente de votre réponse", accepted: "Acceptée", declined: "Refusée", expired: "Expirée", revoked: "Révoquée", unavailable: "Indisponible" },
    fields: { institution: "Institution", relationship: "Relation demandée", facility: "Établissement", parentWorkspace: "Espace parent", invitedBy: "Invité par", invitationDate: "Date de l’invitation", expiry: "Expiration", status: "Statut", unavailable: "Non disponible", noFacilities: "Aucune relation d’établissement n’est incluse." },
    relationship: "Relation de clinicien Institution",
    actions: { accept: "Accepter", decline: "Refuser" },
    toast: { accepted: "Relation de clinicien Institution acceptée", declined: "Relation de clinicien Institution refusée" },
    errors: { signIn: "Veuillez vous reconnecter pour consulter cette invitation.", wrongRecipient: "Cette invitation est adressée à un autre compte AfriMed™.", notFound: "Cette invitation n’est plus disponible.", review: "L’invitation n’a pas pu être chargée. Veuillez réessayer.", refresh: "L’invitation a été mise à jour, mais ses derniers détails n’ont pas pu être actualisés. Veuillez actualiser cette page." },
  },
});

// These active bundles retain the same complete key shape. Their existing
// English fallback policy remains explicit rather than emitting missing-key
// warnings for the governed invitation route.
for (const languageCode of ["pt", "de", "sn", "zu"]) {
  deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES[languageCode], INSTITUTION_CLINICIAN_INVITATION_EN);
}

// NOTIFICATIONS_TODAY_TIME_2026_07
// The canonical detail can appear for every activated language. Keep its date
// grouping key present in the active bundles instead of relying on a runtime
// fallback that emits a missing-key warning.
for (const languageCode of ["sn", "zu"]) {
  deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES[languageCode], {
    notifications: { time: { todayAt: "Today {time}" } },
  });
}

// DOCTOR_FORMATION_AFRIMED_DATEPICKER_2026_07
// Keep the Formation date controls on the shared date-picker vocabulary.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES["en-GB"], {
  common: { ok: "OK", cancel: "Cancel", datePicker: { selectDate: "Select date" } },
  settings: {
    services: {
      serviceName: "Service name",
      serviceNamePlaceholder: "Enter the service name",
    },
    edit: {
      formation: {
        startLabel: "Start date",
        endLabel: "Completion date",
        completionBeforeStart: "Completion date cannot be before Start date.",
        startAfterCompletion: "Start date cannot be after Completion date.",
      },
    },
  },
});

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  common: { ok: "OK", cancel: "Annuler", datePicker: { selectDate: "Sélectionner une date" } },
  settings: {
    services: {
      serviceName: "Nom du service",
      serviceNamePlaceholder: "Saisissez le nom du service",
      saved: "Service enregistré",
      readyToSave: "Service ajouté. Enregistrez vos services pour le conserver.",
      saveFailed: "Le service n’a pas pu être enregistré. Veuillez réessayer.",
    },
    edit: {
      formation: {
        startLabel: "Date de début",
        endLabel: "Date de fin",
        completionBeforeStart: "La date de fin ne peut pas précéder la date de début.",
        startAfterCompletion: "La date de début ne peut pas être postérieure à la date de fin.",
      },
    },
  },
});

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES["en-GB"], {
  settings: {
    services: {
      saved: "Service saved.",
      readyToSave: "Service added. Save your services to keep it.",
      saveFailed: "The service could not be saved. Please try again.",
    },
  },
});


// FRANCO_VISIBLE_SURFACE_CORRECTION_2026_07
// Final French overrides for visible settings copy that was still leaking English
// after language selection.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  common: {
    comingSoon: "Bientôt disponible",
  },
  settings: {
    language: {
      availableNow: "Disponible maintenant",
      availableNowCopy:
        "Choisissez la langue actuelle de l’application AfriMed™ pour cet appareil et ce compte.",
      comingSoonTitle: "Bientôt disponible",
      comingSoonCopy:
        "Les langues d’Afrique australe sont préparées avec soin pour un déploiement sûr dans le contexte des soins de santé.",
    },
  },
});

// INSTITUTION_MOBILE_FRENCH_ENUM_CLOSURE_EFFECTIVE_FINAL_2026_08
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES["en-GB"], {
  institutionPatientCreate: {
    select: "Select an option",
    searchOptions: "Search options…",
  },
  institutionServices: {
    selectOption: "Select an option",
    searchOptions: "Search options…",
  },
});

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  institutionPatientCreate: {
    select: "Sélectionner une option",
    searchOptions: "Rechercher des options…",
  },
  institutionServices: {
    selectOption: "Sélectionner une option",
    searchOptions: "Rechercher des options…",
  },
});

// INSTITUTION_CREATE_SELECTOR_RESOLVED_PATHS_FINAL_2026_08
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {
  institutionClinicians: {
    actions: { searchOrAdd: "Rechercher ou ajouter" },
    startTyping: "Commencez à saisir pour rechercher ou ajouter une spécialité.",
    completionDate: "Date d’obtention",
    addQualificationCertificate: "Ajouter un certificat de qualification",
  },
});

// Current settlement contract overrides historic payment terminology in each locale.
for (const code of Object.keys(LANGUAGE_MESSAGE_PATCHES)) {
  const fr = code.startsWith('fr');
  deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES[code], {
    settlement: { commission: fr ? 'Commission AfriMed' : 'AfriMed commission', commissionCopy: fr ? commissionCopy.fr : commissionCopy.en },
    settings: { preferences: { plans: { doctor: {
      commissionTitle: fr ? commissionCopy.fr : commissionCopy.en,
      commissionBody: fr ? "La capture suit la réalisation du service. Le crédit bancaire suit les délais externes de règlement." : "Capture follows completed service. Bank credit follows external provider settlement timelines.",
      stripeConnected: fr ? "Vérifié" : "Verified",
      stripePending: fr ? "À vérifier" : "Not verified",
      stripeStatus: fr ? 'Versements' : 'Payouts',
      stripeTitle: fr ? 'Configuration des versements' : 'Payout readiness',
      stripeBody: fr ? 'Enregistrez et vérifiez votre compte de versement avant les consultations payantes.' : 'Save and verify your payout account before paid consultations.',
      stripeStatusHint: fr ? 'La destination doit être vérifiée avant les consultations payantes.' : 'A verified destination is required before paid consultations.',
      connectStripeCta: fr ? 'Gérer les versements' : 'Manage payout',
      connectStripeManage: fr ? 'Gérer les versements' : 'Manage payout',
    } } } },
  });
}

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, { legal: { terms: { sections: { paymentsAndBilling: { title: 'Paiements et facturation', body: {
  '1': commissionCopy.fr,
  '2': 'AfriMed™ agit en tant que plateforme et facilitateur. La commission s’applique dès la première consultation payante admissible. Lorsque cette possibilité est prise en charge, les fonds peuvent être autorisés avant le service ; la capture est conditionnée par la confirmation faisant autorité de sa réalisation. Une annulation avant capture peut libérer l’autorisation. Après capture, les remboursements suivent les procédures du prestataire de paiement et de remboursement applicables.',
  '3': 'Le prestataire de soins perçoit le montant du service après déduction de la commission et des frais de traitement. Le crédit bancaire dépend des délais externes du prestataire de paiement et des banques ; aucun versement immédiat n’est garanti. AfriMed™ n’exploite ni portefeuille interne pour les cliniciens ni séquestre.'
} } } } } });
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES['en-GB'], { legal: { terms: { sections: { paymentsAndBilling: { body: {
 '1': commissionCopy.en,
 '2': 'AfriMed™ acts as a platform and facilitator. Commission applies from the first qualifying paid consultation. Where supported, funds may be authorized before service; capture is governed by authoritative service completion. Cancellation before capture may release the authorization. Refunds after capture follow the applicable provider and reversal process.',
 '3': 'The healthcare provider earns the service amount subject to commission and provider processing fees. Bank credit follows external provider and banking settlement timelines; immediate settlement is not promised. AfriMed™ does not operate an internal clinician wallet or escrow.'
} } } } } });

deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, { settlement: { termsEffectiveDate: "21 septembre 2026" } });

// Current payout onboarding contract; retired processor onboarding is not a readiness signal.
for (const [locale, messages] of Object.entries(LANGUAGE_MESSAGE_PATCHES)) {
  const fr = locale.startsWith("fr");
  deepMergeLanguagePatch(messages, {fola:{readiness:{doctor:{payoutCurrentDetail: fr
    ? "Utilisez Gérer les versements pour vérifier votre compte bancaire, puis choisissez et enregistrez les moyens de paiement acceptés."
    : "Use Manage payout to verify your receiving bank account, then select and save your accepted payment methods."}}}});
}

// Returning-account login uses the same French labels as the social entry.
deepMergeLanguagePatch(LANGUAGE_MESSAGE_PATCHES.fr, {auth: {login: {password: {
  password: 'Mot de passe',
  signIn: 'Se connecter',
  signingIn: 'Connexion sécurisée en cours…',
  signingInButton: 'Connexion en cours…',
  back: 'Retour à l’accueil',
  forgot: 'Mot de passe oublié ?',
  secureLinkFallback: 'Vous n’en avez pas ? Recevoir un lien de connexion sécurisé par e-mail',
  enterCredentials: 'Saisissez votre e-mail et votre mot de passe pour continuer.',
  invalidCredentials: 'La connexion a échoué. Vérifiez votre e-mail et votre mot de passe, puis réessayez ou contactez l’assistance.',
  genericError: 'La connexion a échoué. Réessayez ou contactez l’assistance.',
  rateLimit: 'Trop de tentatives. Patientez un instant avant de réessayer.',
}}}});
